// features/workstations/tabs/qqq.js
// ═══════════════════════════════════════════════════════════
// QQQ SESSION BRIEF — day-trading-only tab
// ═══════════════════════════════════════════════════════════
// Phase 1: exact layout from Tim's HTML mockup rendered as
// React. All data is static placeholder values from the mockup
// so the layout can be reviewed before each section is wired
// to its real data source.
//
// Data wiring plan (subsequent phases):
//   Section 1 Price         — Yahoo Finance / FMP quote
//   Section 2 Environment   — CBOE VIX term structure + FRED yields + FMP futures
//   Section 3 Structure     — FlashAlpha /v1/exposure/gex|levels (already wired)
//   Section 4 Flow          — CBOE PCR + FMP sector performance
//   Section 5 Risk          — workstation_calendar_events (already wired)
//   Section 6 Verdict       — synthesized 8-signal score from 1-5
//   Section 7 Session Log   — new table: workstation_session_logs
//   Section 8 Patterns      — rollup from session_logs + trade history
//
// Structure: one component per numbered section. Read top-to-
// bottom as Tim intended. All styling lives in tab-qqq.css.
// ═══════════════════════════════════════════════════════════

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  buildNarratives,
  vixTermPill,
  yieldSpreadFooter,
  sectorLeadershipFooter,
} from '../../../lib/qqq/narratives';
import { suggestDrivers } from '../../../lib/qqq/driver-suggest';

// Phase 2b-1 + 2b-2 — new TODAY-tab dashboard cards. Rendered at the
// top of the TODAY block ABOVE the legacy cards so Tim sees the new
// awareness layer (Macro Frame Zone, Composite, Yellow Candle state,
// F15 retest, Vehicle Decision, Economic Calendar, News Feed,
// Open Observations) without losing the PatternTrackerPanel /
// OpeningCandleCard / sidebar he actively uses today. Phase 4 of the
// rebuild (per docs/QQQ_DASHBOARD_REBUILD_SPEC.md) will retire the
// legacy cards once the new ones are validated in production.
import NewTodayTab from '../qqq/today/index.js';
import PreBreakoutPatternsView from '../qqq/intel/pre-breakout-patterns.js';
import QqqPredictionTab from '../qqq/prediction/index.js';
import QqqNetworkTab from '../qqq/network/index.js';
// today-cards.css and pre-breakout-patterns.css are imported globally
// in pages/_app.js (Next.js Pages Router restricts non-module CSS to _app).

// Context carries the live payload from /api/workstations/qqq-session-brief.
// Defaults to null and useQqqData() falls back to PLACEHOLDER so sub-
// components never crash while the fetch is in flight — they just render
// static data that instantly swaps to live once the request resolves.
const QqqDataContext = createContext(null);

// ─── Marker → JSX renderer ─────────────────────────────────
// Narratives in lib/qqq/narratives.js return plain strings so they
// can be generated server-side. The client parses these markers to
// React nodes here. Supported syntax:
//   **bold text**      → <strong>
//   <up>green text</up>→ <span class="qqq-up">
//   <dn>red text</dn>  → <span class="qqq-dn">
//   <warn>...</warn>   → <span class="qqq-warn">
function renderMarkers(text) {
  if (!text) return null;
  // Single regex catches all four marker types in order of appearance.
  const re = /(\*\*[^*]+\*\*)|(<up>[^<]+<\/up>)|(<dn>[^<]+<\/dn>)|(<warn>[^<]+<\/warn>)/g;
  const out = [];
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      out.push(<strong key={`k${key++}`}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('<up>')) {
      out.push(<span key={`k${key++}`} className="qqq-up">{tok.slice(4, -5)}</span>);
    } else if (tok.startsWith('<dn>')) {
      out.push(<span key={`k${key++}`} className="qqq-dn">{tok.slice(4, -5)}</span>);
    } else if (tok.startsWith('<warn>')) {
      out.push(<span key={`k${key++}`} className="qqq-warn">{tok.slice(6, -7)}</span>);
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// ═══════════════════════════════════════════════════════════
// STATIC PLACEHOLDER DATA — Phase 1
// Every value here is wired to a real source in later phases.
// ═══════════════════════════════════════════════════════════
const PLACEHOLDER = {
  asOf: 'Apr 22 · Pre-Session',
  time: '8:47 AM ET',
  prices: {
    qqq: { price: '438.24', change: '+0.61%', changeDollar: '+2.67', close: '435.57', volume: '17.4' },
    spy: { price: '551.18', change: '+0.48%', changeDollar: '+2.64', close: '548.54', note: 'Broad rally' },
  },
  levels: { pdh: '435.57', pdl: '432.10', gapFlip: '435.00', qqqNow: '438.24' },
  vixTerm: [
    { label: '9-Day',   value: '14.2', pct: 52 },
    { label: '30-Day',  value: '17.4', pct: 67 },
    { label: '3-Month', value: '19.8', pct: 75 },
    { label: '6-Month', value: '21.2', pct: 80 },
  ],
  futures: [
    { label: 'NQ',     value: '+0.58%', tone: 'up' },
    { label: 'ES',     value: '+0.44%', tone: 'up' },
    { label: 'DAX',    value: '+0.72%', tone: 'up' },
    { label: 'DXY $',  value: '−0.22%', tone: 'dn' },
  ],
  yieldCurve: [
    { label: '2Y',  value: '4.82%', tone: 'inv' },
    { label: '5Y',  value: '4.51%', tone: 'inv' },
    { label: '10Y', value: '4.38%', tone: null },
    { label: '20Y', value: '4.64%', tone: null },
    { label: '30Y', value: '4.76%', tone: null },
  ],
  gamma: { flip: '435.00', callWall: '442.00', putWall: '430.00' },
  pcr: { value: '0.82', tag: 'Call-heavy · institutions positioned for upside', needleLeft: '41%' },
  sectors: [
    { name: 'Technology', value: '+0.84%', tone: 'up', fillTone: 'sage', pct: 78 },
    { name: 'Comm Svcs',  value: '+0.67%', tone: 'up', fillTone: 'sage', pct: 61 },
    { name: 'Financials', value: '+0.47%', tone: 'up', fillTone: 'sage', pct: 43 },
    { name: 'Energy',     value: '−0.32%', tone: 'dn', fillTone: 'terra', pct: 29 },
  ],
  events: [
    { time: '9:30 AM',    name: 'Window opens · Clean environment',     note: 'Green ✓',           sev: 'lo' },
    { time: '10:00 AM',   name: 'Existing Home Sales',                  note: 'Medium · in window', sev: 'md' },
    { time: '11:30 AM',   name: 'Window closes',                        note: 'Exit all positions', sev: 'lo' },
    { time: '2:00 PM',    name: 'FOMC Meeting Minutes',                 note: 'After window · High', sev: 'hi' },
    { time: 'After Close',name: 'TSLA Earnings · QQQ 3.8% weight',      note: 'After window',       sev: 'lo' },
  ],
  verdict: {
    eyebrow: 'QQQ / SPY · First 2 Hours · Apr 22',
    direction: 'Bullish',
    directionTone: 'bull',
    scoreN: 6,
    scoreD: 8,
    confidence: 'Moderate confidence',
    confidenceTone: 'mod',
    forces: [
      { text: '↑ Above PDH',     tone: 'bull' },
      { text: '↑ Gamma positive',tone: 'bull' },
      { text: '↑ Contango VIX',  tone: 'bull' },
      { text: '↑ PCR bullish',   tone: 'bull' },
      { text: '↑ Tech leading',  tone: 'bull' },
      { text: '→ Dollar (mild)', tone: 'neutral' },
      { text: '→ Yield curve',   tone: 'neutral' },
      { text: '↑ Clean window',  tone: 'bull' },
    ],
    summary: (
      <>
        All five analytical layers align bullish. Price is above PDH. Gamma structure
        suppresses volatility. VIX fear is resolving. Call flow dominates. Tech leads.{' '}
        <strong>Call wall at 442 is the session target.</strong>{' '}
        <span className="qqq-warn">Do not carry past 1:45pm — FOMC at 2pm.</span>
      </>
    ),
    window: 'Window: 9:30 – 11:30 AM ET',
    actionLabel: 'Call setups active ↑',
    sizingActiveIdx: 1,
    sizingOptions: [
      { score: '6–8 signals', size: 'Full size' },
      { score: 'Today: 6',    size: 'Full size' },
      { score: '4–5 signals', size: '75% size' },
      { score: '0–3 signals', size: 'Skip / 50%' },
    ],
  },
  recentLog: [
    { date: 'Apr 21', outcome: '↑ +0.84%', tone: 'up', reason: 'ORB + sector rotation into tech',  bias: 'y', pattern: 'Squeeze+ORB' },
    { date: 'Apr 18', outcome: '↓ −0.52%', tone: 'dn', reason: 'FOMC-adjacent news reversal',       bias: 'n', pattern: 'YC v3 false' },
    { date: 'Apr 17', outcome: '↑ +1.12%', tone: 'up', reason: 'Gamma flip break structural',       bias: 'y', pattern: 'IFVG+Squeeze' },
    { date: 'Apr 16', outcome: '↓ −0.38%', tone: 'dn', reason: 'PDH rejection · dollar spike',      bias: 'n', pattern: 'Conv Engine' },
    { date: 'Apr 15', outcome: '↑ +0.67%', tone: 'up', reason: 'Opening drive · tech led +1.1%',    bias: 'y', pattern: 'Squeeze+ORB' },
  ],
  // Day-by-day history — surfaces in the sidebar under the verdict.
  // Each row is one prior qqq_session_briefs row (verdict generated
  // that morning + resolution filled that afternoon).
  //
  // `reason` — one-line summary of the drivers from the 7-section
  //   analysis that led to the verdict (what the forces said).
  // `override` — only on misses: what overrode the stated bias
  //   (from the resolution + chart analysis). This is the highest-
  //   signal data for pattern learning — what broke the model.
  sidebarHistory: [
    { date: 'Apr 22', direction: 'bull',  score: '6/8', outcome: null,      bias: null,  status: 'pending',
      reason: 'above PDH · gamma+ · contango · tech lead · clean win',  override: null },
    { date: 'Apr 21', direction: 'bull',  score: '7/8', outcome: '+0.84%',  bias: 'y',   status: 'resolved',
      reason: 'gamma+ · contango · PDH break · tech lead',              override: null },
    { date: 'Apr 18', direction: 'bull',  score: '6/8', outcome: '−0.52%',  bias: 'n',   status: 'resolved',
      reason: 'PDH break · gamma+ · VIX normal',                        override: 'FOMC news spike overrode' },
    { date: 'Apr 17', direction: 'bull',  score: '5/8', outcome: '+1.12%',  bias: 'y',   status: 'resolved',
      reason: 'gamma flip break · structural move',                     override: null },
    { date: 'Apr 16', direction: 'bear',  score: '5/8', outcome: '−0.38%',  bias: 'y',   status: 'resolved',
      reason: 'PDH rejection · dollar spike · yield up',                override: null },
    { date: 'Apr 15', direction: 'bull',  score: '6/8', outcome: '+0.67%',  bias: 'y',   status: 'resolved',
      reason: 'opening drive · tech led +1.1% · gamma+',                override: null },
    { date: 'Apr 14', direction: 'mixed', score: '4/8', outcome: '+0.12%',  bias: 'n',   status: 'resolved',
      reason: 'split signals · no conviction',                          override: 'should have skipped — chop regime' },
    { date: 'Apr 11', direction: 'bear',  score: '6/8', outcome: '−0.94%',  bias: 'y',   status: 'resolved',
      reason: 'below gamma flip · VIX backwardation · dollar strong',   override: null },
    { date: 'Apr 10', direction: 'bull',  score: '7/8', outcome: '+1.41%',  bias: 'y',   status: 'resolved',
      reason: 'gap up + squeeze · tech leading · PCR 0.7',              override: null },
    { date: 'Apr 09', direction: 'bull',  score: '5/8', outcome: '−0.26%',  bias: 'n',   status: 'resolved',
      reason: 'above PDH · gamma+ but neutral sector rotation',         override: 'sector rotation into defensive mid-session' },
    { date: 'Apr 08', direction: 'bull',  score: '6/8', outcome: '+0.58%',  bias: 'y',   status: 'resolved',
      reason: 'PDH reclaim · contango · call wall below',               override: null },
    { date: 'Apr 07', direction: 'bear',  score: '7/8', outcome: '−1.18%',  bias: 'y',   status: 'resolved',
      reason: 'gap down · put wall broke · dollar breakout',            override: null },
    { date: 'Apr 04', direction: 'bull',  score: '6/8', outcome: '+0.43%',  bias: 'y',   status: 'resolved',
      reason: 'gamma+ · tech lead · VIX resolving',                     override: null },
    { date: 'Apr 03', direction: 'bull',  score: '5/8', outcome: '+0.29%',  bias: 'y',   status: 'resolved',
      reason: 'above PDH · PCR bullish · mild environment',             override: null },
  ],
  sidebarHistoryRollup: {
    total: 13,           // resolved days only
    hits: 10,
    hitRate: '77%',
    window: 'last 14 days',
  },
  // ═══ EXECUTION PLAN — Batch A ═══
  // Turns the verdict into a rules-based plan. Every field here
  // is derived from the 7-section forces + today's specific levels.
  // This is what makes the page an actionable briefing instead of
  // a read. Wired in Phase 2 from the forces + user rules.
  executionPlan: {
    stance: 'Call setups only — long bias from gamma+ regime',
    stanceTone: 'bull',
    entryTriggers: [
      { priority: 'primary',   condition: '438.30 break (5-min high)', filter: 'volume > 15k contracts first 5 min' },
      { priority: 'secondary', condition: 'Pullback to 437.80',         filter: 'VWAP reclaim + 5-min hold' },
    ],
    stop: {
      price: '437.00',
      riskR: '0.3R',
      accountRiskPct: '0.5%',
      rationale: 'Below gap flip 435 breaks the structural setup — invalidation, not noise',
    },
    scaleLadder: [
      { tranche: 'T1', share: '50%', price: '440.00',  rMultiple: '+0.7R',  note: null },
      { tranche: 'T2', share: '30%', price: '441.00',  rMultiple: '+1.1R',  note: 'move stop to BE after T1' },
      { tranche: 'T3', share: '20%', price: '442.00+', rMultiple: 'runner', note: 'trail behind prior target' },
    ],
    hinges: [
      { condition: 'QQQ below 435.00 (gamma flip)',     impact: 'regime flip — close longs, reverse bias',    severity: 'hi' },
      { condition: 'VIX spikes above 19.0',              impact: 'vol breakout — reduce size 50%',             severity: 'md' },
      { condition: 'DXY breaks +0.5% intraday',          impact: 'risk-off overlay — take partials',           severity: 'md' },
      { condition: 'FOMC-adjacent news 10-11am',         impact: 'news override — flatten, wait for 11:30',    severity: 'hi' },
    ],
    rules: {
      maxEntries: '2',
      maxEntriesNote: 'Skip if first stops out AND a hinge condition fires',
      maxHold: '11:30 AM ET',
      maxHoldNote: 'Window closes — exit all regardless of P&L',
      reentry: 'Only on second trigger re-forming. No chasing a failed break.',
    },
  },
  sidebarEdgeEmerging: {
    setup: 'Squeeze + Positive Gamma',
    rate: '89%',
    sample: '8 of 9',
    body: 'Your highest-conviction combination in the last 30 days. When both fire on the same open, QQQ has closed the session with bias hit in 8 of 9 cases. Strong candidate for size when it reappears.',
  },
  patterns: {
    stats: [
      { n: '14',   label: 'Sessions logged', tone: 'gold' },
      { n: '71%',  label: 'Win rate',        tone: 'sage' },
      { n: '79%',  label: 'Bias accuracy',   tone: 'teal' },
      { n: '2.8×', label: 'Avg R:R',         tone: 'mid' },
    ],
    chartPatterns: [
      { name: 'Squeeze Breakout + ORB', note: 'Best in positive gamma · high conviction', sessions: '8 sessions', rate: '87%', tone: 'hi' },
      { name: 'IFVG + Squeeze combo',   note: 'Works in both gamma regimes',                sessions: '5 sessions', rate: '80%', tone: 'hi' },
      { name: 'Yellow Candle v3 alone', note: 'Less reliable without Squeeze',              sessions: '6 sessions', rate: '67%', tone: 'mid' },
      { name: 'Any entry on FOMC day',  note: 'Regardless of setup quality',                sessions: '3 sessions', rate: '33%', tone: 'lo' },
    ],
    indicators: [
      { label: 'Gamma regime +',   pct: 85, tone: 'sage' },
      { label: 'Tech leading SPY', pct: 82, tone: 'teal' },
      { label: 'Contango VIX',     pct: 77, tone: 'gold' },
      { label: 'Above PDH open',   pct: 75, tone: 'gold' },
      { label: 'FOMC day',         pct: 33, tone: 'terra' },
    ],
  },
};

const REASON_OPTIONS = [
  { id: 'orb',      icon: '📈', label: 'ORB breakout',        defaultSelected: true  },
  { id: 'gamma',    icon: '⚡', label: 'Gamma structural',    defaultSelected: true  },
  { id: 'news',     icon: '📰', label: 'Fed / macro news',    defaultSelected: false },
  { id: 'vwap',     icon: '🔄', label: 'VWAP reclaim',        defaultSelected: false },
  { id: 'inst',     icon: '🏦', label: 'Institutional flow',  defaultSelected: false },
  { id: 'gapfill',  icon: '🎯', label: 'Gap fill reversal',   defaultSelected: false },
  { id: 'sector',   icon: '🔀', label: 'Sector rotation',     defaultSelected: false },
  { id: 'pdhpdl',   icon: '📊', label: 'PDH/PDL break',       defaultSelected: false },
];

// ═══════════════════════════════════════════════════════════
// MAIN TAB
// ═══════════════════════════════════════════════════════════
export default function QqqTab({ workstation, userId, userRecord }) {
  const [data, setData] = useState(PLACEHOLDER);
  const [meta, setMeta] = useState({ source_status: null, generated_at: null, error: null });

  // Commit 78 — resolve the AI's user-facing name. Prefer per-workstation
  // ai_name, fall back to user-level, final fall back to 'Shift' (the
  // product). NEVER 'Jarvis' — that's internal engine terminology, not a
  // user-facing label. See CLAUDE.md identity model.
  const resolvedAiName = useMemo(() => {
    const w = workstation?.ai_name && String(workstation.ai_name).trim();
    const u = userRecord?.ai_name && String(userRecord.ai_name).trim();
    return w || u || 'Shift';
  }, [workstation?.ai_name, userRecord?.ai_name]);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
      });
      const res = await fetch(`/api/workstations/qqq-session-brief?${qs}`, { cache: 'no-store' });
      if (!res.ok) {
        const errPayload = await res.json().catch(() => ({}));
        setMeta((m) => ({ ...m, error: errPayload?.error || `HTTP ${res.status}` }));
        return;
      }
      const payload = await res.json();
      if (!payload?.brief) return;
      const b = payload.brief;
      // Shallow-merge per block. Any null block falls back to PLACEHOLDER,
      // so the page never breaks when one data source is empty.
      setData((prev) => ({
        ...prev,
        prices:        b.prices        || prev.prices,
        // Server nests levels inside prices; expose top-level too for
        // Section1Price which reads data.levels directly.
        levels:        (b.prices && b.prices.levels) || prev.levels,
        vixTerm:       (b.environment && b.environment.vixTerm)    || prev.vixTerm,
        futures:       (b.environment && b.environment.futures)    || prev.futures,
        yieldCurve:    (b.environment && b.environment.yieldCurve) || prev.yieldCurve,
        gamma:         (b.structure && b.structure.gamma)          || prev.gamma,
        pcr:           (b.flow && b.flow.pcr)                      || prev.pcr,
        sectors:       (b.flow && b.flow.sectors && b.flow.sectors.length > 0)
                         ? b.flow.sectors
                         : prev.sectors,
        events:        Array.isArray(b.risk) && b.risk.length > 0
                         ? b.risk
                         : prev.events,
        verdict:       b.verdict        || prev.verdict,
        executionPlan: b.executionPlan  || prev.executionPlan,
        // Commit 5: opening candle (captured by 9:46 ET cron).
        // Null until cron fires; client renders pending state.
        openingCandle: b.opening_candle || null,
        // Commit 6: window outcome (captured by 11:31 ET cron).
        // Null until window closes; client renders pending state.
        windowOutcome: b.window_outcome || null,
        // Commit 9: pre-trade bias (Tim submits before 9:30 ET).
        // Null until submitted; PreTradeBiasCard renders the form
        // when null, locked summary when set.
        preTradeBias: b.pre_trade_bias || null,
      }));
      // History + edge emerging — wired to qqq_session_briefs from
      // Phase 2.1a. Falls back to PLACEHOLDER if the table is empty
      // (e.g., first day of use).
      setData((prev) => {
        const liveHistory = payload.history?.rows;
        const liveRollup  = payload.history?.rollup;
        const liveEdge    = payload.edge_emerging;
        // Section 7 patterns — server returns null when < 3 resolved
        // sessions; client treats null as "show empty state" rather
        // than falling back to placeholder.
        const livePatterns = payload.patterns;
        return {
          ...prev,
          sidebarHistory:        (liveHistory && liveHistory.length > 0) ? liveHistory : prev.sidebarHistory,
          sidebarHistoryRollup:  (liveHistory && liveHistory.length > 0) ? liveRollup  : prev.sidebarHistoryRollup,
          sidebarEdgeEmerging:   liveEdge || prev.sidebarEdgeEmerging,
          // Patterns is null-tolerant — Section 7 reads `_patternsLive`
          // separately so it can render empty state when null.
          _patternsLive:         livePatterns,
          // Commit 7 — cross-correlation engine output. Null until
          // ≥5 fully-labeled sessions exist. Section 7 renders an
          // "data emerging" state when null; otherwise a list of
          // pattern combos with sample size + lift + dominant outcome.
          _patternEdges:         payload.pattern_edges || null,
          // Commit 11 — analog finder output. Null when today's
          // opening_candle isn't captured yet OR when fewer than
          // 3 fully-labeled past sessions exist. Phase 2 renders
          // a pending state when null, top-3 analogs when present.
          _analogs:              payload.analogs || null,
          // Commit 12 — misdirection warnings. Always an array
          // (empty when no warnings fire). Phase 2 renders one
          // card per warning with severity-coded border.
          _misdirections:        Array.isArray(payload.misdirections) ? payload.misdirections : [],
          // Commit 29 — deprecated; kept for safety (UI no longer uses)
          _derivedPatterns:      Array.isArray(payload.derived_patterns) ? payload.derived_patterns : [],
          // Commit 33 — Method Sheet (THE L3 output, Bruce Lee design).
          // Latest version generated by qqq-method-refine cron Sundays.
          // Phase 5 renders this read-only — no triage, just sharpening.
          _methodSheet:          payload.method_sheet || null,
          // Commit 34 — Tactical Refinement (L4). Concrete suggestions
          // from MFE/MAE distributions across resolved sessions.
          _tacticalRefinements:  payload.tactical_refinements || null,
          // Commit 35 — Proactive Alerts (L5). Today-matches-method-sheet
          // observations. Always an array (possibly empty).
          _methodMatches:        Array.isArray(payload.method_matches) ? payload.method_matches : [],
          // Commit 45 — environment fingerprint for today (VIX bucket,
          // gamma sign, sector leader, event proximity, etc.).
          // Foundation for 30/60/90-day synthesis — every session_briefs
          // row now carries one of these so history can be sliced by regime.
          _regimeTag:            payload.regime_tag || null,
          // Commit 47 — Edge State (30/60/90 + maturity). The compounding-
          // intelligence dashboard: "Shift gets smarter as I log more data."
          _edgeState:            payload.edge_state || null,
          // Commit 49 — Chart Pattern Mining. Rules mined from granular
          // chart features (CHOP / break / squeeze / RSI / etc.) correlated
          // with trade outcomes. Null until ≥5 parsed charts exist.
          _chartPatterns:        payload.chart_patterns || null,
          // Commit 50 — Chart Archive. Lean list of past parsed charts
          // (brief_date + chart_analysis + regime_tags + outcome) so the
          // Intelligence tab can render a clickable archive browser
          // without another round-trip.
          _chartArchive:         Array.isArray(payload.chart_archive) ? payload.chart_archive : [],
          // Commit 51 — Shift Score (the 0-10 tactical formula). Environment
          // + Setup + Trigger components. Primary decision number for today.
          _shiftScore:           payload.shift_score || null,
          _shiftScoreHistory:    Array.isArray(payload.shift_score_history) ? payload.shift_score_history : [],
          _shiftScoreCalibration: Array.isArray(payload.shift_score_calibration) ? payload.shift_score_calibration : [],
          // Commit 52 — Pre-market archetype (7 canonical patterns).
          _premarketArchetype:   payload.premarket_archetype || null,
          _archetypeHistory:     Array.isArray(payload.archetype_history) ? payload.archetype_history : [],
          // Commit 53 — Context Brief (Claude 4-step synthesis).
          _contextBrief:         payload.context_brief || null,
          // Commit 54 — Combined Matrix (5×5 chart × context).
          _combinedMatrix:       payload.combined_matrix || null,
          // Commit 60 — Tier 2 intraday events detected live today
          // by the qqq-intraday-monitor cron. Real-time record of
          // sudden moves during market hours (not just what Vision
          // sees in the EOD chart).
          _intradayEvents:       Array.isArray(payload.intraday_events) ? payload.intraday_events : [],
          // Commit 62 — Tier 4. Morning session features × 90-day
          // afternoon intraday events. Mined rules surface in
          // AfternoonPatternsCard. Null until ≥10 resolved sessions
          // with captured events exist.
          _afternoonPatterns:    payload.afternoon_patterns || null,
          // Commit 67 — joined report (price chart + option pair
          // synthesis). Auto-built when both upload types present
          // for today. Renders in JoinedReportCard.
          _joinedReport:         payload.joined_report || null,
          // Phase 2.1b — resolution data + ids needed by Session Log save
          _todayResolution:      payload.today_resolution || null,
          _todayId:              payload.today_id || null,
          _workstationId:        workstation.id,
          _userId:               userId,
        };
      });
      setMeta({
        source_status: payload.source_status,
        generated_at:  payload.generated_at,
        today_id:      payload.today_id,
        error:         null,
      });
    } catch (e) {
      setMeta((m) => ({ ...m, error: e?.message || 'load failed' }));
      // Keep PLACEHOLDER — widget stays usable.
    }
  }, [workstation?.id, userId]);

  useEffect(() => { load(); }, [load]);

  // Narratives are computed client-side from the same data blocks the
  // sections render. If the API hasn't returned live data yet, the
  // narrative builder falls back to "loading" and each section keeps
  // its hardcoded placeholder JSX.
  const narratives = useMemo(() => {
    try {
      return buildNarratives(data);
    } catch {
      return null;
    }
  }, [data]);

  // Single context value so sub-components can read both the data and
  // the derived narrative output without prop drilling.
  // Commit 78 — inject _aiName at the ctx layer so every descendant
  // can address the AI by the user's chosen name (never 'Jarvis').
  const ctxValue = useMemo(
    () => ({
      data: { ...data, _aiName: resolvedAiName, _generatedAt: meta.generated_at || null },
      narratives,
    }),
    [data, narratives, resolvedAiName, meta.generated_at],
  );

  // ═══════════════════════════════════════════════════════════
  // Commit 37 — 3-sub-tab layout (TODAY / LOG / INTELLIGENCE)
  // ═══════════════════════════════════════════════════════════
  // The page got too long with all 6 layers stacked top-to-bottom.
  // Per Tim, split into 3 modes that match actual usage:
  //   TODAY        — morning trading view (Phases 1-3 + sidebar)
  //   LOG          — after-action capture (Phase 4)
  //   INTELLIGENCE — pattern + method sheet review (Phase 5)
  //
  // State persists in localStorage so navigating away + coming back
  // restores the user's last sub-tab. First-time visitors get the
  // time-appropriate default (before 12 PM = TODAY, 12-4 PM = LOG,
  // after 4 PM = INTELLIGENCE).
  const [activeSubTab, setActiveSubTab] = useState(() => {
    if (typeof window === 'undefined') return 'today';
    const stored = window.localStorage.getItem('qqq_sub_tab');
    // Commit 79c — reverted the 79b Calendar-as-own-sub-tab split.
    // Calendar is back inside Library as a 4th pill.
    // Commit 86 — NEW 'journal' sub-tab for the trade log + calendar
    // + Ask Emily flow. Inserts between Log and Library.
    if (stored === 'calendar') {
      try { window.localStorage.setItem('qqq_sub_tab', 'library'); } catch { /* ignore */ }
      try { window.localStorage.setItem('qqq_library_view', 'calendar'); } catch { /* ignore */ }
      return 'library';
    }
    // Commit 89 — Log sub-tab deleted. 'log' redirects to 'library'.
    if (stored === 'log') {
      try { window.localStorage.setItem('qqq_sub_tab', 'library'); } catch { /* ignore */ }
      return 'library';
    }
    if (stored && ['today', 'journal', 'library', 'intel'].includes(stored)) return stored;
    return defaultSubTabForEt();
  });
  const switchSubTab = useCallback((key) => {
    setActiveSubTab(key);
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem('qqq_sub_tab', key); } catch { /* ignore */ }
    }
  }, []);

  return (
    <QqqDataContext.Provider value={ctxValue}>
      <div className="ws-tab-content qqq-tab">
        {/* Commit 43 — single sticky header containing both the
            mini-bar AND the sub-tab nav so they stick together
            instead of overlapping (the previous bug: each had its
            own position:sticky;top:0 and the higher-z mini-bar
            covered the sub-nav, making it look like sub-nav scrolled
            away). They now scroll out together as a unit. */}
        <div className="qqq-sticky-header">
          <MiniBar />
          <QqqSubTabNav active={activeSubTab} onChange={switchSubTab} />
        </div>

        <div className={`qqq-page-grid${activeSubTab === 'log' ? ' is-log' : ''}${activeSubTab === 'intel' ? ' is-intel' : ''}${activeSubTab === 'library' ? ' is-library' : ''}${activeSubTab === 'journal' ? ' is-journal' : ''}`}>
          <div className="qqq-main-column">
            <div className="qqq-page">
              {/* ═══════════════════════════════════════════════
                  TAB 1 — TODAY (morning trading view)
                  Phases 1-3 + sidebar visible
                  ═══════════════════════════════════════════════ */}
              {activeSubTab === 'today' && (
                <>
                  {/* ───────────────────────────────────────────────
                      NEW TODAY-tab dashboard. Rendered INSIDE the
                      .qqq-main-column so it shares the left half of
                      the screen with the verdict sidebar (Bullish /
                      Edge Emerging / Today's Context) on the right.
                      Tim asked for the new cards to be left-aligned
                      not full-width 2026-04-29.
                      ─────────────────────────────────────────────── */}
                  <NewTodayTab
                    workstation={workstation}
                    userId={userId}
                    userRecord={userRecord}
                    resolvedAiName={resolvedAiName}
                  />
                  {/* 2026-04-29 — PhaseHeader removed per Tim. With
                      the legacy Phase 1 cards gone, the "Phase 1 —
                      Pre-Open Prep / Read this before 9:30 AM ET"
                      label was floating above an empty section. */}
                  {/* Commit 39 — PreTradeBiasCard moved to sidebar.
                      Commit 40 — SectionVerdict removed (sidebar
                                 has it).
                      Commit 41 — Phase 2 (Decision Moment) and
                                 Phase 3 (Active Trade) moved to
                                 sidebar — main column is now just
                                 the morning READING flow (Phase 1
                                 conditions + plan). Decision and
                                 live trade live in the right rail.
                      Commit 57.1 — Context Brief moved OUT of the
                                 main column and into the sidebar
                                 as a collapsible. It's reference
                                 material, not live trading data;
                                 pushing Phase 1 cards below the
                                 fold was wrong. Now it lives in
                                 the sidebar's top section and Tim
                                 can toggle it open when he wants
                                 the strategic read. */}
                  {/* Pattern-focused dashboard (Commit 92) — replaces the
                      verbose ConditionsSnapshot with 3 cards relevant
                      to evaluating the 5 patterns:
                        1. MacroSnapshot (auto L3+L4 read)
                        2. F15Levels + decision countdown
                        3. OpeningCandle 9:30-9:45 (= F15 visualization)
                        4. EventsToday (FOMC/CPI dealbreakers)
                        5. PatternTrackerPanel (the 5 cards)
                      MethodMatchAlertsCard kept (high-signal warnings).
                      ConditionsSnapshot removed (too much non-pattern noise).
                      SectionExecutionPlan removed (replaced by Pattern Tier Tracker). */}
                  <MethodMatchAlertsCard />
                  {/* 2026-04-29 — Per Tim, the legacy TODAY block is
                      now fully replaced by the new NewTodayTab above.
                      Removed: MacroSnapshotCard, legacy F15LevelsCard,
                      EventsTodaySection, OpeningCandleCard,
                      PatternTrackerPanel. MethodMatchAlertsCard is
                      retained as a high-signal alert surface; nothing
                      in the new TodayTab replaces it yet. */}
                </>
              )}

              {/* ═══════════════════════════════════════════════
                  TAB 2 — LIBRARY (Commit 87)
                  2-pill: Timeline (60/40 workspace) · Curated.
                  Moved ahead of Journal per Tim's Commit 89 ask.
                  ═══════════════════════════════════════════════ */}
              {activeSubTab === 'library' && (
                <LibraryTab />
              )}

              {/* ═══════════════════════════════════════════════
                  TAB 3 — JOURNAL (Commit 86)
                  Calendar + 0DTE trade log + Ask Emily coaching.
                  70/30 split: calendar left, trade log right.
                  ═══════════════════════════════════════════════ */}
              {activeSubTab === 'journal' && (
                <JournalTab />
              )}

              {/* ═══════════════════════════════════════════════
                  TAB 4 — INTELLIGENCE (deep review, weekly)
                  Phase 5 only. Full width. Commit 79c reverted the
                  brief Calendar-as-own-sub-tab split; Calendar is
                  once again a Library pill (better colocation with
                  Timeline + Collections since it's a browse-by-date
                  view of the same underlying data).
                  ═══════════════════════════════════════════════ */}
              {activeSubTab === 'intel' && (
                <>
                  {/* 2026-04-29 — Per Tim, the Intelligence tab is
                      replaced with the Pre-Breakout Pattern Research
                      findings view (per docs/QQQ_PRE_BREAKOUT_PATTERN
                      _RESEARCH.md). Reverse-engineered patterns from
                      2 years of QQQ 5-min data; clustered by feature
                      similarity; tabulated up/down/reversal stats per
                      cluster. Tim was not using the legacy cards
                      (OpenObservations, HypothesisWatchlist,
                      PromptRevisionQueue, JoinedReport, EdgeState,
                      IntradayEvents, AfternoonPatterns,
                      ShiftScoreHistory, CombinedMatrix, ChartPatterns,
                      ChartArchive, Section7Patterns, MethodSheet,
                      TacticalRefinement, JarvisDialogue) on this tab.
                      They remain in the codebase but are no longer
                      mounted here — can be rewired into a future tab
                      if useful.

                      NOTE: the discovery pipeline (bar ingestion ->
                      breakout detection -> k-means clustering) is
                      not yet implemented. The view shows pipeline
                      status + empty states until those build pieces
                      land in subsequent sessions. */}
                  <div className="qqq-intel-2col">
                    <div className="qqq-intel-left">
                      <PreBreakoutPatternsView />
                    </div>
                    <div className="qqq-intel-right">
                      <div className="qqq-log-phase-label">
                        Ask {resolvedAiName} · communicate with your data
                      </div>
                      {/* AI Dialogue — full corpus access. Useful for
                          asking questions about the pattern findings
                          once they land. */}
                      <JarvisDialogueCard />
                    </div>
                  </div>
                </>
              )}

              {/* ═══════════════════════════════════════════════════
                  TAB 5 — PREDICTION (added 2026-05-25)
                  Live intraday prediction simulator. Surfaces the
                  validated P2-BIASED + intraday-by-11:30 model in
                  the UI so Tim can run predictions, compare 11:00
                  and 11:30 checkpoints, and iterate on the
                  architecture without leaving the workstation.
                  Backend: /api/qqq/intraday-prediction
                  Baseline: 57.0%/83.5% (N=200 backtest validated)
                  ═══════════════════════════════════════════════ */}
              {activeSubTab === 'prediction' && (
                <QqqPredictionTab
                  workstation={workstation}
                  userId={userId}
                  userRecord={userRecord}
                  resolvedAiName={resolvedAiName}
                />
              )}

              {/* ═══════════════════════════════════════════════════
                  TAB 6 — NETWORK (added 2026-05-25)
                  Obsidian-style mesh visualization of the prediction
                  system. Nodes for shapes / actors / signals /
                  variables / days / recommendations; edges showing
                  what depends on what. Click a node to see all
                  connections. Foundation for cascade simulation
                  (v2/v3 to follow once forward-walk data flows).
                  Backend: /api/qqq/brain-graph
                  ═══════════════════════════════════════════════ */}
              {activeSubTab === 'network' && (
                <QqqNetworkTab />
              )}
            </div>
          </div>

          {/* Sidebar visible ONLY on TODAY sub-tab.
              Commit 41 — sidebar is now ordered by trading-day
              timeline: pre-open input → reference → decision
              moment → active trade. Mirrors the actual flow. */}
          {activeSubTab === 'today' && (
            <aside className="qqq-verdict-sidebar">
              {/* Commit 92 — Tim's requested top-3, in his exact
                  order: QQQ/SPY signal · Edge Emerging · Today's
                  Context. These are the primary decision surface
                  when he opens Today — everything else gets
                  demoted beneath. */}
              <div className="qqq-sidebar-eyebrow">
                The Answer · always visible
                {meta.generated_at && (
                  <span className="qqq-sidebar-freshness"> · {fmtRelative(meta.generated_at)}</span>
                )}
              </div>
              <VerdictBlock compact />
              <EdgeEmergingCard />
              <ContextBriefCollapsible />

              {/* Commit 51/52 — secondary reference cards. Kept
                  below the top-3 but available when Tim wants
                  them. Shift Score, Pre-market Archetype, Today's
                  Regime, History.
                  Commit 96 — Pre-Open Read input + Phase 2
                  (Decision Moment) + Phase 3 (Active Trade)
                  removed per Tim. The pattern-tier flow on the
                  left column now drives the trading decision; the
                  manual phase scaffold is no longer needed. */}
              <ShiftScoreCard />
              <PremarketArchetypeCard />
              <TodaysRegimeCard />
              <HistoryCard />
            </aside>
          )}
        </div>
      </div>
    </QqqDataContext.Provider>
  );
}

// ─── Sub-tab nav (Commit 37) ─────────────────────────────────
// Pill buttons at the top of the QQQ tab. Active sub-tab gets
// gold styling. The "expected" sub-tab for current ET time gets
// a subtle gold dot indicator so Tim can see which mode the
// system thinks he's in (without forcing the switch).
function QqqSubTabNav({ active, onChange }) {
  const expected = defaultSubTabForEt();
  const tabs = [
    { key: 'today',      label: 'Today',        sub: 'morning trading view' },
    { key: 'library',    label: 'Library',      sub: 'timeline · curated' },
    { key: 'journal',    label: 'Journal',      sub: 'trades · calendar · coaching' },
    { key: 'intel',      label: 'Intelligence', sub: 'patterns + ask your AI' },
    { key: 'prediction', label: 'Prediction',   sub: 'live intraday simulator' },
    { key: 'network',    label: 'Network',      sub: 'mesh of signals · cascade' },
  ];
  return (
    <div style={{
      // Commit 43 — sticky behavior moved to parent .qqq-sticky-header
      // so MiniBar + sub-nav stick as one unit. This element is now
      // a plain flex row inside the sticky wrapper.
      // Commit 44 — pills centered horizontally in the page, with
      // small bottom padding so the underline indicator sits above
      // the shared border (not colliding with it).
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      padding: '6px 24px 4px',
      width: '100%',
      maxWidth: 1400,
      boxSizing: 'border-box',
    }}>
      {tabs.map((t) => {
        const isActive = active === t.key;
        const isExpected = expected === t.key && !isActive;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            style={{
              position: 'relative',
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 18px 12px',
              background: 'transparent',
              border: 0,
              borderBottom: isActive ? '2px solid var(--qqq-gold, #c8a96e)' : '2px solid transparent',
              color: isActive ? 'var(--qqq-gold, #c8a96e)' : 'var(--qqq-fg-2, #aaa)',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: isActive ? 600 : 500,
              transition: 'all 0.15s',
              marginBottom: -1,
            }}
          >
            <span>{t.label}</span>
            <span style={{
              fontSize: 11, color: 'var(--qqq-fg-3, #888)',
              fontWeight: 400,
            }}>
              · {t.sub}
            </span>
            {isExpected && (
              <span
                title="System suggests this tab based on current ET time"
                style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: 'var(--qqq-gold, #c8a96e)',
                  marginLeft: 4,
                  opacity: 0.6,
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Pattern tracker panel — A+/B/C live tier cards ──────────
// Renders 5 cards, one per documented TOS pattern. Each card
// has 4 toggleable layer checkboxes (pattern signature / multi-TF
// SQZ / macro regime / cross-asset). Tier auto-computes:
//   A+ = all 4 layers ✓
//   B  = layers 1-2 ✓ AND (3 OR 4) ✓
//   C  = layer 1 ✓ only
// State persists per-day in localStorage so reload doesn't lose
// in-session progress. Each new trading day starts blank.
//
// The data shown (sample $ moves, targets, stops) comes from the
// 2026-04-27 analysis run — see trading/QQQ/pattern-grading-rubric.md
// for the full rules + scripts/qqq-trend-capture-analysis.js for
// the underlying numbers.
const PATTERN_DEFS = [
  {
    id: 'rsi_breadth',
    num: '01',
    name: 'RSI_BREADTH',
    direction: '↑ Bullish',
    dirColor: '#00d489',
    decisionTime: '11:00 ET',
    sample: '$5.32 typical / $6.85 P75',
    targets: { 'A+': '$7-9 hold to close', B: '⅓/⅓/⅓ at $1.50/$4/close', C: '$1.50 quick scalp' },
  },
  {
    id: 'bear_triple_confirm',
    num: '02',
    name: 'BEAR_TRIPLE_CONFIRM',
    direction: '↓ Bearish',
    dirColor: '#ef4444',
    decisionTime: '12:30 ET',
    sample: '$5.91 typical / $9.95 P75',
    targets: { 'A+': '$7-9 hold to close (low at 15:30 100%)', B: '⅓/⅓/⅓ at $1.50/$4/close', C: 'SKIP — proxy weak' },
    warning: 'Daily-proxy hit rate 28.9% — treat in-sample 98.7% with skepticism',
  },
  {
    id: 'first_15_rejection',
    num: '03',
    name: 'FIRST_15_REJECTION',
    direction: '↑↓ Directional',
    dirColor: '#5a9eb5',
    decisionTime: '11:00 ET (90-min)',
    sample: '$5.32-5.91 typical / $7.41-7.85 P75',
    targets: { 'A+': '$7-9 hold to close', B: '⅓/⅓/⅓ at $1.50/$4/close', C: '¼ size, $1.50 only' },
  },
  {
    id: 'continuation',
    num: '04',
    name: 'CONTINUATION',
    direction: '↑↓ Trend day',
    dirColor: '#5a9eb5',
    decisionTime: '11:00 ET',
    sample: '$9.45 trend-capture / $11+ P75',
    targets: { 'A+': '$7-9 hold to close, NO early exit', B: '⅓/⅓/⅓ at $1.50/$4/close', C: 'SKIP — needs trend' },
  },
  {
    id: 'leg_continuation_high_rsi',
    num: '05',
    name: 'LEG_CONTINUATION',
    direction: '⭐ Rare bullish',
    dirColor: '#ffd700',
    decisionTime: 'Anytime (YC trigger)',
    sample: '$9.07 typical / $11+ P75',
    targets: { 'A+': '$7-11 ride to close', B: '⅓/⅓/⅓ at $1.50/$4/close', C: 'NEVER — needs YC' },
  },
];

function todayDateKey() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

function computeTier(layers) {
  // layers = { l1, l2, l3, l4 } booleans
  const checked = ['l1', 'l2', 'l3', 'l4'].filter((k) => layers[k]).length;
  if (checked === 4) return 'A+';
  if (layers.l1 && layers.l2 && (layers.l3 || layers.l4)) return 'B';
  if (layers.l1 && checked >= 1) return 'C';
  return null; // pending
}

const TIER_COLORS = {
  'A+': { bg: 'rgba(255, 215, 0, 0.10)', border: '#ffd700', text: '#ffd700', label: 'A+' },
  B:    { bg: 'rgba(192, 192, 192, 0.08)', border: '#c0c0c0', text: '#c0c0c0', label: 'B'  },
  C:    { bg: 'rgba(196, 122, 0, 0.08)',   border: '#c47a00', text: '#c47a00', label: 'C'  },
};

function PatternTrackerPanel() {
  const dateKey = todayDateKey();
  const storageKey = `qqq_pattern_tracker_${dateKey}`;

  const [state, setState] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    // Default: all unchecked
    return Object.fromEntries(
      PATTERN_DEFS.map((p) => [p.id, { l1: false, l2: false, l3: false, l4: false }]),
    );
  });

  const persist = useCallback((next) => {
    setState(next);
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
    }
  }, [storageKey]);

  const toggleLayer = useCallback((patternId, layer) => {
    persist({ ...state, [patternId]: { ...state[patternId], [layer]: !state[patternId]?.[layer] } });
  }, [persist, state]);

  const reset = useCallback(() => {
    if (!confirm('Reset all pattern layers for today?')) return;
    persist(Object.fromEntries(
      PATTERN_DEFS.map((p) => [p.id, { l1: false, l2: false, l3: false, l4: false }]),
    ));
  }, [persist]);

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Section header */}
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14,
        paddingBottom: 10, borderBottom: '1px solid var(--qqq-b3, #1c1f2e)',
      }}>
        <div style={{
          fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
          color: 'var(--qqq-teal)', fontWeight: 500,
        }}>
          ◆ Pattern Tier Tracker · {dateKey}
        </div>
        <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)', flex: 1 }}>
          Click each layer as conditions stack. Tier auto-computes. State persists for today.
        </div>
        <button
          type="button"
          onClick={reset}
          style={{
            fontSize: 9, letterSpacing: '0.06em', padding: '4px 10px',
            background: 'transparent', border: '1px solid var(--qqq-b2)',
            color: 'var(--qqq-fg-3)', borderRadius: 4, cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          ↻ Reset day
        </button>
      </div>

      {/* Cards grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 12,
      }}>
        {PATTERN_DEFS.map((p) => (
          <PatternCard
            key={p.id}
            pattern={p}
            layers={state[p.id] || { l1: false, l2: false, l3: false, l4: false }}
            onToggle={(layer) => toggleLayer(p.id, layer)}
          />
        ))}
      </div>

      {/* Footer caveat */}
      <div style={{
        marginTop: 14, padding: '8px 12px',
        background: 'rgba(196, 96, 80, 0.06)', border: '1px solid rgba(196, 96, 80, 0.2)',
        borderLeft: '3px solid var(--qqq-terracotta, #c46050)',
        borderRadius: 4, fontSize: 10, color: 'var(--qqq-terracotta, #c46050)', lineHeight: 1.55,
      }}>
        ⚠ In-sample backtest. Forward validation begins 2026-04-28. Treat at 50% conviction until 30+ live signals confirm within 5pp of in-sample hit rates.
      </div>
    </div>
  );
}

function PatternCard({ pattern, layers, onToggle }) {
  const tier = computeTier(layers);
  const tierStyle = tier ? TIER_COLORS[tier] : { bg: 'transparent', border: 'var(--qqq-b3, #1c1f2e)', text: 'var(--qqq-fg-3)', label: 'PENDING' };
  const checkedCount = ['l1', 'l2', 'l3', 'l4'].filter((k) => layers[k]).length;

  const layerLabels = [
    { key: 'l1', label: 'Pattern signature ✓ (TOS conditions met)' },
    { key: 'l2', label: 'Multi-TF SQZ aligned (5m + 15m at Q:3)' },
    { key: 'l3', label: 'Macro regime fit (VIX, day-of-week, gap)' },
    { key: 'l4', label: 'Cross-asset confirm (BTC, sectors)' },
  ];

  return (
    <div style={{
      background: tierStyle.bg,
      border: `1px solid ${tierStyle.border}`,
      borderRadius: 8,
      padding: '12px 14px',
      transition: 'all 0.15s',
    }}>
      {/* Header row */}
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4,
      }}>
        <div style={{
          fontSize: 8, color: 'var(--qqq-fg-3)', letterSpacing: '0.14em',
          textTransform: 'uppercase',
        }}>
          {pattern.num}
        </div>
        <div style={{
          fontFamily: 'Cormorant Garamond, Georgia, serif',
          fontSize: 16, fontWeight: 400, color: 'var(--qqq-fg-1)',
          flex: 1,
        }}>
          {pattern.name}
        </div>
        <div style={{
          fontSize: 9, color: pattern.dirColor, letterSpacing: '0.06em',
          padding: '2px 6px', border: `1px solid ${pattern.dirColor}40`,
          borderRadius: 3, background: `${pattern.dirColor}10`,
        }}>
          {pattern.direction}
        </div>
        {/* Tier badge */}
        <div style={{
          fontSize: 12, fontWeight: 600, fontFamily: 'monospace',
          color: tierStyle.text, letterSpacing: '0.04em',
          padding: '2px 8px', border: `1px solid ${tierStyle.border}`,
          borderRadius: 3, background: `${tierStyle.border}15`,
          minWidth: 36, textAlign: 'center',
        }}>
          {tierStyle.label}
        </div>
      </div>

      {/* Decision time + sample */}
      <div style={{
        fontSize: 10, color: 'var(--qqq-fg-3)', marginBottom: 10,
        display: 'flex', gap: 12,
      }}>
        <span>⏱ {pattern.decisionTime}</span>
        <span>·</span>
        <span>{pattern.sample}</span>
      </div>

      {/* Layer checkboxes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 10 }}>
        {layerLabels.map((l, i) => {
          const checked = !!layers[l.key];
          return (
            <button
              key={l.key}
              type="button"
              onClick={() => onToggle(l.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '4px 8px', textAlign: 'left',
                background: checked ? 'rgba(122, 158, 130, 0.10)' : 'transparent',
                border: `1px solid ${checked ? 'rgba(122, 158, 130, 0.4)' : 'var(--qqq-b3, #1c1f2e)'}`,
                borderRadius: 4, cursor: 'pointer',
                fontFamily: 'inherit',
                color: checked ? 'var(--qqq-fg-1)' : 'var(--qqq-fg-3)',
                fontSize: 11,
                transition: 'all 0.1s',
              }}
            >
              <span style={{
                display: 'inline-flex', width: 14, height: 14,
                alignItems: 'center', justifyContent: 'center',
                border: `1px solid ${checked ? 'var(--qqq-sage, #7a9e82)' : 'var(--qqq-b2, #2a2a2a)'}`,
                background: checked ? 'var(--qqq-sage, #7a9e82)' : 'transparent',
                borderRadius: 3, color: '#000', fontSize: 10, fontWeight: 700,
              }}>
                {checked ? '✓' : ''}
              </span>
              <span style={{ flex: 1 }}>L{i + 1}: {l.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tier-specific rules */}
      {tier && (
        <div style={{
          padding: '8px 10px',
          background: 'var(--surface-2, #14141a)',
          border: '1px solid var(--qqq-b3, #1c1f2e)',
          borderRadius: 4, fontSize: 10, lineHeight: 1.55,
        }}>
          <div style={{ color: tierStyle.text, fontWeight: 500, marginBottom: 3 }}>
            Target: <span style={{ color: 'var(--qqq-fg-1)', fontWeight: 400 }}>{pattern.targets[tier]}</span>
          </div>
          <div style={{ color: 'var(--qqq-fg-3)' }}>
            Stop: 50% drawdown · Layers met: {checkedCount}/4
          </div>
        </div>
      )}

      {/* Pattern-specific warning (if any) */}
      {pattern.warning && (
        <div style={{
          marginTop: 6, fontSize: 9, color: 'var(--qqq-terracotta, #c46050)',
          fontStyle: 'italic', lineHeight: 1.5,
        }}>
          ⚠ {pattern.warning}
        </div>
      )}
    </div>
  );
}

// ─── EventsTodaySection — slim wrapper for Today tab ─────────
// Just the events list (FOMC, CPI, NFP, etc.). Replaces the full
// Section5Risk which had analytical narrative — for the pattern-
// focused dashboard we just need the calendar.
function EventsTodaySection() {
  const data = useQqqData();
  const events = data.events || [];
  if (events.length === 0) return null;
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
        color: 'var(--qqq-teal)', fontWeight: 500, marginBottom: 8,
      }}>
        ◆ Events Today — Pattern 04 dealbreakers
      </div>
      <div style={{
        background: 'var(--surface-2)', border: '1px solid var(--qqq-b2)',
        borderRadius: 8, padding: '10px 14px',
      }}>
        {events.map((e, i) => (
          <div key={i} className="qqq-event-row">
            <div className={`qqq-er-bar ${e.sev}`} />
            <div className="qqq-er-time">{e.time}</div>
            <div className="qqq-er-name">{e.name}</div>
            <div className="qqq-er-note">{e.note}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── MacroSnapshotCard — single L3+L4 auto-eval (Today tab) ──
// Compact card on the Today tab that auto-resolves the macro
// regime layer (VIX × day-of-week × gap behavior × cross-asset)
// and surfaces which of the 5 patterns the macro favors today.
//
// Designed to replace the verbose VIX Term Structure / Futures /
// Yield Curve / Money Flow sections with one focused readout that
// only contains data relevant to evaluating the 5 patterns.
function MacroSnapshotCard() {
  const data = useQqqData();
  const vixVal = parseFloat(data.vixTerm?.[1]?.value);  // 30-day VIX
  const vixRegime = isNaN(vixVal) ? 'unknown'
    : vixVal < 14 ? 'LOW'
    : vixVal < 20 ? 'NORMAL'
    : vixVal < 28 ? 'ELEVATED'
    : 'HIGH';

  const todayET = useMemo(() => new Date(), []);
  const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][todayET.getDay()];

  // Gap behavior — derive from QQQ pre-market vs prev close
  const qqqPrice = parseFloat(data.prices?.qqq?.price);
  const qqqClose = parseFloat(data.prices?.qqq?.close);
  const gapPct = (!isNaN(qqqPrice) && !isNaN(qqqClose) && qqqClose > 0)
    ? ((qqqPrice - qqqClose) / qqqClose) * 100 : null;
  const gapBucket = gapPct == null ? 'unknown'
    : gapPct > 0.5 ? 'GAP_UP_BIG'
    : gapPct > 0.1 ? 'GAP_UP_SMALL'
    : gapPct > -0.1 ? 'FLAT'
    : gapPct > -0.5 ? 'GAP_DN_SMALL'
    : 'GAP_DN_BIG';

  // Sector lead — pull tech vs SPY-equivalent from sectors
  const techSector = (data.sectors || []).find((s) => s.name?.toLowerCase().includes('tech'));
  const techLeading = techSector?.tone === 'up';

  // Auto-resolve which patterns the macro favors
  // Per Q-C analysis:
  //   - Bull patterns (01, 03↑, 04↑) favor: NORMAL+ VIX + Mon/Wed/Fri
  //   - Bear patterns (02, 03↓) favor: ELEVATED+ VIX + Wed/Thu + GAP_DN
  const favorsBull = vixRegime !== 'LOW' && (dow === 'Mon' || dow === 'Wed' || dow === 'Fri');
  const favorsBear = (vixRegime === 'ELEVATED' || vixRegime === 'HIGH')
                     && (dow === 'Wed' || dow === 'Thu')
                     && (gapBucket === 'GAP_DN_SMALL' || gapBucket === 'GAP_DN_BIG');

  const summary = favorsBull && favorsBear ? 'Macro mixed — patterns 01/02/03 all live'
                : favorsBull ? 'Macro favors BULL — Pattern 01 + 03↑ best aligned'
                : favorsBear ? 'Macro favors BEAR — Pattern 02 + 03↓ best aligned'
                : 'Macro neutral — no pattern strongly favored';

  const regimeColor = {
    LOW: '#888888', NORMAL: 'var(--qqq-sage)', ELEVATED: 'var(--qqq-gold)', HIGH: 'var(--qqq-terracotta)',
  }[vixRegime] || 'var(--qqq-fg-3)';

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
        color: 'var(--qqq-teal)', fontWeight: 500, marginBottom: 8,
      }}>
        ◆ Today's Macro Snapshot — Auto L3 + L4
      </div>
      <div style={{
        background: 'var(--surface-2)', border: '1px solid var(--qqq-b2)',
        borderRadius: 8, padding: '14px 18px',
      }}>
        {/* Top row — 4 chips */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12,
          marginBottom: 12,
        }}>
          <MacroChip label="VIX" value={isNaN(vixVal) ? '—' : vixVal.toFixed(1)} tag={vixRegime} tagColor={regimeColor} />
          <MacroChip label="Day" value={dow} tag={
            dow === 'Thu' ? 'BEAR DAY' :
            dow === 'Mon' || dow === 'Wed' || dow === 'Fri' ? 'BULL DAY' :
            'NEUTRAL'
          } tagColor={
            dow === 'Thu' ? 'var(--qqq-terracotta)' :
            dow === 'Mon' || dow === 'Wed' || dow === 'Fri' ? 'var(--qqq-sage)' :
            'var(--qqq-fg-3)'
          } />
          <MacroChip
            label="Gap"
            value={gapPct == null ? '—' : `${gapPct >= 0 ? '+' : ''}${gapPct.toFixed(2)}%`}
            tag={gapBucket}
            tagColor={
              gapBucket === 'GAP_UP_BIG' || gapBucket === 'GAP_UP_SMALL' ? 'var(--qqq-sage)' :
              gapBucket === 'GAP_DN_BIG' || gapBucket === 'GAP_DN_SMALL' ? 'var(--qqq-terracotta)' :
              'var(--qqq-fg-3)'
            }
          />
          <MacroChip
            label="Tech"
            value={techSector?.value || '—'}
            tag={techLeading ? 'LEADING' : 'LAGGING'}
            tagColor={techLeading ? 'var(--qqq-sage)' : 'var(--qqq-terracotta)'}
          />
        </div>
        {/* Summary line */}
        <div style={{
          padding: '8px 10px',
          background: favorsBull || favorsBear ? 'rgba(122,158,130,.08)' : 'rgba(155,143,196,.04)',
          border: `1px solid ${favorsBull || favorsBear ? 'rgba(122,158,130,.25)' : 'var(--qqq-b3)'}`,
          borderRadius: 4, fontSize: 11, color: 'var(--qqq-fg-1)', lineHeight: 1.55,
          fontFamily: 'JetBrains Mono, monospace',
        }}>
          {favorsBull && '✓ '}{favorsBear && '✓ '}{summary}
        </div>
        {/* L3/L4 chip row for each pattern */}
        <div style={{
          marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8,
          fontSize: 9, fontFamily: 'JetBrains Mono, monospace',
        }}>
          <PatternMacroPill name="01 ↑" favored={favorsBull} />
          <PatternMacroPill name="03 ↑↓" favored={favorsBull || favorsBear} />
          <PatternMacroPill name="02 ↓" favored={favorsBear} />
        </div>
      </div>
    </div>
  );
}

function MacroChip({ label, value, tag, tagColor }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 2,
      padding: '8px 10px', background: 'var(--surface-3)', borderRadius: 4,
      border: '1px solid var(--qqq-b3)',
    }}>
      <div style={{
        fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase',
        color: 'var(--qqq-fg-3)',
      }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--qqq-fg-1)', fontFamily: 'JetBrains Mono, monospace' }}>{value}</div>
      <div style={{
        fontSize: 8, color: tagColor, fontFamily: 'JetBrains Mono, monospace',
        letterSpacing: '0.06em',
      }}>{tag}</div>
    </div>
  );
}

function PatternMacroPill({ name, favored }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '4px 8px',
      background: favored ? 'rgba(122,158,130,.10)' : 'transparent',
      border: `1px solid ${favored ? 'rgba(122,158,130,.4)' : 'var(--qqq-b3)'}`,
      borderRadius: 3,
      color: favored ? 'var(--qqq-sage)' : 'var(--qqq-fg-3)',
    }}>
      <span style={{ flex: 1 }}>Pattern {name}</span>
      <span>{favored ? '✓ favored' : '— neutral'}</span>
    </div>
  );
}

// ─── F15LevelsCard — appears once 9:45 ET passes ─────────────
// Compact card showing F15-H / F15-L levels + ext-up / ext-down
// + countdown to the next pattern decision time. Mirrors what
// the thinkScript study computes on the chart, surfaced in the
// dashboard for at-a-glance reference.
//
// Pre-9:45 it shows a "F15 forming…" placeholder with countdown.
function F15LevelsCard() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Determine ET time
  const etHM = useMemo(() => new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(now), [now]);
  const isAfterF15 = etHM >= '09:45';
  const isAfter1100 = etHM >= '11:00';
  const isAfter1230 = etHM >= '12:30';

  // Countdown to next decision
  function minutesUntil(targetHM) {
    const [h, m] = targetHM.split(':').map(Number);
    const [hN, mN] = etHM.split(':').map(Number);
    const target = h * 60 + m;
    const now = hN * 60 + mN;
    const diff = target - now;
    return diff > 0 ? diff : null;
  }
  const minsTo945 = minutesUntil('09:45');
  const minsTo1100 = minutesUntil('11:00');
  const minsTo1230 = minutesUntil('12:30');

  let decisionLabel, decisionColor;
  if (minsTo945 != null) {
    decisionLabel = `F15 forming in ${minsTo945}m (9:45 ET)`;
    decisionColor = 'var(--qqq-fg-3)';
  } else if (minsTo1100 != null) {
    decisionLabel = `Patterns 01/03 decide in ${minsTo1100}m (11:00 ET)`;
    decisionColor = 'var(--qqq-gold)';
  } else if (minsTo1230 != null) {
    decisionLabel = `Pattern 02 decides in ${minsTo1230}m (12:30 ET)`;
    decisionColor = 'var(--qqq-gold)';
  } else {
    decisionLabel = 'All decision windows passed for today';
    decisionColor = 'var(--qqq-fg-3)';
  }

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
        color: 'var(--qqq-teal)', fontWeight: 500, marginBottom: 8,
      }}>
        ◆ F15 Levels + Decision Countdown
      </div>
      <div style={{
        background: 'var(--surface-2)', border: '1px solid var(--qqq-b2)',
        borderRadius: 8, padding: '14px 18px',
      }}>
        {!isAfterF15 ? (
          <div style={{
            padding: '12px 0', textAlign: 'center', fontSize: 11,
            color: 'var(--qqq-fg-3)', fontStyle: 'italic',
          }}>
            F15 (9:30-9:45 ET) hasn't formed yet. Live levels appear after 9:45.
          </div>
        ) : (
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
            padding: '8px 0',
            fontFamily: 'JetBrains Mono, monospace',
          }}>
            <div style={{ textAlign: 'center', padding: '8px', background: 'var(--surface-3)', borderRadius: 4, border: '1px dashed rgba(239, 68, 68, 0.4)' }}>
              <div style={{ fontSize: 8, color: 'var(--qqq-fg-3)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>F15-H</div>
              <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--qqq-terracotta)' }}>$—</div>
              <div style={{ fontSize: 9, color: 'var(--qqq-fg-3)' }}>(check TOS chart)</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px', background: 'var(--surface-3)', borderRadius: 4, border: '1px dashed rgba(0, 212, 137, 0.4)' }}>
              <div style={{ fontSize: 8, color: 'var(--qqq-fg-3)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>F15-L</div>
              <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--qqq-sage)' }}>$—</div>
              <div style={{ fontSize: 9, color: 'var(--qqq-fg-3)' }}>(check TOS chart)</div>
            </div>
          </div>
        )}
        <div style={{
          marginTop: 10, padding: '8px 10px',
          background: minsTo1100 != null && minsTo1100 < 30 ? 'rgba(255, 215, 0, 0.08)' : 'transparent',
          border: `1px solid ${minsTo1100 != null && minsTo1100 < 30 ? 'rgba(255, 215, 0, 0.3)' : 'var(--qqq-b3)'}`,
          borderRadius: 4, fontSize: 11, color: decisionColor,
          fontFamily: 'JetBrains Mono, monospace', textAlign: 'center', letterSpacing: '0.05em',
        }}>
          {decisionLabel}
        </div>
      </div>
    </div>
  );
}

// ─── Collapsible section (Commit 38) ─────────────────────────
// Used for the right-column Pre-Open Prep on TODAY — Tim toggles
// open/closed to peek at the morning data without it dominating
// the screen. Defaults closed; localStorage remembers the state
// per section key.
function CollapsibleSection({ title, subtitle, defaultOpen = false, sectionKey, children }) {
  const storageKey = sectionKey ? `qqq_collapsible_${sectionKey}` : null;
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined' || !storageKey) return defaultOpen;
    const stored = window.localStorage.getItem(storageKey);
    if (stored === 'open') return true;
    if (stored === 'closed') return false;
    return defaultOpen;
  });
  function toggle() {
    const next = !open;
    setOpen(next);
    if (typeof window !== 'undefined' && storageKey) {
      try { window.localStorage.setItem(storageKey, next ? 'open' : 'closed'); } catch { /* ignore */ }
    }
  }
  return (
    <div style={{
      background: 'var(--surface-1, #0c0c11)',
      border: '1px solid var(--qqq-b1, #2a2a2a)',
      borderRadius: 8,
      marginBottom: 12,
      overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={toggle}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '12px 16px',
          background: 'transparent', border: 0,
          color: 'var(--qqq-fg-1, #eee)', cursor: 'pointer',
          textAlign: 'left',
          borderBottom: open ? '1px solid var(--qqq-b1, #2a2a2a)' : 0,
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.01em' }}>{title}</div>
          {subtitle && (
            <div style={{ fontSize: 11, color: 'var(--qqq-fg-3, #888)', marginTop: 2, fontWeight: 400 }}>
              {subtitle}
            </div>
          )}
        </div>
        <div style={{
          fontSize: 12, color: 'var(--qqq-fg-2, #aaa)',
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s',
        }}>
          ▼
        </div>
      </button>
      {open && (
        <div style={{ padding: '14px 16px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

// Returns the sub-tab key the system thinks Tim should be on
// based on current ET wall clock.
function defaultSubTabForEt() {
  if (typeof window === 'undefined') return 'today';
  const now = new Date();
  const wd = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' })).getDay();
  if (wd === 0 || wd === 6) return 'intel'; // weekend → review mode
  const hh = Number(now.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York', hour: '2-digit', hour12: false,
  }).split(':')[0]);
  if (hh < 12) return 'today';   // before noon ET → trading
  if (hh < 16) return 'log';     // 12-4 PM ET → log
  return 'intel';                 // after 4 PM → review
}

// Hook — all sub-components read via this so they get live or fallback data.
function useQqqData() {
  const ctx = useContext(QqqDataContext);
  return ctx?.data || PLACEHOLDER;
}

// Commit 78 — user-facing AI name. Reads _aiName from the QQQ context
// (set once in QqqTab from workstation.ai_name → userRecord.ai_name →
// 'Shift'). Every component that shows text like "Ask X about this"
// or "X is thinking" should call this hook instead of hardcoding.
function useAiName() {
  const ctx = useContext(QqqDataContext);
  return ctx?.data?._aiName || 'Shift';
}

// Sibling hook for the deterministic per-section narratives + footer
// pills. Sections call useNarrative('price') and either get back a
// live narrative or null (in which case they render the original
// hardcoded placeholder JSX). Same fallback pattern as useQqqData.
function useNarrative(section) {
  const ctx = useContext(QqqDataContext);
  const n = ctx?.narratives?.[section];
  // Treat 'WAIT' badges as "no live data yet" so the placeholder
  // copy stays visible until the brief actually loads.
  if (!n || n.badge === 'WAIT') return null;
  return n;
}

function fmtRelative(iso) {
  if (!iso) return '';
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs < 60000)    return 'just now';
    if (diffMs < 3600000)  return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
    return 'stale';
  } catch { return ''; }
}

// Commit 91 — freshness label for section headers. Turns an ISO
// timestamp into a human wall-clock label in ET. Returns "9:46 AM"
// when captured today, "Y'day 3:30 PM" when yesterday, or the date
// otherwise. Pairs with <FreshnessBadge /> for the chip rendering.
function fmtCapturedAt(iso) {
  if (!iso) return '';
  try {
    const captured = new Date(iso);
    const nowStr = new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });
    const capStr = captured.toLocaleDateString('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });
    const timePart = captured.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true });
    if (capStr === nowStr) return timePart;
    // Yesterday?
    const yd = new Date(); yd.setDate(yd.getDate() - 1);
    const ydStr = yd.toLocaleDateString('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });
    if (capStr === ydStr) return `Y'day ${timePart}`;
    return captured.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' }) + ' ' + timePart;
  } catch { return ''; }
}

// Commit 91 — small mono chip rendered alongside a DataCard's
// `source` prop. Three kinds:
//   kind='live'      — teal, refreshing periodically
//   kind='captured'  — dim sage, froze at a specific moment
//   kind='deferred'  — dashed purple, not wired
// Renders nothing if kind is missing/unknown.
function FreshnessBadge({ kind, at, label }) {
  if (!kind) return null;
  const text = label
    ? label
    : kind === 'live'     ? '● live'
    : kind === 'captured' ? `captured ${fmtCapturedAt(at) || 'earlier'}`
    : kind === 'deferred' ? 'deferred'
    : kind;
  const color =
    kind === 'live'     ? 'var(--qqq-teal)'
    : kind === 'captured' ? 'var(--qqq-fg-3)'
    : kind === 'deferred' ? 'var(--qqq-purple)'
    : 'var(--qqq-fg-3)';
  const bg =
    kind === 'live'     ? 'rgba(77,184,184,.08)'
    : kind === 'deferred' ? 'rgba(155,143,196,.06)'
    : 'transparent';
  return (
    <span style={{
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: 9,
      color,
      background: bg,
      border: kind === 'deferred' ? '1px dashed rgba(155,143,196,.3)' : 0,
      padding: kind === 'deferred' ? '1px 6px' : '1px 4px',
      borderRadius: 2,
      letterSpacing: '0.03em',
      whiteSpace: 'nowrap',
    }}>
      {text}
    </span>
  );
}

// Truncate a string to N chars with an ellipsis
function truncateText(s, n) {
  if (!s) return '';
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}

// Format the brief_date ("YYYY-MM-DD") as "Apr 22"
function fmtBriefDate(dateStr) {
  if (!dateStr) return '';
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch { return dateStr; }
}

// Relative time for save confirmation: "just now" / "2m ago" / "1h ago"
function fmtSaveTime(iso) {
  if (!iso) return '';
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs < 60000)    return 'just now';
    if (diffMs < 3600000)  return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}

// ═══════════════════════════════════════════════════════════
// MINI BAR — sticky top
// ═══════════════════════════════════════════════════════════
// Live ET clock + session phase. Refreshes every minute so the
// "Pre-Session / Open / After Hours / Closed" label stays accurate
// across the trading day without a page reload.
function getEtClock() {
  const now = new Date();
  // toLocaleString in America/New_York handles DST automatically.
  const dateStr = now.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
  });
  const timeStr = now.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  // Phase: extract ET hour+minute as a numeric for easy compare.
  const hh = Number(now.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York', hour: '2-digit', hour12: false,
  }).split(':')[0]);
  const mm = Number(now.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York', minute: '2-digit',
  }));
  const wd = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' })).getDay();
  const isWeekend = wd === 0 || wd === 6;
  const minutes = hh * 60 + mm;
  let phase;
  if (isWeekend)            phase = 'Closed';
  else if (minutes < 4 * 60) phase = 'Closed';
  else if (minutes < 9 * 60 + 30) phase = 'Pre-Session';
  else if (minutes < 16 * 60)     phase = 'Open';
  else if (minutes < 20 * 60)     phase = 'After Hours';
  else                            phase = 'Closed';
  return { dateStr, timeStr, phase };
}

function MiniBar() {
  const data = useQqqData();
  const { qqq, spy } = data.prices;
  const qqqTone = qqq.change && qqq.change.startsWith('-') ? 'dn' : 'up';
  const spyTone = spy.change && spy.change.startsWith('-') ? 'dn' : 'up';

  // Live ET clock — refreshes once per minute. SSR-safe via lazy
  // initial state so we never render a different value server vs.
  // client on first paint.
  const [clock, setClock] = useState(() => getEtClock());
  useEffect(() => {
    setClock(getEtClock());
    const id = setInterval(() => setClock(getEtClock()), 60_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="qqq-mini-bar">
      <div className="qqq-mb-left">
        <div className="qqq-mb-date">{clock.dateStr} · {clock.phase}</div>
        <div className="qqq-mb-prices">
          <div className="qqq-mbp">
            <span className="qqq-mbp-t">QQQ</span>
            <span className="qqq-mbp-p">{qqq.price}</span>
            <span className={`qqq-mbp-c ${qqqTone}`}>{qqq.change}</span>
          </div>
          <div className="qqq-mbp">
            <span className="qqq-mbp-t">SPY</span>
            <span className="qqq-mbp-p">{spy.price}</span>
            <span className={`qqq-mbp-c ${spyTone}`}>{spy.change}</span>
          </div>
        </div>
      </div>
      <div className="qqq-mb-right">
        <div className="qqq-live" />
        <div className="qqq-mb-time">{clock.timeStr} ET</div>
      </div>
    </div>
  );
}

function PageTitle({ subtitle }) {
  return (
    <div className="qqq-page-title">
      <div className="qqq-pt-main">Session Brief</div>
      <div className="qqq-pt-sub">
        {subtitle || '5 phases · top → bottom · one workflow, every day'}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// COMMIT 8 — 5-PHASE LAYOUT COMPONENTS
// ═══════════════════════════════════════════════════════════
// Phase headers turn the page from a topic-organized dashboard
// (Section 1: Price, Section 2: Environment, ...) into a time-
// phased workflow (Phase 1: Pre-Open Prep, Phase 2: Decision
// Moment, ...). The page now reads top-to-bottom in execution
// order so Tim always knows what he's supposed to be doing.
//
// Time-aware: the currently-active phase (based on ET clock) is
// visually elevated (gold border + active dot). Other phases
// are visible but subdued.
//
// One thing, repeated every trading day. Bruce Lee discipline.
// ═══════════════════════════════════════════════════════════

// Returns which phase is currently active based on ET wall clock.
// Saturday/Sunday = 'pre_open' so Tim can prep the upcoming week.
function getCurrentPhase() {
  const now = new Date();
  const wd = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' })).getDay();
  if (wd === 0 || wd === 6) return 'pre_open';
  const hh = Number(now.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York', hour: '2-digit', hour12: false,
  }).split(':')[0]);
  const mm = Number(now.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York', minute: '2-digit',
  }));
  const min = hh * 60 + mm;
  if (min < 9 * 60 + 30)        return 'pre_open';   // before 9:30
  if (min < 9 * 60 + 46)        return 'decision';   // 9:30 - 9:45
  if (min < 11 * 60 + 30)       return 'active';     // 9:45 - 11:30
  if (min < 16 * 60)            return 'after';      // 11:30 - 16:00
  return 'patterns';                                  // 16:00 onwards
}

function PhaseHeader({ num, title, subtitle, phaseKey }) {
  const [activePhase, setActivePhase] = useState(() => getCurrentPhase());
  useEffect(() => {
    const id = setInterval(() => setActivePhase(getCurrentPhase()), 60_000);
    return () => clearInterval(id);
  }, []);
  const isActive = activePhase === phaseKey;
  return (
    <div className={`qqq-phase-header ${isActive ? 'is-active' : ''}`} style={{
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      padding: '20px 0 12px',
      margin: '24px 0 8px',
      borderTop: isActive
        ? '2px solid var(--qqq-gold, #c8a96e)'
        : '1px solid var(--qqq-b1, #2a2a2a)',
      position: 'relative',
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: isActive ? 'var(--qqq-gold, #c8a96e)' : 'var(--surface-3, #171723)',
        color: isActive ? 'var(--surface-0, #07070a)' : 'var(--qqq-fg-2, #aaa)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 16,
        flexShrink: 0,
      }}>
        {num}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 18, fontWeight: 600,
          color: isActive ? 'var(--qqq-gold, #c8a96e)' : 'var(--qqq-fg-1, #eee)',
          letterSpacing: '0.01em',
        }}>
          PHASE {num} — {title}
        </div>
        <div style={{
          fontSize: 12, color: 'var(--qqq-fg-2, #aaa)',
          marginTop: 2, letterSpacing: '0.02em', textTransform: 'uppercase',
        }}>
          {subtitle}
        </div>
      </div>
      {isActive && (
        <div style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--qqq-gold, #c8a96e)',
          background: 'rgba(200,169,110,.10)',
          padding: '4px 10px',
          borderRadius: 12,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}>
          ● Active Now
        </div>
      )}
    </div>
  );
}

// ─── Phase 1, Position ① — Pre-Trade Bias Capture (Commit 9) ─
// Tim submits his read BEFORE seeing the rest of the brief.
// Locked at 9:30 AM ET (window opens) — can't be edited after.
// Two states:
//   - Empty (form):  3 direction buttons + 3 conviction buttons
//                    + optional 280-char note + Submit
//   - Submitted (locked): summary card with timestamp
// When the window has already opened and no submission was made,
// renders a "no read recorded" disabled state — preserves the
// honest historical record.
function PreTradeBiasCard() {
  const data = useQqqData();
  const submitted = data.preTradeBias;
  const workstationId = data._workstationId;
  const userId = data._userId;

  // Live ET clock — used to determine if the window is still open
  // for submission and to refresh the display every minute.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  const beforeWindowOpen = (() => {
    const wd = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' })).getDay();
    if (wd === 0 || wd === 6) return true;
    const hh = Number(now.toLocaleTimeString('en-US', {
      timeZone: 'America/New_York', hour: '2-digit', hour12: false,
    }).split(':')[0]);
    const mm = Number(now.toLocaleTimeString('en-US', {
      timeZone: 'America/New_York', minute: '2-digit',
    }));
    return hh * 60 + mm < 9 * 60 + 30;
  })();

  // Form state
  const [direction, setDirection]   = useState(null);
  const [conviction, setConviction] = useState(null);
  const [note, setNote]             = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);
  const [justSubmitted, setJustSubmitted] = useState(null);

  const effectiveBias = submitted || justSubmitted;

  async function handleSubmit() {
    if (!direction || !conviction) {
      setError('Pick a direction and conviction first.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(
        `/api/workstations/qqq-session-brief?action=submit_bias`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workstation_id: workstationId,
            user_id: userId,
            direction,
            conviction,
            note,
          }),
        }
      );
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(payload?.error || `HTTP ${res.status}`);
        return;
      }
      setJustSubmitted(payload.pre_trade_bias);
    } catch (e) {
      setError(e?.message || 'submit failed');
    } finally {
      setSubmitting(false);
    }
  }

  // ─── State A: Already submitted ───────────────────────────
  if (effectiveBias) {
    const dirTone = effectiveBias.direction === 'bull' ? 'bull'
      : effectiveBias.direction === 'bear' ? 'bear'
      : 'neutral';
    const dirLabel = effectiveBias.direction.toUpperCase();
    const convLabel = effectiveBias.conviction.toUpperCase();
    return (
      <DataCard
        label="① Your Pre-Open Read"
        source={`Locked · submitted ${fmtRelative(effectiveBias.submitted_at)}`}
        tone="gold"
      >
        <div className={`qqq-conclusion ${dirTone === 'bull' ? 'price' : dirTone === 'bear' ? 'risk' : 'env'}`}>
          <div className={`qqq-conc-dot ${dirTone}`} />
          <div className="qqq-conc-text">
            <strong>{dirLabel}</strong> · {convLabel} conviction
            {effectiveBias.note && <> · &ldquo;{effectiveBias.note}&rdquo;</>}
          </div>
          <div className={`qqq-conc-badge ${dirTone}`}>LOCKED</div>
        </div>
        <div style={{
          padding: '10px 16px', fontSize: 12,
          color: 'var(--qqq-fg-3, #888)', lineHeight: 1.55,
        }}>
          Captured before you saw the rest of the brief. Phase 4
          will compare this to the system verdict and the actual
          window outcome — that&apos;s how we measure whether Shift
          improves your reads over time.
        </div>
      </DataCard>
    );
  }

  // ─── State B: Window closed, no submission ────────────────
  if (!beforeWindowOpen) {
    return (
      <DataCard label="① Your Pre-Open Read" source="Window already opened — bias locked as 'no read'" tone="gold">
        <div style={{
          padding: '14px 16px', fontSize: 13,
          color: 'var(--qqq-fg-2, #aaa)', lineHeight: 1.55,
        }}>
          You didn&apos;t submit a pre-open read today. The Phase 4 Bias
          Comparison row will show only the system verdict + actual
          outcome — no calibration signal for your gut on this session.
        </div>
      </DataCard>
    );
  }

  // ─── State C: Empty form (before 9:30 ET, not yet submitted) ──
  return (
    <DataCard
      label="① Your Pre-Open Read"
      source="Submit BEFORE looking at the rest of the brief · locks at 9:30 AM ET"
      tone="gold"
    >
      <div style={{ padding: '14px 16px' }}>
        <div style={{ fontSize: 13, color: 'var(--qqq-fg-2, #aaa)', marginBottom: 14, lineHeight: 1.55 }}>
          What&apos;s your read on QQQ for the 9:45–11:30 window TODAY?
          Trust your gut here — record it before the brief biases you.
        </div>

        {/* Direction picker */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--qqq-fg-3, #888)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Direction
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[
              { key: 'bull',  label: '↑ Bullish', tone: 'bull' },
              { key: 'mixed', label: '→ Mixed',   tone: 'neutral' },
              { key: 'bear',  label: '↓ Bearish', tone: 'bear' },
            ].map((opt) => {
              const isSel = direction === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setDirection(opt.key)}
                  style={{
                    padding: '12px 10px',
                    fontSize: 14,
                    fontWeight: isSel ? 700 : 500,
                    background: isSel
                      ? (opt.tone === 'bull' ? 'rgba(122,158,130,.15)'
                        : opt.tone === 'bear' ? 'rgba(196,96,80,.15)'
                        : 'rgba(155,143,196,.15)')
                      : 'var(--surface-2, #111118)',
                    color: isSel
                      ? (opt.tone === 'bull' ? 'var(--qqq-sage, #7a9e82)'
                        : opt.tone === 'bear' ? 'var(--qqq-terra, #c46050)'
                        : 'var(--qqq-purple, #9b8fc4)')
                      : 'var(--qqq-fg-1, #eee)',
                    border: `1px solid ${isSel
                      ? (opt.tone === 'bull' ? 'var(--qqq-sage, #7a9e82)'
                        : opt.tone === 'bear' ? 'var(--qqq-terra, #c46050)'
                        : 'var(--qqq-purple, #9b8fc4)')
                      : 'var(--qqq-b1, #2a2a2a)'}`,
                    borderRadius: 6, cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Conviction picker */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--qqq-fg-3, #888)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Conviction
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[
              { key: 'low',    label: 'Low' },
              { key: 'medium', label: 'Medium' },
              { key: 'high',   label: 'High' },
            ].map((opt) => {
              const isSel = conviction === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setConviction(opt.key)}
                  style={{
                    padding: '10px',
                    fontSize: 13,
                    fontWeight: isSel ? 700 : 500,
                    background: isSel ? 'rgba(200,169,110,.15)' : 'var(--surface-2, #111118)',
                    color: isSel ? 'var(--qqq-gold, #c8a96e)' : 'var(--qqq-fg-1, #eee)',
                    border: `1px solid ${isSel ? 'var(--qqq-gold, #c8a96e)' : 'var(--qqq-b1, #2a2a2a)'}`,
                    borderRadius: 6, cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Optional note */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--qqq-fg-3, #888)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Why? (optional, 280 chars max)
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 280))}
            placeholder="e.g. expecting tech to lead the gap, gamma+ regime, FOMC isn't until 2pm so window should be clean"
            rows={2}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '10px 12px', fontSize: 13,
              background: 'var(--surface-3, #171723)',
              color: 'var(--qqq-fg-1, #eee)',
              border: '1px solid var(--qqq-b1, #2a2a2a)',
              borderRadius: 6, resize: 'vertical', fontFamily: 'inherit',
            }}
          />
          <div style={{ fontSize: 10, color: 'var(--qqq-fg-3, #888)', textAlign: 'right', marginTop: 4 }}>
            {note.length}/280
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div style={{
            padding: '8px 12px', fontSize: 12,
            color: 'var(--qqq-terra, #c46050)',
            background: 'rgba(196,96,80,.05)',
            borderLeft: '2px solid var(--qqq-terra, #c46050)',
            borderRadius: 3, marginBottom: 12,
          }}>
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !direction || !conviction}
          style={{
            width: '100%', padding: '12px',
            fontSize: 14, fontWeight: 600,
            background: (!direction || !conviction)
              ? 'var(--surface-3, #171723)'
              : 'var(--qqq-gold, #c8a96e)',
            color: (!direction || !conviction)
              ? 'var(--qqq-fg-3, #555)'
              : 'var(--surface-0, #07070a)',
            border: 'none', borderRadius: 6,
            cursor: (!direction || !conviction || submitting) ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.7 : 1,
            transition: 'all 0.15s',
          }}
        >
          {submitting ? 'Submitting…' : 'Lock In My Read'}
        </button>

        <div style={{ fontSize: 11, color: 'var(--qqq-fg-3, #888)', marginTop: 10, textAlign: 'center' }}>
          Once submitted, your read is locked for the day — the only honest
          way to measure your gut vs. the system over time.
        </div>
      </div>
    </DataCard>
  );
}

// ─── Phase 1: Method Match Alerts (Commit 35 — Layer 5) ─────
// When today's conditions match observations from the latest
// Method Sheet, surface them in Phase 1 as alerts. Sorted by
// severity (high → medium → low). Hidden if no matches.
function MethodMatchAlertsCard() {
  const data = useQqqData();
  const matches = Array.isArray(data._methodMatches) ? data._methodMatches : [];

  if (matches.length === 0) return null;

  return (
    <DataCard
      label="✦ Method Sheet Alerts · Today's Matches"
      source={`${matches.length} signal${matches.length === 1 ? '' : 's'} from your Method Sheet match today`}
      tone="purple"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 18px' }}>
        {matches.map((m, i) => {
          const sev = m.severity;
          const sevColor = sev === 'high' ? 'var(--qqq-gold)'
            : sev === 'medium' ? 'var(--qqq-purple)'
            : 'var(--qqq-fg-2)';
          const kindLabel = ({
            direction_match: 'DIRECTION MATCH',
            signal_stack:    'SIGNAL STACK',
            decay_warning:   'DECAY WARNING',
            sharpening:      'SHARPENING FOCUS',
          })[m.kind] || m.kind.toUpperCase();
          return (
            <div key={i} style={{
              background: 'var(--surface-3, #171723)',
              borderRadius: 8,
              padding: '12px 14px',
              borderLeft: `3px solid ${sevColor}`,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                fontSize: 10, color: sevColor, textTransform: 'uppercase',
                letterSpacing: '0.05em', fontWeight: 700,
              }}>
                <span>{kindLabel}</span>
                <span style={{ color: 'var(--qqq-fg-3)' }}>·</span>
                <span>{sev}</span>
                {m.match_score != null && (
                  <span style={{ color: 'var(--qqq-fg-3)' }}>
                    · {Math.round(m.match_score * 100)}% match
                  </span>
                )}
              </div>
              <div style={{ fontSize: 14, color: 'var(--qqq-fg-1)', fontWeight: 600, marginBottom: 4 }}>
                {m.title}
              </div>
              {m.observation && (
                <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.5, marginBottom: 6 }}>
                  {m.observation}
                </div>
              )}
              {Array.isArray(m.signals) && m.signals.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--qqq-fg-2)', marginBottom: 6 }}>
                  Active: {m.signals.join(' · ')}
                </div>
              )}
              {m.evidence && (
                <div style={{ fontSize: 11, color: 'var(--qqq-fg-3)', marginBottom: 4 }}>
                  evidence: {m.evidence}
                </div>
              )}
              {m.why && (
                <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', fontStyle: 'italic', lineHeight: 1.5 }}>
                  {m.why}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{
        padding: '10px 18px', fontSize: 11, color: 'var(--qqq-fg-3)',
        background: 'rgba(155,143,196,.04)', borderTop: '1px solid var(--qqq-b1)',
        fontStyle: 'italic',
      }}>
        These match today's captured conditions against your Method Sheet's
        observations. Method Sheet refreshes Sundays at noon ET.
      </div>
    </DataCard>
  );
}

// ─── Phase 1: Conditions Snapshot ────────────────────────────
// Wraps the existing Section1-5 in a single phase block. Visual
// compression comes from the unified PhaseHeader; the section
// content stays identical so all the data Tim relies on remains
// present. Future commit can add collapse/expand toggle for
// power-user mode (read-once-then-collapse).
function ConditionsSnapshot() {
  return (
    <>
      <DeferredSummary />
      <Section1Price />
      <Section2Environment />
      <Section3Structure />
      <Section4Flow />
      <Section5Risk />
    </>
  );
}

// Commit 91 — DeferredSummary. Concise top-of-page index of the
// signals that currently render as deferred. Lists each with a
// one-liner so Tim sees "what's not live" at a glance without
// scrolling through three separate explainer blocks. The full
// explainers stay in each DeferredCard for the user who wants
// the detail; this is the navigator.
function DeferredSummary() {
  const items = [
    { name: 'Session VWAP',       reason: 'needs paid intraday minute-bar feed'     },
    { name: 'Full S&P 500 Breadth', reason: 'needs CBOE NEWA or 500-ticker cron'     },
    { name: 'Gamma Structure',    reason: 'FlashAlpha unreliable — deferred in C90' },
  ];
  return (
    <div style={{
      padding: '10px 14px',
      background: 'rgba(155,143,196,.04)',
      border: '1px dashed rgba(155,143,196,.3)',
      borderRadius: 6,
      marginBottom: 14,
      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    }}>
      <div style={{
        fontSize: 10, color: 'var(--qqq-purple)',
        textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
        fontFamily: 'JetBrains Mono, monospace',
      }}>
        {items.length} signals deferred
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
        {items.map((it) => (
          <div key={it.name} style={{
            fontSize: 11, color: 'var(--qqq-fg-2)',
            padding: '3px 8px', borderRadius: 3,
            background: 'var(--surface-3)',
            fontFamily: 'JetBrains Mono, monospace',
          }}>
            <span style={{ color: 'var(--qqq-purple)', fontWeight: 600 }}>{it.name}</span>
            <span style={{ color: 'var(--qqq-fg-3)' }}> · {it.reason}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Phase 2: Decision Moment Block ──────────────────────────
// Sequence: Opening Candle (the trigger) → Today Looks Like
// (historical analogs to today) → [Misdirection Warnings —
// Commit 12]. Reading top to bottom inside this block answers:
// "what just happened? what does it look like? what should I be
// careful about?"
function DecisionMomentBlock() {
  return (
    <div className="qqq-decision-block">
      <OpeningCandleCard />
      <AnalogFinderCard />
      <MisdirectionWarningsCard />
    </div>
  );
}

// ─── Phase 2, Position ⑥ — Misdirection Warnings (Commit 12) ──
// Active anti-bias layer. Surfaces conflicts between today's
// setup and historically-failing patterns, between Tim's gut and
// the analog consensus, or unusual-setup low-confidence states.
// Empty state hidden entirely (no card if no warnings — keeps
// the page clean when the system has no concerns).
function MisdirectionWarningsCard() {
  const data = useQqqData();
  const warnings = data._misdirections;
  const todayCandle = data.openingCandle;

  // Hidden if no candle yet (warnings depend on it) OR no warnings
  if (!todayCandle || !Array.isArray(warnings) || warnings.length === 0) {
    return null;
  }

  return (
    <DataCard
      label="⚠ Misdirection Warnings"
      source={`${warnings.length} warning${warnings.length === 1 ? '' : 's'} · resolve before sizing in`}
      tone="terra"
    >
      {warnings.map((w, i) => {
        const sevColor = w.severity === 'hi' ? 'var(--qqq-terra, #c46050)'
          : w.severity === 'md' ? 'var(--qqq-gold, #c8a96e)'
          : 'var(--qqq-fg-2, #aaa)';
        const sevBg = w.severity === 'hi' ? 'rgba(196,96,80,.06)'
          : w.severity === 'md' ? 'rgba(200,169,110,.06)'
          : 'rgba(170,170,170,.04)';
        const typeLabel = ({
          pattern_conflict: 'PATTERN CONFLICT',
          bias_conflict:    'GUT vs ANALOGS',
          low_similarity:   'UNUSUAL SETUP',
        })[w.type] || w.type.toUpperCase();
        return (
          <div key={i} style={{
            padding: '14px 16px',
            background: sevBg,
            borderLeft: `3px solid ${sevColor}`,
            borderBottom: i < warnings.length - 1 ? '1px solid var(--qqq-b1)' : 'none',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6,
            }}>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                color: sevColor, padding: '2px 7px',
                background: 'var(--surface-3, #171723)',
                borderRadius: 3,
              }}>
                {typeLabel}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 600, letterSpacing: '0.05em',
                color: sevColor, textTransform: 'uppercase',
              }}>
                {w.severity === 'hi' ? '● High Severity'
                 : w.severity === 'md' ? '● Medium Severity'
                 : '● Low Severity'}
              </span>
            </div>
            <div style={{
              fontSize: 14, fontWeight: 600,
              color: 'var(--qqq-fg-1, #eee)',
              marginBottom: 6, lineHeight: 1.4,
            }}>
              {w.headline}
            </div>
            <div style={{
              fontSize: 12, color: 'var(--qqq-fg-2, #aaa)',
              lineHeight: 1.55,
            }}>
              {w.detail}
            </div>
          </div>
        );
      })}
      <div style={{
        fontSize: 11, color: 'var(--qqq-fg-3, #888)',
        padding: '10px 16px', lineHeight: 1.5,
        borderTop: '1px solid var(--qqq-b1, #2a2a2a)',
      }}>
        These flags don&apos;t mean &ldquo;don&apos;t trade&rdquo; — they mean
        &ldquo;don&apos;t trade BLINDLY.&rdquo; Each warning is conflicting evidence
        from your own history. Reduce size when warnings stack up.
      </div>
    </DataCard>
  );
}

// ─── Phase 2, Position ⑤ — Today Looks Like... (Commit 11) ───
// At 9:46 ET (after the opening candle captures), surface the
// 3 most-similar past sessions. Three states:
//   A. Pending — opening candle not yet captured today
//   B. Need-more-history — captured but < 3 labeled past sessions
//   C. Live — top 3 analogs with consensus summary
function AnalogFinderCard() {
  const data = useQqqData();
  const analogs = data._analogs;
  const todayCandle = data.openingCandle;

  // ─── State A: Opening candle hasn't fired yet ────────────
  if (!todayCandle) {
    return (
      <div style={{ borderStyle: 'dashed', opacity: 0.85, borderRadius: 8, border: '1px dashed var(--qqq-purple, #9b8fc4)', background: 'var(--surface-2, #111118)' }}>
        <div className="qqq-dc-hdr">
          <div className="qqq-dc-lbl" style={{ color: 'var(--qqq-purple, #9b8fc4)' }}>Today Looks Like…</div>
          <div className="qqq-dc-src" style={{ color: 'var(--qqq-fg-3)' }}>
            unlocks at 9:46 ET
          </div>
        </div>
        <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          The analog finder activates after today&apos;s opening candle is
          captured. Once active, it ranks your past sessions by feature
          overlap with today and shows the top 3 closest matches with
          their actual window outcomes — your decision-support moment.
        </div>
      </div>
    );
  }

  // ─── State B: Need more history ───────────────────────────
  if (!analogs || (analogs.reason === 'need_more_history')) {
    const count = analogs?.sample_size || 0;
    return (
      <DataCard
        label="Today Looks Like…"
        source={`${count} fully-labeled past session${count === 1 ? '' : 's'} · need 3+`}
        tone="purple"
      >
        <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          Analogs unlock once you have 3+ resolved sessions with both an
          opening candle AND a window outcome captured. Keep logging — by
          day 5 you&apos;ll see your first matches.
        </div>
      </DataCard>
    );
  }

  // ─── State C: Live analogs ────────────────────────────────
  const consensusLabel = {
    unanimous: '3 of 3 closed same direction',
    majority:  '2 of 3 closed same direction',
    split:     'Split outcomes — no consensus',
  }[analogs.consensus] || '';
  const consensusTone = analogs.dominant_direction === 'up' ? 'bull'
    : analogs.dominant_direction === 'down' ? 'bear'
    : 'neutral';

  return (
    <DataCard
      label="Today Looks Like…"
      source={`Top 3 of ${analogs.sample_size} fully-labeled past sessions · feature overlap`}
      tone="purple"
    >
      {/* Consensus headline */}
      {analogs.dominant_direction && (
        <div className={`qqq-conclusion ${consensusTone === 'bull' ? 'price' : consensusTone === 'bear' ? 'risk' : 'env'}`}>
          <div className={`qqq-conc-dot ${consensusTone}`} />
          <div className="qqq-conc-text">
            <strong>Closest analogs say:</strong> {consensusLabel}
            {analogs.dominant_direction && (
              <> · {analogs.dominant_direction === 'up' ? 'closed up'
                  : analogs.dominant_direction === 'down' ? 'closed down'
                  : 'closed flat'}</>
            )}
          </div>
          <div className={`qqq-conc-badge ${consensusTone}`}>
            {analogs.consensus.toUpperCase()}
          </div>
        </div>
      )}

      {/* Each analog row */}
      <div className="qqq-mini-bars padded">
        {analogs.analogs.map((a, i) => {
          const wo = a.window_outcome;
          const dirTone = wo.direction === 'up' ? 'qqq-up'
            : wo.direction === 'down' ? 'qqq-dn'
            : '';
          const shapeLabel = ({
            trend_up:         'Trend Up',
            trend_down:       'Trend Down',
            v_reversal_up:    'V-Reversal Up',
            inverted_v:       'Inverted V',
            late_breakout_up: 'Late Breakout',
            late_breakdown:   'Late Breakdown',
            fade:             'Fade',
            chop:             'Chop',
          })[wo.shape] || wo.shape;
          return (
            <div key={i} className="qqq-pattern-row" style={{
              padding: '12px 16px',
              borderBottom: i < analogs.analogs.length - 1 ? '1px solid var(--qqq-b1)' : 'none',
            }}>
              <div className="qqq-pr-name">
                <div className="qqq-pr-n" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600,
                    color: 'var(--qqq-fg-3, #888)',
                    background: 'var(--surface-3, #171723)',
                    padding: '2px 7px', borderRadius: 10,
                  }}>
                    #{i + 1}
                  </span>
                  {fmtBriefDate(a.brief_date)}
                  <span style={{
                    fontSize: 11, color: 'var(--qqq-fg-3)',
                    fontWeight: 400, marginLeft: 4,
                  }}>
                    {a.score} of {a.comparable} features matched ({a.match_pct}%)
                  </span>
                </div>
                <div className="qqq-pr-m" style={{ marginTop: 4 }}>
                  → <strong>{shapeLabel}</strong>
                  <span className={dirTone} style={{ marginLeft: 6, fontWeight: 600 }}>
                    {wo.close_change_pct >= 0 ? '+' : ''}{wo.close_change_pct?.toFixed(2)}%
                  </span>
                  {wo.mfe_pct != null && wo.mae_pct != null && (
                    <span style={{ color: 'var(--qqq-fg-3)', marginLeft: 8, fontSize: 11 }}>
                      · MFE +{wo.mfe_pct.toFixed(2)}% / MAE {wo.mae_pct.toFixed(2)}%
                    </span>
                  )}
                </div>
                {wo.read && (
                  <div style={{
                    fontSize: 12, color: 'var(--qqq-fg-2)',
                    marginTop: 6, lineHeight: 1.5,
                    fontStyle: 'italic',
                  }}>
                    {wo.read}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer note explaining what this is good for */}
      <div style={{
        fontSize: 12, color: 'var(--qqq-fg-2)',
        padding: '10px 16px',
        background: 'rgba(155,143,196,.04)',
        borderLeft: '2px solid var(--qqq-purple, #9b8fc4)',
        margin: '6px 0 0',
        lineHeight: 1.55,
      }}>
        These are the past sessions whose features (candle shape, position,
        regime, day of week, events, etc.) most closely match today.
        Read what happened in those sessions — it&apos;s your highest-signal
        prior on today&apos;s window. Not a guarantee, but the best evidence
        you have for &ldquo;what tends to happen in setups like this.&rdquo;
      </div>
    </DataCard>
  );
}

// ─── Phase 3: Window Progress Bar ────────────────────────────
// Visual indicator of where we are in the 9:45-11:30 trading
// window. Three states:
//   - Before 9:45: "Window opens in N min" countdown
//   - 9:45 – 11:30: live progress bar with elapsed/remaining
//   - After 11:30: "Window closed at 11:30 ET" + duration
// Live MFE/MAE display lands here in Commit 13 — for now this is
// just the progress visual.
function WindowProgressBar() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  // ET minutes-of-day right now
  const wd = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' })).getDay();
  const isWeekend = wd === 0 || wd === 6;
  const hh = Number(now.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York', hour: '2-digit', hour12: false,
  }).split(':')[0]);
  const mm = Number(now.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York', minute: '2-digit',
  }));
  const minOfDay = hh * 60 + mm;
  const windowStart = 9 * 60 + 45;   // 585
  const windowEnd   = 11 * 60 + 30;  // 690
  const windowLen   = windowEnd - windowStart; // 105 min

  let stateLabel, progressPct, subLabel;
  if (isWeekend) {
    stateLabel = 'Window closed · Weekend';
    progressPct = 0;
    subLabel = 'Next opens Monday at 9:45 AM ET';
  } else if (minOfDay < windowStart) {
    const minsTo = windowStart - minOfDay;
    stateLabel = `Window opens in ${formatMinAsDuration(minsTo)}`;
    progressPct = 0;
    subLabel = '9:45 AM ET — entry trigger';
  } else if (minOfDay <= windowEnd) {
    const elapsed = minOfDay - windowStart;
    const remaining = windowEnd - minOfDay;
    progressPct = Math.round((elapsed / windowLen) * 100);
    stateLabel = `Window in progress · ${formatMinAsDuration(remaining)} remaining`;
    subLabel = `Elapsed ${formatMinAsDuration(elapsed)} · Exit at 11:30 AM ET`;
  } else {
    stateLabel = 'Window closed at 11:30 AM ET';
    progressPct = 100;
    subLabel = 'Outcome captures at 11:31 ET — see Phase 4 below';
  }

  const isLive = !isWeekend && minOfDay >= windowStart && minOfDay <= windowEnd;

  return (
    <div className="qqq-data-card" style={{
      marginTop: 12,
      borderLeft: `3px solid ${isLive ? 'var(--qqq-gold, #c8a96e)' : 'var(--qqq-fg-3, #555)'}`,
    }}>
      <div className="qqq-dc-hdr">
        <div className="qqq-dc-lbl" style={{ color: isLive ? 'var(--qqq-gold, #c8a96e)' : 'var(--qqq-fg-2, #aaa)' }}>
          {stateLabel}
        </div>
        <div className="qqq-dc-src">{subLabel}</div>
      </div>
      <div style={{ padding: '14px 16px' }}>
        {/* Visual progress bar with start + end markers */}
        <div style={{
          position: 'relative',
          height: 10,
          background: 'var(--surface-3, #171723)',
          borderRadius: 5,
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute',
            left: 0, top: 0, bottom: 0,
            width: `${progressPct}%`,
            background: isLive
              ? 'linear-gradient(90deg, var(--qqq-gold, #c8a96e), var(--qqq-sage, #7a9e82))'
              : 'var(--qqq-fg-3, #555)',
            transition: 'width 0.5s ease-out',
          }} />
          {isLive && (
            <div style={{
              position: 'absolute',
              left: `${progressPct}%`,
              top: -4, bottom: -4,
              width: 2,
              background: 'var(--qqq-gold, #c8a96e)',
              boxShadow: '0 0 8px var(--qqq-gold, #c8a96e)',
            }} />
          )}
        </div>
        {/* Time markers */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: 11, color: 'var(--qqq-fg-3, #888)',
          marginTop: 6,
        }}>
          <span>9:45 AM</span>
          <span>10:15</span>
          <span>10:45</span>
          <span>11:15</span>
          <span>11:30 AM</span>
        </div>
      </div>
      {/* Commit 13 — Live MFE/MAE excursion display.
          Polls every 60s during active window. Shows entry,
          current, MFE (max favorable up), MAE (max adverse down). */}
      {isLive && <LiveExcursionDisplay />}
    </div>
  );
}

// ─── Phase 3, Position ⑧ — Live MFE/MAE Display (Commit 13) ──
// Polls /api/workstations/qqq-window-live every 60s during the
// active window. Renders entry / current / max-up / max-down
// with a visual excursion bar showing where current price sits
// between the day's adverse and favorable extremes.
function LiveExcursionDisplay() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchLive() {
      try {
        const res = await fetch('/api/workstations/qqq-window-live?ticker=QQQ', { cache: 'no-store' });
        const payload = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !payload.ok) {
          setError(payload?.error || `HTTP ${res.status}`);
          setLoading(false);
          return;
        }
        setSnapshot(payload);
        setError(null);
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'fetch failed');
          setLoading(false);
        }
      }
    }
    fetchLive();
    const id = setInterval(fetchLive, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading && !snapshot) {
    return (
      <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13 }}>
        Loading live snapshot…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{
        padding: '12px 16px', fontSize: 12,
        color: 'var(--qqq-terra, #c46050)',
        background: 'rgba(196,96,80,.05)',
        borderTop: '1px solid var(--qqq-b1)',
      }}>
        Live snapshot error: {error} · retrying in 60s
      </div>
    );
  }
  if (!snapshot || snapshot.status !== 'live' || snapshot.entry_price == null) {
    return (
      <div style={{
        padding: '14px 16px', fontSize: 13,
        color: 'var(--qqq-fg-2)',
        borderTop: '1px solid var(--qqq-b1)',
      }}>
        {snapshot?.message || 'Waiting for first bar…'}
      </div>
    );
  }

  const entry      = snapshot.entry_price;
  const current    = snapshot.current_price;
  const exc        = snapshot.current_excursion_pct;
  const maxUp      = snapshot.max_up_pct;
  const maxDown    = snapshot.max_down_pct;
  const excTone    = exc > 0 ? 'qqq-up' : exc < 0 ? 'qqq-dn' : '';

  // Excursion bar: visualize current position between -maxDown and +maxUp
  // Center is 0%. Bar shows where `exc` sits in [-maxDown, +maxUp].
  const range = (maxUp + maxDown) || 1;
  const currentPos = ((exc + maxDown) / range) * 100;

  return (
    <div style={{
      borderTop: '1px solid var(--qqq-b1, #2a2a2a)',
      padding: '14px 16px',
      background: 'rgba(200,169,110,.02)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        fontSize: 11, color: 'var(--qqq-fg-3, #888)',
        textTransform: 'uppercase', letterSpacing: '0.05em',
        marginBottom: 12,
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: 'var(--qqq-gold, #c8a96e)',
          animation: 'pulse 2s infinite',
        }} />
        Live · {snapshot.bars_count} bars · last update {snapshot.last_bar_time} ET
      </div>

      {/* Three-column metrics: entry, current, excursion */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14,
        marginBottom: 14,
      }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Entry (9:45)
          </div>
          <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--qqq-fg-1)' }}>
            {entry.toFixed(2)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Current
          </div>
          <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--qqq-fg-1)' }}>
            {current.toFixed(2)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Excursion
          </div>
          <div className={excTone} style={{ fontSize: 22, fontWeight: 700 }}>
            {exc >= 0 ? '+' : ''}{exc.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* MFE/MAE excursion bar */}
      <div style={{ marginBottom: 14 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: 11, color: 'var(--qqq-fg-3)', marginBottom: 4,
        }}>
          <span className="qqq-dn">MAE −{maxDown.toFixed(2)}% @ {snapshot.lod_time}</span>
          <span style={{ color: 'var(--qqq-fg-2)' }}>0% (entry)</span>
          <span className="qqq-up">MFE +{maxUp.toFixed(2)}% @ {snapshot.hod_time}</span>
        </div>
        <div style={{
          position: 'relative',
          height: 14,
          background: 'linear-gradient(90deg, rgba(196,96,80,.20), rgba(255,255,255,.03) 50%, rgba(122,158,130,.20))',
          borderRadius: 7,
          overflow: 'visible',
        }}>
          {/* Center line (entry / 0%) */}
          <div style={{
            position: 'absolute',
            left: `${(maxDown / range) * 100}%`,
            top: -2, bottom: -2,
            width: 1,
            background: 'var(--qqq-fg-3, #888)',
          }} />
          {/* Current position marker */}
          <div style={{
            position: 'absolute',
            left: `${currentPos}%`,
            top: -4, bottom: -4,
            width: 3,
            background: 'var(--qqq-gold, #c8a96e)',
            boxShadow: '0 0 10px var(--qqq-gold, #c8a96e)',
            borderRadius: 2,
            transform: 'translateX(-1.5px)',
            transition: 'left 0.6s ease-out',
          }} />
        </div>
      </div>

      {/* HOD / LOD reference */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14,
        fontSize: 12, color: 'var(--qqq-fg-2)',
      }}>
        <div>
          <span style={{ color: 'var(--qqq-fg-3)' }}>HOD: </span>
          <span style={{ fontWeight: 600 }}>{snapshot.hod?.toFixed(2)}</span>
          {snapshot.hod_time && <span style={{ color: 'var(--qqq-fg-3)' }}> @ {snapshot.hod_time}</span>}
        </div>
        <div>
          <span style={{ color: 'var(--qqq-fg-3)' }}>LOD: </span>
          <span style={{ fontWeight: 600 }}>{snapshot.lod?.toFixed(2)}</span>
          {snapshot.lod_time && <span style={{ color: 'var(--qqq-fg-3)' }}> @ {snapshot.lod_time}</span>}
        </div>
      </div>

      {/* Refresh hint */}
      <div style={{
        marginTop: 12, fontSize: 11, color: 'var(--qqq-fg-3, #888)',
        fontStyle: 'italic',
      }}>
        Refreshes every 60 seconds. Yahoo 1-min bars — small lag (1-2 min)
        vs. real-time feeds. Use your broker for fill execution.
      </div>
    </div>
  );
}

function formatMinAsDuration(min) {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// ═══════════════════════════════════════════════════════════
// SEQ RAIL — left-side step indicator
// ═══════════════════════════════════════════════════════════
function SeqSection({ step, stepTone, eyebrowLabel, eyebrowTone, children, lastSection }) {
  return (
    <div className="qqq-seq-section">
      <div className="qqq-seq-rail">
        <div className={`qqq-seq-num ${stepTone}`}>{step}</div>
        {!lastSection && <div className="qqq-seq-line" />}
      </div>
      <div className="qqq-seq-content">
        <div className="qqq-sec-eyebrow">
          <div className={`qqq-se-label ${eyebrowTone}`}>{eyebrowLabel}</div>
        </div>
        {children}
      </div>
    </div>
  );
}

function Conclusion({ tone, dotTone, text, badge, badgeTone }) {
  return (
    <div className={`qqq-conclusion ${tone}`}>
      <div className={`qqq-conc-dot ${dotTone}`} />
      <div className="qqq-conc-text">{text}</div>
      <div className={`qqq-conc-badge ${badgeTone}`}>{badge}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CARD DESIGN SYSTEM (Commit 42 — Tim's 4-color spec)
// ═══════════════════════════════════════════════════════════
// Always tone-coded by category. Each card type instantly
// recognizable. Brand colors used in priority order:
//   GOLD   — identity / decision-driving (Verdict, Method Sheet,
//            Sharpening Focus, Plan rules)
//   SAGE   — healthy / working / data foundation (Yields, VIX,
//            sectors, positive signals, validated patterns)
//   PURPLE — depth / synthesis (Jarvis, derived patterns,
//            Edge Emerging, calibration, sentiment)
//   TERRA  — warning / contradiction (alerts, decay, stops,
//            misdirections, false signals)
//   TEAL   — tertiary, used sparingly for market structure
//            differentiation when other 4 don't fit
// ═══════════════════════════════════════════════════════════

const TONE_COLORS = {
  gold:   'var(--qqq-gold, #c8a96e)',
  sage:   'var(--qqq-sage, #7a9e82)',
  purple: 'var(--qqq-purple, #9b8fc4)',
  terra:  'var(--qqq-terra, #c46050)',
  teal:   'var(--qqq-teal, #4db8b8)',
  muted:  'var(--qqq-fg-3, #888)',
};

function DataCard({ label, source, children, tone, status, freshness, capturedAt }) {
  // tone-coded card: 3px left rail in tone color + tone-tinted eyebrow
  const railColor = tone ? TONE_COLORS[tone] : null;
  const cardStyle = railColor ? {
    borderLeft: `3px solid ${railColor}`,
  } : {};
  const labelStyle = railColor ? {
    color: railColor,
  } : {};
  return (
    <div className="qqq-data-card" style={cardStyle}>
      <div className="qqq-dc-hdr">
        <div className="qqq-dc-lbl" style={labelStyle}>
          {label}
        </div>
        <div className="qqq-dc-src" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {source && <span>{source}</span>}
          {freshness && <FreshnessBadge kind={freshness} at={capturedAt} />}
        </div>
      </div>
      {/* Optional status line — single sentence below the eyebrow
          telling user the read at-a-glance ("inverted · long-term
          caution", "elevated · stressed regime", etc.) */}
      {status && (
        <div style={{
          padding: '6px 16px 0',
          fontSize: 11,
          color: railColor || 'var(--qqq-fg-2)',
          fontStyle: 'italic',
          letterSpacing: '0.02em',
        }}>
          {status}
        </div>
      )}
      {children}
    </div>
  );
}

// ─── StatusCard — for decision-driving big-typography cards ──
// Use for: Verdict, Method Statement, Sharpening Focus.
// Heavy visual weight — top accent + larger typography.
function StatusCard({ tone = 'gold', eyebrow, headline, sub, children }) {
  const color = TONE_COLORS[tone] || TONE_COLORS.gold;
  return (
    <div className="qqq-data-card" style={{
      borderTop: `3px solid ${color}`,
      background: 'var(--surface-2, #111118)',
    }}>
      {eyebrow && (
        <div style={{
          padding: '14px 18px 4px',
          fontSize: 11, color: color, textTransform: 'uppercase',
          letterSpacing: '0.05em', fontWeight: 700,
        }}>
          {eyebrow}
        </div>
      )}
      {headline && (
        <div style={{
          padding: '0 18px 6px',
          fontFamily: 'Literata, Georgia, serif',
          fontSize: 28, fontWeight: 600, color: color, lineHeight: 1.1,
        }}>
          {headline}
        </div>
      )}
      {sub && (
        <div style={{
          padding: '0 18px 14px',
          fontSize: 13, color: 'var(--qqq-fg-2)', lineHeight: 1.5,
        }}>
          {sub}
        </div>
      )}
      {children && (
        <div style={{ padding: '0 0 14px' }}>{children}</div>
      )}
    </div>
  );
}

// ─── AlertCard — for warnings, decay, contradictions ────────
// Tinted background + tone left rail + bold headline. Use
// terra for danger, gold for medium, purple for low-attention.
function AlertCard({ tone = 'terra', headline, detail, children }) {
  const color = TONE_COLORS[tone] || TONE_COLORS.terra;
  // Background tint — convert tone to a subtle rgba background
  const bgTint = tone === 'terra' ? 'rgba(196,96,80,.06)'
    : tone === 'gold' ? 'rgba(200,169,110,.06)'
    : tone === 'sage' ? 'rgba(122,158,130,.06)'
    : tone === 'purple' ? 'rgba(155,143,196,.06)'
    : 'rgba(155,143,196,.04)';
  return (
    <div style={{
      background: bgTint,
      borderLeft: `3px solid ${color}`,
      borderRadius: 8,
      padding: '12px 16px',
      marginBottom: 10,
    }}>
      {headline && (
        <div style={{
          fontSize: 13, color: color, fontWeight: 600,
          marginBottom: detail ? 6 : 0, lineHeight: 1.4,
        }}>
          {headline}
        </div>
      )}
      {detail && (
        <div style={{ fontSize: 12, color: 'var(--qqq-fg-2)', lineHeight: 1.55 }}>
          {detail}
        </div>
      )}
      {children}
    </div>
  );
}

// ─── MutedCard — for low-priority reference data ───────────
// Subdued treatment. No left rail. Inline single-strip layout.
function MutedCard({ label, items }) {
  return (
    <div style={{
      padding: '10px 16px',
      background: 'transparent',
      borderTop: '1px solid var(--qqq-b1, #2a2a2a)',
      borderBottom: '1px solid var(--qqq-b1, #2a2a2a)',
      marginBottom: 12,
    }}>
      {label && (
        <div style={{
          fontSize: 10, color: 'var(--qqq-fg-3)', textTransform: 'uppercase',
          letterSpacing: '0.05em', marginBottom: 6,
        }}>
          {label}
        </div>
      )}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 16,
        fontSize: 13, color: 'var(--qqq-fg-2)',
      }}>
        {items}
      </div>
    </div>
  );
}

// ─── Opening Candle Card (Commit 5) ──────────────────────────
// Surfaces the 9:30-9:45 ET 15-min candle that drives Tim's
// 9:45 0DTE entry. Three states:
//   1. Pending — cron hasn't run yet (before 9:46 ET or weekend)
//   2. Captured — full candle data with shape + position + volume + read
//   3. Error — cron ran but failed (handled by null-tolerant rendering)
function OpeningCandleCard() {
  const data = useQqqData();
  const c = data.openingCandle;

  // ─── State 1: Pending ─────────────────────────────────────
  if (!c) {
    return (
      <div className="qqq-data-card" style={{
        borderStyle: 'dashed', opacity: 0.85,
        borderLeft: '3px dashed var(--qqq-gold, #c8a96e)',
      }}>
        <div className="qqq-dc-hdr">
          <div className="qqq-dc-lbl" style={{ color: 'var(--qqq-gold, #c8a96e)' }}>
            Opening Candle · 9:30–9:45 ET
          </div>
          <div className="qqq-dc-src" style={{ color: 'var(--qqq-fg-3, #888)' }}>
            captured at 9:46 AM ET
          </div>
        </div>
        <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          Waiting for today&apos;s 15-min opening candle. Refreshes after the
          9:46 AM ET cron fires.
        </div>
      </div>
    );
  }

  // ─── State 2: Captured ────────────────────────────────────
  // Tone the card based on the candle direction
  const dirTone = c.direction === 'bull' ? 'bull'
    : c.direction === 'bear' ? 'bear'
    : 'neutral';
  const dirColor = c.direction === 'bull' ? 'qqq-up'
    : c.direction === 'bear' ? 'qqq-dn'
    : '';
  // Pretty labels for shape + gap interaction
  const shapeLabel = {
    marubozu:      'Marubozu',
    hammer:        'Hammer',
    shooting_star: 'Shooting Star',
    doji:          'Doji',
    normal:        'Normal',
  }[c.shape] || c.shape;
  const gapLabel = {
    gap_up_continuation:    'Gap Up · Continuation',
    gap_up_fade:            'Gap Up · Fade',
    gap_up_indecision:      'Gap Up · Indecision',
    gap_down_continuation:  'Gap Down · Continuation',
    gap_down_fade:          'Gap Down · Fade',
    gap_down_indecision:    'Gap Down · Indecision',
    no_gap:                 'No Gap',
  }[c.gap_interaction] || c.gap_interaction;

  return (
    <DataCard label="Opening Candle · 9:30–9:45 ET" source={`${c.ticker} · 1-min bars · captured ${fmtRelative(c.captured_at)}`} tone="gold">
      {/* Headline: shape + direction badge */}
      <div className={`qqq-conclusion ${dirTone === 'bull' ? 'price' : dirTone === 'bear' ? 'risk' : 'env'}`}>
        <div className={`qqq-conc-dot ${dirTone}`} />
        <div className="qqq-conc-text">
          <strong>{shapeLabel}</strong> · {gapLabel}
        </div>
        <div className={`qqq-conc-badge ${dirTone}`}>
          {c.direction.toUpperCase()}
        </div>
      </div>

      {/* OHLCV strip */}
      <div className="qqq-levels-strip">
        <div className="qqq-ls-item">
          <div className="qqq-ls-lbl">Open</div>
          <div className="qqq-ls-val">{c.ohlcv.open?.toFixed(2)}</div>
        </div>
        <div className="qqq-ls-item">
          <div className="qqq-ls-lbl">High</div>
          <div className="qqq-ls-val">{c.ohlcv.high?.toFixed(2)}</div>
        </div>
        <div className="qqq-ls-item">
          <div className="qqq-ls-lbl">Low</div>
          <div className="qqq-ls-val">{c.ohlcv.low?.toFixed(2)}</div>
        </div>
        <div className="qqq-ls-item">
          <div className="qqq-ls-lbl">Close</div>
          <div className={`qqq-ls-val ${dirColor}`}>{c.ohlcv.close?.toFixed(2)}</div>
        </div>
        <div className="qqq-ls-item">
          <div className="qqq-ls-lbl">Body</div>
          <div className="qqq-ls-val">{Math.round((c.body?.pct || 0) * 100)}%</div>
        </div>
      </div>

      {/* Position vs reference levels — only show populated ones */}
      {c.position && (
        <div className="qqq-mini-bars padded" style={{ borderTop: '1px solid var(--qqq-b1)', paddingTop: 8 }}>
          {[
            { label: 'vs PDH',        v: c.position.vs_pdh },
            { label: 'vs PDL',        v: c.position.vs_pdl },
            { label: 'vs Gap Flip',   v: c.position.vs_gap_flip },
            { label: 'vs 20D MA',     v: c.position.vs_20d_ma },
            { label: 'vs 50D MA',     v: c.position.vs_50d_ma },
            { label: 'vs 200D MA',    v: c.position.vs_200d_ma },
          ]
          .filter((row) => row.v != null)
          .map((row, i) => {
            const tone = row.v === 'above' ? 'qqq-up'
              : row.v === 'below' ? 'qqq-dn'
              : '';
            return (
              <div key={i} className="qqq-mb-row">
                <div className="qqq-mb-lbl">{row.label}</div>
                <div className="qqq-mb-wrap"><div className="qqq-mb-fill" style={{ width: '0%' }} /></div>
                <div className={`qqq-mb-v ${tone}`}>{row.v}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Volume context */}
      {c.volume_context && c.volume_context.classification && (
        <div className="qqq-spread-row" style={{
          background: c.volume_context.classification === 'high' ? 'rgba(122,158,130,.04)'
            : c.volume_context.classification === 'low' ? 'rgba(196,96,80,.04)'
            : 'transparent',
        }}>
          <span className="qqq-spread-lbl">Volume vs 30-day avg</span>
          <span className={`qqq-spread-val ${
            c.volume_context.classification === 'high' ? 'qqq-up'
            : c.volume_context.classification === 'low' ? 'qqq-dn' : ''
          }`}>
            {c.volume_context.ratio}× ({c.volume_context.classification})
          </span>
        </div>
      )}

      {/* Plain-language read */}
      {c.read && (
        <div style={{
          fontSize: 13,
          color: 'var(--qqq-fg-1)',
          padding: '10px 16px',
          background: 'rgba(200,169,110,.04)',
          borderLeft: '2px solid var(--qqq-gold, #c8a96e)',
          margin: '6px 0 0',
          lineHeight: 1.55,
        }}>
          {c.read}
        </div>
      )}
    </DataCard>
  );
}

// ─── Phase 1 — Volume Profile Card (Commit 15) ───────────────
// Surfaces yesterday's POC / VAH / VAL — the institutional
// transaction-level reference for today's morning prep. Three
// states:
//   A. Pending — cron hasn't captured a profile yet (first day
//      of use OR before today's 4:05 PM cron has fired)
//   B. Captured — full layout: POC + VA range + position relative
//      to current price + plain-language read
function VolumeProfileCard() {
  const data = useQqqData();
  const vp = data.volumeProfile;
  const currentPrice = Number(data.prices?.qqq?.price) || null;

  // ─── State A: Pending ─────────────────────────────────────
  if (!vp) {
    return (
      <div className="qqq-data-card" style={{ borderStyle: 'dashed', opacity: 0.85 }}>
        <div className="qqq-dc-hdr">
          <div className="qqq-dc-lbl">Volume Profile · Yesterday</div>
          <div className="qqq-dc-src" style={{ color: 'var(--qqq-fg-3)' }}>
            captures after market close (4:05 PM ET)
          </div>
        </div>
        <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          POC (highest-volume price), VAH, and VAL from yesterday&apos;s
          regular session. Computed from Yahoo&apos;s 5-min bars after market
          close. Refreshes every weekday around 4:05 PM ET.
        </div>
      </div>
    );
  }

  // ─── State B: Captured ────────────────────────────────────
  // Position of current price vs VA: above VAH, inside VA, below VAL
  let posLabel, posTone;
  if (currentPrice == null) {
    posLabel = 'Now: —';
    posTone = '';
  } else if (currentPrice > vp.vah) {
    posLabel = `Now ${currentPrice.toFixed(2)} · ABOVE Value Area (extended)`;
    posTone = 'qqq-up';
  } else if (currentPrice < vp.val) {
    posLabel = `Now ${currentPrice.toFixed(2)} · BELOW Value Area (rejected)`;
    posTone = 'qqq-dn';
  } else {
    posLabel = `Now ${currentPrice.toFixed(2)} · INSIDE Value Area (in balance)`;
    posTone = '';
  }

  // Where does the current price sit visually within the VA range?
  // Show as a marker on a horizontal bar from VAL → VAH.
  const range = vp.vah - vp.val;
  const currentInRange = currentPrice != null
    ? Math.max(0, Math.min(1, (currentPrice - vp.val) / range))
    : null;
  const pocInRange = Math.max(0, Math.min(1, (vp.poc - vp.val) / range));

  return (
    <DataCard
      label="Volume Profile · Yesterday"
      source={`${vp.session_date} · ${vp.bin_count} price levels · ${vp.value_area_pct}% VA`}
    >
      {/* POC + VAH + VAL strip */}
      <div className="qqq-levels-strip">
        <div className="qqq-ls-item">
          <div className="qqq-ls-lbl">VAH</div>
          <div className="qqq-ls-val">{vp.vah?.toFixed(2)}</div>
        </div>
        <div className="qqq-ls-item">
          <div className="qqq-ls-lbl">POC</div>
          <div className="qqq-ls-val active">{vp.poc?.toFixed(2)}</div>
        </div>
        <div className="qqq-ls-item">
          <div className="qqq-ls-lbl">VAL</div>
          <div className="qqq-ls-val">{vp.val?.toFixed(2)}</div>
        </div>
        <div className="qqq-ls-item" style={{ flex: 2 }}>
          <div className="qqq-ls-lbl">Position</div>
          <div className={`qqq-ls-val ${posTone}`}>
            {posLabel.replace(/^Now \S+ · /, '')}
          </div>
        </div>
      </div>

      {/* Visual VA bar with POC + current price markers */}
      <div style={{ padding: '10px 16px 14px' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: 11, color: 'var(--qqq-fg-3)', marginBottom: 4,
        }}>
          <span>VAL {vp.val?.toFixed(2)}</span>
          <span>POC {vp.poc?.toFixed(2)}</span>
          <span>VAH {vp.vah?.toFixed(2)}</span>
        </div>
        <div style={{
          position: 'relative',
          height: 16,
          background: 'linear-gradient(90deg, rgba(155,143,196,.10), rgba(200,169,110,.20) 50%, rgba(155,143,196,.10))',
          borderRadius: 8,
          border: '1px solid var(--qqq-b1, #2a2a2a)',
        }}>
          {/* POC marker */}
          <div style={{
            position: 'absolute',
            left: `${pocInRange * 100}%`,
            top: -2, bottom: -2,
            width: 2,
            background: 'var(--qqq-gold, #c8a96e)',
            transform: 'translateX(-1px)',
          }} title="POC" />
          {/* Current price marker */}
          {currentInRange != null && currentPrice >= vp.val && currentPrice <= vp.vah && (
            <div style={{
              position: 'absolute',
              left: `${currentInRange * 100}%`,
              top: -5, bottom: -5,
              width: 3,
              background: 'var(--qqq-sage, #7a9e82)',
              transform: 'translateX(-1.5px)',
              boxShadow: '0 0 8px var(--qqq-sage, #7a9e82)',
              borderRadius: 2,
            }} title="QQQ Now" />
          )}
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: 11, color: 'var(--qqq-fg-3)', marginTop: 6,
        }}>
          <span>Below VA</span>
          <span style={{ color: 'var(--qqq-gold, #c8a96e)' }}>● POC anchor</span>
          <span>Above VA</span>
        </div>
      </div>

      {/* Plain-language read */}
      {vp.read && (
        <div style={{
          fontSize: 13, color: 'var(--qqq-fg-1)',
          padding: '10px 16px',
          background: 'rgba(200,169,110,.04)',
          borderLeft: '2px solid var(--qqq-gold, #c8a96e)',
          margin: '0',
          lineHeight: 1.55,
        }}>
          {vp.read}
        </div>
      )}
    </DataCard>
  );
}

// ─── Deferred / paid-feed placeholder ────────────────────────
// Used for data we KNOW we want but can't surface today because
// the free feeds don't expose it (Volume Profile, Session VWAP,
// full S&P 500 breadth — all need intraday-bar or paid tiers).
// Renders an elevated card with a clear "requires paid feed"
// label, the would-be sample structure greyed out, and a tldr
// of what we'd do with the data once we wire it. Intent: set
// clear expectations on the page instead of pretending we have
// these signals or hiding the gap entirely.
function DeferredCard({ label, why, sampleRows, useCase, tier }) {
  return (
    <div className="qqq-data-card" style={{
      borderStyle: 'dashed',
      opacity: 0.85,
    }}>
      <div className="qqq-dc-hdr">
        <div className="qqq-dc-lbl">{label}</div>
        <div className="qqq-dc-src" style={{ color: 'var(--qqq-fg-3, #888)' }}>
          {tier} · not yet wired
        </div>
      </div>
      <div style={{ padding: '10px 16px 4px', display: 'grid', gap: 10 }}>
        {/* Greyed sample rows so the user sees what the card WILL look like */}
        <div className="qqq-mini-bars" style={{ opacity: 0.4 }}>
          {sampleRows.map((r, i) => (
            <div key={i} className="qqq-mb-row">
              <div className="qqq-mb-lbl">{r.label}</div>
              <div className="qqq-mb-wrap">
                <div className="qqq-mb-fill" style={{
                  width: `${r.pct}%`,
                  background: 'var(--qqq-b1, #2a2a2a)',
                }} />
              </div>
              <div className="qqq-mb-v">—</div>
            </div>
          ))}
        </div>
        {/* Why this is greyed out */}
        <div style={{
          fontSize: 12,
          color: 'var(--qqq-fg-2)',
          padding: '8px 10px',
          background: 'rgba(155,143,196,.04)',
          borderLeft: '2px solid var(--qqq-purple, #9b8fc4)',
          borderRadius: 3,
          lineHeight: 1.55,
        }}>
          <strong style={{ color: 'var(--qqq-fg-1)' }}>Why deferred:</strong> {why}
          <br />
          <strong style={{ color: 'var(--qqq-fg-1)' }}>What you&apos;ll get:</strong> {useCase}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SECTION 1 — PRICE
// ═══════════════════════════════════════════════════════════
function Section1Price() {
  const data = useQqqData();
  const n = useNarrative('price');
  const { qqq, spy } = data.prices;
  const levels = data.levels;
  const trend = data.prices?.trend;
  return (
    <SeqSection step="1" stepTone="price" eyebrowLabel="Where Are We" eyebrowTone="price">
      <Conclusion
        tone="price"
        dotTone={n?.dotTone || 'bull'}
        text={n
          ? renderMarkers(n.text)
          : levels?.pdh && levels.pdh !== '—'
            ? <><strong>QQQ vs yesterday&apos;s high (PDH {levels.pdh})</strong> — pre-market read will populate once the morning brief runs.</>
            : <>Pre-market read pending. Morning brief populates this before 9:30 AM ET.</>
        }
        badge={n?.badge || 'BULL'}
        badgeTone={n?.badgeTone || 'bull'}
      />
      <DataCard label="Pre-Market Prices" source="Yahoo Finance · free" tone="sage"
        freshness="captured" capturedAt={data._generatedAt}>
        <div className="qqq-price-pair">
          <div className="qqq-ppi">
            <div className="qqq-ppi-t">QQQ</div>
            <div className="qqq-ppi-p">{qqq.price}</div>
            <div className="qqq-ppi-c up">▲ {qqq.change} · {qqq.changeDollar}</div>
            <div className="qqq-ppi-m">Close {qqq.close} · Vol {qqq.volume}</div>
            <div className="qqq-ppi-tags">
              <span className="qqq-ppi-tag g">Above PDH</span>
              <span className="qqq-ppi-tag g">+3.24 above flip</span>
            </div>
          </div>
          <div className="qqq-pdiv" />
          <div className="qqq-ppi">
            <div className="qqq-ppi-t">SPY</div>
            <div className="qqq-ppi-p">{spy.price}</div>
            <div className="qqq-ppi-c up">▲ {spy.change} · {spy.changeDollar}</div>
            <div className="qqq-ppi-m">Close {spy.close} · {spy.note}</div>
            <div className="qqq-ppi-tags">
              <span className="qqq-ppi-tag g">Tech leading</span>
            </div>
          </div>
        </div>
        <div className="qqq-levels-strip">
          {[
            { lbl: 'PDH',      val: levels.pdh,      cls: 'warn' },
            { lbl: 'PDL',      val: levels.pdl,      cls: '' },
            { lbl: 'Gap Flip', val: levels.gapFlip,  cls: '' },
            { lbl: 'QQQ Now',  val: levels.qqqNow,   cls: 'active' },
          ].map((l, i) => (
            <div key={i} className="qqq-ls-item">
              <div className="qqq-ls-lbl">{l.lbl}</div>
              <div className={`qqq-ls-val ${l.cls}`}>{l.val}</div>
            </div>
          ))}
        </div>
        {/* Batch B v3 + Commit 3: Trend levels strip — 20D / 50D / 200D MAs.
            Hidden until at least one MA is populated (cron writes them on
            the next 15-min run after the migration). */}
        {trend && (trend.twentyDayAvg !== '—'
          || trend.fiftyDayAvg !== '—'
          || trend.twoHundredDayAvg !== '—') && (() => {
          const above = [trend.vs20d, trend.vs50d, trend.vs200d].filter((v) => v === 'above').length;
          const below = [trend.vs20d, trend.vs50d, trend.vs200d].filter((v) => v === 'below').length;
          let stackLabel, stackTone;
          if (above === 3)      { stackLabel = 'Fully stacked · Strong uptrend';     stackTone = 'active'; }
          else if (above === 2) { stackLabel = 'Mostly above · Trend intact';        stackTone = 'active'; }
          else if (above === 1) { stackLabel = 'Mixed · Trend in transition';        stackTone = ''; }
          else if (below === 3) { stackLabel = 'Fully stacked · Stacked downtrend';  stackTone = 'warn'; }
          else if (below === 2) { stackLabel = 'Mostly below · Downtrend intact';    stackTone = 'warn'; }
          else                  { stackLabel = '—';                                  stackTone = ''; }
          return (
            <div className="qqq-levels-strip">
              <div className="qqq-ls-item">
                <div className="qqq-ls-lbl">20D MA</div>
                <div className={`qqq-ls-val ${trend.vs20d === 'above' ? 'active' : trend.vs20d === 'below' ? 'warn' : ''}`}>
                  {trend.twentyDayAvg}
                </div>
              </div>
              <div className="qqq-ls-item">
                <div className="qqq-ls-lbl">50D MA</div>
                <div className={`qqq-ls-val ${trend.vs50d === 'above' ? 'active' : trend.vs50d === 'below' ? 'warn' : ''}`}>
                  {trend.fiftyDayAvg}
                </div>
              </div>
              <div className="qqq-ls-item">
                <div className="qqq-ls-lbl">200D MA</div>
                <div className={`qqq-ls-val ${trend.vs200d === 'above' ? 'active' : trend.vs200d === 'below' ? 'warn' : ''}`}>
                  {trend.twoHundredDayAvg}
                </div>
              </div>
              <div className="qqq-ls-item" style={{ flex: 2 }}>
                <div className="qqq-ls-lbl">Stack</div>
                <div className={`qqq-ls-val ${stackTone}`}>{stackLabel}</div>
              </div>
            </div>
          );
        })()}
      </DataCard>

      {/* Commit 5 — Opening Candle (9:30-9:45 ET). The decision
          input for Tim's 0DTE entry at 9:45. Captured by the
          qqq-opening-candle cron; renders a pending state until
          that cron has fired today. */}
      <OpeningCandleCard />

      {/* Commit 15 — Volume Profile (LIVE, replaces the
          DeferredCard placeholder). Computed from yesterday's
          5-min bars via Yahoo, captured at 4:05 PM ET. */}
      <VolumeProfileCard />

      <DeferredCard
        label="Session VWAP · Live"
        tier="Requires intraday minute bars"
        why="Session VWAP needs minute-level bars updated through the trading day. Yahoo only exposes daily aggregates. Polygon Stocks Starter or Tradier's market-data feed would unlock streaming intraday VWAP + standard deviation bands."
        useCase="Above VWAP = mean buyers paying up. Below VWAP = sellers in control. ±1σ bands act as mean-reversion targets. Institutional desks trade against VWAP all day — having it on this page closes a big intraday-context gap."
        sampleRows={[
          { label: 'VWAP',     pct: 70 },
          { label: '+1σ band', pct: 88 },
          { label: '−1σ band', pct: 52 },
        ]}
      />
    </SeqSection>
  );
}

// ═══════════════════════════════════════════════════════════
// SECTION 2 — ENVIRONMENT
// ═══════════════════════════════════════════════════════════
function Section2Environment() {
  const data = useQqqData();
  const n = useNarrative('environment');
  // Live sub-card footers fall back to the static placeholder strings
  // when the brief hasn't loaded yet (helper returns 'loading').
  const vixPill   = vixTermPill(data);
  const ycFooter  = yieldSpreadFooter(data);
  return (
    <SeqSection step="2" stepTone="env" eyebrowLabel="The Weather Today" eyebrowTone="env">
      <Conclusion
        tone="env"
        dotTone={n?.dotTone || 'purple'}
        text={n
          ? renderMarkers(n.text)
          : <><strong>Calm backdrop.</strong> VIX normal at 17.4 with <span className="qqq-up">contango term structure</span> — near-term fear is resolving. Dollar weak. Global futures aligned.</>
        }
        badge={n?.badge || 'BULL'}
        badgeTone={n?.badgeTone || 'bull'}
      />
      <DataCard label="VIX Term Structure" source="CBOE · free" tone="purple"
        freshness="captured" capturedAt={data._generatedAt}>
        <div className="qqq-mini-bars">
          {(data.vixTerm || []).map((r, i) => (
            <div key={i} className="qqq-mb-row">
              <div className="qqq-mb-lbl">{r.label}</div>
              <div className="qqq-mb-wrap"><div className="qqq-mb-fill teal" style={{ width: `${r.pct}%` }} /></div>
              <div className="qqq-mb-v">{r.value}</div>
            </div>
          ))}
        </div>
        <div className="qqq-structure-tag">
          <span className={`qqq-pill ${vixPill.tone === 'bull' ? 'sage'
            : vixPill.tone === 'bear' ? 'terra' : 'teal'}`}>
            {vixPill.text}
          </span>
        </div>
      </DataCard>
      <DataCard label="Futures + Dollar" source="Yahoo Finance · free" tone="sage"
        freshness="captured" capturedAt={data._generatedAt}>
        <div className="qqq-compact-grid col4">
          {(data.futures || []).map((f, i) => (
            <div key={i} className="qqq-cg-cell">
              <div className="qqq-cg-lbl">{f.label}</div>
              <div className={`qqq-cg-val ${f.tone || ''}`}>{f.value}</div>
            </div>
          ))}
        </div>
      </DataCard>
      <DataCard label="Yield Curve" source="FRED · Treasury · free" tone="sage"
        freshness="captured" capturedAt={data._generatedAt}>
        <div className="qqq-compact-grid col5">
          {(data.yieldCurve || []).map((y, i) => (
            <div key={i} className="qqq-cg-cell">
              <div className="qqq-cg-lbl">{y.label}</div>
              <div className={`qqq-cg-val ${y.tone || ''}`}>{y.value}</div>
            </div>
          ))}
        </div>
        <div className="qqq-spread-row">
          <span className="qqq-spread-lbl">10Y−2Y Spread</span>
          <span className={`qqq-spread-val ${ycFooter.tone === 'bull' ? 'qqq-up'
            : ycFooter.tone === 'bear' ? 'qqq-dn' : ''}`}>
            {ycFooter.text}
          </span>
        </div>
      </DataCard>

      {/* Batch B v1: Credit spreads — early risk-off signal */}
      {data.credit && (
        <DataCard label="Credit Spreads · HYG vs LQD" source="Yahoo · free" tone="sage"
          freshness="captured" capturedAt={data._generatedAt}>
          <div className="qqq-compact-grid col4">
            <div className="qqq-cg-cell">
              <div className="qqq-cg-lbl">HYG</div>
              <div className={`qqq-cg-val ${data.credit.hyg.tone || ''}`}>{data.credit.hyg.value}</div>
            </div>
            <div className="qqq-cg-cell">
              <div className="qqq-cg-lbl">LQD</div>
              <div className={`qqq-cg-val ${data.credit.lqd.tone || ''}`}>{data.credit.lqd.value}</div>
            </div>
            <div className="qqq-cg-cell" style={{ gridColumn: 'span 2' }}>
              <div className="qqq-cg-lbl">Spread (HYG − LQD)</div>
              <div className={`qqq-cg-val ${data.credit.spread.tone || ''}`}>{data.credit.spread.value}</div>
            </div>
          </div>
          <div className="qqq-spread-row" style={{
            background: data.credit.spread.tone === 'dn' ? 'rgba(196,96,80,.04)'
              : data.credit.spread.tone === 'up' ? 'rgba(122,158,130,.04)'
              : 'transparent',
          }}>
            <span className="qqq-spread-lbl">Risk regime</span>
            <span className={`qqq-spread-val ${
              data.credit.spread.tone === 'dn' ? 'qqq-dn'
              : data.credit.spread.tone === 'up' ? 'qqq-up' : ''
            }`}>{data.credit.spread.label}</span>
          </div>
        </DataCard>
      )}

      {/* Batch B v1: Tech breadth — % of QQQ top-10 components positive */}
      {data.breadth && (
        <DataCard label="Tech Breadth · QQQ Top 10" source="Yahoo · free"
          freshness="captured" capturedAt={data._generatedAt}>
          <div className="qqq-pcr-row">
            <div className="qqq-pcr-n">{data.breadth.pct != null ? `${data.breadth.pct}%` : '—'}</div>
            <div className="qqq-pcr-meta">
              <div className="qqq-pcr-tag">{data.breadth.summary}</div>
              <div className="qqq-pcr-gauge">
                <div className="qqq-pcr-needle" style={{
                  left: data.breadth.pct != null ? `${data.breadth.pct}%` : '50%',
                }} />
              </div>
              <div className="qqq-pcr-scale">
                <span className="qqq-pcs t">0% (negative)</span>
                <span className="qqq-pcs s">50% (mixed)</span>
                <span className="qqq-pcs g">100% (strong)</span>
              </div>
            </div>
          </div>
          {data.breadth.movers && data.breadth.movers.length > 0 && (
            <div className="qqq-mini-bars padded" style={{ borderTop: '1px solid var(--qqq-b1)' }}>
              {data.breadth.movers.map((m, i) => (
                <div key={i} className="qqq-mb-row">
                  <div className="qqq-mb-lbl">{m.ticker}</div>
                  <div className="qqq-mb-wrap">
                    <div className={`qqq-mb-fill ${m.fillTone}`} style={{ width: `${m.pct}%` }} />
                  </div>
                  <div className={`qqq-mb-v ${m.tone}`}>{m.value}</div>
                </div>
              ))}
            </div>
          )}
        </DataCard>
      )}

      {/* Commit 4/4 — paid-feed placeholder. Top-10 breadth (above)
          is a free proxy; full S&P 500 breadth is the real signal
          and needs a paid index feed or 500 component pulls. */}
      <DeferredCard
        label="Full S&P 500 Breadth"
        tier="Requires breadth index or 500-ticker pull"
        why="The top-10 QQQ breadth above is a fast proxy. Real breadth (% of all 500 SPX components above their 50D MA, advance/decline line, new 52wk highs vs lows) needs either CBOE's NEWA / breadth index feed (~$50/mo) or 500 individual ticker pulls per cron run (heavy + brittle on free Yahoo)."
        useCase="Distinguishes 'tech leading' from 'broad rally' from 'narrow squeeze.' A QQQ rally with NEGATIVE S&P breadth = narrow tech-only move = fragile. A QQQ rally with POSITIVE S&P breadth = sustained risk-on = trend day. Major edge for sizing decisions."
        sampleRows={[
          { label: '% above 50D',  pct: 65 },
          { label: 'A/D line',     pct: 58 },
          { label: 'New highs−lows', pct: 72 },
        ]}
      />
    </SeqSection>
  );
}

// ═══════════════════════════════════════════════════════════
// SECTION 3 — STRUCTURE (FlashAlpha)
// ═══════════════════════════════════════════════════════════
function Section3Structure() {
  // Commit 90 — Gamma data deferred. FlashAlpha was returning stale /
  // cached values (gamma_flip pinned at 435 when QQQ is above 660).
  // Until we have a stable real-time gamma feed, show the standard
  // deferred treatment instead of wrong numbers. The entire section
  // (conclusion + data card) is replaced with the DeferredCard
  // pattern we use for Session VWAP / Full S&P Breadth.
  return (
    <SeqSection step="3" stepTone="structure" eyebrowLabel="What Dealers Must Do" eyebrowTone="structure">
      <DeferredCard
        label="Gamma Structure — QQQ"
        tier="Needs stable gamma feed"
        sampleRows={[
          { label: 'Gamma Flip',  pct: 65 },
          { label: 'Call Wall',   pct: 82 },
          { label: 'Put Wall',    pct: 40 },
        ]}
        why="FlashAlpha was returning stale gamma levels that didn't match current QQQ price. Until a reliable intraday gamma feed (CBOE Options Intelligence, SpotGamma, or equivalent) is wired, this section renders deferred rather than misleading."
        useCase="Tells you the mechanical regime — when dealers are LONG gamma they suppress volatility (dips get bought, rips get sold). When SHORT gamma they amplify moves (both directions stretch). Biggest single signal for whether today's setup will be clean or whippy."
      />
    </SeqSection>
  );
}

// ═══════════════════════════════════════════════════════════
// SECTION 4 — FLOW
// ═══════════════════════════════════════════════════════════
function Section4Flow() {
  const data = useQqqData();
  const n = useNarrative('flow');
  const secFooter = sectorLeadershipFooter(data);
  const pcr = data.pcr || {};
  return (
    <SeqSection step="4" stepTone="flow" eyebrowLabel="Where Is Money Going" eyebrowTone="flow">
      <Conclusion
        tone="flow"
        dotTone={n?.dotTone || 'bull'}
        text={n
          ? renderMarkers(n.text)
          : <><strong>Money flowing into calls and tech.</strong> PCR 0.82 confirms call buying. <span className="qqq-up">Tech sector leading SPY by +0.37%</span> — money is rotating into QQQ components.</>
        }
        badge={n?.badge || 'BULL'}
        badgeTone={n?.badgeTone || 'bull'}
      />
      <DataCard label="Put / Call Ratio · QQQ" source="CBOE · free"
        freshness="captured" capturedAt={data._generatedAt}>
        <div className="qqq-pcr-row">
          <div className="qqq-pcr-n">{pcr.value}</div>
          <div className="qqq-pcr-meta">
            <div className="qqq-pcr-tag">{pcr.tag}</div>
            <div className="qqq-pcr-gauge">
              <div className="qqq-pcr-needle" style={{ left: pcr.needleLeft }} />
            </div>
            <div className="qqq-pcr-scale">
              <span className="qqq-pcs g">0.6 greed</span>
              <span className="qqq-pcs s">1.0 neutral</span>
              <span className="qqq-pcs t">1.3 fear</span>
            </div>
          </div>
        </div>
      </DataCard>
      <DataCard label="Sector Leadership" source="FMP · free"
        freshness="captured" capturedAt={data._generatedAt}>
        <div className="qqq-mini-bars padded">
          {(data.sectors || []).map((s, i) => (
            <div key={i} className="qqq-mb-row">
              <div className="qqq-mb-lbl">{s.name}</div>
              <div className="qqq-mb-wrap"><div className={`qqq-mb-fill ${s.fillTone}`} style={{ width: `${s.pct}%` }} /></div>
              <div className={`qqq-mb-v ${s.tone}`}>{s.value}</div>
            </div>
          ))}
        </div>
        <div className="qqq-sector-footer">
          <div className={`qqq-sector-dot ${secFooter.tone === 'bear' ? 'dn'
            : secFooter.tone === 'neutral' ? 'neutral' : ''}`} />
          <div className={`qqq-sector-note ${secFooter.tone === 'bear' ? 'qqq-dn'
            : secFooter.tone === 'bull' ? 'qqq-up' : ''}`}>
            {secFooter.text}
          </div>
        </div>
      </DataCard>
    </SeqSection>
  );
}

// ═══════════════════════════════════════════════════════════
// SECTION 5 — RISK GATE
// ═══════════════════════════════════════════════════════════
function Section5Risk() {
  const data = useQqqData();
  const n = useNarrative('risk');
  return (
    <SeqSection step="5" stepTone="risk" eyebrowLabel="What Could Override Everything" eyebrowTone="risk">
      <Conclusion
        tone="risk"
        dotTone={n?.dotTone || 'neutral'}
        text={n
          ? renderMarkers(n.text)
          : <><strong>Window is clean 9:30–11:30.</strong> FOMC minutes at 2pm are outside Tim&apos;s window. Home Sales at 10am is medium impact — watch but do not pause entries for this.</>
        }
        badge={n?.badge || 'WATCH'}
        badgeTone={n?.badgeTone || 'neutral'}
      />
      <DataCard label="Events Today" source="Fed calendar · BLS · FMP · free"
        freshness="captured" capturedAt={data._generatedAt}>
        {(data.events || []).map((e, i) => (
          <div key={i} className="qqq-event-row">
            <div className={`qqq-er-bar ${e.sev}`} />
            <div className="qqq-er-time">{e.time}</div>
            <div className="qqq-er-name">{e.name}</div>
            <div className="qqq-er-note">{e.note}</div>
          </div>
        ))}
      </DataCard>
    </SeqSection>
  );
}

// ═══════════════════════════════════════════════════════════
// VERDICT
// ═══════════════════════════════════════════════════════════
// SectionVerdict wraps the verdict block in the SeqSection rail
// (used for the mobile inline placement between section 5 and
// the divider). VerdictBlock is the standalone block used in the
// desktop sticky sidebar.
function SectionVerdict() {
  return (
    <div className="qqq-seq-section">
      <div className="qqq-seq-rail">
        <div className="qqq-seq-num verdict">▣</div>
        <div className="qqq-seq-line qqq-seq-line-empty" />
      </div>
      <div className="qqq-seq-content">
        <div className="qqq-sec-eyebrow">
          <div className="qqq-se-label verdict">The Answer</div>
        </div>
        <VerdictBlock />
      </div>
    </div>
  );
}

function VerdictBlock({ compact = false }) {
  const data = useQqqData();
  const v = data.verdict || PLACEHOLDER.verdict;
  const ringTotal = 207.3;
  const ringOffset = ringTotal * (1 - v.scoreN / v.scoreD);
  return (
    <div className={`qqq-verdict-block ${compact ? 'is-compact' : ''}`}>
      <div className="qqq-vb-top">
        <div className="qqq-vbt-left">
          <div className="qqq-vbt-eyebrow">{v.eyebrow}</div>
          <div className={`qqq-vbt-direction ${v.directionTone}`}>{v.direction}</div>
          <div className="qqq-vbt-conf">
            <div className="qqq-vbt-score">{v.scoreN} of {v.scoreD} signals aligned</div>
            <div className={`qqq-vbt-conf-badge ${v.confidenceTone}`}>{v.confidence}</div>
          </div>
        </div>
        <div className="qqq-vb-ring">
          <svg width="80" height="80" viewBox="0 0 80 80">
            <circle className="qqq-vbr-bg" cx="40" cy="40" r="33" />
            <circle
              className={`qqq-vbr-fill ${v.directionTone}`}
              cx="40" cy="40" r="33"
              strokeDasharray={ringTotal}
              strokeDashoffset={ringOffset}
              transform="rotate(-90 40 40)"
            />
          </svg>
          <div className="qqq-vbr-center">
            <div className={`qqq-vbrc-n ${v.directionTone}`}>{v.scoreN}</div>
            <div className="qqq-vbrc-d">of {v.scoreD}</div>
          </div>
        </div>
      </div>
      <div className="qqq-vb-forces">
        {v.forces.map((f, i) => (
          <div key={i} className={`qqq-vbf ${f.tone}`}>{f.text}</div>
        ))}
      </div>
      <div className="qqq-vb-line">{v.summary}</div>
      <div className="qqq-vb-action">
        <div className="qqq-vba-window">{v.window}</div>
        <div className="qqq-vba-btn">{v.actionLabel}</div>
      </div>
      <div className="qqq-size-rule">
        {v.sizingOptions.map((s, i) => (
          <div key={i} className={`qqq-sr-item ${i === v.sizingActiveIdx ? 'active' : ''}`}>
            <div className="qqq-sri-score">{s.score}</div>
            <div className={`qqq-sri-size ${i === v.sizingActiveIdx ? 'active' : ''}`}>{s.size}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// EXECUTION PLAN — the actionable layer
// ═══════════════════════════════════════════════════════════
// Turns the verdict into rules: entry triggers, stop, scale
// ladder, hinge conditions, session rules. This is what makes
// the page a briefing INSTEAD of a read. Generated each morning
// from forces + user's stated risk rules.
function SectionExecutionPlan() {
  const data = useQqqData();
  const p = data.executionPlan || PLACEHOLDER.executionPlan;
  return (
    <div className="qqq-seq-section">
      <div className="qqq-seq-rail">
        <div className="qqq-seq-num plan">▶</div>
        <div className="qqq-seq-line qqq-seq-line-empty" />
      </div>
      <div className="qqq-seq-content">
        <div className="qqq-sec-eyebrow">
          <div className="qqq-se-label plan">The Plan</div>
        </div>

        <div className={`qqq-conclusion plan`}>
          <div className={`qqq-conc-dot ${p.stanceTone}`} />
          <div className="qqq-conc-text">{p.stance}</div>
          <div className={`qqq-conc-badge ${p.stanceTone}`}>RULES</div>
        </div>

        {/* Entry triggers */}
        <DataCard label="Entry Triggers" source="rules-based · no discretionary fills">
          {p.entryTriggers.map((e, i) => (
            <div key={i} className="qqq-plan-trigger">
              <div className={`qqq-plan-trigger-priority qqq-plan-trigger-priority-${e.priority}`}>
                {e.priority}
              </div>
              <div className="qqq-plan-trigger-body">
                <div className="qqq-plan-trigger-condition">{e.condition}</div>
                <div className="qqq-plan-trigger-filter">{e.filter}</div>
              </div>
            </div>
          ))}
        </DataCard>

        {/* Stop */}
        <DataCard label="Stop Placement" source="structural · not arbitrary">
          {p.stop ? (
            <div className="qqq-plan-stop">
              <div className="qqq-plan-stop-price">${p.stop.price}</div>
              <div className="qqq-plan-stop-meta">
                <span className="qqq-plan-stop-r">{p.stop.riskR}</span>
                <span className="qqq-plan-stop-acct">· {p.stop.accountRiskPct} account</span>
              </div>
              <div className="qqq-plan-stop-rationale">{p.stop.rationale}</div>
            </div>
          ) : (
            <div className="qqq-plan-stop">
              <div className="qqq-plan-stop-rationale">
                Stop pending — needs structural levels (gamma flip / put wall) from FlashAlpha.
              </div>
            </div>
          )}
        </DataCard>

        {/* Scale ladder */}
        <DataCard label="Scale-Out Ladder" source="plan before, execute mechanically">
          {p.scaleLadder && p.scaleLadder.length > 0 ? (
            <>
              <div className="qqq-plan-ladder-head">
                <span>tranche</span>
                <span>share</span>
                <span>price</span>
                <span>R</span>
              </div>
              {p.scaleLadder.map((l, i) => (
                <div key={i} className="qqq-plan-ladder-row">
                  <span className="qqq-plan-ladder-tranche">{l.tranche}</span>
                  <span className="qqq-plan-ladder-share">{l.share}</span>
                  <span className="qqq-plan-ladder-price">${l.price}</span>
                  <span className="qqq-plan-ladder-r">{l.rMultiple}</span>
                  {l.note && <span className="qqq-plan-ladder-note">{l.note}</span>}
                </div>
              ))}
            </>
          ) : (
            <div className="qqq-plan-stop">
              <div className="qqq-plan-stop-rationale">
                Ladder pending — needs call wall / put wall from FlashAlpha to size targets.
              </div>
            </div>
          )}
        </DataCard>

        {/* Hinge conditions */}
        <DataCard label="Hinge Conditions — verdict flips if any fires" source="real-time falsification rules">
          {p.hinges.map((h, i) => (
            <div key={i} className={`qqq-plan-hinge qqq-plan-hinge-${h.severity}`}>
              <div className={`qqq-plan-hinge-sev qqq-plan-hinge-sev-${h.severity}`}>
                {h.severity === 'hi' ? 'HIGH' : 'MED'}
              </div>
              <div className="qqq-plan-hinge-body">
                <div className="qqq-plan-hinge-condition">{h.condition}</div>
                <div className="qqq-plan-hinge-impact">→ {h.impact}</div>
              </div>
            </div>
          ))}
        </DataCard>

        {/* Rules strip */}
        <div className="qqq-plan-rules">
          <div className="qqq-plan-rule">
            <div className="qqq-plan-rule-label">max entries</div>
            <div className="qqq-plan-rule-value">{p.rules.maxEntries}</div>
            <div className="qqq-plan-rule-note">{p.rules.maxEntriesNote}</div>
          </div>
          <div className="qqq-plan-rule">
            <div className="qqq-plan-rule-label">max hold</div>
            <div className="qqq-plan-rule-value">{p.rules.maxHold}</div>
            <div className="qqq-plan-rule-note">{p.rules.maxHoldNote}</div>
          </div>
          <div className="qqq-plan-rule qqq-plan-rule-full">
            <div className="qqq-plan-rule-label">re-entry</div>
            <div className="qqq-plan-rule-note">{p.rules.reentry}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SHIFT SCORE CARD — Commit 51 (the 0-10 tactical decision number)
// ═══════════════════════════════════════════════════════════
// The Bruce Lee formula. Three zones × weighted points = one number
// that says "trade this, trade it smaller, or skip". Sits at the top
// of the sidebar because it IS the decision surface.
//
//   ENVIRONMENT  (0-3) — filled by 9:30, lives in regime_tags
//   SETUP        (0-4) — filled by 9:45, CHOP-equivalent from YC
//   TRIGGER      (0-3) — filled post-chart-upload, break candle quality
//
// Sizing:
//   8-10  full size   target $2.00
//   6-7   50% size    target $1.50 trail
//   4-5   25% size    target $1.00 scalp
//   ≤ 3   SKIP
//
// Pre-trigger scores show as "forecast" (provisional) since the
// trigger can still add up to 3 points.
function ShiftScoreCard() {
  const data = useQqqData();
  const s = data._shiftScore;
  if (!s) return null;

  const toneColor = (tone) => ({
    sage:   'var(--qqq-sage, #7a9e82)',
    gold:   'var(--qqq-gold, #c8a96e)',
    purple: 'var(--qqq-purple, #9b8fc4)',
    terra:  'var(--qqq-terra, #c46050)',
    teal:   'var(--qqq-teal, #4db8b8)',
  })[tone] || 'var(--qqq-fg-2, #aaa)';

  const scoreColor = toneColor(s.sizing.tone);
  const pct = (s.total / 10) * 100;
  const maxPct = (s.max_possible_now / 10) * 100;

  return (
    <div style={{
      background: 'var(--qqq-s1, #0c0c11)',
      border: '1px solid var(--qqq-b1, #1e1e2a)',
      borderLeft: `3px solid ${scoreColor}`,
      borderRadius: 8,
      padding: '14px 14px 12px',
      marginBottom: 12,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 8,
      }}>
        <div style={{
          fontSize: 10, color: scoreColor,
          textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
        }}>
          Shift Score
          {s.sizing.provisional && (
            <span style={{ color: 'var(--qqq-fg-3)', marginLeft: 6, fontWeight: 500 }}>
              · forecast
            </span>
          )}
        </div>
        <div style={{
          fontSize: 9, color: 'var(--qqq-fg-3)',
          textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          {s.phase === 'pre_open' ? 'env only' : s.phase === 'post_yc' ? 'env + setup' : 'full'}
        </div>
      </div>

      {/* Big number + sizing recommendation */}
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10,
      }}>
        <div style={{
          fontFamily: 'Literata, Georgia, serif',
          fontSize: 38, fontWeight: 700, lineHeight: 1,
          color: scoreColor,
        }}>
          {s.total}
        </div>
        <div style={{
          fontSize: 13, color: 'var(--qqq-fg-3)', lineHeight: 1.2,
        }}>
          / 10
          {s.sizing.provisional && (
            <span style={{ display: 'block', fontSize: 10, marginTop: 2 }}>
              max now: {s.max_possible_now}
            </span>
          )}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{
          padding: '4px 10px', borderRadius: 4,
          background: `rgba(${tonePaint(s.sizing.tone)},.12)`,
          border: `1px solid ${scoreColor}`,
          textAlign: 'right', minWidth: 80,
        }}>
          <div style={{
            fontSize: 10, color: scoreColor, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            {s.sizing.label}
          </div>
          <div style={{ fontSize: 11, color: 'var(--qqq-fg-1)', marginTop: 2, fontWeight: 600 }}>
            {s.sizing.size}
            {s.sizing.target && (
              <span style={{ color: 'var(--qqq-fg-2)', fontWeight: 400 }}> · {s.sizing.target}</span>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar — total vs max possible */}
      <div style={{
        height: 5, background: 'var(--surface-3, #171723)',
        borderRadius: 3, overflow: 'hidden', marginBottom: 12,
        position: 'relative',
      }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: `${pct}%`,
          background: scoreColor,
          transition: 'width 0.5s',
        }} />
        <div style={{
          position: 'absolute', left: `${maxPct}%`, top: 0, bottom: 0,
          width: 1, background: 'var(--qqq-fg-3)',
          opacity: 0.5,
        }} />
      </div>

      {/* Three component rows */}
      <ScoreComponentRow
        label="Environment"
        score={s.environment.score}
        max={s.environment.max}
        source="regime_tags"
        items={[
          { k: 'VIX',    fired: s.environment.components.vix_favorable,   hint: s.environment.vix_bucket },
          { k: 'gamma',  fired: s.environment.components.gamma_aligned,   hint: s.environment.gamma_sign },
          { k: 'sector', fired: s.environment.components.sector_confirms, hint: s.environment.sector_leader },
        ]}
      />
      <ScoreComponentRow
        label="Setup"
        score={s.setup.score}
        max={s.setup.max}
        source={s.setup.source === 'pending' ? 'awaits YC · 9:45 ET'
              : s.setup.source === 'opening_candle' ? 'live approx'
              : 'chart analysis'}
        items={[
          { k: 'body',   fired: s.setup.components.body       },
          { k: 'atr',    fired: s.setup.components.atr        },
          { k: 'vol',    fired: s.setup.components.vol        },
          { k: 'prevC',  fired: s.setup.components.prev_close },
        ]}
      />
      <ScoreComponentRow
        label="Trigger"
        score={s.trigger.score}
        max={s.trigger.max}
        source={s.trigger.source === 'pending' ? 'awaits break + chart upload' : 'chart analysis'}
        items={[
          { k: 'body>60',  fired: s.trigger.components.break_body },
          { k: 'vol+',     fired: s.trigger.components.break_vol  },
          { k: 'mtf',      fired: s.trigger.components.mtf_agree  },
        ]}
        last
      />
    </div>
  );
}

function ScoreComponentRow({ label, score, max, source, items, last }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '80px 46px 1fr',
      gap: 10, alignItems: 'center',
      padding: '6px 0',
      borderTop: '1px solid var(--qqq-b1, #1e1e2a)',
      borderBottom: last ? 'none' : 'none',
    }}>
      <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', fontWeight: 600 }}>
        {label}
      </div>
      <div style={{
        fontSize: 13, color: 'var(--qqq-fg-1)', fontWeight: 700,
        fontFamily: 'JetBrains Mono, monospace',
      }}>
        {score}<span style={{ color: 'var(--qqq-fg-3)', fontWeight: 400 }}>/{max}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
        {items.map((it, i) => (
          <span key={i} title={it.hint || ''} style={{
            padding: '1px 6px', borderRadius: 6,
            fontSize: 9, fontWeight: 600,
            fontFamily: 'JetBrains Mono, monospace',
            color: it.fired === true  ? 'var(--qqq-sage)'
                 : it.fired === false ? 'var(--qqq-fg-3)'
                 : 'var(--qqq-fg-3)',
            background: it.fired === true
              ? 'rgba(122,158,130,.1)'
              : 'var(--surface-3)',
            border: `1px solid ${it.fired === true
              ? 'var(--qqq-sage)'
              : 'var(--qqq-b2)'}`,
            opacity: it.fired === null ? 0.4 : 1,
          }}>
            {it.fired === true ? '✓ ' : it.fired === null ? '· ' : ''}{it.k}
          </span>
        ))}
      </div>
    </div>
  );
}

function tonePaint(tone) {
  return ({
    sage:   '122,158,130',
    gold:   '200,169,110',
    purple: '155,143,196',
    terra:  '196,96,80',
    teal:   '77,184,184',
  })[tone] || '170,170,170';
}

// ═══════════════════════════════════════════════════════════
// SHIFT SCORE HISTORY CARD — Commit 51 (calibration table)
// ═══════════════════════════════════════════════════════════
// Intelligence tab. Shows empirical performance of the Shift Score
// formula — did 8-10 scores actually win more than 6-7? This is how
// the formula validates (or invalidates) itself over time.
function ShiftScoreHistoryCard() {
  const data = useQqqData();
  const history = Array.isArray(data._shiftScoreHistory) ? data._shiftScoreHistory : [];
  const calibration = Array.isArray(data._shiftScoreCalibration) ? data._shiftScoreCalibration : [];

  if (history.length === 0) {
    return (
      <div className="qqq-data-card" style={{
        borderStyle: 'dashed', opacity: 0.85, marginTop: 16,
        borderLeft: '3px dashed var(--qqq-gold, #c8a96e)',
      }}>
        <div className="qqq-dc-hdr">
          <div className="qqq-dc-lbl" style={{ color: 'var(--qqq-gold)' }}>
            Shift Score Calibration · formula validation
          </div>
          <div className="qqq-dc-src" style={{ color: 'var(--qqq-fg-3)' }}>
            unlocks after first session with YC captured
          </div>
        </div>
        <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          Every session gets scored 0-10 by the Shift Score formula
          (environment + setup + trigger). This card shows the empirical
          hit rate per score bucket over time. If 8-10 scores hit higher
          than 6-7 → formula is working. If they invert → re-weight the
          signals. Validation is self-measurable.
        </div>
      </div>
    );
  }

  const bucketTone = (b) => b === '8-10' ? 'sage' : b === '6-7' ? 'gold' : b === '4-5' ? 'purple' : 'terra';
  const toneColor = (tone) => ({
    sage: 'var(--qqq-sage)',
    gold: 'var(--qqq-gold)',
    purple: 'var(--qqq-purple)',
    terra: 'var(--qqq-terra)',
  })[tone];

  return (
    <DataCard
      label="Shift Score · Calibration Table"
      source={`${history.length} scored session${history.length === 1 ? '' : 's'} · is the formula working?`}
      tone="gold"
    >
      {/* Bucket calibration summary */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--qqq-b1)' }}>
        <div style={{
          fontSize: 11, color: 'var(--qqq-fg-3)',
          textTransform: 'uppercase', letterSpacing: '0.05em',
          marginBottom: 10, fontWeight: 600,
        }}>
          Hit rate by score bucket
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8,
        }}>
          {calibration.map((c) => {
            const tone = bucketTone(c.bucket);
            const color = toneColor(tone);
            return (
              <div key={c.bucket} style={{
                padding: '10px 12px',
                background: `rgba(${tonePaint(tone)},.06)`,
                borderLeft: `3px solid ${color}`,
                borderRadius: 4,
              }}>
                <div style={{
                  fontSize: 10, color, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  {c.bucket}
                </div>
                <div style={{
                  fontSize: 22, fontWeight: 700,
                  color: 'var(--qqq-fg-1)',
                  fontFamily: 'Literata, Georgia, serif',
                  marginTop: 2,
                }}>
                  {c.hit_2_00_pct != null ? `${c.hit_2_00_pct}%` : '—'}
                </div>
                <div style={{
                  fontSize: 10, color: 'var(--qqq-fg-3)',
                  marginTop: 2, fontFamily: 'JetBrains Mono, monospace',
                }}>
                  $2.00 · n={c.hit_2_00_n}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Individual sessions */}
      <div style={{ padding: '10px 0', maxHeight: 320, overflowY: 'auto' }}>
        <div style={{
          padding: '0 18px 8px',
          fontSize: 11, color: 'var(--qqq-fg-3)',
          textTransform: 'uppercase', letterSpacing: '0.05em',
          fontWeight: 600,
        }}>
          Session log · score vs outcome
        </div>
        {history.slice().reverse().map((h) => {
          const tone = bucketTone(h.bucket);
          const outcomeColor = h.hit_2_00 === true ? 'var(--qqq-sage)'
            : h.hit_2_00 === false ? 'var(--qqq-terra)'
            : 'var(--qqq-fg-3)';
          return (
            <div key={h.brief_date} style={{
              display: 'grid',
              gridTemplateColumns: '80px 60px 80px 1fr auto',
              gap: 10, alignItems: 'center',
              padding: '6px 18px',
              borderBottom: '1px solid var(--qqq-b1)',
              fontSize: 11,
            }}>
              <div style={{
                fontFamily: 'JetBrains Mono, monospace',
                color: 'var(--qqq-fg-2)',
              }}>
                {fmtBriefDate(h.brief_date)}
              </div>
              <div style={{
                fontFamily: 'Literata, serif', fontWeight: 700,
                color: toneColor(tone), fontSize: 15,
              }}>
                {h.score}
              </div>
              <div style={{
                fontSize: 9, padding: '2px 6px', borderRadius: 3,
                color: toneColor(tone),
                background: `rgba(${tonePaint(tone)},.1)`,
                textAlign: 'center', fontWeight: 700,
                letterSpacing: '0.03em',
              }}>
                {h.sizing_label}
              </div>
              <div style={{ color: 'var(--qqq-fg-3)', fontSize: 10 }}>
                {h.direction?.toUpperCase() || '—'}
              </div>
              <div style={{
                fontSize: 10, fontWeight: 700, color: outcomeColor,
                padding: '2px 8px', borderRadius: 3,
                background: h.hit_2_00 === true ? 'rgba(122,158,130,.12)'
                  : h.hit_2_00 === false ? 'rgba(196,96,80,.12)'
                  : 'var(--surface-3)',
                minWidth: 60, textAlign: 'center',
              }}>
                {h.hit_2_00 === true ? '✓ $2' : h.hit_2_00 === false ? '✗ miss' : '—'}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{
        padding: '10px 18px', fontSize: 11,
        color: 'var(--qqq-fg-3)',
        background: 'rgba(200,169,110,.04)',
        borderTop: '1px solid var(--qqq-b1)',
        fontStyle: 'italic', lineHeight: 1.55,
      }}>
        If 8-10 bucket hits higher than 6-7 (after ~20 sessions) → formula is working.
        If buckets invert → one of the 10 signals is weighted wrong and we retune.
      </div>
    </DataCard>
  );
}

// ═══════════════════════════════════════════════════════════
// PRE-MARKET ARCHETYPE CARD — Commit 52
// ═══════════════════════════════════════════════════════════
// Classifies today's pre-market into 1 of 7 canonical patterns
// and shows the implication. Lives in sidebar above Today's Regime.
function PremarketArchetypeCard() {
  const data = useQqqData();
  const a = data._premarketArchetype;
  if (!a) return null;

  const toneColor = (tone) => ({
    sage: 'var(--qqq-sage)',
    gold: 'var(--qqq-gold)',
    purple: 'var(--qqq-purple)',
    terra: 'var(--qqq-terra)',
    teal: 'var(--qqq-teal)',
  })[tone] || 'var(--qqq-fg-2)';

  // Map archetype → tone
  const archTone = ({
    gap_up_trending:    'sage',
    gap_up_sideways:    'gold',
    gap_up_early_fade:  'terra',
    gap_down_v_recovery:'sage',
    gap_down_sideways:  'gold',
    gap_down_continuing:'terra',
    flat_to_bull:       'gold',
    flat_to_bear:       'gold',
    flat_open:          'purple',
    pending:            'teal',
  })[a.archetype] || 'teal';

  const color = toneColor(archTone);

  return (
    <div style={{
      background: 'var(--qqq-s1)',
      border: '1px solid var(--qqq-b1)',
      borderLeft: `3px solid ${color}`,
      borderRadius: 8,
      padding: '10px 12px 12px',
      marginBottom: 12,
    }}>
      <div style={{
        fontSize: 10, color, textTransform: 'uppercase', letterSpacing: '0.05em',
        fontWeight: 700, marginBottom: 6,
      }}>
        Pre-Market Archetype
      </div>
      <div style={{
        fontSize: 14, color: 'var(--qqq-fg-1)', fontWeight: 600, marginBottom: 4,
      }}>
        {a.archetype_label}
        {a.gap_pct != null && (
          <span style={{ color: 'var(--qqq-fg-3)', fontWeight: 400, marginLeft: 8, fontSize: 11 }}>
            ({a.gap_pct >= 0 ? '+' : ''}{Number(a.gap_pct).toFixed(2)}%)
          </span>
        )}
      </div>
      <div style={{
        fontSize: 11, color: 'var(--qqq-fg-2)', lineHeight: 1.55,
      }}>
        {a.implication}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CONTEXT BRIEF CARD — Commit 53 (4-step synthesis)
// ═══════════════════════════════════════════════════════════
// CONTEXT → CATALYSTS → INSIGHT → ACTION — Claude's morning narrative.
// Top of TODAY main column, above Phase 1. The strategic companion
// to Shift Score's tactical number.
function ContextBriefCard() {
  const data = useQqqData();
  const b = data._contextBrief;
  if (!b) return null;

  // Fallback data renders identically — indicator pill just changes
  const isFallback = b._fallback === true || b._meta == null;
  const brief = b._fallback ? b : b;

  const actionToneColor = (b) => ({
    'favor calls':  'var(--qqq-sage)',
    'favor puts':   'var(--qqq-terra)',
    'stand aside':  'var(--qqq-gold)',
  })[brief.action?.bias] || 'var(--qqq-fg-1)';

  return (
    <div style={{
      background: 'transparent',
      padding: 0,
      margin: 0,
    }}>
      {/* Header — lean badge showing freshness + source */}
      <div style={{
        padding: '0 16px 10px',
        display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline',
      }}>
        <div style={{
          fontSize: 9, color: 'var(--qqq-fg-3)',
          textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          {isFallback ? 'deterministic fallback' : 'Claude synthesis'}
          {brief._meta?.generated_at && ` · ${new Date(brief._meta.generated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} ET`}
        </div>
      </div>

      {/* 4 sections */}
      <div style={{ padding: '0 16px 14px' }}>
        {/* CONTEXT */}
        <BriefSection label="① Context" tone="teal">
          {brief.context?.regime && <BriefLine k="Regime" v={brief.context.regime} />}
          {brief.context?.phase && <BriefLine k="Phase" v={brief.context.phase} />}
          {brief.context?.week_position && <BriefLine k="Week" v={brief.context.week_position} />}
        </BriefSection>

        {/* CATALYSTS */}
        <BriefSection label="② Catalysts" tone="gold">
          {brief.catalysts?.scheduled  && <BriefLine k="Scheduled"  v={brief.catalysts.scheduled} />}
          {brief.catalysts?.overnight  && <BriefLine k="Overnight"  v={brief.catalysts.overnight} />}
          {brief.catalysts?.pre_market && <BriefLine k="Pre-market" v={brief.catalysts.pre_market} />}
          {brief.catalysts?.sector     && <BriefLine k="Sector"     v={brief.catalysts.sector} />}
          {brief.catalysts?.policy     && <BriefLine k="Policy"     v={brief.catalysts.policy} />}
        </BriefSection>

        {/* INSIGHT */}
        <BriefSection label="③ Insight" tone="purple">
          <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.6, paddingLeft: 8 }}>
            {brief.insight}
          </div>
        </BriefSection>

        {/* ACTION */}
        <BriefSection label="④ Action" tone={brief.action?.bias === 'stand aside' ? 'gold' : brief.action?.bias === 'favor puts' ? 'terra' : 'sage'} last>
          {brief.action?.bias && (
            <div style={{
              padding: '6px 12px', marginBottom: 6, borderRadius: 4,
              background: 'var(--surface-3)',
              display: 'inline-block',
              fontSize: 12, fontWeight: 700,
              color: actionToneColor(brief.action.bias),
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              {brief.action.bias} · method {brief.action.method_fits || '—'}
            </div>
          )}
          {brief.action?.hard_cutoff  && <BriefLine k="Hard cutoff"  v={brief.action.hard_cutoff} />}
          {brief.action?.avoid        && <BriefLine k="Avoid"        v={brief.action.avoid} />}
          {brief.action?.watch_for    && <BriefLine k="Watch for"    v={brief.action.watch_for} />}
          {brief.action?.dealbreakers && <BriefLine k="Dealbreakers" v={brief.action.dealbreakers} />}
        </BriefSection>

        {/* Analogs */}
        {Array.isArray(brief.analog_sessions) && brief.analog_sessions.length > 0 && (
          <div style={{
            marginTop: 12, padding: '8px 12px',
            background: 'var(--surface-3)', borderRadius: 4,
            fontSize: 11, color: 'var(--qqq-fg-3)',
          }}>
            <span style={{ fontWeight: 600, color: 'var(--qqq-fg-2)' }}>Analog sessions (same archetype): </span>
            {brief.analog_sessions.join(' · ')}
          </div>
        )}
      </div>
    </div>
  );
}

function BriefSection({ label, tone, children, last }) {
  const color = ({
    teal: 'var(--qqq-teal)', gold: 'var(--qqq-gold)',
    purple: 'var(--qqq-purple)', sage: 'var(--qqq-sage)', terra: 'var(--qqq-terra)',
  })[tone] || 'var(--qqq-fg-2)';
  return (
    <div style={{
      marginBottom: last ? 0 : 14,
      paddingBottom: last ? 0 : 12,
      borderBottom: last ? 'none' : '1px solid var(--qqq-b1)',
    }}>
      <div style={{
        fontSize: 10, color, textTransform: 'uppercase',
        letterSpacing: '0.05em', fontWeight: 700, marginBottom: 6,
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function BriefLine({ k, v }) {
  return (
    <div style={{
      display: 'flex', gap: 10, padding: '3px 0 3px 8px',
      fontSize: 12, lineHeight: 1.55,
    }}>
      <span style={{
        color: 'var(--qqq-fg-3)', minWidth: 90, flexShrink: 0,
        fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
      }}>
        {k}
      </span>
      <span style={{ color: 'var(--qqq-fg-1)' }}>{v}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CONTEXT BRIEF COLLAPSIBLE — Commit 57.1 (sidebar variant)
// ═══════════════════════════════════════════════════════════
// Wraps ContextBriefCard in a collapsible sidebar section.
// Subtitle dynamically surfaces the ACTION BIAS + hard cutoff
// so even when collapsed, Tim sees the headline read:
//   "favor calls · flat by 1:30 PM"
// Defaults to CLOSED — the briefing lives in the sidebar as
// reference, not as a top-of-fold essay pushing everything down.
function ContextBriefCollapsible() {
  const data = useQqqData();
  const b = data._contextBrief;
  if (!b) return null;

  // Build dynamic subtitle from action block (works for Claude + fallback)
  const bias       = b.action?.bias || null;
  const cutoff     = b.action?.hard_cutoff || null;
  const methodFits = b.action?.method_fits || null;
  const subtitleParts = [];
  if (bias)       subtitleParts.push(bias);
  if (methodFits) subtitleParts.push(`method ${methodFits}`);
  if (cutoff)     subtitleParts.push(cutoff.replace(/ per method$/i, ''));
  const subtitle = subtitleParts.length > 0
    ? subtitleParts.join(' · ')
    : 'today\u2019s strategic frame · click to expand';

  return (
    <CollapsibleSection
      title="Today's Context · 4-step read"
      subtitle={subtitle}
      sectionKey="context_brief"
      defaultOpen={false}
    >
      <div style={{ padding: '0 0 12px' }}>
        <ContextBriefCard />
      </div>
    </CollapsibleSection>
  );
}

// ═══════════════════════════════════════════════════════════
// COMBINED MATRIX CARD — Commit 54 (chart × context heatmap)
// ═══════════════════════════════════════════════════════════
// The 5×5 matrix: chart clusters (rows) × context clusters (cols).
// Each cell = hit rate when that chart setup fires in that context.
// TODAY's cell is highlighted. Click a cell to see sessions in it.
function CombinedMatrixCard() {
  const data = useQqqData();
  const matrix = data._combinedMatrix;
  const [openCell, setOpenCell] = useState(null);

  if (!matrix) {
    return (
      <div className="qqq-data-card" style={{
        borderStyle: 'dashed', opacity: 0.85, marginTop: 16,
        borderLeft: '3px dashed var(--qqq-teal)',
      }}>
        <div className="qqq-dc-hdr">
          <div className="qqq-dc-lbl" style={{ color: 'var(--qqq-teal)' }}>
            Combined Matrix · chart × context join
          </div>
          <div className="qqq-dc-src" style={{ color: 'var(--qqq-fg-3)' }}>
            unlocks with first resolved chart
          </div>
        </div>
        <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          This 5×5 matrix joins chart-cluster (rows: v-reversal /
          clean-break / absorption / late-break / failed-break) with
          context-cluster (cols: clean-trend / absorption / event-day /
          chop / shock). Each cell = hit rate when that setup fires in
          that context. Unlocks once you have resolved charts.
        </div>
      </div>
    );
  }

  const cellColor = (cell) => {
    if (cell.n === 0) return 'var(--surface-3)';
    if (cell.n < 3) return 'rgba(170,170,170,.15)';
    if (cell.hit_rate_pct >= 75) return 'rgba(122,158,130,.30)';
    if (cell.hit_rate_pct >= 50) return 'rgba(200,169,110,.25)';
    return 'rgba(196,96,80,.25)';
  };

  const cellBorder = (cell) => {
    if (cell.is_today) return 'var(--qqq-gold)';
    if (cell.n >= 3) return 'var(--qqq-b2)';
    return 'var(--qqq-b1)';
  };

  return (
    <DataCard
      label="Combined Matrix · chart × context"
      source={`${matrix.total_samples} resolved · click any cell for sessions · today highlighted gold`}
      tone="teal"
    >
      {/* Matrix grid */}
      <div style={{ padding: '14px 18px', overflowX: 'auto' }}>
        <table style={{
          width: '100%', borderCollapse: 'collapse',
          fontSize: 11, tableLayout: 'fixed', minWidth: 500,
        }}>
          <thead>
            <tr>
              <th style={{ padding: '6px 8px', textAlign: 'left',
                color: 'var(--qqq-fg-3)', fontSize: 10, textTransform: 'uppercase',
                letterSpacing: '0.05em', fontWeight: 600 }}>
                chart \ context
              </th>
              {matrix.context_clusters.map((cc) => (
                <th key={cc.key} style={{
                  padding: '6px 4px', textAlign: 'center',
                  color: 'var(--qqq-fg-2)', fontSize: 10, fontWeight: 600,
                  writingMode: 'horizontal-tb',
                  maxWidth: 80, wordWrap: 'break-word', lineHeight: 1.2,
                }}>
                  {cc.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.chart_clusters.map((cc) => (
              <tr key={cc.key}>
                <td style={{
                  padding: '6px 8px', color: 'var(--qqq-fg-1)',
                  fontSize: 11, fontWeight: 600,
                }}>
                  {cc.label}
                </td>
                {matrix.context_clusters.map((xc) => {
                  const key = `${cc.key}|${xc.key}`;
                  const cell = matrix.cells[key];
                  const cellKey = cell?.is_today ? `today-${key}` : key;
                  return (
                    <td key={xc.key} style={{ padding: 2 }}>
                      <button
                        type="button"
                        onClick={() => setOpenCell(openCell === cellKey ? null : cellKey)}
                        style={{
                          width: '100%', padding: '10px 4px',
                          background: cellColor(cell),
                          border: `2px solid ${cellBorder(cell)}`,
                          borderRadius: 4,
                          cursor: cell.n > 0 ? 'pointer' : 'default',
                          fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
                          color: 'var(--qqq-fg-1)', fontWeight: 600,
                          textAlign: 'center', lineHeight: 1.3,
                        }}
                      >
                        {cell.n === 0 ? (
                          <span style={{ color: 'var(--qqq-fg-3)', fontSize: 10 }}>·</span>
                        ) : (
                          <>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>
                              {cell.hit_rate_pct != null ? `${cell.hit_rate_pct}%` : '—'}
                            </div>
                            <div style={{ fontSize: 9, color: 'var(--qqq-fg-3)' }}>
                              n={cell.n}
                            </div>
                          </>
                        )}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Best + worst cells */}
      {matrix.best_cell && (
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--qqq-b1)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{
              padding: '8px 12px', background: 'rgba(122,158,130,.08)',
              borderLeft: '3px solid var(--qqq-sage)', borderRadius: 4,
            }}>
              <div style={{ fontSize: 10, color: 'var(--qqq-sage)', fontWeight: 700, textTransform: 'uppercase' }}>
                ✓ Best cell
              </div>
              <div style={{ fontSize: 12, color: 'var(--qqq-fg-1)', marginTop: 2 }}>
                <strong>{matrix.best_cell.chart_cluster.replace(/_/g, ' ')}</strong> in <strong>{matrix.best_cell.context_cluster.replace(/_/g, ' ')}</strong>
              </div>
              <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)' }}>
                {matrix.best_cell.hit_rate_pct}% hit · n={matrix.best_cell.n} · Wilson ≥{matrix.best_cell.wilson_low}%
              </div>
            </div>
            {matrix.worst_cell && (
              <div style={{
                padding: '8px 12px', background: 'rgba(196,96,80,.08)',
                borderLeft: '3px solid var(--qqq-terra)', borderRadius: 4,
              }}>
                <div style={{ fontSize: 10, color: 'var(--qqq-terra)', fontWeight: 700, textTransform: 'uppercase' }}>
                  ✗ Worst cell
                </div>
                <div style={{ fontSize: 12, color: 'var(--qqq-fg-1)', marginTop: 2 }}>
                  <strong>{matrix.worst_cell.chart_cluster.replace(/_/g, ' ')}</strong> in <strong>{matrix.worst_cell.context_cluster.replace(/_/g, ' ')}</strong>
                </div>
                <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)' }}>
                  {matrix.worst_cell.hit_rate_pct}% hit · n={matrix.worst_cell.n}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Today's cell highlighted */}
      {matrix.today_cell && (
        <div style={{
          padding: '10px 18px', borderTop: '1px solid var(--qqq-b1)',
          background: 'rgba(200,169,110,.04)',
        }}>
          <div style={{
            fontSize: 10, color: 'var(--qqq-gold)', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            ◉ Today falls in cell
          </div>
          <div style={{ fontSize: 12, color: 'var(--qqq-fg-1)', marginTop: 2 }}>
            <strong>{matrix.today_cell.chart_cluster.replace(/_/g, ' ')}</strong> × <strong>{matrix.today_cell.context_cluster.replace(/_/g, ' ')}</strong>
            {matrix.today_cell.n > 0 && (
              <> · historical {matrix.today_cell.hit_rate_pct}% hit (n={matrix.today_cell.n})</>
            )}
            {matrix.today_cell.n === 0 && (
              <span style={{ color: 'var(--qqq-fg-3)' }}> · no past samples in this cell yet</span>
            )}
          </div>
        </div>
      )}

      {/* Expanded cell sessions */}
      {openCell && matrix.cells[openCell.replace(/^today-/, '')] && matrix.cells[openCell.replace(/^today-/, '')].sessions.length > 0 && (
        <div style={{
          padding: '10px 18px', borderTop: '1px solid var(--qqq-b1)',
          background: 'var(--surface-3)',
        }}>
          <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Sessions in this cell
          </div>
          {matrix.cells[openCell.replace(/^today-/, '')].sessions.map((s) => (
            <div key={s.date} style={{
              fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
              padding: '2px 0', color: 'var(--qqq-fg-2)',
            }}>
              {s.date}
              <span style={{ marginLeft: 10, color: s.hit_2_00 === true ? 'var(--qqq-sage)' : 'var(--qqq-terra)' }}>
                {s.hit_2_00 === true ? '✓ $2' : '✗ miss'}
              </span>
            </div>
          ))}
        </div>
      )}
    </DataCard>
  );
}

// ═══════════════════════════════════════════════════════════
// OPEN-OBSERVATIONS CARD — Commit 80d
// ═══════════════════════════════════════════════════════════
// Cross-session ledger of "novel" observations surfaced by the
// three-voice convergence (Commit 80b). Auto-ingested on every
// converge; this card is the review surface.
//
// Each row: recurrence count, observation text, per-date sources,
// and actions (Promote / Dismiss / Edit). Observations with
// recurrence >= PROMOTION_THRESHOLD show a "★ Ready to promote"
// badge; promoting queues a Vision-prompt addition for Commit 80f.
// ═══════════════════════════════════════════════════════════
const PROMOTION_THRESHOLD = 5;

function OpenObservationsCard() {
  const data = useQqqData();
  const workstationId = data._workstationId;
  const userId = data._userId;

  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active'); // active | promoted | dismissed | all
  const [manualOpen, setManualOpen] = useState(false);
  const [manualText, setManualText] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!workstationId || !userId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ workstation_id: workstationId, user_id: userId, status: filter });
      const res = await fetch(`/api/workstations/qqq-observations?${qs}`);
      const payload = await res.json();
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      else { setObservations(payload.observations || []); setError(null); }
    } catch (e) { setError(e?.message || 'load failed'); }
    finally { setLoading(false); }
  }, [workstationId, userId, filter]);

  useEffect(() => { load(); }, [load]);

  const patchStatus = async (id, status, promoted_to) => {
    try {
      const body = { id, status };
      if (promoted_to !== undefined) body.promoted_to = promoted_to;
      const res = await fetch('/api/workstations/qqq-observations', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) await load();
    } catch { /* ignore */ }
  };

  const addManual = async () => {
    if (!manualText.trim() || adding) return;
    setAdding(true);
    try {
      const res = await fetch('/api/workstations/qqq-observations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstationId,
          user_id:        userId,
          action:         'manual',
          observation:    manualText.trim(),
        }),
      });
      if (res.ok) {
        setManualText('');
        setManualOpen(false);
        await load();
      }
    } catch { /* ignore */ }
    finally { setAdding(false); }
  };

  // Split active observations into "ready to promote" (>= threshold)
  // vs "still accumulating" (< threshold).
  const readyToPromote    = filter === 'active' ? observations.filter((o) => (o.recurrence_count || 0) >= PROMOTION_THRESHOLD) : [];
  const stillAccumulating = filter === 'active' ? observations.filter((o) => (o.recurrence_count || 0) <  PROMOTION_THRESHOLD) : [];
  const filtered          = filter === 'active' ? null : observations;

  const totals = {
    active:    filter === 'active'    ? observations.length : null,
    promoted:  filter === 'promoted'  ? observations.length : null,
    dismissed: filter === 'dismissed' ? observations.length : null,
  };

  const sourceLabel = readyToPromote.length > 0
    ? `${readyToPromote.length} ready to promote · threshold ≥${PROMOTION_THRESHOLD}`
    : observations.length > 0
      ? `${observations.length} ${filter}`
      : 'no observations yet';

  return (
    <DataCard
      label="Open Observations · cross-session ledger"
      source={error ? `error: ${error}` : loading ? 'loading…' : sourceLabel}
      tone="purple"
    >
      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, padding: '10px 18px 4px' }}>
        {['active', 'promoted', 'dismissed', 'all'].map((s) => {
          const active = filter === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              style={{
                padding: '4px 10px',
                background: active ? 'rgba(155,143,196,.15)' : 'var(--surface-3)',
                color: active ? 'var(--qqq-purple)' : 'var(--qqq-fg-2)',
                border: `1px solid ${active ? 'var(--qqq-purple)' : 'var(--qqq-b2)'}`,
                borderRadius: 4, fontSize: 11,
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
                textTransform: 'capitalize',
              }}
            >
              {s}
              {totals[s] != null && <span style={{ opacity: 0.7, marginLeft: 4 }}>({totals[s]})</span>}
            </button>
          );
        })}
      </div>

      {/* Empty state */}
      {!loading && observations.length === 0 && (
        <div style={{
          padding: '16px 18px',
          color: 'var(--qqq-fg-2)', fontSize: 12, fontStyle: 'italic', lineHeight: 1.55,
        }}>
          {filter === 'active' && 'Nothing pending. Every converge (End-of-Day Review · Section 5) surfaces novel observations into this ledger — drop your two charts + your read + Claude\'s read + converge to start accumulating.'}
          {filter === 'promoted' && 'Nothing promoted yet. Promote an active observation to queue it as a Vision-prompt addition (review queue arrives in a later commit).'}
          {filter === 'dismissed' && 'Nothing dismissed.'}
          {filter === 'all' && 'Ledger empty.'}
        </div>
      )}

      {/* Ready-to-promote (always first in the active view) */}
      {readyToPromote.length > 0 && (
        <div style={{ padding: '8px 18px 0' }}>
          <div style={{
            fontSize: 10, color: 'var(--qqq-gold)',
            textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
            marginBottom: 6,
          }}>
            ★ Ready to promote · {readyToPromote.length}
          </div>
          {readyToPromote.map((o) => (
            <ObservationRow key={o.id} observation={o} onPatch={patchStatus} highlight />
          ))}
        </div>
      )}

      {/* Still accumulating */}
      {stillAccumulating.length > 0 && (
        <div style={{ padding: '12px 18px 0' }}>
          <div style={{
            fontSize: 10, color: 'var(--qqq-fg-3)',
            textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
            marginBottom: 6,
          }}>
            Accumulating · {stillAccumulating.length}
          </div>
          {stillAccumulating.map((o) => (
            <ObservationRow key={o.id} observation={o} onPatch={patchStatus} />
          ))}
        </div>
      )}

      {/* Flat list for non-active filters */}
      {filtered && filtered.map((o) => (
        <div key={o.id} style={{ padding: '0 18px' }}>
          <ObservationRow observation={o} onPatch={patchStatus} readOnly={filter !== 'active'} />
        </div>
      ))}

      {/* Manual add */}
      <div style={{ padding: '14px 18px', borderTop: '1px solid var(--qqq-b1)', marginTop: 10 }}>
        {!manualOpen ? (
          <button
            type="button"
            onClick={() => setManualOpen(true)}
            style={{
              padding: '6px 12px',
              background: 'transparent', color: 'var(--qqq-purple)',
              border: '1px solid var(--qqq-purple)', borderRadius: 4,
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            + Add observation manually
          </button>
        ) : (
          <div>
            <textarea
              value={manualText}
              onChange={(e) => setManualText(e.target.value.slice(0, 2000))}
              placeholder="Something worth tracking across sessions — e.g. 'late-day OTM collapse on trend days'"
              rows={2}
              style={{
                width: '100%', padding: '8px 10px',
                background: 'var(--surface-3)', color: 'var(--qqq-fg-1)',
                border: '1px solid var(--qqq-b2)', borderRadius: 4,
                fontFamily: 'inherit', fontSize: 12, resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button
                type="button"
                onClick={addManual}
                disabled={!manualText.trim() || adding}
                style={{
                  padding: '6px 12px',
                  background: manualText.trim() && !adding ? 'var(--qqq-purple)' : 'var(--surface-3)',
                  color: manualText.trim() && !adding ? 'var(--surface-0)' : 'var(--qqq-fg-3)',
                  border: 0, borderRadius: 4,
                  fontSize: 11, fontWeight: 700,
                  cursor: manualText.trim() && !adding ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                }}
              >
                {adding ? 'Adding…' : 'Add'}
              </button>
              <button
                type="button"
                onClick={() => { setManualOpen(false); setManualText(''); }}
                style={{
                  padding: '6px 12px',
                  background: 'transparent', color: 'var(--qqq-fg-2)',
                  border: '1px solid var(--qqq-b2)', borderRadius: 4,
                  fontSize: 11, cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </DataCard>
  );
}

// ─── ObservationRow ────────────────────────────────────────
function ObservationRow({ observation, onPatch, highlight, readOnly }) {
  const [expanded, setExpanded] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promotedToText, setPromotedToText] = useState('');

  const sources = Array.isArray(observation.sources) ? observation.sources : [];
  const dates = sources
    .map((s) => s.brief_date)
    .filter(Boolean)
    .sort();
  const firstDate = dates[0];
  const lastDate  = dates[dates.length - 1];
  const count = observation.recurrence_count || sources.length || 1;

  const statusColor = observation.status === 'promoted' ? 'var(--qqq-sage)'
    : observation.status === 'dismissed' ? 'var(--qqq-fg-3)'
    : 'var(--qqq-fg-1)';

  return (
    <div style={{
      marginBottom: 8,
      padding: '10px 12px',
      background: highlight ? 'rgba(200,169,110,.04)' : 'var(--surface-3)',
      border: `1px solid ${highlight ? 'rgba(200,169,110,.3)' : 'var(--qqq-b2)'}`,
      borderLeft: highlight ? '3px solid var(--qqq-gold)' : '2px solid var(--qqq-purple)',
      borderRadius: 4,
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '48px 1fr',
        gap: 12, alignItems: 'start',
      }}>
        <div style={{
          fontSize: 18, fontWeight: 700, color: highlight ? 'var(--qqq-gold)' : 'var(--qqq-purple)',
          fontFamily: 'JetBrains Mono, monospace',
          textAlign: 'center',
          padding: '2px 0',
          background: 'var(--surface-0)', borderRadius: 3,
        }}>
          {count}×
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 12.5, color: statusColor, lineHeight: 1.5,
            fontStyle: observation.status === 'dismissed' ? 'italic' : 'normal',
            textDecoration: observation.status === 'dismissed' ? 'line-through' : 'none',
          }}>
            {observation.observation}
          </div>
          <div style={{
            fontSize: 10, color: 'var(--qqq-fg-3)', marginTop: 4,
            fontFamily: 'JetBrains Mono, monospace',
          }}>
            {firstDate && lastDate && firstDate !== lastDate && <>first {firstDate} · last {lastDate}</>}
            {firstDate && (!lastDate || firstDate === lastDate) && <>seen {firstDate}</>}
            {observation.status === 'promoted' && observation.promoted_to && (
              <> · ★ promoted to <strong style={{ color: 'var(--qqq-sage)' }}>{observation.promoted_to}</strong></>
            )}
          </div>
          {!readOnly && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {observation.status === 'active' && !promoting && (
                <>
                  <button
                    type="button"
                    onClick={() => setPromoting(true)}
                    style={{
                      padding: '4px 10px',
                      background: highlight ? 'var(--qqq-gold)' : 'transparent',
                      color: highlight ? 'var(--surface-0)' : 'var(--qqq-gold)',
                      border: `1px solid var(--qqq-gold)`,
                      borderRadius: 3, fontSize: 10, fontWeight: 700,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    ★ Promote →
                  </button>
                  <button
                    type="button"
                    onClick={() => onPatch(observation.id, 'dismissed')}
                    style={{
                      padding: '4px 10px',
                      background: 'transparent', color: 'var(--qqq-fg-3)',
                      border: '1px solid var(--qqq-b2)', borderRadius: 3,
                      fontSize: 10, cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    Dismiss
                  </button>
                  {sources.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setExpanded(!expanded)}
                      style={{
                        padding: '4px 10px',
                        background: 'transparent', color: 'var(--qqq-fg-3)',
                        border: 0,
                        fontSize: 10, cursor: 'pointer', fontFamily: 'inherit',
                        textDecoration: 'underline',
                      }}
                    >
                      {expanded ? 'hide sources' : `show sources (${sources.length})`}
                    </button>
                  )}
                </>
              )}
              {observation.status === 'active' && promoting && (
                <>
                  <input
                    type="text"
                    value={promotedToText}
                    onChange={(e) => setPromotedToText(e.target.value.slice(0, 80))}
                    placeholder="structured field name · e.g. counter_trend_rally_max"
                    autoFocus
                    style={{
                      flex: 1, minWidth: 240,
                      padding: '5px 8px',
                      background: 'var(--surface-0)', color: 'var(--qqq-fg-1)',
                      border: '1px solid var(--qqq-gold)', borderRadius: 3,
                      fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!promotedToText.trim()) return;
                      onPatch(observation.id, 'promoted', promotedToText.trim());
                      setPromoting(false);
                      setPromotedToText('');
                    }}
                    disabled={!promotedToText.trim()}
                    style={{
                      padding: '4px 10px',
                      background: promotedToText.trim() ? 'var(--qqq-gold)' : 'var(--surface-3)',
                      color: promotedToText.trim() ? 'var(--surface-0)' : 'var(--qqq-fg-3)',
                      border: 0, borderRadius: 3,
                      fontSize: 10, fontWeight: 700,
                      cursor: promotedToText.trim() ? 'pointer' : 'not-allowed',
                      fontFamily: 'inherit',
                    }}
                  >
                    Confirm promote
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPromoting(false); setPromotedToText(''); }}
                    style={{
                      padding: '4px 10px',
                      background: 'transparent', color: 'var(--qqq-fg-3)',
                      border: '1px solid var(--qqq-b2)', borderRadius: 3,
                      fontSize: 10, cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    Cancel
                  </button>
                </>
              )}
              {observation.status !== 'active' && (
                <button
                  type="button"
                  onClick={() => onPatch(observation.id, 'active')}
                  style={{
                    padding: '4px 10px',
                    background: 'transparent', color: 'var(--qqq-fg-2)',
                    border: '1px solid var(--qqq-b2)', borderRadius: 3,
                    fontSize: 10, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Restore
                </button>
              )}
            </div>
          )}
          {expanded && sources.length > 0 && (
            <div style={{ marginTop: 8, padding: '6px 8px', background: 'var(--surface-0)', borderRadius: 3 }}>
              {sources.map((s, i) => (
                <div key={i} style={{ fontSize: 10, color: 'var(--qqq-fg-3)', fontFamily: 'JetBrains Mono, monospace', marginBottom: 2 }}>
                  {s.brief_date || '—'} · {s.origin} {s.raw && s.raw !== observation.observation && (
                    <span style={{ fontStyle: 'italic' }}>· &ldquo;{s.raw.slice(0, 80)}{s.raw.length > 80 ? '…' : ''}&rdquo;</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// HYPOTHESIS WATCHLIST CARD — Commit 80e
// ═══════════════════════════════════════════════════════════
// Tim logs working theses; every convergence scores each active
// hypothesis against that day's evidence. Samples accumulate;
// stats get summarized as hit-rate + confidence tier (early/
// confirmed/high). When a claim earns enough weight Shift flags
// it as a validated signature — candidate for 80f's prompt-
// revision queue.
// ═══════════════════════════════════════════════════════════
const HYP_CONFIDENCE_EARLY     = 5;   // n < 5 = "sampling"
const HYP_CONFIDENCE_CONFIRMED = 14;  // n 14-29 = "confirmed"
const HYP_CONFIDENCE_HIGH      = 30;  // n >= 30 = "high"
const HYP_HOLDS_PCT            = 0.70;
const HYP_REJECTS_PCT          = 0.30;

function HypothesisWatchlistCard() {
  const data = useQqqData();
  const workstationId = data._workstationId;
  const userId = data._userId;

  const [hypotheses, setHypotheses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active');
  const [expandedId, setExpandedId] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newClaim, setNewClaim] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const load = useCallback(async () => {
    if (!workstationId || !userId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ workstation_id: workstationId, user_id: userId, status: filter });
      const res = await fetch(`/api/workstations/qqq-hypotheses?${qs}`);
      const payload = await res.json();
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      else { setHypotheses(payload.hypotheses || []); setError(null); }
    } catch (e) { setError(e?.message || 'load failed'); }
    finally { setLoading(false); }
  }, [workstationId, userId, filter]);

  useEffect(() => { load(); }, [load]);

  const createHypothesis = async () => {
    if (!newTitle.trim() || !newClaim.trim() || adding) return;
    setAdding(true);
    setAddError(null);
    try {
      const res = await fetch('/api/workstations/qqq-hypotheses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstationId,
          user_id:        userId,
          title:          newTitle.trim(),
          claim:          newClaim.trim(),
        }),
      });
      const payload = await res.json();
      if (!res.ok) { setAddError(payload?.error || 'create failed'); return; }
      setNewTitle(''); setNewClaim('');
      setAddOpen(false);
      await load();
    } catch (e) { setAddError(e?.message || 'create failed'); }
    finally { setAdding(false); }
  };

  const patchStatus = async (id, status) => {
    try {
      const res = await fetch('/api/workstations/qqq-hypotheses', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status }),
      });
      if (res.ok) await load();
    } catch { /* ignore */ }
  };

  const deleteHypothesis = async (id) => {
    if (!confirm('Delete this hypothesis? Samples will be discarded.')) return;
    try {
      const res = await fetch(`/api/workstations/qqq-hypotheses?id=${id}`, { method: 'DELETE' });
      if (res.ok) {
        if (expandedId === id) setExpandedId(null);
        await load();
      }
    } catch { /* ignore */ }
  };

  const countsFor = (status) => filter === status ? hypotheses.length : null;

  return (
    <DataCard
      label="Hypothesis Watchlist · scored on every converge"
      source={error
        ? `error: ${error}`
        : loading
          ? 'loading…'
          : hypotheses.length === 0
            ? 'no hypotheses yet'
            : `${hypotheses.length} ${filter}`}
      tone="gold"
    >
      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, padding: '10px 18px 4px' }}>
        {['active', 'validated', 'refuted', 'paused', 'all'].map((s) => {
          const active = filter === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              style={{
                padding: '4px 10px',
                background: active ? 'rgba(200,169,110,.15)' : 'var(--surface-3)',
                color: active ? 'var(--qqq-gold)' : 'var(--qqq-fg-2)',
                border: `1px solid ${active ? 'var(--qqq-gold)' : 'var(--qqq-b2)'}`,
                borderRadius: 4, fontSize: 11,
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
                textTransform: 'capitalize',
              }}
            >
              {s}
              {countsFor(s) != null && <span style={{ opacity: 0.7, marginLeft: 4 }}>({countsFor(s)})</span>}
            </button>
          );
        })}
      </div>

      {/* Add new */}
      <div style={{ padding: '10px 18px 0' }}>
        {!addOpen ? (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            style={{
              padding: '7px 14px',
              background: 'var(--qqq-gold)', color: 'var(--surface-0)',
              border: 0, borderRadius: 4,
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            + Add hypothesis
          </button>
        ) : (
          <div style={{
            padding: '12px 14px',
            background: 'rgba(200,169,110,.04)',
            border: '1px solid rgba(200,169,110,.3)',
            borderRadius: 4,
          }}>
            <div style={{ fontSize: 10, color: 'var(--qqq-gold)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              New hypothesis
            </div>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value.slice(0, 120))}
              placeholder="Short title · e.g. Options favor trend"
              style={{
                width: '100%', padding: '8px 10px',
                background: 'var(--surface-3)', color: 'var(--qqq-fg-1)',
                border: '1px solid var(--qqq-b2)', borderRadius: 4,
                fontFamily: 'inherit', fontSize: 13,
                marginBottom: 8, boxSizing: 'border-box',
              }}
            />
            <textarea
              value={newClaim}
              onChange={(e) => setNewClaim(e.target.value.slice(0, 2000))}
              placeholder={'Full claim · e.g. "Options asymmetrically respond to the prevailing trend; counter-trend rallies on the losing side are small and short-lived, especially EOD."'}
              rows={3}
              style={{
                width: '100%', padding: '8px 10px',
                background: 'var(--surface-3)', color: 'var(--qqq-fg-1)',
                border: '1px solid var(--qqq-b2)', borderRadius: 4,
                fontFamily: 'inherit', fontSize: 12.5, lineHeight: 1.55,
                resize: 'vertical', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                onClick={createHypothesis}
                disabled={!newTitle.trim() || !newClaim.trim() || adding}
                style={{
                  padding: '6px 12px',
                  background: newTitle.trim() && newClaim.trim() && !adding ? 'var(--qqq-gold)' : 'var(--surface-3)',
                  color: newTitle.trim() && newClaim.trim() && !adding ? 'var(--surface-0)' : 'var(--qqq-fg-3)',
                  border: 0, borderRadius: 4,
                  fontSize: 11, fontWeight: 700,
                  cursor: newTitle.trim() && newClaim.trim() && !adding ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                }}
              >
                {adding ? 'Adding…' : 'Add + start tracking'}
              </button>
              <button
                type="button"
                onClick={() => { setAddOpen(false); setNewTitle(''); setNewClaim(''); setAddError(null); }}
                style={{
                  padding: '6px 12px',
                  background: 'transparent', color: 'var(--qqq-fg-2)',
                  border: '1px solid var(--qqq-b2)', borderRadius: 4,
                  fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
              {addError && <span style={{ fontSize: 11, color: 'var(--qqq-terra)' }}>{addError}</span>}
            </div>
          </div>
        )}
      </div>

      {/* Empty state */}
      {!loading && hypotheses.length === 0 && !addOpen && (
        <div style={{
          padding: '16px 18px',
          color: 'var(--qqq-fg-2)', fontSize: 12, fontStyle: 'italic', lineHeight: 1.55,
        }}>
          No hypotheses in this status. Add a claim above and every future converge will score it against the day's evidence. After 5-14 samples you'll see whether it holds.
        </div>
      )}

      {/* Hypothesis list */}
      <div style={{ padding: '10px 18px 0' }}>
        {hypotheses.map((h) => (
          <HypothesisRow
            key={h.id}
            hypothesis={h}
            expanded={expandedId === h.id}
            onExpand={() => setExpandedId(expandedId === h.id ? null : h.id)}
            onPatchStatus={patchStatus}
            onDelete={deleteHypothesis}
          />
        ))}
      </div>
    </DataCard>
  );
}

// ─── HypothesisRow ─────────────────────────────────────────
function HypothesisRow({ hypothesis, expanded, onExpand, onPatchStatus, onDelete }) {
  const [samples, setSamples] = useState(null);
  const [samplesLoading, setSamplesLoading] = useState(false);

  useEffect(() => {
    if (!expanded || samples != null) return;
    let cancelled = false;
    (async () => {
      setSamplesLoading(true);
      try {
        const res = await fetch(`/api/workstations/qqq-hypotheses?id=${hypothesis.id}`);
        const payload = await res.json();
        if (!cancelled && res.ok) setSamples(payload.samples || []);
      } catch { /* ignore */ }
      finally { if (!cancelled) setSamplesLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [expanded, hypothesis.id, samples]);

  const n = hypothesis.sample_count || 0;
  const hits = hypothesis.hit_count || 0;
  const miss = hypothesis.miss_count || 0;
  const unclear = hypothesis.unclear_count || 0;
  const decidedN = hits + miss;
  const holdsPct = decidedN > 0 ? hits / decidedN : null;

  const confidence = n >= HYP_CONFIDENCE_HIGH      ? 'high'
                    : n >= HYP_CONFIDENCE_CONFIRMED ? 'confirmed'
                    : n >= HYP_CONFIDENCE_EARLY     ? 'early'
                    : 'sampling';

  const signal = holdsPct == null ? null
               : holdsPct >= HYP_HOLDS_PCT   ? 'holds'
               : holdsPct <= HYP_REJECTS_PCT ? 'rejects'
               : 'inconclusive';

  const signalColor = signal === 'holds'   ? 'var(--qqq-sage)'
                    : signal === 'rejects' ? 'var(--qqq-terra)'
                    : 'var(--qqq-fg-3)';

  const statusColor = hypothesis.status === 'validated' ? 'var(--qqq-sage)'
                    : hypothesis.status === 'refuted'   ? 'var(--qqq-terra)'
                    : hypothesis.status === 'paused'    ? 'var(--qqq-fg-3)'
                    : 'var(--qqq-gold)';

  return (
    <div style={{
      marginBottom: 10,
      padding: '10px 12px',
      background: 'var(--surface-3)',
      border: `1px solid ${hypothesis.status === 'active' ? 'var(--qqq-b2)' : 'var(--qqq-b3)'}`,
      borderLeft: `3px solid ${statusColor}`,
      borderRadius: 4,
    }}>
      <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', fontWeight: 700, marginBottom: 3 }}>
            {hypothesis.title}
          </div>
          <div style={{ fontSize: 12, color: 'var(--qqq-fg-2)', lineHeight: 1.55, marginBottom: 6 }}>
            {hypothesis.claim}
          </div>
          <div style={{
            display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
            fontSize: 10, fontFamily: 'JetBrains Mono, monospace',
          }}>
            <span style={{ color: 'var(--qqq-sage)', fontWeight: 700 }}>{hits} hit</span>
            <span style={{ color: 'var(--qqq-terra)', fontWeight: 700 }}>{miss} miss</span>
            <span style={{ color: 'var(--qqq-fg-3)' }}>{unclear} unclear</span>
            <span style={{ color: 'var(--qqq-fg-2)' }}>· n={n}</span>
            {holdsPct != null && (
              <span style={{ color: signalColor, fontWeight: 700 }}>
                · {Math.round(holdsPct * 100)}% holds
              </span>
            )}
            <span style={{ color: 'var(--qqq-fg-3)' }}>· {confidence}</span>
            {signal && (
              <span style={{
                color: signalColor, fontWeight: 700,
                padding: '1px 6px', borderRadius: 2,
                background: signal === 'holds' ? 'rgba(122,158,130,.12)'
                          : signal === 'rejects' ? 'rgba(196,96,80,.12)'
                          : 'var(--surface-2)',
              }}>
                {signal}
              </span>
            )}
          </div>
          {hypothesis.last_sampled_at && (
            <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)', marginTop: 4, fontFamily: 'JetBrains Mono, monospace' }}>
              last sampled {fmtRelative(hypothesis.last_sampled_at)}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
          {hypothesis.status === 'active' && (
            <>
              <button type="button" onClick={() => onPatchStatus(hypothesis.id, 'validated')}
                disabled={!(holdsPct != null && holdsPct >= HYP_HOLDS_PCT && n >= HYP_CONFIDENCE_EARLY)}
                title={holdsPct != null && holdsPct >= HYP_HOLDS_PCT && n >= HYP_CONFIDENCE_EARLY ? '' : 'needs ≥5 samples and ≥70% hold-rate'}
                style={{
                  padding: '3px 8px',
                  background: holdsPct != null && holdsPct >= HYP_HOLDS_PCT && n >= HYP_CONFIDENCE_EARLY ? 'var(--qqq-sage)' : 'var(--surface-2)',
                  color: holdsPct != null && holdsPct >= HYP_HOLDS_PCT && n >= HYP_CONFIDENCE_EARLY ? 'var(--surface-0)' : 'var(--qqq-fg-3)',
                  border: 0, borderRadius: 3, fontSize: 10, fontWeight: 700,
                  cursor: holdsPct != null && holdsPct >= HYP_HOLDS_PCT && n >= HYP_CONFIDENCE_EARLY ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit', whiteSpace: 'nowrap',
                }}
              >
                ✓ Validate
              </button>
              <button type="button" onClick={() => onPatchStatus(hypothesis.id, 'paused')}
                style={{ padding: '3px 8px', background: 'transparent', color: 'var(--qqq-fg-3)', border: '1px solid var(--qqq-b2)', borderRadius: 3, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
                Pause
              </button>
            </>
          )}
          {(hypothesis.status === 'validated' || hypothesis.status === 'refuted' || hypothesis.status === 'paused') && (
            <button type="button" onClick={() => onPatchStatus(hypothesis.id, 'active')}
              style={{ padding: '3px 8px', background: 'transparent', color: 'var(--qqq-fg-2)', border: '1px solid var(--qqq-b2)', borderRadius: 3, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
              Reactivate
            </button>
          )}
          <button type="button" onClick={onExpand}
            style={{ padding: '3px 8px', background: 'transparent', color: 'var(--qqq-fg-3)', border: 0, fontSize: 10, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}>
            {expanded ? 'hide samples' : 'samples'}
          </button>
          <button type="button" onClick={() => onDelete(hypothesis.id)}
            style={{ padding: '3px 8px', background: 'transparent', color: 'var(--qqq-fg-3)', border: 0, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}
            title="Delete hypothesis">
            ✕
          </button>
        </div>
      </div>

      {expanded && (
        <div style={{
          marginTop: 10, padding: '8px 10px',
          background: 'var(--surface-0)',
          borderRadius: 3,
        }}>
          {samplesLoading && (
            <div style={{ fontSize: 11, color: 'var(--qqq-fg-3)' }}>Loading samples…</div>
          )}
          {!samplesLoading && samples && samples.length === 0 && (
            <div style={{ fontSize: 11, color: 'var(--qqq-fg-3)', fontStyle: 'italic' }}>
              No samples yet. Run an End-of-Day converge to score today.
            </div>
          )}
          {samples && samples.length > 0 && samples.map((s) => {
            const vColor = s.verdict === 'hit' ? 'var(--qqq-sage)'
                         : s.verdict === 'miss' ? 'var(--qqq-terra)'
                         : 'var(--qqq-fg-3)';
            return (
              <div key={s.id} style={{
                display: 'grid',
                gridTemplateColumns: '80px 50px 1fr',
                gap: 8, alignItems: 'start',
                padding: '5px 0',
                borderBottom: '1px solid var(--qqq-b3)',
              }}>
                <span style={{
                  fontSize: 10, color: 'var(--qqq-fg-3)',
                  fontFamily: 'JetBrains Mono, monospace',
                }}>
                  {s.brief_date}
                </span>
                <span style={{
                  fontSize: 10, color: vColor, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  {s.verdict}
                </span>
                <span style={{
                  fontSize: 11, color: 'var(--qqq-fg-2)', lineHeight: 1.5,
                }}>
                  {s.evidence || '—'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// PROMPT REVISION QUEUE — Commit 80f
// ═══════════════════════════════════════════════════════════
// Third self-evolution card. Three upstream sources (promoted
// observations from 80d, validated hypotheses' tracked_fields
// from 80e, converge_report.prompt_notes from 80b) feed proposed
// revisions to the Vision prompts (PRICE_PAIR_V3 / OPTION_PAIR).
// Tim reviews (approve / reject); approved rows are manually
// applied to the prompt constants and marked with applied_version.
// Closes the self-evolution loop end-to-end.
// ═══════════════════════════════════════════════════════════
function PromptRevisionQueueCard() {
  const data = useQqqData();
  const workstationId = data._workstationId;
  const userId = data._userId;

  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('proposed');
  const [seeding, setSeeding] = useState(false);
  const [seedHint, setSeedHint] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    if (!workstationId || !userId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ workstation_id: workstationId, user_id: userId, status: filter });
      const res = await fetch(`/api/workstations/qqq-prompt-revisions?${qs}`);
      const payload = await res.json();
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      else { setRevisions(payload.revisions || []); setError(null); }
    } catch (e) { setError(e?.message || 'load failed'); }
    finally { setLoading(false); }
  }, [workstationId, userId, filter]);

  useEffect(() => { load(); }, [load]);

  const runSeed = async () => {
    setSeeding(true);
    setSeedHint(null);
    try {
      const res = await fetch('/api/workstations/qqq-prompt-revisions?action=seed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workstation_id: workstationId, user_id: userId }),
      });
      const payload = await res.json();
      if (res.ok) {
        setSeedHint(`Scanned. ${payload.seeded || 0} new proposals.`);
        await load();
      } else {
        setSeedHint(payload?.error || 'seed failed');
      }
    } catch (e) { setSeedHint(e?.message || 'seed failed'); }
    finally {
      setSeeding(false);
      setTimeout(() => setSeedHint(null), 3500);
    }
  };

  const patchStatus = async (id, status, extra) => {
    try {
      const body = { id, status };
      if (extra) Object.assign(body, extra);
      const res = await fetch('/api/workstations/qqq-prompt-revisions', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) await load();
    } catch { /* ignore */ }
  };

  const deleteRevision = async (id) => {
    if (!confirm('Delete this revision proposal?')) return;
    try {
      const res = await fetch(`/api/workstations/qqq-prompt-revisions?id=${id}`, { method: 'DELETE' });
      if (res.ok) {
        if (expandedId === id) setExpandedId(null);
        await load();
      }
    } catch { /* ignore */ }
  };

  return (
    <DataCard
      label="Prompt Revision Queue · self-evolution"
      source={error
        ? `error: ${error}`
        : loading
          ? 'loading…'
          : revisions.length === 0
            ? 'no revisions in this status'
            : `${revisions.length} ${filter}`}
      tone="sand"
    >
      <div style={{ display: 'flex', gap: 6, padding: '10px 18px 4px', flexWrap: 'wrap' }}>
        {['proposed', 'approved', 'applied', 'rejected', 'all'].map((s) => {
          const active = filter === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              style={{
                padding: '4px 10px',
                background: active ? 'rgba(212,149,106,.15)' : 'var(--surface-3)',
                color: active ? 'var(--qqq-amber, #d4956a)' : 'var(--qqq-fg-2)',
                border: `1px solid ${active ? 'var(--qqq-amber, #d4956a)' : 'var(--qqq-b2)'}`,
                borderRadius: 4, fontSize: 11,
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
                textTransform: 'capitalize',
              }}
            >
              {s}
            </button>
          );
        })}
        <button
          type="button"
          onClick={runSeed}
          disabled={seeding}
          style={{
            marginLeft: 'auto',
            padding: '4px 10px',
            background: 'transparent',
            color: 'var(--qqq-amber, #d4956a)',
            border: '1px solid var(--qqq-amber, #d4956a)',
            borderRadius: 4, fontSize: 11,
            cursor: seeding ? 'default' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {seeding ? 'Scanning…' : '↻ Scan sources'}
        </button>
      </div>
      {seedHint && (
        <div style={{ padding: '2px 18px 0', fontSize: 10, color: 'var(--qqq-fg-3)' }}>
          {seedHint}
        </div>
      )}

      {!loading && revisions.length === 0 && (
        <div style={{ padding: '16px 18px', color: 'var(--qqq-fg-2)', fontSize: 12, fontStyle: 'italic', lineHeight: 1.55 }}>
          {filter === 'proposed' && 'Nothing pending. Promote an observation (Open-Observations) or validate a hypothesis (Watchlist) — new proposals land here automatically. Click "Scan sources" to pull in converge-report prompt notes too.'}
          {filter === 'approved' && 'Nothing approved yet. Approve proposals to mark them ready for application.'}
          {filter === 'applied' && 'Nothing applied yet. Mark approved revisions as applied once they ship in a prompt version.'}
          {filter === 'rejected' && 'Nothing rejected.'}
          {filter === 'all' && 'Queue empty. Promote observations, validate hypotheses, or scan sources to populate.'}
        </div>
      )}

      <div style={{ padding: '10px 18px 4px' }}>
        {revisions.map((r) => (
          <RevisionRow
            key={r.id}
            revision={r}
            expanded={expandedId === r.id}
            onExpand={() => setExpandedId(expandedId === r.id ? null : r.id)}
            onPatchStatus={patchStatus}
            onDelete={deleteRevision}
          />
        ))}
      </div>

      {filter === 'proposed' && revisions.length > 0 && (
        <div style={{
          padding: '10px 18px',
          background: 'rgba(212,149,106,.04)',
          borderTop: '1px solid var(--qqq-b1)',
          fontSize: 11, color: 'var(--qqq-fg-3)', lineHeight: 1.55, fontStyle: 'italic',
        }}>
          Approved revisions stay in the queue until you mark them applied. Edit
          <code style={{ fontFamily: 'JetBrains Mono, monospace', marginLeft: 4, marginRight: 4, color: 'var(--qqq-amber, #d4956a)' }}>
            pages/api/workstations/qqq-chart-upload.js
          </code>
          to add the field to <code style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--qqq-amber, #d4956a)' }}>PRICE_PAIR_V3_PROMPT</code> or <code style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--qqq-amber, #d4956a)' }}>OPTION_PAIR_PROMPT</code>, bump the version, then click "Mark applied" with the new version number.
        </div>
      )}
    </DataCard>
  );
}

// ─── RevisionRow ───────────────────────────────────────────
function RevisionRow({ revision, expanded, onExpand, onPatchStatus, onDelete }) {
  const [applyingVersion, setApplyingVersion] = useState('');
  const [applyOpen, setApplyOpen] = useState(false);

  const sourceTone = revision.source_type === 'observation' ? 'var(--qqq-purple)'
                   : revision.source_type === 'hypothesis'  ? 'var(--qqq-gold)'
                   : revision.source_type === 'converge_note' ? 'var(--qqq-teal)'
                   : 'var(--qqq-fg-3)';
  const targetLabel = revision.target_prompt === 'option_pair' ? 'OPTION_PAIR' : 'PRICE_PAIR_V3';

  const statusColor = revision.status === 'approved' ? 'var(--qqq-sage)'
                    : revision.status === 'rejected' ? 'var(--qqq-terra)'
                    : revision.status === 'applied'  ? 'var(--qqq-teal)'
                    : 'var(--qqq-amber, #d4956a)';

  return (
    <div style={{
      marginBottom: 10,
      padding: '10px 12px',
      background: 'var(--surface-3)',
      border: `1px solid var(--qqq-b2)`,
      borderLeft: `3px solid ${statusColor}`,
      borderRadius: 4,
    }}>
      <div style={{ display: 'flex', alignItems: 'start', gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{
              fontSize: 9, color: 'var(--qqq-fg-3)',
              padding: '1px 6px', borderRadius: 2,
              background: 'var(--surface-0)',
              textTransform: 'uppercase', letterSpacing: '0.05em',
              fontFamily: 'JetBrains Mono, monospace',
            }}>
              {targetLabel}
            </span>
            <span style={{
              fontSize: 12, color: 'var(--qqq-fg-1)', fontWeight: 700,
              fontFamily: 'JetBrains Mono, monospace',
            }}>
              {revision.field_name}
            </span>
            <span style={{
              fontSize: 9, color: sourceTone, fontWeight: 700,
              padding: '1px 6px', borderRadius: 2,
              background: 'var(--surface-0)',
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              {revision.source_type.replace(/_/g, ' ')}
            </span>
            {revision.status === 'applied' && revision.applied_version != null && (
              <span style={{
                fontSize: 9, color: 'var(--qqq-teal)', fontWeight: 700,
                padding: '1px 6px', borderRadius: 2,
                background: 'rgba(77,184,184,.1)',
              }}>
                v{revision.applied_version}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--qqq-fg-1)', lineHeight: 1.55, marginBottom: 4 }}>
            {revision.field_description}
          </div>
          {revision.rationale && (
            <div style={{ fontSize: 11, color: 'var(--qqq-fg-3)', lineHeight: 1.5, fontStyle: 'italic' }}>
              {revision.rationale}
            </div>
          )}
          {expanded && revision.source_context && (
            <pre style={{
              marginTop: 8, padding: '6px 8px',
              background: 'var(--surface-0)', borderRadius: 3,
              fontSize: 10, color: 'var(--qqq-fg-3)', lineHeight: 1.4,
              fontFamily: 'JetBrains Mono, monospace',
              overflow: 'auto', maxHeight: 200,
              whiteSpace: 'pre-wrap',
            }}>
{JSON.stringify(revision.source_context, null, 2)}
            </pre>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
          {revision.status === 'proposed' && (
            <>
              <button type="button" onClick={() => onPatchStatus(revision.id, 'approved')}
                style={{ padding: '3px 10px', background: 'var(--qqq-sage)', color: 'var(--surface-0)', border: 0, borderRadius: 3, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                ✓ Approve
              </button>
              <button type="button" onClick={() => onPatchStatus(revision.id, 'rejected')}
                style={{ padding: '3px 10px', background: 'transparent', color: 'var(--qqq-terra)', border: '1px solid var(--qqq-terra)', borderRadius: 3, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
                Reject
              </button>
            </>
          )}
          {revision.status === 'approved' && !applyOpen && (
            <button type="button" onClick={() => setApplyOpen(true)}
              style={{ padding: '3px 10px', background: 'var(--qqq-teal)', color: 'var(--surface-0)', border: 0, borderRadius: 3, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              Mark applied
            </button>
          )}
          {revision.status === 'approved' && applyOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <input
                type="number"
                value={applyingVersion}
                onChange={(e) => setApplyingVersion(e.target.value)}
                placeholder="version #"
                style={{
                  padding: '3px 6px', width: 80,
                  background: 'var(--surface-0)', color: 'var(--qqq-fg-1)',
                  border: '1px solid var(--qqq-teal)', borderRadius: 3,
                  fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
                }}
              />
              <button type="button" onClick={() => {
                const n = parseInt(applyingVersion, 10);
                if (!Number.isFinite(n)) return;
                onPatchStatus(revision.id, 'applied', { applied_version: n });
                setApplyOpen(false);
                setApplyingVersion('');
              }}
                disabled={!applyingVersion}
                style={{
                  padding: '3px 10px',
                  background: applyingVersion ? 'var(--qqq-teal)' : 'var(--surface-2)',
                  color: applyingVersion ? 'var(--surface-0)' : 'var(--qqq-fg-3)',
                  border: 0, borderRadius: 3, fontSize: 10, fontWeight: 700,
                  cursor: applyingVersion ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                }}>
                Confirm
              </button>
              <button type="button" onClick={() => { setApplyOpen(false); setApplyingVersion(''); }}
                style={{ padding: '3px 10px', background: 'transparent', color: 'var(--qqq-fg-3)', border: 0, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
                cancel
              </button>
            </div>
          )}
          {(revision.status === 'rejected' || revision.status === 'applied') && (
            <button type="button" onClick={() => onPatchStatus(revision.id, 'proposed')}
              style={{ padding: '3px 10px', background: 'transparent', color: 'var(--qqq-fg-2)', border: '1px solid var(--qqq-b2)', borderRadius: 3, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
              Reopen
            </button>
          )}
          <button type="button" onClick={onExpand}
            style={{ padding: '3px 10px', background: 'transparent', color: 'var(--qqq-fg-3)', border: 0, fontSize: 10, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}>
            {expanded ? 'hide' : 'context'}
          </button>
          <button type="button" onClick={() => onDelete(revision.id)}
            style={{ padding: '3px 10px', background: 'transparent', color: 'var(--qqq-fg-3)', border: 0, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}
            title="Delete revision proposal">
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// JOINED REPORT CARD — Commit 67 (price + option synthesis)
// ═══════════════════════════════════════════════════════════
// Renders the structured joined report built from a price-chart
// extraction and an option-pair extraction for the same day.
// Auto-fires after both uploads land; no manual trigger required.
//
// Five sections (one per joined-report block):
//   ① Day classification + primary direction
//   ② Your trade evaluation (P&L vs signal-quality gap)
//   ③ Option lessons (asymmetry, bases, decoupling)
//   ④ Pattern signatures detected today
//   ⑤ Frequency tracking keys (mined across all sessions)
function JoinedReportCard() {
  const data = useQqqData();
  const r = data._joinedReport;
  if (!r) return null;

  const dc = r.day_classification || {};
  const tev = r.your_trade_evaluation || {};
  const lessons = Array.isArray(r.option_lessons_today) ? r.option_lessons_today : [];
  const sigs = Array.isArray(r.repeating_pattern_signatures) ? r.repeating_pattern_signatures : [];
  const keys = Array.isArray(r.frequency_tracking_keys) ? r.frequency_tracking_keys : [];

  const gapColor = ({
    edge_realized:                     'var(--qqq-sage)',
    'good_signal_bad_outcome (likely strike selection or IV)': 'var(--qqq-gold)',
    'lucky_win_weak_signal (do not size up next time)':         'var(--qqq-gold)',
    'expected_outcome_skip_next_time': 'var(--qqq-terra)',
    mixed:                             'var(--qqq-purple)',
  })[tev.pnl_vs_signal_gap] || 'var(--qqq-fg-2)';

  return (
    <DataCard
      label="Joined Report · price × option synthesis"
      source={`${r.brief_date || 'today'} · auto-built · ${sigs.length} pattern signature${sigs.length === 1 ? '' : 's'}`}
      tone="purple"
    >
      {/* ① Day classification */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--qqq-b1)' }}>
        <div style={{
          fontSize: 11, color: 'var(--qqq-purple)',
          textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
          marginBottom: 6,
        }}>
          ① Day classification
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--qqq-fg-1)', fontFamily: 'Literata, Georgia, serif' }}>
          {(dc.day_type || 'unknown').replace(/_/g, ' ')}
          {dc.primary_direction && (
            <span style={{
              marginLeft: 10, fontSize: 12, fontWeight: 600,
              color: dc.primary_direction === 'down' ? 'var(--qqq-terra)'
                   : dc.primary_direction === 'up' ? 'var(--qqq-sage)'
                   : 'var(--qqq-gold)',
            }}>
              · {dc.primary_direction}
            </span>
          )}
        </div>
        {dc.rationale && (
          <div style={{ fontSize: 12, color: 'var(--qqq-fg-2)', marginTop: 4, fontStyle: 'italic' }}>
            {dc.rationale}
          </div>
        )}
      </div>

      {/* ② Trade evaluation */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--qqq-b1)' }}>
        <div style={{
          fontSize: 11, color: 'var(--qqq-purple)',
          textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
          marginBottom: 8,
        }}>
          ② Your trade · signal quality vs P&L
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10,
        }}>
          <ScoreTileMini label="Outcome" value={(tev.outcome_class || '—').replace(/_/g, ' ')} />
          <ScoreTileMini label="Signal quality" value={tev.signal_quality_score ? `${tev.signal_quality_score}/10` : '—'} tone={tev.signal_quality_score >= 7 ? 'sage' : tev.signal_quality_score <= 4 ? 'terra' : 'gold'} />
          <ScoreTileMini label="Setup executed" value={tev.setup_executed_correctly ? '✓ yes' : '✗ no'} />
        </div>
        {tev.pnl_vs_signal_gap && (
          <div style={{
            marginTop: 10, padding: '8px 12px',
            background: `rgba(${gapColor === 'var(--qqq-sage)' ? '122,158,130' : gapColor === 'var(--qqq-terra)' ? '196,96,80' : gapColor === 'var(--qqq-gold)' ? '200,169,110' : '155,143,196'},.08)`,
            borderLeft: `3px solid ${gapColor}`,
            borderRadius: 4, fontSize: 12, color: 'var(--qqq-fg-1)',
          }}>
            <strong>{tev.pnl_vs_signal_gap.replace(/_/g, ' ')}</strong>
          </div>
        )}
        {(tev.your_call_return_pct != null || tev.your_put_return_pct != null) && (
          <div style={{
            marginTop: 8, fontSize: 11, color: 'var(--qqq-fg-2)',
            fontFamily: 'JetBrains Mono, monospace',
          }}>
            CALL {tev.your_call_return_pct}% (max {tev.call_max_potential_pct}%) ·
            {' '}PUT {tev.your_put_return_pct}% (max {tev.put_max_potential_pct}%)
          </div>
        )}
      </div>

      {/* ③ Option lessons */}
      {lessons.length > 0 && (
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--qqq-b1)' }}>
          <div style={{
            fontSize: 11, color: 'var(--qqq-purple)',
            textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
            marginBottom: 8,
          }}>
            ③ Option lessons today
          </div>
          {lessons.map((l, i) => (
            <div key={i} style={{
              padding: '8px 12px', marginBottom: 6,
              background: 'var(--surface-3)',
              borderLeft: '3px solid var(--qqq-purple)', borderRadius: 4,
              fontSize: 12, color: 'var(--qqq-fg-1)', lineHeight: 1.55,
            }}>
              {l}
            </div>
          ))}
        </div>
      )}

      {/* ④ Pattern signatures */}
      {sigs.length > 0 && (
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--qqq-b1)' }}>
          <div style={{
            fontSize: 11, color: 'var(--qqq-purple)',
            textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
            marginBottom: 8,
          }}>
            ④ Pattern signatures fired today ({sigs.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {sigs.map((s, i) => (
              <div key={i} title={JSON.stringify(s, null, 2)} style={{
                padding: '4px 10px', borderRadius: 10,
                background: 'var(--surface-3)',
                border: '1px solid var(--qqq-purple)',
                fontSize: 11, color: 'var(--qqq-purple)',
                fontFamily: 'JetBrains Mono, monospace', fontWeight: 600,
              }}>
                {s.signature}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ⑤ Tracking keys (for cross-session mining) */}
      {keys.length > 0 && (
        <div style={{
          padding: '10px 18px',
          background: 'rgba(155,143,196,.04)',
          borderTop: '1px solid var(--qqq-b1)',
        }}>
          <div style={{
            fontSize: 9, color: 'var(--qqq-fg-3)',
            textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
            marginBottom: 4,
          }}>
            Tracking keys · {keys.length} (cross-session frequency mining)
          </div>
          <div style={{ fontSize: 10, color: 'var(--qqq-fg-2)', fontFamily: 'JetBrains Mono, monospace', wordBreak: 'break-all' }}>
            {keys.join(' · ')}
          </div>
        </div>
      )}

      {/* Footer insight */}
      {r.insight && (
        <div style={{
          padding: '12px 18px', fontSize: 12, color: 'var(--qqq-fg-1)',
          background: 'rgba(155,143,196,.06)',
          borderTop: '1px solid var(--qqq-b1)',
          fontStyle: 'italic', lineHeight: 1.6,
        }}>
          {r.insight}
        </div>
      )}
    </DataCard>
  );
}

// ═══════════════════════════════════════════════════════════
// INTRADAY EVENTS CARD — Commit 60 (Tier 2 real-time monitor)
// ═══════════════════════════════════════════════════════════
// Shows events detected by the qqq-intraday-monitor cron during
// TODAY's session. Sudden price moves, volume spikes, range
// expansions — flagged with severity + scheduled-event attribution
// when possible.
//
// Distinct from Vision's intraday_events (Tier 1) — that one
// extracts events retroactively from the EOD chart. This one
// catches them in real-time every 10 min.
function IntradayEventsCard() {
  const data = useQqqData();
  const events = Array.isArray(data._intradayEvents) ? data._intradayEvents : [];
  if (events.length === 0) return null;

  const severityColor = (s) => ({
    severe:   'var(--qqq-terra)',
    moderate: 'var(--qqq-gold)',
    mild:     'var(--qqq-fg-2)',
  })[s] || 'var(--qqq-fg-3)';

  return (
    <DataCard
      label="Intraday Events · detected live today"
      source={`${events.length} event${events.length === 1 ? '' : 's'} · qqq-intraday-monitor · every 10 min`}
      tone="terra"
    >
      <div style={{ padding: '10px 0' }}>
        {events.map((e, i) => {
          const color = severityColor(e.severity);
          const triggers = Array.isArray(e.trigger_type) ? e.trigger_type : [];
          return (
            <div key={e.id || i} style={{
              padding: '10px 18px',
              borderBottom: i < events.length - 1 ? '1px solid var(--qqq-b1)' : 'none',
            }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 10,
                marginBottom: 4,
              }}>
                <span style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 13, color: 'var(--qqq-gold)', fontWeight: 600,
                }}>
                  {e.et_time_str || '—'} ET
                </span>
                <span style={{
                  fontSize: 15, color: e.direction === 'down' ? 'var(--qqq-terra)' : 'var(--qqq-sage)',
                }}>
                  {e.direction === 'down' ? '↓' : '↑'}
                </span>
                <span style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 14, fontWeight: 700,
                  color: e.direction === 'down' ? 'var(--qqq-terra)' : 'var(--qqq-sage)',
                }}>
                  {e.price_change_pct >= 0 ? '+' : ''}{e.price_change_pct}%
                </span>
                <span style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 10,
                  background: `rgba(${e.severity === 'severe' ? '196,96,80' : e.severity === 'moderate' ? '200,169,110' : '170,170,170'},.12)`,
                  color, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  {e.severity}
                </span>
                {e.volume_ratio != null && e.volume_ratio >= 2 && (
                  <span style={{
                    fontSize: 10, color: 'var(--qqq-fg-3)',
                    fontFamily: 'JetBrains Mono, monospace',
                  }}>
                    vol {e.volume_ratio}×
                  </span>
                )}
              </div>

              {/* Trigger types */}
              {triggers.length > 0 && (
                <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                  {triggers.map((t) => (
                    <span key={t} style={{
                      fontSize: 9, padding: '1px 6px', borderRadius: 3,
                      background: 'var(--surface-3)',
                      color: 'var(--qqq-fg-2)', fontFamily: 'JetBrains Mono, monospace',
                      border: '1px solid var(--qqq-b2)',
                    }}>
                      {t.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}

              {/* Attribution */}
              {e.attribution && (
                <div style={{
                  fontSize: 11, color: 'var(--qqq-purple)',
                  marginTop: 6, fontStyle: 'italic',
                }}>
                  {e.attribution_source === 'auto' ? '⚡ scheduled'
                    : e.attribution_source === 'news' ? '📰 news match'
                    : e.attribution_source === 'news_inferred' ? `🤖 Claude inferred (${e.inference_confidence || 'low'})`
                    : '✎ manual'}: {e.attribution}
                </div>
              )}
              {!e.attribution && (
                <div style={{
                  fontSize: 10, color: 'var(--qqq-fg-3)',
                  marginTop: 6, fontStyle: 'italic',
                }}>
                  No scheduled event matched · cause unknown
                </div>
              )}

              {/* Commit 61 — cited news headlines (when news-enriched) */}
              {Array.isArray(e.news_attributions) && e.news_attributions.length > 0 && (
                <div style={{
                  marginTop: 6, padding: '6px 10px',
                  background: 'var(--surface-3)',
                  borderRadius: 4, borderLeft: '2px solid var(--qqq-purple)',
                }}>
                  <div style={{
                    fontSize: 9, color: 'var(--qqq-fg-3)',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    marginBottom: 4, fontWeight: 600,
                  }}>
                    Cited headlines ({e.news_attributions.length})
                  </div>
                  {e.news_attributions.slice(0, 3).map((h, hi) => (
                    <div key={hi} style={{
                      fontSize: 11, color: 'var(--qqq-fg-2)',
                      padding: '2px 0', lineHeight: 1.4,
                    }}>
                      <span style={{ color: 'var(--qqq-fg-3)', marginRight: 6 }}>
                        [{h.publisher || '—'}]
                      </span>
                      {h.link ? (
                        <a href={h.link} target="_blank" rel="noopener noreferrer" style={{
                          color: 'var(--qqq-fg-1)', textDecoration: 'none',
                        }}>
                          {h.title}
                        </a>
                      ) : (
                        <span style={{ color: 'var(--qqq-fg-1)' }}>{h.title}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Price context */}
              <div style={{
                fontSize: 10, color: 'var(--qqq-fg-3)',
                marginTop: 4, fontFamily: 'JetBrains Mono, monospace',
              }}>
                {e.prev_price} → {e.price_at_event} · range {e.bar_range_pct}%
              </div>
            </div>
          );
        })}
      </div>
      <div style={{
        padding: '10px 18px', fontSize: 11,
        color: 'var(--qqq-fg-3)',
        background: 'rgba(196,96,80,.04)',
        borderTop: '1px solid var(--qqq-b1)',
        fontStyle: 'italic', lineHeight: 1.55,
      }}>
        Detection runs every 10 min during market hours. Events are market-wide
        (not workstation-specific). Future: news feed correlation (Tier 3) +
        morning×afternoon pattern engine (Tier 4).
      </div>
    </DataCard>
  );
}

// ═══════════════════════════════════════════════════════════
// AFTERNOON PATTERNS CARD — Commit 62 (Tier 4 mined rules)
// ═══════════════════════════════════════════════════════════
// Renders output of lib/qqq/afternoon-pattern-engine.js.
// Five sections:
//   ① Baseline — avg events/day, direction mix
//   ② By regime — VIX / gamma / sector / event proximity
//   ③ By archetype — gap-up-trending vs gap-up-absorb vs etc
//   ④ By outcome — wins vs losses
//   ⑤ Reversals — morning direction vs afternoon counter-events
//   ⑥ Timing — hour-of-day event clustering
function AfternoonPatternsCard() {
  const data = useQqqData();
  const p = data._afternoonPatterns;
  if (!p) return null;

  // Insufficient-data state
  if (!p.sufficient) {
    return (
      <div className="qqq-data-card" style={{
        borderStyle: 'dashed', opacity: 0.85, marginTop: 16,
        borderLeft: '3px dashed var(--qqq-purple)',
      }}>
        <div className="qqq-dc-hdr">
          <div className="qqq-dc-lbl" style={{ color: 'var(--qqq-purple)' }}>
            Morning × Afternoon Patterns · Tier 4
          </div>
          <div className="qqq-dc-src" style={{ color: 'var(--qqq-fg-3)' }}>
            {p.with_events}/{p.sample_size} sessions with events · unlocks at 10+
          </div>
        </div>
        <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          {p.insight}
          <br /><br />
          Each trading day the intraday monitor logs events; once 10+ sessions
          have both morning capture AND logged events, this engine mines rules
          like <em>&ldquo;gap-up-absorb mornings → 62% afternoon reversal within ±45
          min of 13:00 ET.&rdquo;</em>
        </div>
      </div>
    );
  }

  return (
    <DataCard
      label="Morning × Afternoon Patterns · Tier 4"
      source={`${p.sample_size} sessions · ${p.with_events} with events · mined weekly`}
      tone="purple"
    >
      {/* ① Baseline */}
      <div style={{
        padding: '12px 18px', borderBottom: '1px solid var(--qqq-b1)',
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10,
      }}>
        <ScoreTileMini label="Avg events/day" value={p.baseline?.avg_events_per_session ?? '—'} />
        <ScoreTileMini label="% w/ events"    value={`${p.baseline?.pct_sessions_with_events ?? '—'}%`} />
        <ScoreTileMini label="Up / Down" value={`${p.baseline?.direction_mix?.up ?? 0} / ${p.baseline?.direction_mix?.down ?? 0}`} />
        <ScoreTileMini label="Severe" value={p.baseline?.severity_mix?.severe ?? 0} />
      </div>

      {/* ② By regime */}
      {Array.isArray(p.by_regime) && p.by_regime.length > 0 && (
        <AfternoonRuleSection title="By Regime · VIX / gamma / sector" rules={p.by_regime} tone="teal" />
      )}

      {/* ③ By archetype */}
      {Array.isArray(p.by_archetype) && p.by_archetype.length > 0 && (
        <AfternoonRuleSection title="By Pre-market Archetype" rules={p.by_archetype} tone="gold" />
      )}

      {/* ④ By outcome */}
      {Array.isArray(p.by_outcome) && p.by_outcome.length > 0 && (
        <AfternoonRuleSection title="By Your Outcome · wins vs losses" rules={p.by_outcome} tone="sage" />
      )}

      {/* ⑤ Reversal rules */}
      {Array.isArray(p.reversal_rules) && p.reversal_rules.length > 0 && (
        <div style={{ padding: '14px 18px', borderTop: '1px solid var(--qqq-b1)' }}>
          <div style={{
            fontSize: 11, color: 'var(--qqq-terra)',
            textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
            marginBottom: 10,
          }}>
            ⚡ Reversal Signatures
          </div>
          {p.reversal_rules.map((r, i) => (
            <div key={i} style={{
              padding: '8px 12px', marginBottom: 6,
              background: 'rgba(196,96,80,.06)',
              borderLeft: '3px solid var(--qqq-terra)', borderRadius: 4,
            }}>
              <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', fontWeight: 600 }}>
                {r.archetype.replace(/_/g, ' ')} → {r.reversal_direction.replace(/_/g, ' ')}
              </div>
              <div style={{ fontSize: 12, color: 'var(--qqq-fg-2)', marginTop: 2 }}>
                <strong style={{ color: 'var(--qqq-terra)' }}>{r.reversal_pct}%</strong> of {r.n} sessions saw counter-direction afternoon event · Wilson ≥{r.wilson_low}%
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ⑥ Timing distribution */}
      {Array.isArray(p.by_timing) && p.by_timing.length > 0 && (
        <div style={{ padding: '14px 18px', borderTop: '1px solid var(--qqq-b1)' }}>
          <div style={{
            fontSize: 11, color: 'var(--qqq-fg-3)',
            textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
            marginBottom: 10,
          }}>
            Event timing · by hour
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 6 }}>
            {p.by_timing.map((t) => {
              const skewColor = t.skew === 'up-heavy' ? 'var(--qqq-sage)'
                : t.skew === 'down-heavy' ? 'var(--qqq-terra)'
                : 'var(--qqq-fg-2)';
              return (
                <div key={t.hour} style={{
                  padding: '6px 10px',
                  background: 'var(--surface-3)',
                  borderLeft: `2px solid ${skewColor}`,
                  borderRadius: 3,
                  fontSize: 10,
                }}>
                  <div style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    color: 'var(--qqq-fg-2)',
                  }}>
                    {t.hour_bucket.split('-')[0]}
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--qqq-fg-1)', fontWeight: 700, marginTop: 2 }}>
                    {t.count}
                  </div>
                  <div style={{ fontSize: 9, color: skewColor, marginTop: 2 }}>
                    ↑{t.up} ↓{t.down}{t.severe > 0 ? ` ⚠${t.severe}` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Insight footer */}
      {p.insight && (
        <div style={{
          padding: '12px 18px', fontSize: 12, color: 'var(--qqq-fg-2)',
          background: 'rgba(155,143,196,.04)',
          borderTop: '1px solid var(--qqq-b1)',
          fontStyle: 'italic', lineHeight: 1.6,
        }}>
          {p.insight}
        </div>
      )}
    </DataCard>
  );
}

function ScoreTileMini({ label, value }) {
  return (
    <div style={{
      padding: '8px 10px',
      background: 'var(--surface-3)',
      borderRadius: 4,
    }}>
      <div style={{
        fontSize: 9, color: 'var(--qqq-fg-3)',
        textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 16, fontWeight: 700, color: 'var(--qqq-fg-1)',
        fontFamily: 'Literata, Georgia, serif', marginTop: 2, lineHeight: 1,
      }}>
        {value}
      </div>
    </div>
  );
}

function AfternoonRuleSection({ title, rules, tone }) {
  const color = ({
    teal:   'var(--qqq-teal)',
    gold:   'var(--qqq-gold)',
    sage:   'var(--qqq-sage)',
    purple: 'var(--qqq-purple)',
    terra:  'var(--qqq-terra)',
  })[tone] || 'var(--qqq-fg-2)';
  return (
    <div style={{ padding: '14px 18px', borderTop: '1px solid var(--qqq-b1)' }}>
      <div style={{
        fontSize: 11, color, textTransform: 'uppercase',
        letterSpacing: '0.05em', fontWeight: 600, marginBottom: 10,
      }}>
        {title}
      </div>
      {rules.map((r, i) => {
        const arrow = r.direction === 'higher' ? '↑' : r.direction === 'lower' ? '↓' : '→';
        const ruleColor = r.direction === 'higher' ? 'var(--qqq-gold)' : r.direction === 'lower' ? 'var(--qqq-sage)' : 'var(--qqq-fg-2)';
        return (
          <div key={i} style={{
            padding: '6px 12px', marginBottom: 4,
            background: 'var(--surface-3)',
            borderLeft: `3px solid ${ruleColor}`,
            borderRadius: 3,
          }}>
            <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', fontWeight: 600 }}>
              {r.label}  <span style={{ color: ruleColor, marginLeft: 4 }}>{arrow} {r.lift}×</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', marginTop: 2 }}>
              {r.avg_events} events/session · {r.pct_sessions_with_events}% had events · n={r.n}
              {typeof r.severe_session_pct === 'number' && r.severe_session_pct > 0 && (
                <> · {r.severe_session_pct}% severe</>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TODAY'S REGIME CARD — Commit 45 (environment fingerprint)
// ═══════════════════════════════════════════════════════════
// Surfaces the regime_tag computed by lib/qqq/regime-tag.js and
// saved on every session_briefs row. Six pill chips:
//   VIX · GAMMA · SECTOR LEAD · YIELD CURVE · EVENT · PHASE
// Gives Tim an at-a-glance read on "what kind of day is today"
// and is the foundation for the 30/60/90-day regime-slicing work
// coming next. Hidden entirely when no tag is available.
function TodaysRegimeCard() {
  const data = useQqqData();
  const tag = data._regimeTag;
  if (!tag) return null;

  const pills = [
    { label: 'VIX',     value: humanVix(tag.vix_bucket, tag.vix_term),       tone: vixTone(tag.vix_bucket) },
    { label: 'GAMMA',   value: humanGamma(tag.gamma_sign),                    tone: gammaTone(tag.gamma_sign) },
    { label: 'LEAD',    value: humanSector(tag.sector_leader, tag.tech_vs_spy_pp), tone: sectorTone(tag.sector_leader) },
    { label: 'CURVE',   value: humanCurve(tag.yield_curve),                   tone: curveTone(tag.yield_curve) },
    { label: 'DAY',     value: (tag.day_type || '—').toUpperCase(),          tone: 'muted' },
    { label: 'EVENT',   value: humanEvent(tag.event_proximity),              tone: eventTone(tag.event_proximity) },
    { label: 'PHASE',   value: humanPhase(tag.market_phase),                 tone: phaseTone(tag.market_phase) },
  ].filter((p) => p.value != null);

  return (
    <div style={{
      background: 'var(--qqq-s1, #0c0c11)',
      border: '1px solid var(--qqq-b1, #1e1e2a)',
      borderLeft: '3px solid var(--qqq-teal, #4db8b8)',
      borderRadius: 8,
      padding: '10px 12px 12px',
      marginTop: 18,
      marginBottom: 12,
    }}>
      <div style={{
        fontSize: 10, color: 'var(--qqq-teal, #4db8b8)',
        textTransform: 'uppercase', letterSpacing: '0.05em',
        fontWeight: 700, marginBottom: 8,
      }}>
        Today&apos;s Regime
      </div>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 6,
      }}>
        {pills.map((p, i) => (
          <div key={i} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 8px',
            background: pillBg(p.tone),
            border: `1px solid ${pillBorder(p.tone)}`,
            borderRadius: 10,
            fontSize: 10,
          }}>
            <span style={{
              color: 'var(--qqq-fg-3, #888)', fontWeight: 600,
              letterSpacing: '0.05em',
            }}>
              {p.label}
            </span>
            <span style={{ color: pillText(p.tone), fontWeight: 600 }}>
              {p.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function humanVix(bucket, term) {
  if (!bucket || bucket === 'unknown') return null;
  const termShort = term === 'backwardation' ? ' / backwardation'
    : term === 'contango' ? ' / contango'
    : '';
  return `${bucket.toUpperCase()}${termShort}`;
}
function humanGamma(sign)   { return sign === 'positive' ? '+ (suppress)' : sign === 'negative' ? '− (amplify)' : sign === 'unknown' ? null : sign; }
function humanSector(lead, pp) {
  if (!lead || lead === 'unknown') return null;
  if (lead === 'tech' && pp != null)     return `TECH +${pp}pp`;
  if (lead === 'defensive' && pp != null) return `DEFENSIVE ${pp}pp`;
  return lead.toUpperCase();
}
function humanCurve(curve)  { return curve === 'inverted' ? 'INVERTED' : curve === 'normal' ? 'normal' : curve === 'flat' ? 'flat' : null; }
function humanEvent(ev)     { if (!ev || ev === 'unknown') return null; if (ev === 'clean') return 'clean'; return ev.replace(/_/g, ' '); }
function humanPhase(p)      { if (!p || p === 'unknown') return null; return p; }

function vixTone(b)    { return b === 'high' ? 'terra' : b === 'mid' ? 'gold' : b === 'low' ? 'sage' : 'muted'; }
function gammaTone(s)  { return s === 'positive' ? 'sage' : s === 'negative' ? 'terra' : 'muted'; }
function sectorTone(s) { return s === 'tech' ? 'sage' : s === 'defensive' ? 'terra' : s === 'broad' ? 'gold' : 'muted'; }
function curveTone(c)  { return c === 'inverted' ? 'terra' : c === 'flat' ? 'gold' : c === 'normal' ? 'sage' : 'muted'; }
function eventTone(e)  { return e === 'fomc_day' ? 'terra' : (e === 'fomc_week' || e === 'cpi_week' || e === 'nfp_week') ? 'gold' : e === 'clean' ? 'sage' : 'muted'; }
function phaseTone(p)  { return p === 'rally' ? 'sage' : p === 'correction' ? 'terra' : p === 'chop' ? 'gold' : p === 'trend' ? 'purple' : 'muted'; }

function pillBg(tone) {
  return ({
    sage:   'rgba(122,158,130,.08)',
    gold:   'rgba(200,169,110,.08)',
    purple: 'rgba(155,143,196,.08)',
    terra:  'rgba(196,96,80,.08)',
    teal:   'rgba(77,184,184,.08)',
    muted:  'transparent',
  })[tone] || 'transparent';
}
function pillBorder(tone) {
  return ({
    sage:   'var(--qqq-sage, #7a9e82)',
    gold:   'var(--qqq-gold, #c8a96e)',
    purple: 'var(--qqq-purple, #9b8fc4)',
    terra:  'var(--qqq-terra, #c46050)',
    teal:   'var(--qqq-teal, #4db8b8)',
    muted:  'var(--qqq-b2, #272736)',
  })[tone] || 'var(--qqq-b2, #272736)';
}
function pillText(tone) {
  return ({
    sage:   'var(--qqq-sage, #7a9e82)',
    gold:   'var(--qqq-gold, #c8a96e)',
    purple: 'var(--qqq-purple, #9b8fc4)',
    terra:  'var(--qqq-terra, #c46050)',
    teal:   'var(--qqq-teal, #4db8b8)',
    muted:  'var(--qqq-fg-2, #aaa)',
  })[tone] || 'var(--qqq-fg-2, #aaa)';
}

// ═══════════════════════════════════════════════════════════
// HISTORY CARD — day-by-day verdict vs outcome record
// ═══════════════════════════════════════════════════════════
// Sidebar card below the verdict. Renders the compound-intelligence
// record — one row per prior qqq_session_briefs row. Month 1 it
// looks thin. Month 6 it's the moat.
function HistoryCard() {
  const data = useQqqData();
  const rows = data.sidebarHistory || [];
  const rollup = data.sidebarHistoryRollup || { hits: 0, hitRate: '—', total: 0 };
  return (
    <div className="qqq-history-card">
      <div className="qqq-history-hdr">
        <div className="qqq-history-title">History · last 14 days</div>
        <div className="qqq-history-rollup">
          <span className="qqq-history-rollup-hits">{rollup.hits} hit</span>
          <span className="qqq-history-rollup-sep">·</span>
          <span className="qqq-history-rollup-rate">{rollup.hitRate}</span>
        </div>
      </div>
      <div className="qqq-history-list">
        {rows.length > 0 ? (
          rows.map((h, i) => <HistoryRow key={i} row={h} />)
        ) : (
          <div className="qqq-history-empty">
            No prior briefs yet. Today&apos;s brief is logged on first open;
            tomorrow it appears here.
          </div>
        )}
      </div>
      <div className="qqq-history-foot">
        Every morning&apos;s verdict + afternoon&apos;s outcome logged.
        Training data compounds.
      </div>
    </div>
  );
}

function HistoryRow({ row }) {
  const dirTone = row.direction === 'bull' ? 'bull'
    : row.direction === 'bear' ? 'bear'
    : 'mixed';
  const outcomeTone = row.outcome
    ? (row.outcome.startsWith('+') ? 'up' : row.outcome.startsWith('−') ? 'dn' : 'fl')
    : 'pending';
  const hitBadge = row.bias === 'y' ? '✓' : row.bias === 'n' ? '✗' : '…';
  const hitTone = row.bias === 'y' ? 'y' : row.bias === 'n' ? 'n' : 'p';
  const isMiss = row.bias === 'n';
  return (
    <div className={`qqq-hr ${row.status === 'pending' ? 'is-pending' : ''} ${isMiss ? 'is-miss' : ''}`}>
      <div className="qqq-hr-top">
        <div className="qqq-hr-date">{row.date}</div>
        <div className={`qqq-hr-dir-dot ${dirTone}`} />
        <div className={`qqq-hr-dir ${dirTone}`}>{row.direction}</div>
        <div className="qqq-hr-score">{row.score}</div>
        <div className={`qqq-hr-outcome ${outcomeTone}`}>
          {row.outcome || 'pending'}
        </div>
        <div className={`qqq-hr-hit ${hitTone}`}>{hitBadge}</div>
      </div>
      {(row.reason || row.override) && (
        <div className="qqq-hr-reason">
          {row.reason && <span className="qqq-hr-reason-drivers">{row.reason}</span>}
          {row.override && (
            <span className="qqq-hr-reason-override">
              <span className="qqq-hr-reason-override-tag">override:</span>{' '}
              {row.override}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// EDGE EMERGING CARD — pattern insight from accumulated history
// ═══════════════════════════════════════════════════════════
function EdgeEmergingCard() {
  const data = useQqqData();
  const e = data.sidebarEdgeEmerging;
  if (!e) return null;   // not enough data yet — hide the card
  return (
    <div className="qqq-edge-card">
      <div className="qqq-edge-eyebrow">Edge emerging</div>
      <div className="qqq-edge-setup">
        <div className="qqq-edge-name">{e.setup}</div>
        <div className="qqq-edge-stats">
          <span className="qqq-edge-rate">{e.rate}</span>
          <span className="qqq-edge-sample">· {e.sample}</span>
        </div>
      </div>
      <div className="qqq-edge-body">{e.body}</div>
    </div>
  );
}

// (PhaseDivider removed in Commit 8 — replaced by PhaseHeader.)

// ═══════════════════════════════════════════════════════════
// END-OF-DAY REVIEW CARD — Commit 80a
// ═══════════════════════════════════════════════════════════
// Two drop zones (price chart · option chart). Auto-runs Vision
// when both are present. Auto-files into the user's designated
// default collection per type (see qqq_library_folders.default_for).
// The primary daily-ritual surface Tim asked for: no tab-hopping,
// no hunting for upload buttons — drag, drop, done.
// ═══════════════════════════════════════════════════════════
function EndOfDayReviewCard() {
  const data = useQqqData();
  const workstationId = data._workstationId;
  const userId = data._userId;

  const [priceFile, setPriceFile] = useState(null);
  const [optionFile, setOptionFile] = useState(null);
  const [priceState, setPriceState] = useState({ status: 'idle', analysis: null, error: null, filed: null, url: null });
  const [optionState, setOptionState] = useState({ status: 'idle', analysis: null, error: null, filed: null, url: null });

  // Hydrate from existing session row so a page refresh shows prior
  // uploads. Reads the same resolution payload Section6SessionLog does.
  const resolution = data._todayResolution;
  useEffect(() => {
    if (!resolution) return;
    // Back-compat: resolution.chart_analysis might hold price or generic.
    // Newer rows have resolution.charts.{price_pair, option_pair}.
    const charts = resolution.charts || {};
    const priceAnalysis  = charts.price_pair  || charts.price_15m || (resolution.chart_analysis && !charts.option_pair ? resolution.chart_analysis : null);
    const optionAnalysis = charts.option_pair || charts.option_chain || null;
    if (priceAnalysis) setPriceState((s) => s.analysis ? s : { status: 'done', analysis: priceAnalysis, error: null, filed: null, url: priceAnalysis._meta?.image_url || null });
    if (optionAnalysis) setOptionState((s) => s.analysis ? s : { status: 'done', analysis: optionAnalysis, error: null, filed: null, url: optionAnalysis._meta?.image_url || null });
  }, [resolution]);

  const runUpload = useCallback(async (chartType, file, setState) => {
    if (!workstationId || !userId) {
      setState({ status: 'error', analysis: null, error: 'Workstation context missing.', filed: null, url: null });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setState({ status: 'error', analysis: null, error: 'Image > 5MB. Compress before upload.', filed: null, url: null });
      return;
    }
    setState({ status: 'uploading', analysis: null, error: null, filed: null, url: null });
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          if (typeof result !== 'string') return reject(new Error('read failed'));
          resolve(result.split(',')[1] || '');
        };
        reader.onerror = () => reject(reader.error || new Error('read error'));
        reader.readAsDataURL(file);
      });
      const res = await fetch('/api/workstations/qqq-chart-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id:   workstationId,
          user_id:          userId,
          image_base64:     base64,
          image_media_type: file.type || 'image/jpeg',
          chart_type:       chartType,
        }),
      });
      const payload = await res.json();
      if (!res.ok) {
        // Commit 85 — surface detail when the server has one (e.g. the
        // "response truncated at max_tokens" hint from the JSON
        // extractor) so Tim sees WHY the call failed, not just that
        // it did.
        const msg = payload?.detail
          ? `${payload.error || 'failed'} — ${payload.detail}`
          : (payload?.error || `HTTP ${res.status}`);
        setState({ status: 'error', analysis: null, error: msg, filed: null, url: null });
        return;
      }
      setState({
        status:   'done',
        analysis: payload.analysis,
        error:    null,
        filed:    payload.auto_filed || null,
        url:      payload.image_url || null,
      });
    } catch (err) {
      setState({ status: 'error', analysis: null, error: err?.message || 'Upload failed', filed: null, url: null });
    }
  }, [workstationId, userId]);

  // Auto-analyze on drop (per Tim's pick in the Commit 80 plan:
  // fire immediately when a slot gets a file).
  //
  // Commit 84 — gate on workstationId + userId. Previously if the
  // user dropped a file before QqqTab's initial load() had populated
  // _workstationId / _userId into the context, runUpload errored
  // with "Workstation context missing" and stayed errored — the
  // auto-retry effect wouldn't refire because status was 'error'
  // not 'idle'. Now the effect waits until context is available
  // AND the slot is idle, so the race resolves cleanly.
  useEffect(() => {
    if (!workstationId || !userId) return;
    if (priceFile && priceState.status === 'idle') runUpload('price_pair', priceFile, setPriceState);
  }, [priceFile, priceState.status, runUpload, workstationId, userId]);
  useEffect(() => {
    if (!workstationId || !userId) return;
    if (optionFile && optionState.status === 'idle') runUpload('option_pair', optionFile, setOptionState);
  }, [optionFile, optionState.status, runUpload, workstationId, userId]);

  const reset = (which) => {
    if (which === 'price')  { setPriceFile(null);  setPriceState({ status: 'idle', analysis: null, error: null, filed: null, url: null }); }
    if (which === 'option') { setOptionFile(null); setOptionState({ status: 'idle', analysis: null, error: null, filed: null, url: null }); }
  };

  // Commit 84 — Retry keeps the file and just flips status back to
  // 'idle' so the auto-analyze effect re-fires. Previously "Try
  // again" called reset() which nuked the file, forcing a re-drag.
  const retry = (which) => {
    if (which === 'price')  setPriceState({ status: 'idle', analysis: null, error: null, filed: null, url: null });
    if (which === 'option') setOptionState({ status: 'idle', analysis: null, error: null, filed: null, url: null });
  };

  return (
    <DataCard
      label="End-of-Day Review · drop charts, Shift reads, auto-files"
      source={[
        priceState.status === 'done'  ? 'price ✓' : priceState.status === 'uploading' ? 'price …' : null,
        optionState.status === 'done' ? 'option ✓' : optionState.status === 'uploading' ? 'option …' : null,
      ].filter(Boolean).join(' · ') || 'drop your two charts below'}
      tone="teal"
    >
      {/* Commit 82 — drop zones stacked vertically (1fr instead of
          1fr 1fr). The card now lives in the narrow right column of
          the Log tab (~40% of grid width), so side-by-side zones
          cramped. Vertical stack reads cleaner and still fits the
          compact state (~96px each). */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: 10,
        padding: '12px 14px',
      }}>
        <ChartDropZone
          label="Price Chart"
          sublabel="15-min + 5-min dual"
          tone="teal"
          file={priceFile}
          onFile={setPriceFile}
          state={priceState}
          onReset={() => reset('price')}
          onRetry={() => retry('price')}
        />
        <ChartDropZone
          label="Option Chart"
          sublabel="Call LEFT · Put RIGHT"
          tone="sand"
          file={optionFile}
          onFile={setOptionFile}
          state={optionState}
          onReset={() => reset('option')}
          onRetry={() => retry('option')}
        />
      </div>

      {/* Commit 80b — sections 3 · 4 · 5: three-voice dialogue +
          convergence. Only render once at least one chart has
          been parsed; otherwise there's nothing to converge
          against. */}
      {(priceState.status === 'done' || optionState.status === 'done') && (
        <ThreeVoiceConvergePanel briefDate={resolution?.brief_date || null} />
      )}
    </DataCard>
  );
}

// ─── ThreeVoiceConvergePanel — sections 3 · 4 · 5 (Commit 80b) ──
// Your Read (gold) + Claude's Read (purple) + Converge (teal).
// Reads current values from /api/workstations/qqq-chart-converge
// on mount so page refreshes don't lose work. Saves each voice
// inline — Converge POSTs all three to the server along with
// Shift's structured extraction, returns the synthesis.
function ThreeVoiceConvergePanel({ briefDate }) {
  const data = useQqqData();
  const aiName = useAiName();
  const workstationId = data._workstationId;
  const userId = data._userId;

  const [userRead, setUserRead] = useState('');
  const [claudeReadRaw, setClaudeReadRaw] = useState('');
  const [claudeReadParsed, setClaudeReadParsed] = useState(null);
  const [claudeParseError, setClaudeParseError] = useState(null);

  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [convergeStatus, setConvergeStatus] = useState('idle'); // idle | running | done | error
  const [convergeError, setConvergeError] = useState(null);
  const [convergeReport, setConvergeReport] = useState(null);

  // Load persisted state once on mount
  useEffect(() => {
    if (!workstationId || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ workstation_id: workstationId, user_id: userId });
        if (briefDate) qs.set('brief_date', briefDate);
        const res = await fetch(`/api/workstations/qqq-chart-converge?${qs}`);
        const payload = await res.json();
        if (cancelled || !res.ok || !payload?.row) return;
        const row = payload.row;
        if (row.user_chart_read) setUserRead(row.user_chart_read);
        if (row.claude_chart_read) {
          setClaudeReadParsed(row.claude_chart_read);
          setClaudeReadRaw(JSON.stringify(row.claude_chart_read, null, 2));
        }
        if (row.converge_report) setConvergeReport(row.converge_report);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [workstationId, userId, briefDate]);

  // Parse Claude's JSON paste lazily as the user edits
  useEffect(() => {
    const t = claudeReadRaw.trim();
    if (!t) { setClaudeReadParsed(null); setClaudeParseError(null); return; }
    try {
      const cleaned = t.replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/i, '').trim();
      const parsed = JSON.parse(cleaned);
      setClaudeReadParsed(parsed);
      setClaudeParseError(null);
    } catch (err) {
      setClaudeReadParsed(null);
      setClaudeParseError(err.message);
    }
  }, [claudeReadRaw]);

  const canConverge = (userRead.trim().length > 0 || claudeReadParsed) && workstationId && userId;

  const runConverge = async () => {
    if (!canConverge || convergeStatus === 'running') return;
    setConvergeStatus('running');
    setConvergeError(null);
    try {
      const res = await fetch('/api/workstations/qqq-chart-converge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id:     workstationId,
          user_id:            userId,
          brief_date:         briefDate || undefined,
          user_chart_read:    userRead,
          claude_chart_read:  claudeReadParsed,
        }),
      });
      const payload = await res.json();
      if (!res.ok) {
        setConvergeStatus('error');
        setConvergeError(payload?.error || `HTTP ${res.status}`);
        return;
      }
      setConvergeReport(payload.converge_report);
      setConvergeStatus('done');
    } catch (err) {
      setConvergeStatus('error');
      setConvergeError(err?.message || 'converge failed');
    }
  };

  // Fire-and-forget save of user_chart_read on blur (via converge
  // endpoint's persist side-effect). No dedicated endpoint needed —
  // the converge POST persists partial input.
  const saveUserReadOnBlur = async () => {
    if (!workstationId || !userId) return;
    setSaveStatus('saving');
    try {
      // Minimal PATCH against qqq-session-brief
      const res = await fetch('/api/workstations/qqq-session-brief', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstationId,
          user_id: userId,
          user_chart_read: userRead || null,
        }),
      });
      setSaveStatus(res.ok ? 'saved' : 'error');
      if (res.ok) setTimeout(() => setSaveStatus('idle'), 1500);
    } catch { setSaveStatus('error'); }
  };

  return (
    <div style={{ padding: '0 18px 18px' }}>
      {/* Section 3 — YOUR READ */}
      <div style={{ marginTop: 14 }}>
        <div style={{
          fontSize: 11, color: 'var(--qqq-gold)',
          textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
          marginBottom: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>3 · Your read</span>
          {saveStatus === 'saving' && <span style={{ fontSize: 10, color: 'var(--qqq-fg-3)', fontWeight: 400 }}>saving…</span>}
          {saveStatus === 'saved'  && <span style={{ fontSize: 10, color: 'var(--qqq-sage)', fontWeight: 400 }}>✓ saved</span>}
          {saveStatus === 'error'  && <span style={{ fontSize: 10, color: 'var(--qqq-terra)', fontWeight: 400 }}>save failed</span>}
        </div>
        <textarea
          value={userRead}
          onChange={(e) => setUserRead(e.target.value.slice(0, 10000))}
          onBlur={saveUserReadOnBlur}
          placeholder="What do YOU see? Method-lens (YC v3) + fresh eyes on the full day. Where does your read differ from what Shift extracted above?"
          rows={5}
          style={{
            width: '100%', padding: '10px 12px',
            background: 'var(--surface-3)', color: 'var(--qqq-fg-1)',
            border: '1px solid var(--qqq-b2)', borderRadius: 4,
            fontFamily: 'inherit', fontSize: 13, lineHeight: 1.6,
            resize: 'vertical', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Section 4 — CLAUDE'S READ */}
      <div style={{ marginTop: 18 }}>
        <div style={{
          fontSize: 11, color: 'var(--qqq-purple)',
          textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
          marginBottom: 6,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>4 · Claude's read (paste JSON)</span>
          {claudeReadRaw && claudeParseError && (
            <span style={{ fontSize: 10, color: 'var(--qqq-terra)', fontWeight: 400 }}>
              JSON parse: {claudeParseError}
            </span>
          )}
          {claudeReadParsed && (
            <span style={{ fontSize: 10, color: 'var(--qqq-sage)', fontWeight: 400 }}>
              ✓ parsed
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--qqq-fg-3)', marginBottom: 6, lineHeight: 1.5 }}>
          Use the template at <code style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--qqq-purple)' }}>docs/templates/claude-chart-read.md</code> — paste it into your Claude CLI conversation along with today's two chart images, then paste Claude's JSON output here.
        </div>
        <textarea
          value={claudeReadRaw}
          onChange={(e) => setClaudeReadRaw(e.target.value.slice(0, 40000))}
          placeholder={'{\n  "brief_date": "…",\n  "method_lens": { … },\n  "fresh_eyes": { … },\n  "option_chart_read": { … },\n  "novel_observations": [ … ],\n  "hypothesis_signals": [ … ],\n  "confidence": "…"\n}'}
          rows={8}
          style={{
            width: '100%', padding: '10px 12px',
            background: 'var(--surface-3)', color: 'var(--qqq-fg-1)',
            border: `1px solid ${claudeParseError ? 'var(--qqq-terra)' : claudeReadParsed ? 'var(--qqq-sage)' : 'var(--qqq-b2)'}`,
            borderRadius: 4,
            fontFamily: 'JetBrains Mono, monospace', fontSize: 12, lineHeight: 1.55,
            resize: 'vertical', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Section 5 — CONVERGE */}
      <div style={{ marginTop: 18 }}>
        <div style={{
          fontSize: 11, color: 'var(--qqq-teal)',
          textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
          marginBottom: 8,
        }}>
          5 · Converge · three-voice synthesis
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={runConverge}
            disabled={!canConverge || convergeStatus === 'running'}
            style={{
              padding: '9px 18px',
              background: canConverge && convergeStatus !== 'running' ? 'var(--qqq-teal)' : 'var(--surface-3)',
              color: canConverge && convergeStatus !== 'running' ? 'var(--surface-0)' : 'var(--qqq-fg-3)',
              border: 0, borderRadius: 4,
              fontSize: 12, fontWeight: 700,
              cursor: canConverge && convergeStatus !== 'running' ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
            }}
          >
            {convergeStatus === 'running'
              ? `${aiName} is synthesizing…`
              : convergeReport
                ? 'Re-run convergence'
                : `Ask ${aiName} to converge`}
          </button>
          {!canConverge && (
            <span style={{ fontSize: 11, color: 'var(--qqq-fg-3)', fontStyle: 'italic' }}>
              Add your read and/or Claude's read above to enable.
            </span>
          )}
          {convergeError && (
            <span style={{ fontSize: 11, color: 'var(--qqq-terra)' }}>{convergeError}</span>
          )}
        </div>

        {convergeReport && <ConvergeReportView report={convergeReport} />}
      </div>

      {/* Section 6 — CANONICAL DAY RECORD (Commit 80c) */}
      {convergeReport && (
        <CanonicalDraftPanel
          briefDate={briefDate}
          workstationId={workstationId}
          userId={userId}
          convergeReport={convergeReport}
        />
      )}
    </div>
  );
}

// ─── Section 6 — Canonical Day Record (Commit 80c) ──────────
// The "import back into Shift" step. Tim edits the convergence
// synthesis into an authoritative prose summary and locks it.
// Locked drafts are cited as highest-trust evidence in folder
// Q&A (qqq-folders handleAskJarvis weights them above raw Vision).
function CanonicalDraftPanel({ briefDate, workstationId, userId, convergeReport }) {
  const data = useQqqData();
  const resolution = data._todayResolution;

  // Seed from existing canonical_draft on the row; if none, seed
  // from a readable render of converge_report the user can edit.
  const seedFromConverge = useMemo(() => renderConvergeAsDraft(convergeReport), [convergeReport]);
  const [draft, setDraft]           = useState('');
  const [lockedAt, setLockedAt]     = useState(null);
  const [version, setVersion]       = useState(0);
  const [editing, setEditing]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);
  const [hint, setHint]             = useState(null);

  // Hydrate from resolution on mount and when it updates
  useEffect(() => {
    if (!resolution) return;
    const seed = resolution.canonical_draft || seedFromConverge || '';
    setDraft(seed);
    setLockedAt(resolution.canonical_locked_at || null);
    setVersion(resolution.canonical_version || 0);
    // If nothing locked yet, start in edit mode so Tim sees the
    // seeded text ready to refine. If already locked, read-only.
    setEditing(!resolution.canonical_locked_at);
  }, [resolution?.canonical_locked_at, resolution?.canonical_draft, resolution?.canonical_version, seedFromConverge]);

  const isLocked = !!lockedAt && !editing;

  const submit = async (action) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    setHint(null);
    try {
      const res = await fetch('/api/workstations/qqq-session-brief', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstationId,
          user_id: userId,
          canonical_action: action,   // 'lock' | 'revise' | 'draft' | 'clear'
          canonical_draft: draft,
        }),
      });
      const payload = await res.json();
      if (!res.ok) {
        setError(payload?.error || `HTTP ${res.status}`);
        return;
      }
      const row = payload.row || {};
      if (action === 'clear') {
        setLockedAt(null);
        setVersion(row.canonical_version || 0);
        setEditing(true);
        setHint('Canonical draft cleared.');
      } else if (action === 'lock' || action === 'revise') {
        setLockedAt(row.canonical_locked_at || new Date().toISOString());
        setVersion(row.canonical_version || (version + 1));
        setEditing(false);
        setHint(action === 'lock' ? 'Locked as canonical ✓' : 'Locked new version ✓');
      } else if (action === 'draft') {
        setHint('Draft saved.');
      }
      setTimeout(() => setHint(null), 2500);
    } catch (e) { setError(e?.message || 'save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{
        fontSize: 11, color: 'var(--qqq-sage)',
        textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
        marginBottom: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
      }}>
        <span>6 · Canonical day record · import for intelligence</span>
        {isLocked && (
          <span style={{
            fontSize: 10, color: 'var(--qqq-sage)', fontWeight: 700,
            fontFamily: 'JetBrains Mono, monospace',
            padding: '2px 8px', borderRadius: 3,
            background: 'rgba(122,158,130,.12)',
            border: '1px solid var(--qqq-sage)',
          }}>
            ★ locked v{version} · {fmtRelative(lockedAt)}
          </span>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--qqq-fg-3)', marginBottom: 8, lineHeight: 1.55 }}>
        {isLocked
          ? 'This is the authoritative record for ' + (briefDate || 'this day') + '. Used as highest-trust evidence in Collections Q&A and (soon) pattern-engine weighting. Click Revise to update.'
          : 'Distill the convergence into the one thing you want Shift to remember from today. Edit freely — this is your voice. Lock when ready; it becomes highest-trust evidence for this day going forward.'}
      </div>

      {isLocked ? (
        <div style={{
          padding: '12px 14px',
          background: 'rgba(122,158,130,.05)',
          border: '1px solid var(--qqq-sage)',
          borderLeft: '3px solid var(--qqq-sage)',
          borderRadius: 4,
          fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.65,
          whiteSpace: 'pre-wrap',
        }}>
          {draft || '(no content — locked but empty)'}
        </div>
      ) : (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 20000))}
          placeholder="Your synthesized record for the day. What's true? What should Shift treat as ground truth going forward?"
          rows={12}
          style={{
            width: '100%', padding: '10px 12px',
            background: 'var(--surface-3)', color: 'var(--qqq-fg-1)',
            border: '1px solid var(--qqq-sage)', borderRadius: 4,
            fontFamily: 'inherit', fontSize: 13, lineHeight: 1.65,
            resize: 'vertical', boxSizing: 'border-box',
          }}
        />
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
        {isLocked ? (
          <>
            <button
              type="button"
              onClick={() => setEditing(true)}
              style={{
                padding: '7px 14px',
                background: 'var(--qqq-sage)', color: 'var(--surface-0)',
                border: 0, borderRadius: 4,
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Revise
            </button>
            <span style={{ fontSize: 11, color: 'var(--qqq-fg-3)' }}>
              canonical v{version}
            </span>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => submit(version > 0 ? 'revise' : 'lock')}
              disabled={saving || !draft.trim()}
              style={{
                padding: '8px 16px',
                background: draft.trim() && !saving ? 'var(--qqq-sage)' : 'var(--surface-3)',
                color: draft.trim() && !saving ? 'var(--surface-0)' : 'var(--qqq-fg-3)',
                border: 0, borderRadius: 4,
                fontSize: 12, fontWeight: 700,
                cursor: draft.trim() && !saving ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
              }}
            >
              {saving
                ? 'Locking…'
                : version > 0
                  ? `★ Lock revision (v${version + 1})`
                  : '★ Lock as canonical'}
            </button>
            <button
              type="button"
              onClick={() => submit('draft')}
              disabled={saving}
              style={{
                padding: '8px 14px',
                background: 'transparent',
                color: 'var(--qqq-fg-2)',
                border: '1px solid var(--qqq-b2)',
                borderRadius: 4,
                fontSize: 12, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Save draft (no lock)
            </button>
            {version > 0 && (
              <button
                type="button"
                onClick={() => { setEditing(false); }}
                disabled={saving}
                style={{
                  padding: '8px 14px',
                  background: 'transparent', color: 'var(--qqq-fg-3)',
                  border: '1px solid var(--qqq-b2)',
                  borderRadius: 4, fontSize: 12, cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Cancel revision
              </button>
            )}
          </>
        )}
        {hint && <span style={{ fontSize: 11, color: 'var(--qqq-sage)' }}>{hint}</span>}
        {error && <span style={{ fontSize: 11, color: 'var(--qqq-terra)' }}>{error}</span>}
      </div>
    </div>
  );
}

// Render a converge_report as editable prose seed text.
function renderConvergeAsDraft(report) {
  if (!report) return '';
  const lines = [];
  const agreed    = Array.isArray(report.agreed)       ? report.agreed       : [];
  const diverged  = Array.isArray(report.diverged)     ? report.diverged     : [];
  const novel     = Array.isArray(report.novel)        ? report.novel        : [];
  const notes     = Array.isArray(report.prompt_notes) ? report.prompt_notes : [];

  if (agreed.length > 0) {
    lines.push('AGREED:');
    for (const a of agreed) lines.push(`· ${a}`);
    lines.push('');
  }
  if (diverged.length > 0) {
    lines.push('DIVERGED:');
    for (const d of diverged) {
      lines.push(`· ${d.topic || '(topic)'}`);
      if (d.my_take) lines.push(`  my take: ${d.my_take}`);
    }
    lines.push('');
  }
  if (novel.length > 0) {
    lines.push('NOVEL:');
    for (const n of novel) lines.push(`· ${n}`);
    lines.push('');
  }
  if (report.next_time) {
    lines.push('NEXT TIME:');
    lines.push(report.next_time);
    lines.push('');
  }
  if (notes.length > 0) {
    lines.push('PROMPT NOTES (add to Vision prompts next version):');
    for (const n of notes) lines.push(`· ${n}`);
  }
  return lines.join('\n').trim();
}

// ─── ConvergeReportView — renders the three-way JSON report ──
function ConvergeReportView({ report }) {
  if (!report) return null;
  if (report.parse_error) {
    return (
      <div style={{
        marginTop: 12, padding: '10px 14px',
        background: 'rgba(196,96,80,.06)',
        borderLeft: '3px solid var(--qqq-terra)', borderRadius: 4,
        fontSize: 12, color: 'var(--qqq-fg-1)',
      }}>
        Model returned unparseable output. Raw text:
        <pre style={{ marginTop: 8, fontSize: 11, whiteSpace: 'pre-wrap' }}>{report.raw}</pre>
      </div>
    );
  }

  const agreed    = Array.isArray(report.agreed) ? report.agreed : [];
  const diverged  = Array.isArray(report.diverged) ? report.diverged : [];
  const novel     = Array.isArray(report.novel) ? report.novel : [];
  const promptNotes = Array.isArray(report.prompt_notes) ? report.prompt_notes : [];

  return (
    <div style={{
      marginTop: 14,
      padding: '14px 16px',
      background: 'var(--surface-2)',
      border: '1px solid var(--qqq-teal)',
      borderRadius: 6,
    }}>
      {report._meta?.generated_at && (
        <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)', marginBottom: 10, fontFamily: 'JetBrains Mono, monospace' }}>
          generated {fmtRelative(report._meta.generated_at)}
          {report._meta.had_user_read && ' · with your read'}
          {report._meta.had_claude_read && ' · with Claude\'s read'}
        </div>
      )}

      {agreed.length > 0 && (
        <ConvergeBlock tone="sage" title={`Agreed · ${agreed.length}`}>
          {agreed.map((a, i) => (
            <div key={i} style={{ fontSize: 12.5, color: 'var(--qqq-fg-1)', marginBottom: 4, lineHeight: 1.55 }}>
              · {a}
            </div>
          ))}
        </ConvergeBlock>
      )}

      {diverged.length > 0 && (
        <ConvergeBlock tone="amber" title={`Diverged · ${diverged.length}`}>
          {diverged.map((d, i) => (
            <div key={i} style={{
              marginBottom: 10,
              padding: '8px 10px',
              background: 'var(--surface-3)',
              borderLeft: '2px solid var(--qqq-amber, #d4956a)',
              borderRadius: 3,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--qqq-fg-1)', marginBottom: 4 }}>
                {d.topic || '(untitled)'}
              </div>
              {d.shift_says && (
                <div style={{ fontSize: 11.5, marginBottom: 3 }}>
                  <span style={{ color: 'var(--qqq-teal)', fontWeight: 700 }}>Shift:</span>{' '}
                  <span style={{ color: 'var(--qqq-fg-2)' }}>{d.shift_says}</span>
                </div>
              )}
              {d.tim_says && (
                <div style={{ fontSize: 11.5, marginBottom: 3 }}>
                  <span style={{ color: 'var(--qqq-gold)', fontWeight: 700 }}>You:</span>{' '}
                  <span style={{ color: 'var(--qqq-fg-2)' }}>{d.tim_says}</span>
                </div>
              )}
              {d.claude_says && (
                <div style={{ fontSize: 11.5, marginBottom: 3 }}>
                  <span style={{ color: 'var(--qqq-purple)', fontWeight: 700 }}>Claude:</span>{' '}
                  <span style={{ color: 'var(--qqq-fg-2)' }}>{d.claude_says}</span>
                </div>
              )}
              {d.my_take && (
                <div style={{ fontSize: 12, marginTop: 5, paddingTop: 5, borderTop: '1px dashed var(--qqq-b3)' }}>
                  <span style={{ color: 'var(--qqq-sage)', fontWeight: 700 }}>My take:</span>{' '}
                  <span style={{ color: 'var(--qqq-fg-1)', lineHeight: 1.55 }}>{d.my_take}</span>
                </div>
              )}
            </div>
          ))}
        </ConvergeBlock>
      )}

      {novel.length > 0 && (
        <ConvergeBlock tone="purple" title={`Novel · ${novel.length} · candidates for the observations ledger`}>
          {novel.map((n, i) => (
            <div key={i} style={{ fontSize: 12.5, color: 'var(--qqq-fg-1)', marginBottom: 4, lineHeight: 1.55 }}>
              · {n}
            </div>
          ))}
        </ConvergeBlock>
      )}

      {report.next_time && (
        <ConvergeBlock tone="teal" title="Next time">
          <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.65, fontWeight: 500 }}>
            {report.next_time}
          </div>
        </ConvergeBlock>
      )}

      {promptNotes.length > 0 && (
        <ConvergeBlock tone="sand" title={`Prompt proposals · ${promptNotes.length}`}>
          <div style={{ fontSize: 11, color: 'var(--qqq-fg-3)', marginBottom: 6, fontStyle: 'italic' }}>
            Suggested additions to the Vision prompts — human review queue in a later commit.
          </div>
          {promptNotes.map((p, i) => (
            <div key={i} style={{ fontSize: 12, color: 'var(--qqq-fg-2)', marginBottom: 4, lineHeight: 1.55 }}>
              · {p}
            </div>
          ))}
        </ConvergeBlock>
      )}
    </div>
  );
}

function ConvergeBlock({ tone, title, children }) {
  const toneColor = ({
    sage:   'var(--qqq-sage)',
    amber:  'var(--qqq-amber, #d4956a)',
    purple: 'var(--qqq-purple)',
    teal:   'var(--qqq-teal)',
    sand:   'var(--qqq-amber, #d4956a)',
  })[tone] || 'var(--qqq-teal)';
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 10, color: toneColor,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        fontWeight: 700, marginBottom: 6,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

// ─── ChartDropZone — one of the two slots ──────────────────
function ChartDropZone({ label, sublabel, tone, file, onFile, state, onReset, onRetry }) {
  const [dragOver, setDragOver] = useState(false);
  const toneColor = tone === 'sand' ? 'var(--qqq-amber, #d4956a)' : 'var(--qqq-teal)';
  const toneRgba = tone === 'sand' ? 'rgba(212,149,106,0.1)' : 'rgba(77,184,184,0.1)';

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f && f.type.startsWith('image/')) onFile(f);
  };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);
  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) onFile(f);
    e.target.value = '';
  };

  // Empty slot — show the drop area
  // Commit 81 — tightened drop zones: min-height 160→96, padding
  // 24/16→10/14. The old size felt oversized vs the rest of the page.
  if (state.status === 'idle' && !file) {
    return (
      <label
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 3,
          minHeight: 96,
          padding: '12px 14px',
          border: `1.5px dashed ${toneColor}`,
          borderRadius: 6,
          background: dragOver ? toneRgba : 'var(--surface-3)',
          cursor: 'pointer',
          transition: 'background 0.15s',
        }}
      >
        <div style={{
          fontSize: 12, fontWeight: 700, color: toneColor,
          textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>
          {label}
        </div>
        <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)' }}>
          {sublabel}
        </div>
        <div style={{ fontSize: 10, color: 'var(--qqq-fg-2)', marginTop: 2 }}>
          Drag & drop · or click to browse
        </div>
        <input
          type="file"
          accept="image/*"
          onChange={onPick}
          style={{ display: 'none' }}
        />
      </label>
    );
  }

  // Uploading
  if (state.status === 'uploading') {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        minHeight: 96,
        padding: '12px 14px',
        border: `1.5px solid ${toneColor}`,
        borderRadius: 6,
        background: toneRgba,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: toneColor, marginBottom: 4 }}>
          {label}
        </div>
        <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)' }}>
          Analyzing with Vision…
        </div>
      </div>
    );
  }

  // Error
  if (state.status === 'error') {
    return (
      <div style={{
        minHeight: 96, padding: '12px 14px',
        border: '1.5px solid var(--qqq-terra)',
        borderRadius: 6,
        background: 'rgba(196,96,80,.06)',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--qqq-terra)', marginBottom: 4 }}>
          {label} — failed
        </div>
        <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', marginBottom: 8, lineHeight: 1.5 }}>
          {state.error}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={onRetry} style={{
            padding: '4px 10px',
            background: 'var(--qqq-teal)', color: 'var(--surface-0)',
            border: 0, borderRadius: 4,
            fontSize: 10, fontWeight: 700, cursor: 'pointer',
            fontFamily: 'inherit',
          }}>
            Retry
          </button>
          <button type="button" onClick={onReset} style={{
            padding: '4px 10px',
            background: 'transparent', color: 'var(--qqq-fg-3)',
            border: '1px solid var(--qqq-b2)', borderRadius: 4,
            fontSize: 10, cursor: 'pointer',
            fontFamily: 'inherit',
          }}>
            Clear
          </button>
        </div>
      </div>
    );
  }

  // Done
  const a = state.analysis || {};
  return (
    <div style={{
      padding: '12px 14px',
      border: `1px solid ${toneColor}`,
      borderRadius: 8,
      background: toneRgba,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 8,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: toneColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label} · ✓ parsed
        </div>
        <button type="button" onClick={onReset} style={{
          background: 'transparent', border: 0, color: 'var(--qqq-fg-3)',
          fontSize: 11, cursor: 'pointer', padding: 2,
        }}>
          replace
        </button>
      </div>
      {state.url && (
        <img
          src={state.url}
          alt=""
          style={{
            width: '100%', maxHeight: 120, objectFit: 'cover',
            borderRadius: 4, marginBottom: 8, border: '1px solid var(--qqq-b2)',
          }}
        />
      )}
      <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', fontWeight: 600, marginBottom: 4 }}>
        {a.pattern || a.option_summary || '(parsed)'}
      </div>
      <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', lineHeight: 1.6 }}>
        {tone === 'sand' ? (
          <>
            {a.call_dominance != null && <>call {a.call_dominance} · </>}
            {a.put_dominance != null && <>put {a.put_dominance} · </>}
            {a.asymmetry_ratio && <>asym {a.asymmetry_ratio}</>}
          </>
        ) : (
          <>
            {a.chop_score?.score && <>CHOP {a.chop_score.score} · </>}
            {a.break_candle?.index != null && <>T+{a.break_candle.index} · </>}
            {a.path_to_2_00?.hit === true && <span style={{ color: 'var(--qqq-sage)' }}>✓ $2.00 hit</span>}
            {a.path_to_2_00?.hit === false && <span style={{ color: 'var(--qqq-terra)' }}>✗ $2.00 miss</span>}
          </>
        )}
      </div>
      {state.filed && (
        <div style={{
          marginTop: 8, padding: '5px 8px',
          background: 'rgba(122,158,130,.1)',
          color: 'var(--qqq-sage)',
          fontSize: 11,
          borderRadius: 3,
        }}>
          ✓ Auto-filed to <strong>{state.filed.folder_name}</strong>
        </div>
      )}
    </div>
  );
}


function ChartAnalysis({ analysis }) {
  // No upload yet → don't render (avoids fake placeholder confusing the user)
  if (!analysis) return null;

  // Vision returned an "unreadable" error
  if (analysis.error === 'unreadable') {
    return (
      <div className="qqq-chart-analysis">
        <div className="qqq-ca-hdr">
          <div className="qqq-ca-title">Shift Chart Analysis</div>
          <div className="qqq-ca-badge">Claude Vision</div>
        </div>
        <div className="qqq-ca-body">
          <div className="qqq-ca-insight">
            <strong>Couldn&apos;t read this chart.</strong> Try a clearer image,
            higher resolution, or make sure your studies are visible.
          </div>
        </div>
      </div>
    );
  }

  const analyzedAt = analysis._meta?.analyzed_at;
  // Commit 62.1 — image thumbnail (when Storage bucket is wired)
  const imageUrl   = analysis._meta?.image_url || null;
  const studiesActive   = Array.isArray(analysis.studies_active)   ? analysis.studies_active   : [];
  const studiesInactive = Array.isArray(analysis.studies_inactive) ? analysis.studies_inactive : [];
  const keyLevels       = Array.isArray(analysis.key_levels)       ? analysis.key_levels       : [];

  // Commit 48 — granular structured features extracted by Vision v2
  const yc       = analysis.yellow_candle;
  const chop     = analysis.chop_score;
  const brk      = analysis.break_candle;
  const p150     = analysis.path_to_1_50;
  const p200     = analysis.path_to_2_00;
  const stopProx = analysis.stop_proximity;
  const sqz      = analysis.squeeze;
  const rsi      = analysis.rsi;
  const tf       = analysis.tf_alignment;
  // Commit 59.1 — intraday events (post-window shocks, news reactions, etc.)
  const intradayEvents = Array.isArray(analysis.intraday_events) ? analysis.intraday_events : [];
  const hasGranular = !!(yc || chop || brk || p150 || p200 || stopProx || sqz || rsi);

  return (
    <div className="qqq-chart-analysis">
      <div className="qqq-ca-hdr">
        <div className="qqq-ca-title">Shift Chart Analysis</div>
        <div className="qqq-ca-badge">
          Claude Vision{analyzedAt ? ` · ${fmtSaveTime(analyzedAt)}` : ''}
        </div>
      </div>
      <div className="qqq-ca-body">
        {/* Commit 62.1 — chart image thumbnail (when Storage persisted) */}
        {imageUrl && (
          <div style={{
            marginBottom: 14, padding: 6,
            background: 'var(--surface-3)',
            borderRadius: 6,
            border: '1px solid var(--qqq-b2)',
          }}>
            <a href={imageUrl} target="_blank" rel="noopener noreferrer"
               title="Click to open full-size">
              <img
                src={imageUrl}
                alt="Uploaded chart"
                style={{
                  width: '100%', maxWidth: '100%',
                  display: 'block', borderRadius: 4,
                }}
              />
            </a>
          </div>
        )}
        {analysis._meta?.storage_error && !imageUrl && (
          <div style={{
            padding: '8px 12px', marginBottom: 10,
            background: 'rgba(200,169,110,.06)',
            borderLeft: '2px solid var(--qqq-gold)',
            borderRadius: 3, fontSize: 11, color: 'var(--qqq-fg-2)',
            fontFamily: 'JetBrains Mono, monospace',
          }}>
            image not stored: {analysis._meta.storage_error} — see
            docs/sql/qqq-storage-bucket.md to set up the bucket
          </div>
        )}

        {analysis.pattern && (
          <div>
            <div className="qqq-ca-mini-label">Pattern Detected</div>
            <div className="qqq-ca-pattern">{analysis.pattern}</div>
            {analysis.pattern_description && (
              <div className="qqq-ca-desc">{analysis.pattern_description}</div>
            )}
          </div>
        )}

        {/* Commit 48 — granular micro-features extracted by Vision v2.
            Tim's compounding-intelligence pivot: every chart contributes
            ~50 structured features the pattern engine can cross-correlate
            on. Each row below = one feature cluster from the extraction. */}
        {hasGranular && (
          <div style={{
            marginTop: 12,
            padding: '12px 14px',
            background: 'rgba(77,184,184,.05)',
            border: '1px solid var(--qqq-b1, #1e1e2a)',
            borderLeft: '3px solid var(--qqq-teal, #4db8b8)',
            borderRadius: 6,
          }}>
            <div style={{
              fontSize: 10, color: 'var(--qqq-teal, #4db8b8)',
              textTransform: 'uppercase', letterSpacing: '0.05em',
              fontWeight: 700, marginBottom: 10,
            }}>
              Structured Features · feeds pattern engine
            </div>

            {/* Yellow Candle */}
            {yc && (yc.captured || yc.shape) && (
              <GranularRow label="Yellow Candle" items={[
                yc.direction && { k: 'dir',   v: yc.direction.toUpperCase() },
                yc.shape     && { k: 'shape', v: yc.shape.replace(/_/g, ' ') },
                yc.body_pct  != null && { k: 'body', v: `${yc.body_pct}%` },
                yc.range_dollars != null && { k: 'range', v: `$${yc.range_dollars}` },
              ]} />
            )}

            {/* CHOP Score */}
            {chop && chop.score && (
              <GranularRow label={`CHOP Score ${chop.score}`} items={[
                chop.body_fired       && { k: 'body',       v: '✓', tone: 'sage' },
                chop.atr_fired        && { k: 'atr',        v: '✓', tone: 'sage' },
                chop.vol_fired        && { k: 'vol',        v: '✓', tone: 'sage' },
                chop.prev_close_fired && { k: 'prevClose',  v: '✓', tone: 'sage' },
                chop.yc_range_dollars != null && { k: 'YCrange', v: `$${chop.yc_range_dollars}` },
              ]} />
            )}

            {/* Break Candle */}
            {brk && brk.occurred && (
              <GranularRow label="Break Candle" items={[
                brk.index        != null && { k: 'T+', v: brk.index },
                brk.broke        && { k: 'broke', v: brk.broke.toUpperCase(), tone: brk.broke === 'high' ? 'sage' : 'terra' },
                brk.body_pct     != null && { k: 'body', v: `${brk.body_pct}%` },
                brk.volume_class && { k: 'vol',  v: brk.volume_class.replace(/_/g, ' ') },
              ]} />
            )}

            {/* Path to $1.50 */}
            {p150 && p150.hit != null && (
              <GranularRow label="Path to $1.50" items={[
                { k: 'hit', v: p150.hit ? '✓' : '✗', tone: p150.hit ? 'sage' : 'terra' },
                p150.candles_from_break != null && { k: 'candles', v: p150.candles_from_break },
                p150.shape && { k: 'shape', v: p150.shape.replace(/_/g, ' ') },
              ]} />
            )}

            {/* Path to $2.00 */}
            {p200 && (p200.hit != null || p200.trade_status) && (
              <GranularRow label="Path to $2.00" items={[
                p200.hit != null && { k: 'hit', v: p200.hit ? '✓' : '✗', tone: p200.hit ? 'sage' : 'terra' },
                p200.candles_from_break != null && { k: 'candles', v: p200.candles_from_break },
                p200.shape && { k: 'shape', v: p200.shape.replace(/_/g, ' ') },
                p200.trade_status && { k: 'status', v: p200.trade_status, tone: p200.trade_status === 'TRADE COMPLETE' ? 'sage' : p200.trade_status === 'STOPPED' ? 'terra' : 'gold' },
              ]} />
            )}

            {/* Stop proximity */}
            {stopProx && (stopProx.near_miss != null || stopProx.min_distance_pct != null) && (
              <GranularRow label="Stop Proximity" items={[
                stopProx.stop_price != null && { k: 'stop', v: `$${stopProx.stop_price}` },
                stopProx.min_distance_pct != null && { k: 'minDist', v: `${stopProx.min_distance_pct}%` },
                stopProx.near_miss === true && { k: 'NEAR MISS', v: '!', tone: 'terra' },
              ]} />
            )}

            {/* Squeeze transitions */}
            {sqz && (sqz.state_at_entry || sqz.fired_during != null) && (
              <GranularRow label="Squeeze" items={[
                sqz.state_at_entry && { k: '@entry', v: sqz.state_at_entry },
                sqz.state_at_2_00  && { k: '@T2',    v: sqz.state_at_2_00 },
                sqz.fired_during === true && { k: 'fired', v: '✓', tone: 'sage' },
              ]} />
            )}

            {/* RSI */}
            {rsi && (rsi.at_entry != null || rsi.peak != null) && (
              <GranularRow label="RSI" items={[
                rsi.at_entry != null && { k: '@entry', v: rsi.at_entry },
                rsi.at_2_00  != null && { k: '@T2',    v: rsi.at_2_00 },
                rsi.peak     != null && { k: 'peak',   v: rsi.peak, tone: rsi.peak > 70 ? 'terra' : rsi.peak < 30 ? 'terra' : 'sage' },
                rsi.divergence === true && { k: 'divergence', v: '⚠', tone: 'terra' },
              ]} />
            )}

            {/* Multi-timeframe alignment */}
            {tf && (tf.tf_5m_chop || tf.tf_15m_chop) && (
              <GranularRow label="MTF Alignment" items={[
                tf.tf_5m_chop  && { k: '5m CHOP',  v: tf.tf_5m_chop },
                tf.tf_15m_chop && { k: '15m CHOP', v: tf.tf_15m_chop },
                tf.agree != null && { k: 'agree', v: tf.agree ? '✓' : '✗', tone: tf.agree ? 'sage' : 'terra' },
              ]} />
            )}
          </div>
        )}

        {(studiesActive.length > 0 || studiesInactive.length > 0) && (
          <div style={{ marginTop: 12 }}>
            <div className="qqq-ca-mini-label">Studies</div>
            <div className="qqq-studies-row">
              {studiesActive.map((s, i) => (
                <div key={`a-${i}`} className="qqq-sc">{s} ✓</div>
              ))}
              {studiesInactive.map((s, i) => (
                <div key={`i-${i}`} className="qqq-sc off">{s}</div>
              ))}
            </div>
          </div>
        )}

        {keyLevels.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div className="qqq-ca-mini-label">Key Levels</div>
            <div className="qqq-studies-row">
              {keyLevels.map((lvl, i) => (
                <div key={i} className="qqq-sc">{lvl}</div>
              ))}
            </div>
          </div>
        )}

        {analysis.insight && (
          <div className="qqq-ca-insight">
            <strong>Shift&apos;s read:</strong> {analysis.insight}
          </div>
        )}

        {analysis.trade_implication && (
          <div className="qqq-ca-insight">
            <strong>Next time:</strong> {analysis.trade_implication}
          </div>
        )}

        {/* Commit 59.1 — intraday events (news shocks, afternoon
            reversals, EOD spikes). Vision flags them from the chart
            visual. Future: auto-correlate with scheduled events +
            news feed. */}
        {intradayEvents.length > 0 && (
          <div style={{
            marginTop: 14,
            padding: '12px 14px',
            background: 'rgba(196,96,80,.04)',
            border: '1px solid var(--qqq-b1, #1e1e2a)',
            borderLeft: '3px solid var(--qqq-terra, #c46050)',
            borderRadius: 6,
          }}>
            <div style={{
              fontSize: 10, color: 'var(--qqq-terra, #c46050)',
              textTransform: 'uppercase', letterSpacing: '0.05em',
              fontWeight: 700, marginBottom: 10,
            }}>
              ⚡ Intraday Events · outside the trading window
            </div>
            {intradayEvents.map((e, i) => (
              <div key={i} style={{
                padding: '8px 0',
                borderTop: i > 0 ? '1px solid var(--qqq-b1)' : 'none',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: 10,
                  fontSize: 13, fontWeight: 600, color: 'var(--qqq-fg-1)',
                }}>
                  <span style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    color: 'var(--qqq-gold)', fontSize: 12,
                  }}>
                    {e.time_approx || '—'} ET
                  </span>
                  {e.direction && (
                    <span style={{
                      color: e.direction === 'down' ? 'var(--qqq-terra)' : 'var(--qqq-sage)',
                      fontSize: 14,
                    }}>
                      {e.direction === 'down' ? '↓' : '↑'}
                    </span>
                  )}
                  {e.magnitude_pct != null && (
                    <span style={{
                      fontFamily: 'JetBrains Mono, monospace',
                      color: e.magnitude_pct < 0 ? 'var(--qqq-terra)' : 'var(--qqq-sage)',
                      fontWeight: 700,
                    }}>
                      {e.magnitude_pct >= 0 ? '+' : ''}{e.magnitude_pct}%
                    </span>
                  )}
                  {e.recovery && (
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 3,
                      background: 'var(--surface-3)',
                      color: e.recovery === 'full' ? 'var(--qqq-sage)'
                           : e.recovery === 'none' ? 'var(--qqq-terra)'
                           : 'var(--qqq-gold)',
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                      fontWeight: 700,
                    }}>
                      {e.recovery} recovery
                    </span>
                  )}
                </div>
                {e.candle_pattern && (
                  <div style={{
                    fontSize: 12, color: 'var(--qqq-fg-2)', marginTop: 4,
                    lineHeight: 1.55,
                  }}>
                    {e.candle_pattern}
                  </div>
                )}
                {e.context_hint && (
                  <div style={{
                    fontSize: 11, color: 'var(--qqq-purple)', marginTop: 4,
                    fontStyle: 'italic',
                  }}>
                    possible cause: {e.context_hint}
                  </div>
                )}
                {e.notes && (
                  <div style={{
                    fontSize: 11, color: 'var(--qqq-fg-3)', marginTop: 4,
                    lineHeight: 1.5,
                  }}>
                    {e.notes}
                  </div>
                )}
              </div>
            ))}
            <div style={{
              fontSize: 10, color: 'var(--qqq-fg-3)',
              marginTop: 8, fontStyle: 'italic', lineHeight: 1.5,
            }}>
              Flagged by Vision from the chart visual. Real-time
              detection + news correlation coming in Tier 2.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Granular feature row — compact pill strip (Commit 48) ──
// Renders a label + inline key:value pills. Used by ChartAnalysis
// to show each feature cluster extracted by Vision v2.
function GranularRow({ label, items }) {
  const real = (items || []).filter(Boolean);
  if (real.length === 0) return null;
  const toneColor = (tone) => ({
    sage:   'var(--qqq-sage, #7a9e82)',
    gold:   'var(--qqq-gold, #c8a96e)',
    terra:  'var(--qqq-terra, #c46050)',
    purple: 'var(--qqq-purple, #9b8fc4)',
    teal:   'var(--qqq-teal, #4db8b8)',
  })[tone] || 'var(--qqq-fg-1, #eee)';
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '140px 1fr',
      gap: 10, alignItems: 'center',
      padding: '5px 0', borderTop: '1px solid var(--qqq-b1, #1e1e2a)',
      fontSize: 11,
    }}>
      <div style={{
        color: 'var(--qqq-fg-2, #aaa)', fontWeight: 600,
        letterSpacing: '0.02em',
      }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {real.map((it, i) => (
          <span key={i} style={{
            display: 'inline-flex', gap: 4,
            padding: '2px 7px', borderRadius: 8,
            background: 'var(--surface-3, #171723)',
            border: `1px solid ${it.tone ? toneColor(it.tone) : 'var(--qqq-b2, #272736)'}`,
            fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
          }}>
            <span style={{ color: 'var(--qqq-fg-3, #888)' }}>{it.k}</span>
            <span style={{ color: it.tone ? toneColor(it.tone) : 'var(--qqq-fg-1, #eee)', fontWeight: 600 }}>
              {it.v}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SECTION 7 — PATTERNS
// ═══════════════════════════════════════════════════════════
// Reads from data._patternsLive (server-computed from
// qqq_session_briefs). Renders four sub-blocks:
//   1. Stats grid (4 tiles)         — always shown
//   2. Chart Pattern Win Rates card — empty state if no resolved
//                                     sessions have chart_analysis
//   3. Indicator Accuracy card      — empty state if no force has
//                                     ≥3 resolved samples
//   4. Insight block                — synthesized leader + detractor
// When _patternsLive is null (< 3 resolved sessions), the whole
// section renders a single "data emerging" empty state.
function Section7Patterns() {
  const data = useQqqData();
  const live = data._patternsLive;
  const sessionCount = Array.isArray(data.sidebarHistory) ? data.sidebarHistory.length : 0;
  const resolvedCount = Array.isArray(data.sidebarHistory)
    ? data.sidebarHistory.filter((r) => r.status === 'resolved').length
    : 0;

  // ─── Empty state — first 3 days of use ────────────────────
  if (!live) {
    const remaining = Math.max(0, 3 - resolvedCount);
    return (
      <SeqSection step="7" stepTone="patterns" eyebrowLabel="What History Shows" eyebrowTone="patterns" lastSection>
        <div className="qqq-conclusion env">
          <div className="qqq-conc-dot purple" />
          <div className="qqq-conc-text">
            <strong>Patterns emerge after 3+ resolved sessions.</strong>{' '}
            {sessionCount === 0 ? (
              <>Today is your first brief. Save the Session Log this afternoon to start the loop.</>
            ) : (
              <>{resolvedCount} of {sessionCount} sessions resolved.{' '}
              {remaining > 0 ? `${remaining} more before stats unlock.` : ''}</>
            )}
          </div>
          <div className="qqq-conc-badge neutral">DATA EMERGING</div>
        </div>
        <DataCard label="Why this is empty" source="compound-learning loop" tone="purple">
          <div style={{ padding: '12px 16px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
            Section 7 is the payoff for keeping a Session Log every day.
            Each resolved session adds a row to <code>qqq_session_briefs</code>.
            After ~30 sessions, the win-rate / bias-accuracy / chart-pattern
            stats become directionally honest. After ~50, they beat what most
            traders ever measure about themselves.
          </div>
        </DataCard>
      </SeqSection>
    );
  }

  return (
    <SeqSection step="7" stepTone="patterns" eyebrowLabel="What History Shows" eyebrowTone="patterns" lastSection>
      {/* Stats grid — 4 headline tiles */}
      <div className="qqq-patterns-grid">
        {live.stats.map((s, i) => (
          <div key={i} className="qqq-pg-card">
            <div className={`qqq-pgc-n ${s.tone}`}>{s.n}</div>
            <div className="qqq-pgc-l">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Chart pattern win rates */}
      <DataCard
        label="Chart Pattern Win Rates"
        source={`Claude Vision · ${resolvedCount} resolved session${resolvedCount === 1 ? '' : 's'}`}
        tone="sage"
      >
        {live.chartPatterns.length > 0 ? (
          live.chartPatterns.map((c, i) => (
            <div key={i} className="qqq-pattern-row">
              <div className="qqq-pr-name">
                <div className="qqq-pr-n">{c.name}</div>
                <div className="qqq-pr-m">{c.note}</div>
              </div>
              <div className="qqq-pr-sess">{c.sessions}</div>
              <div className={`qqq-pr-rate ${c.tone}`}>{c.rate}</div>
            </div>
          ))
        ) : (
          <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13 }}>
            No chart patterns repeat ≥2 times yet. Upload a chart from the
            Session Log to build this card.
          </div>
        )}
      </DataCard>

      {/* Indicator accuracy */}
      <DataCard
        label="Indicator Accuracy When True"
        source={`${resolvedCount} resolved session${resolvedCount === 1 ? '' : 's'} · signal correlation`}
        tone="sage"
      >
        {live.indicators.length > 0 ? (
          <div className="qqq-acc-body">
            {live.indicators.map((a, i) => (
              <div key={i} className="qqq-acc-row">
                <div className="qqq-ar-lbl">{a.label}</div>
                <div className="qqq-ar-bw">
                  <div className={`qqq-ar-bf ${a.tone}`} style={{ width: `${a.pct}%` }} />
                </div>
                <div className={`qqq-ar-val ${a.tone}`}>{a.pct}%</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13 }}>
            No indicator has ≥3 samples yet. Resolve a few more sessions
            to see which morning forces actually predict outcomes.
          </div>
        )}
      </DataCard>

      {/* Insight synthesis */}
      <div className="qqq-insight-block">
        <div className="qqq-ib-eyebrow">{live.insight.eyebrow}</div>
        <div className="qqq-ib-text">
          {renderMarkers(live.insight.text)}
        </div>
      </div>

      {/* Commit 7 — Cross-correlation Pattern Engine.
          Renders below the existing Section 7 cards. Surfaces
          combinations of (morning forces) × (opening candle) ×
          (gamma regime) that predict (window outcome shape)
          above chance, with sample size + lift over base rate. */}
      <PatternEdgesCard />
      {/* L3/L4/L6 cards moved OUT of Section7Patterns — they have
          their own empty states and should always render, not be
          gated by Section7's "data emerging" early return. See
          QqqTab JSX for actual placement. */}
    </SeqSection>
  );
}

// ─── Pattern Edges Card (Commit 7) ───────────────────────────
// Reads from data._patternEdges (server-computed by lib/qqq/
// pattern-engine.js). Renders 3 states:
//   1. Pending — < 5 fully-labeled sessions, can't mine yet
//   2. Empty   — ≥5 sessions but no pattern crosses the lift gate
//   3. Live    — top patterns ranked by score
// ─── Method Sheet Card (Commit 33 — Layer 3, Bruce Lee design) ─
// THE evolving playbook. Generated weekly by qqq-method-refine.
// Read-only — no triage. Just the singular method, sharpened.
//
// 8 sections (mirrors the Claude prompt schema):
//   ① Method Statement       — the core thesis (mostly stable)
//   ② Stage 1: Direction Read — what predicts CALL vs PUT today
//   ③ Stage 2: Timing         — what we've seen about entry/exit
//   ④ Signal Hierarchy        — ranked predictive power
//   ⑤ Regime Modifiers        — what changes per market regime
//   ⑥ Recent Refinements      — what changed this week
//   ⑦ Decay Watch             — signals losing edge
//   ⑧ Sharpening Focus        — ONE thing this week (Bruce Lee)

// ═══════════════════════════════════════════════════════════
// EDGE STATE CARD — Commit 47 (compounding intelligence)
// ═══════════════════════════════════════════════════════════
// Frames the 30/60/90 rolling stats + regime slices as a COMPOUNDING
// INTELLIGENCE story — "where am I on the maturity curve, and what
// unlocks next". Tim doesn't care about rolling windows; he cares
// about "is Shift getting smarter about helping me, and am I getting
// sharper?" This card answers both.
//
// Four visual zones:
//   ① Maturity bar         — current stage + progress to next unlock
//   ② Trend metrics        — 3-column 30/60/90 comparison with arrows
//   ③ Regime edge map      — where your edge is concentrated / absent
//   ④ Insights             — 3-5 plain-English observations
function EdgeStateCard() {
  const data = useQqqData();
  const es = data._edgeState;

  if (!es) return null;
  const m = es.maturity;

  return (
    <DataCard
      label="Your Compounding Intelligence"
      source={`${es.sample_size} resolved · stage ${m?.current_stage ?? 0}: ${m?.current_name ?? 'logging'} · refreshes every session`}
      tone="purple"
    >
      {/* ① Maturity bar */}
      {m && (
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--qqq-b1)' }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            marginBottom: 10,
          }}>
            <div>
              <div style={{
                fontSize: 11, color: 'var(--qqq-purple)',
                textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
              }}>
                Stage {m.current_stage} · {m.current_name}
              </div>
              <div style={{
                fontSize: 13, color: 'var(--qqq-fg-1)', marginTop: 4, lineHeight: 1.5,
              }}>
                {m.current_unlocks}
              </div>
            </div>
            {m.next_name && (
              <div style={{ textAlign: 'right', minWidth: 120 }}>
                <div style={{
                  fontSize: 10, color: 'var(--qqq-fg-3)',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  Next unlock
                </div>
                <div style={{ fontSize: 13, color: 'var(--qqq-gold)', fontWeight: 600 }}>
                  {m.next_name}
                </div>
                <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)', marginTop: 2 }}>
                  {m.sessions_to_next} session{m.sessions_to_next === 1 ? '' : 's'} away
                </div>
              </div>
            )}
          </div>

          {/* Progress bar to next stage */}
          {m.next_name && (
            <div style={{
              height: 6, background: 'var(--surface-3, #171723)',
              borderRadius: 3, overflow: 'hidden', marginBottom: 8,
            }}>
              <div style={{
                height: '100%',
                width: `${m.progress_to_next_pct}%`,
                background: 'linear-gradient(90deg, var(--qqq-purple, #9b8fc4), var(--qqq-gold, #c8a96e))',
                transition: 'width 0.5s',
              }} />
            </div>
          )}

          {/* Stage ladder — compact horizontal timeline */}
          <div style={{
            display: 'flex', gap: 4, marginTop: 10,
          }}>
            {m.all_stages.map((s) => {
              const state = s.stage < m.current_stage ? 'done'
                : s.stage === m.current_stage ? 'current'
                : 'future';
              return (
                <div key={s.stage}
                  title={`${s.name} (${s.threshold}+ sessions): ${s.unlocks}`}
                  style={{
                    flex: 1, textAlign: 'center',
                    padding: '4px 2px',
                    fontSize: 9,
                    fontWeight: state === 'current' ? 700 : 500,
                    letterSpacing: '0.03em', textTransform: 'uppercase',
                    color: state === 'done'    ? 'var(--qqq-sage)'
                         : state === 'current' ? 'var(--qqq-gold)'
                         : 'var(--qqq-fg-3)',
                    background: state === 'current' ? 'rgba(200,169,110,.08)' : 'transparent',
                    borderRadius: 3,
                    borderTop: state === 'current' ? '2px solid var(--qqq-gold)' : '2px solid transparent',
                  }}
                >
                  {s.short}
                </div>
              );
            })}
          </div>
          <div style={{
            fontSize: 10, color: 'var(--qqq-fg-3)', marginTop: 6,
            display: 'flex', justifyContent: 'space-between',
          }}>
            <span>{m.all_stages[0].threshold}</span>
            <span>regime coverage: {m.regime_coverage_pct}% · {m.regime_cells_filled}/{m.regime_cells_total} cells filled</span>
            <span>{m.all_stages[m.all_stages.length - 1].threshold}+</span>
          </div>
        </div>
      )}

      {/* ② Trend metrics — 3-column comparison */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--qqq-b1)' }}>
        <div style={{
          fontSize: 11, color: 'var(--qqq-fg-3)',
          textTransform: 'uppercase', letterSpacing: '0.05em',
          marginBottom: 10, fontWeight: 600,
        }}>
          Rolling Edge · 30d / 60d / 90d
        </div>
        <TrendRow
          label="Direction accuracy"
          w30={es.windows.d30.direction_accuracy_pct}
          w60={es.windows.d60.direction_accuracy_pct}
          w90={es.windows.d90.direction_accuracy_pct}
          trend={es.trend.direction_accuracy}
          suffix="%"
        />
        <TrendRow
          label="Your gut accuracy"
          w30={es.windows.d30.bias_accuracy_pct}
          w60={es.windows.d60.bias_accuracy_pct}
          w90={es.windows.d90.bias_accuracy_pct}
          trend={es.trend.bias_accuracy}
          suffix="%"
        />
        <TrendRow
          label="Hit rate (log-tagged)"
          w30={es.windows.d30.hit_rate_pct}
          w60={es.windows.d60.hit_rate_pct}
          w90={es.windows.d90.hit_rate_pct}
          trend={es.trend.hit_rate}
          suffix="%"
        />
        <TrendRow
          label="MFE / MAE ratio"
          w30={es.windows.d30.mfe_mae_ratio}
          w60={es.windows.d60.mfe_mae_ratio}
          w90={es.windows.d90.mfe_mae_ratio}
          trend={es.trend.mfe_mae_ratio}
          suffix="×"
        />
      </div>

      {/* ③ Regime edge map — best + worst */}
      {(es.best_regime || es.worst_regime) && (
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--qqq-b1)' }}>
          <div style={{
            fontSize: 11, color: 'var(--qqq-fg-3)',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            marginBottom: 10, fontWeight: 600,
          }}>
            Where Your Edge Lives · 90d slice
          </div>
          {es.best_regime && (
            <div style={{
              padding: '10px 14px', marginBottom: 8,
              background: 'rgba(122,158,130,.06)',
              borderLeft: '3px solid var(--qqq-sage)', borderRadius: 4,
            }}>
              <div style={{ fontSize: 10, color: 'var(--qqq-sage)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                ✓ Best regime
              </div>
              <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', marginTop: 3, fontWeight: 500 }}>
                {dimPretty(es.best_regime.dim)} = <strong>{es.best_regime.key}</strong>
              </div>
              <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', marginTop: 2 }}>
                {es.best_regime.hit_rate_pct}% hit · n={es.best_regime.n} · Wilson ≥{es.best_regime.wilson_low}%
              </div>
            </div>
          )}
          {es.worst_regime && es.worst_regime.key !== es.best_regime?.key && (
            <div style={{
              padding: '10px 14px',
              background: 'rgba(196,96,80,.06)',
              borderLeft: '3px solid var(--qqq-terra)', borderRadius: 4,
            }}>
              <div style={{ fontSize: 10, color: 'var(--qqq-terra)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                ✗ Worst regime
              </div>
              <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', marginTop: 3, fontWeight: 500 }}>
                {dimPretty(es.worst_regime.dim)} = <strong>{es.worst_regime.key}</strong>
              </div>
              <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', marginTop: 2 }}>
                {es.worst_regime.hit_rate_pct}% hit · n={es.worst_regime.n} · Wilson ≥{es.worst_regime.wilson_low}%
              </div>
            </div>
          )}
        </div>
      )}

      {/* ④ Insights */}
      {Array.isArray(es.insights) && es.insights.length > 0 && (
        <div style={{ padding: '14px 18px' }}>
          <div style={{
            fontSize: 11, color: 'var(--qqq-fg-3)',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            marginBottom: 8, fontWeight: 600,
          }}>
            What your data says
          </div>
          <ul style={{
            margin: 0, paddingLeft: 18,
            fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.65,
          }}>
            {es.insights.map((ins, i) => (
              <li key={i} style={{ marginBottom: 6 }}>{ins}</li>
            ))}
          </ul>
        </div>
      )}
    </DataCard>
  );
}

function TrendRow({ label, w30, w60, w90, trend, suffix }) {
  const arrow = trend === 'improving' ? '↑'
    : trend === 'decaying' ? '↓'
    : trend === 'stable' ? '→'
    : trend === 'volatile' ? '≈'
    : '·';
  const arrowColor = trend === 'improving' ? 'var(--qqq-sage)'
    : trend === 'decaying' ? 'var(--qqq-terra)'
    : trend === 'stable' ? 'var(--qqq-gold)'
    : trend === 'volatile' ? 'var(--qqq-purple)'
    : 'var(--qqq-fg-3)';
  const fmt = (v) => v == null ? '—' : `${v}${suffix || ''}`;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1.6fr 0.8fr 0.8fr 0.8fr 0.4fr',
      gap: 10, alignItems: 'center',
      padding: '6px 0', borderTop: '1px solid var(--qqq-b1)',
    }}>
      <div style={{ fontSize: 12, color: 'var(--qqq-fg-1)', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', fontWeight: 600, textAlign: 'right' }}>{fmt(w30)}</div>
      <div style={{ fontSize: 13, color: 'var(--qqq-fg-2)',  textAlign: 'right' }}>{fmt(w60)}</div>
      <div style={{ fontSize: 13, color: 'var(--qqq-fg-2)',  textAlign: 'right' }}>{fmt(w90)}</div>
      <div style={{
        fontSize: 16, fontWeight: 700, color: arrowColor, textAlign: 'center',
      }} title={trend}>
        {arrow}
      </div>
    </div>
  );
}

function dimPretty(dim) {
  return ({
    by_vix:    'VIX regime',
    by_gamma:  'Gamma sign',
    by_sector: 'Sector lead',
    by_event:  'Event proximity',
    by_day:    'Day of week',
  })[dim] || dim;
}

// ═══════════════════════════════════════════════════════════
// CHART PATTERNS CARD — Commit 49 (chart-feature edges)
// ═══════════════════════════════════════════════════════════
// Renders the output of lib/qqq/chart-pattern-mining.js:
// cross-correlated rules from granular chart features × trade
// outcomes. The payoff for uploading charts in the Log tab —
// every chart adds ~50 features, each one the pattern engine
// can slice on to surface rules like:
//   "CHOP=4/4 + Break T+1 + vol above_avg → $2.00 hit 87% (n=14)"
//
// Two sections:
//   ① HIGH-PROBABILITY RULES — features predicting TARGET HIT
//   ② SKIP SIGNALS           — features predicting TARGET MISS
function ChartPatternsCard() {
  const data = useQqqData();
  const cp = data._chartPatterns;

  // Empty state — no charts parsed yet
  if (!cp) {
    return (
      <div className="qqq-data-card" style={{
        borderStyle: 'dashed', opacity: 0.85, marginTop: 16,
        borderLeft: '3px dashed var(--qqq-purple, #9b8fc4)',
      }}>
        <div className="qqq-dc-hdr">
          <div className="qqq-dc-lbl" style={{ color: 'var(--qqq-purple)' }}>
            Chart-Feature Rules · Layer 5.5
          </div>
          <div className="qqq-dc-src" style={{ color: 'var(--qqq-fg-3)' }}>
            unlocks at 5+ parsed charts · upload in Log tab
          </div>
        </div>
        <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          Every chart you upload in the Log tab extracts ~50 structured
          features (CHOP components, break candle, squeeze state, RSI, path
          shape, etc.). Once 5+ charts are parsed, this engine mines those
          features against your actual trade outcomes and surfaces rules
          like &ldquo;CHOP=4/4 + Break T+1 + vol above_avg → $2.00 hit 87%&rdquo;.
          <br /><br />
          <strong>Upload your first chart today.</strong> Each one sharpens every future rule.
        </div>
      </div>
    );
  }

  return (
    <DataCard
      label="Chart-Feature Rules · Layer 5.5"
      source={`${cp.sample_size} parsed chart${cp.sample_size === 1 ? '' : 's'} · base rates: $1.50 ${cp.base_rate_1_50}% · $2.00 ${cp.base_rate_2_00}%`}
      tone="purple"
    >
      {/* ① HIGH-PROBABILITY RULES */}
      {cp.rules.length > 0 && (
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--qqq-b1)' }}>
          <div style={{
            fontSize: 11, color: 'var(--qqq-sage)',
            textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
            marginBottom: 10,
          }}>
            ✓ High-Probability Setups ({cp.rules.length})
          </div>
          {cp.rules.map((r, i) => (
            <div key={i} style={{
              padding: '10px 14px', marginBottom: 8,
              background: 'rgba(122,158,130,.06)',
              borderLeft: '3px solid var(--qqq-sage)', borderRadius: 4,
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                gap: 10, marginBottom: 4,
              }}>
                <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', fontWeight: 600 }}>
                  {r.headline}
                </div>
                <div style={{
                  fontSize: 10, color: 'var(--qqq-sage)',
                  textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
                  flexShrink: 0,
                }}>
                  {r.direction.toUpperCase()} · {r.confidence}
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--qqq-fg-2)', lineHeight: 1.55 }}>
                <strong style={{ color: 'var(--qqq-fg-1)' }}>
                  ${r.target === '1_50' ? '1.50' : '2.00'} hit {r.hit_rate_pct}%
                </strong>
                {' · '}n={r.n}
                {' · '}{r.lift}× base rate
                {' · '}Wilson ≥{r.wilson_low}%
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ② SKIP SIGNALS */}
      {cp.pass_rules.length > 0 && (
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--qqq-b1)' }}>
          <div style={{
            fontSize: 11, color: 'var(--qqq-terra)',
            textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
            marginBottom: 10,
          }}>
            ✗ Skip Signals ({cp.pass_rules.length})
          </div>
          {cp.pass_rules.map((r, i) => (
            <div key={i} style={{
              padding: '10px 14px', marginBottom: 8,
              background: 'rgba(196,96,80,.06)',
              borderLeft: '3px solid var(--qqq-terra)', borderRadius: 4,
            }}>
              <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', fontWeight: 600, marginBottom: 4 }}>
                {r.headline}
              </div>
              <div style={{ fontSize: 12, color: 'var(--qqq-fg-2)' }}>
                <strong style={{ color: 'var(--qqq-fg-1)' }}>
                  $2.00 miss {r.miss_rate_pct}%
                </strong>
                {' · '}n={r.n} · {r.confidence}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Insight footer */}
      {cp.insight && (
        <div style={{
          padding: '12px 18px',
          background: 'rgba(155,143,196,.04)',
          fontSize: 12, color: 'var(--qqq-fg-2)',
          fontStyle: 'italic', lineHeight: 1.55,
        }}>
          {cp.insight}
        </div>
      )}
    </DataCard>
  );
}

// ═══════════════════════════════════════════════════════════
// CALENDAR TAB — Commit 58 (month view with P&L / trades / per-day)
// ═══════════════════════════════════════════════════════════
// Calendar-of-sessions view. Each day cell shows:
//   - Date number
//   - Shift Score (if available)
//   - Outcome badge ($2 hit ✓ / $1.50 / miss / skip / no-session)
//   - Underlying move % (proxy for P&L direction)
//   - Bias correct indicator
// Click a day → panel below the grid expands to show full day:
//   chart analysis + context brief + journal notes + Shift Score
//   breakdown.
//
// Month stats strip across top: session count, $2 hit rate, best/
// worst day, current streak.
//
// Data sources (all already in payload):
//   - data._shiftScoreHistory — per-session scores + outcomes
//   - data._chartArchive      — full chart_analysis payloads
// Future (Commit 59): real $ P&L from qqq_trade_log table.
// ═══════════════════════════════════════════════════════════
// JOURNAL TAB — Commit 86 (calendar + 0DTE trade log + Ask Emily)
// ═══════════════════════════════════════════════════════════
// 70/30 split: calendar left (green/red days, Saturday weekly
// rollups, monthly totals strip, click-day drill-in) + trade
// log right (+ Add trade form, today's trades, daily rollup,
// day thesis, Ask Emily coaching button).
//
// Scoped tight to Tim's workflow: single-leg 0DTE QQQ calls
// and puts. Every trade links to brief_date so Ask Emily can
// correlate trades with chart readings, canonical records, and
// hypothesis samples.
// ═══════════════════════════════════════════════════════════

const SETUP_OPTIONS = [
  { key: 'yc_bear_breakout',    label: 'YC Bear Breakout' },
  { key: 'yc_bull_breakout',    label: 'YC Bull Breakout' },
  { key: 'shooting_star_retest', label: 'Shooting Star Retest' },
  { key: 'gap_fill',             label: 'Gap Fill' },
  { key: 'false_breakout',       label: 'False Breakout' },
  { key: 'trend_continuation',   label: 'Trend Continuation' },
  { key: 'chop_skipped',         label: 'Chop (skipped)' },
  { key: 'other',                label: 'Other' },
];

const RULE_BREAK_OPTIONS = [
  { key: 'late_entry',       label: 'Late entry' },
  { key: 'oversized',        label: 'Oversized' },
  { key: 'chased',           label: 'Chased' },
  { key: 'moved_stop',       label: 'Moved stop' },
  { key: 'held_past_plan',   label: 'Held past plan' },
  { key: 'revenge_trade',    label: 'Revenge trade' },
  { key: 'no_plan_yolo',     label: 'No plan / YOLO' },
  { key: 'added_to_loser',   label: 'Added to loser' },
  { key: 'early_exit_fear',  label: 'Early exit (fear)' },
  { key: 'ignored_news',     label: 'Ignored news event' },
];

function JournalTab() {
  const data = useQqqData();
  const aiName = useAiName();
  const workstationId = data._workstationId;
  const userId = data._userId;

  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(todayDateStringETLocal());
  const today = new Date();
  const [viewMonth, setViewMonth] = useState({ y: today.getFullYear(), m: today.getMonth() });

  const loadTrades = useCallback(async () => {
    if (!workstationId || !userId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ workstation_id: workstationId, user_id: userId });
      const res = await fetch(`/api/workstations/qqq-trades?${qs}`);
      const payload = await res.json();
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      else { setTrades(payload.trades || []); setError(null); }
    } catch (e) { setError(e?.message || 'load failed'); }
    finally { setLoading(false); }
  }, [workstationId, userId]);

  useEffect(() => { loadTrades(); }, [loadTrades]);

  // Index trades by day for fast lookup
  const tradesByDate = useMemo(() => {
    const m = new Map();
    for (const t of trades) {
      const arr = m.get(t.brief_date) || [];
      arr.push(t);
      m.set(t.brief_date, arr);
    }
    return m;
  }, [trades]);

  // Compute daily rollups
  const dayRollup = useCallback((iso) => {
    const ts = tradesByDate.get(iso) || [];
    if (ts.length === 0) return null;
    let net = 0;
    let wins = 0;
    let losses = 0;
    for (const t of ts) {
      const p = Number(t.pnl_dollars) || 0;
      net += p;
      if (p > 0) wins++;
      else if (p < 0) losses++;
    }
    return { count: ts.length, net, wins, losses };
  }, [tradesByDate]);

  // Weekly rollup for a Saturday cell (ISO week of that date)
  const weekRollup = useCallback((satIso) => {
    // The week is Sun (6 days before Sat) through Sat
    const sat = new Date(satIso + 'T12:00:00');
    const sun = new Date(sat); sun.setDate(sat.getDate() - 6);
    const weekDays = [];
    for (let d = new Date(sun); d <= sat; d.setDate(d.getDate() + 1)) {
      weekDays.push(d.toISOString().slice(0, 10));
    }
    let net = 0, count = 0, wins = 0, losses = 0, best = null, worst = null;
    for (const d of weekDays) {
      const ts = tradesByDate.get(d) || [];
      for (const t of ts) {
        const p = Number(t.pnl_dollars) || 0;
        net += p; count++;
        if (p > 0) wins++; else if (p < 0) losses++;
        if (best == null || p > best) best = p;
        if (worst == null || p < worst) worst = p;
      }
    }
    if (count === 0) return null;
    return { count, net, wins, losses, best, worst };
  }, [tradesByDate]);

  // Monthly totals for the top strip
  const monthTotals = useMemo(() => {
    let net = 0, count = 0, wins = 0, losses = 0;
    for (const t of trades) {
      const [y, m] = t.brief_date.split('-').map(Number);
      if (y !== viewMonth.y || (m - 1) !== viewMonth.m) continue;
      const p = Number(t.pnl_dollars) || 0;
      net += p; count++;
      if (p > 0) wins++; else if (p < 0) losses++;
    }
    return { net, count, wins, losses };
  }, [trades, viewMonth]);

  const shiftMonth = (delta) => {
    setViewMonth(({ y, m }) => {
      const d = new Date(y, m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  const monthLabel = new Date(viewMonth.y, viewMonth.m, 1)
    .toLocaleString('en-US', { month: 'long', year: 'numeric' });

  const monthDays = buildMonthGrid(viewMonth.y, viewMonth.m);
  const todayIso = todayDateStringETLocal();

  return (
    <>
      <PhaseHeader
        num="📅"
        title="Journal"
        subtitle="Trade log + calendar · ask your AI coach"
        phaseKey="journal"
      />

      <div style={{
        display: 'grid',
        gridTemplateColumns: '70fr 30fr',
        gap: 20,
        marginTop: 14,
        alignItems: 'start',
      }}>
        {/* LEFT 70% — Calendar */}
        <div>
          {/* Month nav + monthly totals strip */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 14px', marginBottom: 10,
            background: 'var(--surface-2)',
            border: '1px solid var(--qqq-b2)', borderRadius: 6,
          }}>
            <button type="button" onClick={() => shiftMonth(-1)}
              style={monthNavBtn}>◀</button>
            <button type="button" onClick={() => shiftMonth(1)}
              style={monthNavBtn}>▶</button>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--qqq-fg-1)', minWidth: 150 }}>
              {monthLabel}
            </div>
            <button type="button" onClick={() => {
              const d = new Date();
              setViewMonth({ y: d.getFullYear(), m: d.getMonth() });
              setSelectedDate(todayIso);
            }} style={{ ...monthNavBtn, padding: '4px 12px', fontSize: 11 }}>
              Today
            </button>
            <div style={{ flex: 1, display: 'flex', gap: 16, justifyContent: 'flex-end', fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
              <span style={{ color: 'var(--qqq-fg-3)' }}>
                month: <strong style={{ color: monthTotals.net >= 0 ? 'var(--qqq-sage)' : 'var(--qqq-terra)' }}>
                  {monthTotals.net >= 0 ? '+' : ''}${monthTotals.net.toFixed(0)}
                </strong>
              </span>
              <span style={{ color: 'var(--qqq-fg-3)' }}>
                {monthTotals.count}t · {monthTotals.wins}W/{monthTotals.losses}L
              </span>
            </div>
          </div>

          {/* Weekday header */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 4, marginBottom: 4,
          }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} style={{
                padding: '6px 8px',
                fontSize: 10, color: 'var(--qqq-fg-3)',
                fontFamily: 'JetBrains Mono, monospace',
                textTransform: 'uppercase', letterSpacing: '0.08em',
                textAlign: 'center',
              }}>
                {d}
              </div>
            ))}
          </div>

          {/* Calendar cells */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 4,
          }}>
            {monthDays.map((cell, i) => {
              if (!cell.iso) {
                return <div key={`pad-${i}`} style={{ minHeight: 88, background: 'transparent' }} />;
              }
              const isSat = (i % 7) === 6;
              const isSel = selectedDate === cell.iso;
              const isToday = cell.iso === todayIso;
              if (isSat) {
                const wk = weekRollup(cell.iso);
                return (
                  <CalendarCell
                    key={cell.iso}
                    iso={cell.iso}
                    day={cell.d}
                    isSelected={isSel}
                    isToday={isToday}
                    isSaturday
                    weekly={wk}
                    daily={dayRollup(cell.iso)}
                    onClick={() => setSelectedDate(cell.iso)}
                  />
                );
              }
              return (
                <CalendarCell
                  key={cell.iso}
                  iso={cell.iso}
                  day={cell.d}
                  isSelected={isSel}
                  isToday={isToday}
                  daily={dayRollup(cell.iso)}
                  onClick={() => setSelectedDate(cell.iso)}
                />
              );
            })}
          </div>

          {/* Day drill-in panel */}
          <DayDetailPanel
            date={selectedDate}
            trades={tradesByDate.get(selectedDate) || []}
            onReload={loadTrades}
          />

          {/* Chart-reading session log for the selected day. Reads
              from Supabase (qqq_session_logs) when workstationId +
              userId are passed, falls back to the gitignored disk
              file at trading/QQQ/sessions/<date>.md. */}
          <SessionLogPanel
            date={selectedDate}
            workstationId={workstationId}
            userId={userId}
          />
        </div>

        {/* RIGHT 30% — Log */}
        <div>
          <TradeLogColumn
            date={selectedDate}
            trades={tradesByDate.get(selectedDate) || []}
            onReload={loadTrades}
            workstationId={workstationId}
            userId={userId}
            aiName={aiName}
            loading={loading}
            loadError={error}
          />
        </div>
      </div>
    </>
  );
}

const monthNavBtn = {
  padding: '4px 10px',
  background: 'var(--surface-3)',
  color: 'var(--qqq-fg-2)',
  border: '1px solid var(--qqq-b2)',
  borderRadius: 4,
  fontSize: 12,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

// ─── CalendarCell ──────────────────────────────────────────
function CalendarCell({ iso, day, isSelected, isToday, isSaturday, weekly, daily, onClick }) {
  let bg = 'var(--surface-2)';
  let borderColor = isSelected ? 'var(--qqq-gold)' : 'var(--qqq-b2)';
  let toneColor = 'var(--qqq-fg-3)';
  if (daily) {
    if (daily.net > 0) {
      const intensity = Math.min(0.3, 0.08 + Math.abs(daily.net) / 5000);
      bg = `rgba(122,158,130,${intensity})`;
      toneColor = 'var(--qqq-sage)';
    } else if (daily.net < 0) {
      const intensity = Math.min(0.3, 0.08 + Math.abs(daily.net) / 5000);
      bg = `rgba(196,96,80,${intensity})`;
      toneColor = 'var(--qqq-terra)';
    }
  }
  if (isToday && !isSelected) borderColor = 'var(--qqq-teal)';

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        minHeight: 88,
        padding: '6px 8px',
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: 4,
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex', flexDirection: 'column', gap: 2,
        fontFamily: 'inherit',
        outline: isSelected ? '2px solid var(--qqq-gold)' : 'none',
        outlineOffset: -2,
      }}
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      }}>
        <span style={{ fontSize: 13, fontWeight: isToday ? 700 : 500, color: 'var(--qqq-fg-1)' }}>
          {day}
        </span>
        {isSaturday && weekly && (
          <span style={{ fontSize: 8, color: 'var(--qqq-fg-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            wk
          </span>
        )}
      </div>
      {daily && (
        <div style={{ fontSize: 11, color: toneColor, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace' }}>
          {daily.net >= 0 ? '+' : ''}${daily.net.toFixed(0)}
        </div>
      )}
      {daily && (
        <div style={{ fontSize: 9, color: 'var(--qqq-fg-3)', fontFamily: 'JetBrains Mono, monospace' }}>
          {daily.count}t · {daily.wins}W/{daily.losses}L
        </div>
      )}
      {isSaturday && weekly && (
        <div style={{
          marginTop: 4, paddingTop: 4, borderTop: '1px dashed var(--qqq-b3)',
          fontSize: 9, color: 'var(--qqq-fg-2)', fontFamily: 'JetBrains Mono, monospace',
          lineHeight: 1.4,
        }}>
          <div style={{
            color: weekly.net >= 0 ? 'var(--qqq-sage)' : 'var(--qqq-terra)',
            fontWeight: 700,
          }}>
            wk {weekly.net >= 0 ? '+' : ''}${weekly.net.toFixed(0)}
          </div>
          <div>{weekly.count}t · {weekly.wins}W/{weekly.losses}L</div>
        </div>
      )}
    </button>
  );
}

// ─── DayDetailPanel ───────────────────────────────────────
// Renders the chart-reading session log for the selected day under
// the calendar/day-detail strip. Reads from
// /api/workstations/qqq-session-log → trading/QQQ/sessions/<date>.md.
// Hidden if no log exists for that day. The narrative log is the
// "what we read and why" record — distinct from the trade log
// (TradeLogColumn) which captures executed positions.
function SessionLogPanel({ date, workstationId, userId }) {
  const [state, setState] = useState({ loading: true, exists: false, content: '', source: null });

  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setState({ loading: true, exists: false, content: '', source: null });
    const qs = new URLSearchParams({ date });
    if (workstationId) qs.set('workstation_id', workstationId);
    if (userId)        qs.set('user_id',        userId);
    fetch(`/api/workstations/qqq-session-log?${qs.toString()}`)
      .then((r) => r.ok ? r.json() : { exists: false })
      .then((j) => {
        if (!cancelled) setState({
          loading: false,
          exists: !!j.exists,
          content: j.content || '',
          source: j.source || null,
        });
      })
      .catch(() => { if (!cancelled) setState({ loading: false, exists: false, content: '', source: null }); });
    return () => { cancelled = true; };
  }, [date, workstationId, userId]);

  if (!date) return null;
  if (state.loading) return null;
  if (!state.exists) {
    // No log for this day → render an unobtrusive empty state so Tim
    // knows the panel exists and where logs would appear.
    return (
      <div style={{
        marginTop: 14, padding: '12px 16px',
        background: 'var(--surface-2)', border: '1px solid var(--qqq-b3)',
        borderRadius: 8, fontSize: 11, color: 'var(--qqq-fg-3)',
        fontStyle: 'italic', lineHeight: 1.55,
      }}>
        <div style={{
          fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'var(--qqq-fg-3)', marginBottom: 4, fontStyle: 'normal',
        }}>
          ◆ Chart-Reading Session Log · {date}
        </div>
        No session log saved for this day. Logs live in
        {' '}<code style={{ fontFamily: 'inherit', color: 'var(--qqq-teal)' }}>trading/QQQ/sessions/{date}.md</code>{' '}
        — Claude saves them after each chart-reading session.
      </div>
    );
  }

  return (
    <div style={{
      marginTop: 14, padding: '16px 18px',
      background: 'var(--surface-2)', border: '1px solid var(--qqq-b2)',
      borderRadius: 8,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12,
        paddingBottom: 10, borderBottom: '1px solid var(--qqq-b3)',
      }}>
        <div style={{
          fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'var(--qqq-teal)', fontWeight: 500,
        }}>
          ◆ Chart-Reading Session Log
        </div>
        <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)' }}>
          {date} · {(state.content.length / 1000).toFixed(1)}k chars
        </div>
      </div>
      <div
        className="qqq-session-log-md"
        style={{
          fontSize: 12, lineHeight: 1.65, color: 'var(--qqq-fg-1)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
        dangerouslySetInnerHTML={{ __html: renderSessionLogMarkdown(state.content) }}
      />
    </div>
  );
}

// Tiny markdown → HTML for the session log. Handles the subset we
// actually use in trading/QQQ/sessions/*.md: headers, bold, italic,
// inline code, code fences, links, ordered/unordered lists, blockquotes,
// horizontal rules, and pipe tables. Not a full CommonMark parser —
// just enough to render the log readably without adding a dependency.
function renderSessionLogMarkdown(md) {
  if (!md) return '';
  const escapeHtml = (s) => s
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let i = 0;
  let inUl = false, inOl = false, inQuote = false;
  const closeBlocks = () => {
    if (inUl) { out.push('</ul>'); inUl = false; }
    if (inOl) { out.push('</ol>'); inOl = false; }
    if (inQuote) { out.push('</blockquote>'); inQuote = false; }
  };

  const inline = (s) => {
    let t = escapeHtml(s);
    // inline code
    t = t.replace(/`([^`]+)`/g, '<code style="font-family:ui-monospace,Menlo,monospace;background:var(--surface-3);padding:1px 5px;border-radius:3px;font-size:11px;color:var(--qqq-teal)">$1</code>');
    // bold
    t = t.replace(/\*\*([^*]+)\*\*/g, '<strong style="color:var(--qqq-fg-1);font-weight:600">$1</strong>');
    // italic
    t = t.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em style="color:var(--qqq-fg-2)">$2</em>');
    // links
    t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:var(--qqq-teal);text-decoration:underline" target="_blank" rel="noopener">$1</a>');
    return t;
  };

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.replace(/\s+$/, '');

    // Code fence
    if (/^```/.test(line)) {
      closeBlocks();
      const buf = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) { buf.push(lines[i]); i++; }
      i++;
      out.push(`<pre style="background:var(--surface-3);padding:10px 12px;border-radius:5px;overflow-x:auto;font-size:11px;line-height:1.5;color:var(--qqq-fg-1);font-family:ui-monospace,Menlo,monospace">${escapeHtml(buf.join('\n'))}</pre>`);
      continue;
    }

    // Pipe table — header | --- | rows
    if (/^\|.+\|$/.test(line) && i + 1 < lines.length && /^\|[\s:|-]+\|$/.test(lines[i + 1])) {
      closeBlocks();
      const headerCells = line.slice(1, -1).split('|').map(c => c.trim());
      i += 2;
      const bodyRows = [];
      while (i < lines.length && /^\|.+\|$/.test(lines[i])) {
        bodyRows.push(lines[i].slice(1, -1).split('|').map(c => c.trim()));
        i++;
      }
      const thead = `<thead><tr>${headerCells.map(c => `<th style="text-align:left;padding:6px 10px;border-bottom:1px solid var(--qqq-b2);font-weight:600;font-size:10px;letter-spacing:0.04em;text-transform:uppercase;color:var(--qqq-fg-2)">${inline(c)}</th>`).join('')}</tr></thead>`;
      const tbody = `<tbody>${bodyRows.map(r => `<tr>${r.map(c => `<td style="padding:6px 10px;border-bottom:1px solid var(--qqq-b3);vertical-align:top">${inline(c)}</td>`).join('')}</tr>`).join('')}</tbody>`;
      out.push(`<table style="border-collapse:collapse;width:100%;margin:10px 0;font-size:11px">${thead}${tbody}</table>`);
      continue;
    }

    // Horizontal rule
    if (/^---+\s*$/.test(line)) {
      closeBlocks();
      out.push('<hr style="border:0;border-top:1px solid var(--qqq-b2);margin:18px 0">');
      i++; continue;
    }

    // Blockquote
    const q = line.match(/^>\s?(.*)$/);
    if (q) {
      if (!inQuote) { closeBlocks(); out.push('<blockquote style="margin:10px 0;padding:8px 14px;border-left:3px solid var(--qqq-gold);background:var(--surface-3);font-style:italic;color:var(--qqq-fg-2)">'); inQuote = true; }
      out.push(`<div>${inline(q[1])}</div>`);
      i++; continue;
    } else if (inQuote) {
      closeBlocks();
    }

    // Headers
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      closeBlocks();
      const level = h[1].length;
      const sizes = { 1: 18, 2: 16, 3: 14, 4: 13, 5: 12, 6: 11 };
      const margins = { 1: '20px 0 12px', 2: '18px 0 10px', 3: '14px 0 8px' };
      const colors = { 1: 'var(--qqq-fg-1)', 2: 'var(--qqq-gold)', 3: 'var(--qqq-teal)' };
      out.push(`<h${level} style="font-size:${sizes[level]}px;margin:${margins[level] || '12px 0 6px'};color:${colors[level] || 'var(--qqq-fg-1)'};font-weight:600;letter-spacing:0.01em">${inline(h[2])}</h${level}>`);
      i++; continue;
    }

    // Ordered list
    const ol = line.match(/^(\d+)\.\s+(.*)$/);
    if (ol) {
      if (!inOl) { closeBlocks(); out.push('<ol style="margin:8px 0 8px 22px;padding:0">'); inOl = true; }
      out.push(`<li style="margin-bottom:4px">${inline(ol[2])}</li>`);
      i++; continue;
    }

    // Unordered list
    const ul = line.match(/^[-*]\s+(.*)$/);
    if (ul) {
      if (!inUl) { closeBlocks(); out.push('<ul style="margin:8px 0 8px 22px;padding:0">'); inUl = true; }
      out.push(`<li style="margin-bottom:4px">${inline(ul[1])}</li>`);
      i++; continue;
    }

    // Blank line
    if (line === '') {
      closeBlocks();
      i++; continue;
    }

    // Paragraph
    closeBlocks();
    out.push(`<p style="margin:8px 0">${inline(line)}</p>`);
    i++;
  }

  closeBlocks();
  return out.join('');
}

function DayDetailPanel({ date, trades, onReload }) {
  const data = useQqqData();
  if (!date) return null;
  const archive = Array.isArray(data._chartArchive) ? data._chartArchive : [];
  const dayArchive = archive.find((r) => r.brief_date === date);

  return (
    <div style={{
      marginTop: 14, padding: '14px 16px',
      background: 'var(--surface-2)',
      border: '1px solid var(--qqq-b2)', borderRadius: 6,
    }}>
      <div style={{
        fontSize: 10, color: 'var(--qqq-fg-3)',
        textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
        marginBottom: 10,
      }}>
        Detail · {fmtBriefDate(date)}
      </div>
      {trades.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--qqq-fg-3)', fontStyle: 'italic' }}>
          No trades logged for this day.
        </div>
      )}
      {trades.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {trades.map((t) => (
            <TradeRow key={t.id} trade={t} onReload={onReload} compact />
          ))}
        </div>
      )}
      {dayArchive?.chart_analysis && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--qqq-b1)' }}>
          <div style={{ fontSize: 10, color: 'var(--qqq-teal)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
            Chart read
          </div>
          <div style={{ fontSize: 12, color: 'var(--qqq-fg-1)' }}>
            {dayArchive.chart_analysis.pattern || '(unnamed)'}
            {' · '}
            <span style={{ color: 'var(--qqq-fg-2)' }}>
              CHOP {dayArchive.chart_analysis.chop_score?.score || '—'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TradeLogColumn (right 30%) ──────────────────────────
function TradeLogColumn({ date, trades, onReload, workstationId, userId, aiName, loading, loadError }) {
  const [addOpen, setAddOpen] = useState(false);
  const [askRange, setAskRange] = useState('week');
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState(null);
  const [report, setReport] = useState(null);

  const rollup = useMemo(() => {
    if (trades.length === 0) return null;
    let net = 0, wins = 0, losses = 0;
    for (const t of trades) {
      const p = Number(t.pnl_dollars) || 0;
      net += p;
      if (p > 0) wins++; else if (p < 0) losses++;
    }
    return { count: trades.length, net, wins, losses, winRate: wins + losses > 0 ? wins / (wins + losses) : null };
  }, [trades]);

  const runAsk = async () => {
    if (!workstationId || !userId || asking) return;
    setAsking(true);
    setAskError(null);
    try {
      const res = await fetch('/api/workstations/qqq-trades?action=ask_emily', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workstation_id: workstationId, user_id: userId, range: askRange }),
      });
      const payload = await res.json();
      if (!res.ok) setAskError(payload?.error || `HTTP ${res.status}`);
      else setReport(payload.report);
    } catch (e) { setAskError(e?.message || 'ask failed'); }
    finally { setAsking(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Date header */}
      <div style={{
        fontSize: 13, fontWeight: 700, color: 'var(--qqq-fg-1)',
        padding: '8px 12px',
        background: 'var(--surface-2)',
        border: '1px solid var(--qqq-b2)', borderRadius: 6,
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      }}>
        <span>{fmtBriefDate(date)}</span>
        <span style={{ fontSize: 10, color: 'var(--qqq-fg-3)', fontFamily: 'JetBrains Mono, monospace' }}>
          {trades.length} trade{trades.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* Add trade */}
      {!addOpen && (
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          style={{
            padding: '10px 14px',
            background: 'var(--qqq-teal)', color: 'var(--surface-0)',
            border: 0, borderRadius: 6,
            fontSize: 12, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          + Add trade
        </button>
      )}
      {addOpen && (
        <TradeForm
          date={date}
          workstationId={workstationId}
          userId={userId}
          onCancel={() => setAddOpen(false)}
          onSaved={async () => { setAddOpen(false); await onReload(); }}
        />
      )}

      {/* Trade list */}
      {loading && <div style={{ padding: 10, color: 'var(--qqq-fg-3)', fontSize: 12 }}>Loading…</div>}
      {loadError && <div style={{ padding: 10, color: 'var(--qqq-terra)', fontSize: 12 }}>Error: {loadError}</div>}
      {trades.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {trades.map((t) => (
            <TradeRow key={t.id} trade={t} onReload={onReload} />
          ))}
        </div>
      )}

      {/* Daily rollup */}
      {rollup && (
        <div style={{
          padding: '10px 14px',
          background: 'var(--surface-2)',
          border: `1px solid ${rollup.net >= 0 ? 'rgba(122,158,130,.3)' : 'rgba(196,96,80,.3)'}`,
          borderLeft: `3px solid ${rollup.net >= 0 ? 'var(--qqq-sage)' : 'var(--qqq-terra)'}`,
          borderRadius: 4,
        }}>
          <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 4 }}>
            Day rollup
          </div>
          <div style={{
            fontSize: 18, fontWeight: 700,
            color: rollup.net >= 0 ? 'var(--qqq-sage)' : 'var(--qqq-terra)',
            fontFamily: 'JetBrains Mono, monospace',
          }}>
            {rollup.net >= 0 ? '+' : ''}${rollup.net.toFixed(2)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', fontFamily: 'JetBrains Mono, monospace', marginTop: 2 }}>
            {rollup.count}t · {rollup.wins}W / {rollup.losses}L
            {rollup.winRate != null && (
              <> · {Math.round(rollup.winRate * 100)}%</>
            )}
          </div>
        </div>
      )}

      {/* Ask Emily */}
      <div style={{
        marginTop: 4,
        padding: '14px 16px',
        background: 'rgba(155,143,196,.04)',
        border: '1px solid rgba(155,143,196,.3)',
        borderRadius: 6,
      }}>
        <div style={{
          fontSize: 10, color: 'var(--qqq-purple)', textTransform: 'uppercase',
          letterSpacing: '0.08em', fontWeight: 700, marginBottom: 8,
        }}>
          Ask {aiName} · coaching
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          {['week', 'month', 'ytd'].map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setAskRange(r)}
              style={{
                padding: '4px 10px',
                background: askRange === r ? 'rgba(155,143,196,.2)' : 'var(--surface-3)',
                color: askRange === r ? 'var(--qqq-purple)' : 'var(--qqq-fg-2)',
                border: `1px solid ${askRange === r ? 'var(--qqq-purple)' : 'var(--qqq-b2)'}`,
                borderRadius: 4, fontSize: 11,
                fontWeight: askRange === r ? 700 : 500,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {r}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={runAsk}
          disabled={asking}
          style={{
            padding: '8px 14px',
            background: asking ? 'var(--surface-3)' : 'var(--qqq-purple)',
            color: asking ? 'var(--qqq-fg-3)' : 'var(--surface-0)',
            border: 0, borderRadius: 4,
            fontSize: 12, fontWeight: 700,
            cursor: asking ? 'default' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {asking ? `${aiName} is thinking…` : `Ask ${aiName} about this ${askRange}`}
        </button>
        {askError && (
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--qqq-terra)' }}>
            {askError}
          </div>
        )}
        {report && <CoachingReport report={report} />}
      </div>
    </div>
  );
}

// ─── TradeForm ──────────────────────────────────────────
function TradeForm({ date, workstationId, userId, onCancel, onSaved }) {
  const [direction, setDirection] = useState('call');
  const [strike, setStrike] = useState('');
  const [contracts, setContracts] = useState('1');
  const [entryTime, setEntryTime] = useState('');
  const [entryPrice, setEntryPrice] = useState('');
  const [exitTime, setExitTime] = useState('');
  const [exitPrice, setExitPrice] = useState('');
  const [setupTag, setSetupTag] = useState('');
  const [reasoning, setReasoning] = useState('');
  const [ruleBreaks, setRuleBreaks] = useState(new Set());
  const [plannedStop, setPlannedStop] = useState('');
  const [plannedTarget, setPlannedTarget] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const toggleRb = (key) => {
    setRuleBreaks((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const canSave = Number(strike) > 0 && Number(contracts) >= 1;

  const save = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch('/api/workstations/qqq-trades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstationId,
          user_id: userId,
          brief_date: date,
          direction,
          strike: Number(strike),
          contracts: Number(contracts),
          entry_time: entryTime || null,
          entry_price: entryPrice ? Number(entryPrice) : null,
          exit_time: exitTime || null,
          exit_price: exitPrice ? Number(exitPrice) : null,
          setup_tag: setupTag || null,
          reasoning: reasoning || null,
          rule_breaks: Array.from(ruleBreaks),
          planned_stop: plannedStop ? Number(plannedStop) : null,
          planned_target: plannedTarget ? Number(plannedTarget) : null,
        }),
      });
      const payload = await res.json();
      if (!res.ok) { setSaveError(payload?.error || 'save failed'); return; }
      await onSaved();
    } catch (e) { setSaveError(e?.message || 'save failed'); }
    finally { setSaving(false); }
  };

  const lblStyle = { fontSize: 10, color: 'var(--qqq-fg-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: 4 };
  const inpStyle = {
    width: '100%', padding: '6px 10px',
    background: 'var(--surface-3)', color: 'var(--qqq-fg-1)',
    border: '1px solid var(--qqq-b2)', borderRadius: 4,
    fontFamily: 'JetBrains Mono, monospace', fontSize: 12,
    boxSizing: 'border-box',
  };

  return (
    <div style={{
      padding: '12px 14px',
      background: 'rgba(77,184,184,.04)',
      border: '1px solid rgba(77,184,184,.3)',
      borderRadius: 6,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ fontSize: 11, color: 'var(--qqq-teal)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        New trade · {date}
      </div>

      {/* Direction */}
      <div>
        <div style={lblStyle}>Direction</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['call', 'put'].map((d) => (
            <button key={d} type="button" onClick={() => setDirection(d)}
              style={{
                flex: 1, padding: '8px 12px',
                background: direction === d ? (d === 'call' ? 'rgba(122,158,130,.18)' : 'rgba(196,96,80,.18)') : 'var(--surface-3)',
                color: direction === d ? (d === 'call' ? 'var(--qqq-sage)' : 'var(--qqq-terra)') : 'var(--qqq-fg-2)',
                border: `1px solid ${direction === d ? (d === 'call' ? 'var(--qqq-sage)' : 'var(--qqq-terra)') : 'var(--qqq-b2)'}`,
                borderRadius: 4, fontSize: 12, fontWeight: direction === d ? 700 : 500,
                cursor: 'pointer', fontFamily: 'inherit', textTransform: 'uppercase',
              }}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* Strike + Contracts */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
        <div>
          <div style={lblStyle}>Strike</div>
          <input type="number" step="0.01" value={strike} onChange={(e) => setStrike(e.target.value)} placeholder="e.g. 660" style={inpStyle} />
        </div>
        <div>
          <div style={lblStyle}>Contracts</div>
          <input type="number" min="1" value={contracts} onChange={(e) => setContracts(e.target.value)} style={inpStyle} />
        </div>
      </div>

      {/* Entry */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <div style={lblStyle}>Entry time (ET)</div>
          <input type="text" value={entryTime} onChange={(e) => setEntryTime(e.target.value)} placeholder="9:48" style={inpStyle} />
        </div>
        <div>
          <div style={lblStyle}>Entry premium</div>
          <input type="number" step="0.01" value={entryPrice} onChange={(e) => setEntryPrice(e.target.value)} placeholder="1.25" style={inpStyle} />
        </div>
      </div>

      {/* Exit */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <div style={lblStyle}>Exit time (ET)</div>
          <input type="text" value={exitTime} onChange={(e) => setExitTime(e.target.value)} placeholder="10:15" style={inpStyle} />
        </div>
        <div>
          <div style={lblStyle}>Exit premium</div>
          <input type="number" step="0.01" value={exitPrice} onChange={(e) => setExitPrice(e.target.value)} placeholder="2.75" style={inpStyle} />
        </div>
      </div>

      {/* Planned stop + target */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <div style={lblStyle}>Planned stop</div>
          <input type="number" step="0.01" value={plannedStop} onChange={(e) => setPlannedStop(e.target.value)} placeholder="0.80" style={inpStyle} />
        </div>
        <div>
          <div style={lblStyle}>Planned target</div>
          <input type="number" step="0.01" value={plannedTarget} onChange={(e) => setPlannedTarget(e.target.value)} placeholder="2.50" style={inpStyle} />
        </div>
      </div>

      {/* Setup */}
      <div>
        <div style={lblStyle}>Setup</div>
        <select value={setupTag} onChange={(e) => setSetupTag(e.target.value)} style={{ ...inpStyle, fontFamily: 'inherit' }}>
          <option value="">— choose setup —</option>
          {SETUP_OPTIONS.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Reasoning */}
      <div>
        <div style={lblStyle}>Reasoning</div>
        <textarea value={reasoning} onChange={(e) => setReasoning(e.target.value.slice(0, 2000))} rows={2} placeholder="Why this trade — short prose." style={{ ...inpStyle, fontFamily: 'inherit', fontSize: 12, resize: 'vertical' }} />
      </div>

      {/* Rule breaks */}
      <div>
        <div style={lblStyle}>Rule breaks (select any that apply)</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {RULE_BREAK_OPTIONS.map((r) => {
            const on = ruleBreaks.has(r.key);
            return (
              <button key={r.key} type="button" onClick={() => toggleRb(r.key)}
                style={{
                  padding: '4px 10px',
                  background: on ? 'rgba(196,96,80,.18)' : 'var(--surface-3)',
                  color: on ? 'var(--qqq-terra)' : 'var(--qqq-fg-2)',
                  border: `1px solid ${on ? 'var(--qqq-terra)' : 'var(--qqq-b2)'}`,
                  borderRadius: 4, fontSize: 10,
                  fontWeight: on ? 700 : 500,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button type="button" onClick={save} disabled={!canSave || saving}
          style={{
            padding: '8px 14px',
            background: canSave && !saving ? 'var(--qqq-teal)' : 'var(--surface-3)',
            color: canSave && !saving ? 'var(--surface-0)' : 'var(--qqq-fg-3)',
            border: 0, borderRadius: 4, fontSize: 12, fontWeight: 700,
            cursor: canSave && !saving ? 'pointer' : 'not-allowed',
            fontFamily: 'inherit',
          }}
        >
          {saving ? 'Saving…' : 'Save trade'}
        </button>
        <button type="button" onClick={onCancel}
          style={{
            padding: '8px 14px',
            background: 'transparent', color: 'var(--qqq-fg-2)',
            border: '1px solid var(--qqq-b2)', borderRadius: 4,
            fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Cancel
        </button>
        {saveError && <span style={{ fontSize: 11, color: 'var(--qqq-terra)', alignSelf: 'center' }}>{saveError}</span>}
      </div>
    </div>
  );
}

// ─── TradeRow ───────────────────────────────────────────
function TradeRow({ trade, onReload, compact }) {
  const pnl = Number(trade.pnl_dollars) || 0;
  const toneColor = pnl > 0 ? 'var(--qqq-sage)' : pnl < 0 ? 'var(--qqq-terra)' : 'var(--qqq-fg-3)';

  const del = async () => {
    if (!confirm('Delete this trade?')) return;
    try {
      const res = await fetch(`/api/workstations/qqq-trades?id=${trade.id}`, { method: 'DELETE' });
      if (res.ok) await onReload();
    } catch { /* ignore */ }
  };

  const setupLabel = SETUP_OPTIONS.find((s) => s.key === trade.setup_tag)?.label || trade.setup_tag || '—';
  const breaks = Array.isArray(trade.rule_breaks) ? trade.rule_breaks : [];

  return (
    <div style={{
      padding: compact ? '6px 10px' : '10px 12px',
      background: 'var(--surface-3)',
      border: '1px solid var(--qqq-b2)',
      borderLeft: `3px solid ${toneColor}`,
      borderRadius: 4,
      display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          padding: '1px 6px', borderRadius: 2,
          background: trade.direction === 'call' ? 'rgba(122,158,130,.15)' : 'rgba(196,96,80,.15)',
          color: trade.direction === 'call' ? 'var(--qqq-sage)' : 'var(--qqq-terra)',
          fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
          fontFamily: 'JetBrains Mono, monospace',
        }}>
          {trade.direction}
        </span>
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: 'var(--qqq-fg-1)', fontWeight: 600 }}>
          ${trade.strike} × {trade.contracts}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{
          fontSize: 13, fontWeight: 700, color: toneColor,
          fontFamily: 'JetBrains Mono, monospace',
        }}>
          {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
        </span>
        {!compact && (
          <button type="button" onClick={del}
            style={{ background: 'transparent', border: 0, color: 'var(--qqq-fg-3)', cursor: 'pointer', fontSize: 12, padding: 2 }}
            title="Delete"
          >
            ✕
          </button>
        )}
      </div>
      {!compact && (
        <>
          <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)', fontFamily: 'JetBrains Mono, monospace' }}>
            in {trade.entry_time || '—'}@{trade.entry_price || '—'} · out {trade.exit_time || '—'}@{trade.exit_price || '—'}
            {trade.pnl_pct != null && <> · {Number(trade.pnl_pct) >= 0 ? '+' : ''}{Number(trade.pnl_pct).toFixed(1)}%</>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)' }}>
            setup: <span style={{ color: 'var(--qqq-gold)' }}>{setupLabel}</span>
            {breaks.length > 0 && (
              <> · breaks: <span style={{ color: 'var(--qqq-terra)' }}>{breaks.map((b) => RULE_BREAK_OPTIONS.find((x) => x.key === b)?.label || b).join(', ')}</span></>
            )}
          </div>
          {trade.reasoning && (
            <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', fontStyle: 'italic', lineHeight: 1.45 }}>
              &ldquo;{trade.reasoning}&rdquo;
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── CoachingReport ─────────────────────────────────────
function CoachingReport({ report }) {
  if (!report) return null;
  if (report.parse_error) {
    return (
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--qqq-terra)' }}>
        Model returned unparseable output.
      </div>
    );
  }
  const sections = [
    { key: 'how_youre_doing',        label: 'How you\'re doing',     tone: 'sage'   },
    { key: 'whats_working',          label: 'What\'s working',       tone: 'sage'   },
    { key: 'whats_not',              label: 'What\'s not',           tone: 'terra'  },
    { key: 'rule_break_correlation', label: 'Rule breaks',           tone: 'amber'  },
    { key: 'pattern_strategy',       label: 'Pattern strategy',      tone: 'teal'   },
    { key: 'watch_for',              label: 'Watch for',             tone: 'purple' },
  ];
  const toneColor = (t) => ({ sage: 'var(--qqq-sage)', terra: 'var(--qqq-terra)', amber: 'var(--qqq-amber, #d4956a)', teal: 'var(--qqq-teal)', purple: 'var(--qqq-purple)' })[t];

  return (
    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {sections.map((s) => {
        const val = report[s.key];
        if (!val || (Array.isArray(val) && val.length === 0)) return null;
        return (
          <div key={s.key}>
            <div style={{ fontSize: 10, color: toneColor(s.tone), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
              {s.label}
            </div>
            {typeof val === 'string' ? (
              <div style={{ fontSize: 12, color: 'var(--qqq-fg-1)', lineHeight: 1.55 }}>{val}</div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--qqq-fg-1)', lineHeight: 1.55 }}>
                {val.map((item, i) => <li key={i} style={{ marginBottom: 3 }}>{item}</li>)}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CalendarTab({ embedded = false }) {
  const data = useQqqData();
  const history = Array.isArray(data._shiftScoreHistory) ? data._shiftScoreHistory : [];
  const archive = Array.isArray(data._chartArchive) ? data._chartArchive : [];

  // Build lookup by date → session record (merge history + archive)
  const byDate = new Map();
  for (const h of history) {
    byDate.set(h.brief_date, { ...h });
  }
  for (const a of archive) {
    const existing = byDate.get(a.brief_date) || { brief_date: a.brief_date };
    byDate.set(a.brief_date, {
      ...existing,
      chart_analysis: a.chart_analysis,
      regime_tags:    a.regime_tags,
      window_outcome: a.window_outcome,
    });
  }

  // Month state
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(() => ({
    y: today.getFullYear(),
    m: today.getMonth(), // 0-11
  }));
  const [selectedDate, setSelectedDate] = useState(null);

  function shiftMonth(delta) {
    setViewMonth(({ y, m }) => {
      const d = new Date(y, m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
    setSelectedDate(null);
  }
  function jumpToday() {
    setViewMonth({ y: today.getFullYear(), m: today.getMonth() });
  }

  // Build the month grid (6 rows × 7 cols)
  const monthDays = buildMonthGrid(viewMonth.y, viewMonth.m);

  // Month stats
  const monthSessions = Array.from(byDate.values()).filter((s) => {
    if (!s.brief_date) return false;
    const [y, m] = s.brief_date.split('-').map(Number);
    return y === viewMonth.y && (m - 1) === viewMonth.m;
  });
  const hit200Count = monthSessions.filter((s) => s.hit_2_00 === true).length;
  const hit200Total = monthSessions.filter((s) => s.hit_2_00 != null).length;
  const hit200Pct = hit200Total > 0 ? Math.round((hit200Count / hit200Total) * 100) : null;
  const biasCorrect = monthSessions.filter((s) => s.bias_correct === true).length;
  const biasTotal = monthSessions.filter((s) => typeof s.bias_correct === 'boolean').length;
  const biasPct = biasTotal > 0 ? Math.round((biasCorrect / biasTotal) * 100) : null;
  const avgScore = monthSessions.filter((s) => typeof s.score === 'number').reduce(
    (acc, s, _i, arr) => acc + s.score / arr.length, 0
  );

  const monthLabel = new Date(viewMonth.y, viewMonth.m, 1).toLocaleString('en-US',
    { month: 'long', year: 'numeric' });
  const isCurrentMonth = viewMonth.y === today.getFullYear() && viewMonth.m === today.getMonth();

  const selected = selectedDate ? byDate.get(selectedDate) : null;

  return (
    <>
      {!embedded && (
        <PhaseHeader
          num="📅"
          title="Calendar"
          subtitle="Daily P&L · trades · per-day analysis"
          phaseKey="calendar"
        />
      )}

      {/* Month navigator */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16, padding: '0 2px',
      }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" onClick={() => shiftMonth(-1)} style={navBtnStyle}>
            ◀
          </button>
          <div style={{
            fontSize: 18, fontWeight: 600, color: 'var(--qqq-fg-1)',
            fontFamily: 'Literata, Georgia, serif',
            minWidth: 200,
          }}>
            {monthLabel}
          </div>
          <button type="button" onClick={() => shiftMonth(1)} style={navBtnStyle}>
            ▶
          </button>
        </div>
        {!isCurrentMonth && (
          <button type="button" onClick={jumpToday} style={{
            ...navBtnStyle, padding: '6px 12px',
            fontSize: 11, fontWeight: 700,
            color: 'var(--qqq-gold)', borderColor: 'var(--qqq-gold)',
          }}>
            Today
          </button>
        )}
      </div>

      {/* Month stats strip */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10,
        marginBottom: 18,
      }}>
        <StatTileCal label="Sessions"      value={String(monthSessions.length)} />
        <StatTileCal label="$2.00 hit"     value={hit200Pct != null ? `${hit200Pct}%` : '—'} sub={hit200Total > 0 ? `${hit200Count}/${hit200Total}` : ''} tone="sage" />
        <StatTileCal label="Gut accuracy"  value={biasPct != null ? `${biasPct}%` : '—'}    sub={biasTotal > 0 ? `${biasCorrect}/${biasTotal}` : ''} tone="gold" />
        <StatTileCal label="Avg Shift Score" value={avgScore ? avgScore.toFixed(1) : '—'}    tone="purple" />
      </div>

      {/* Calendar grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 6,
        marginBottom: 18,
      }}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d) => (
          <div key={d} style={{
            fontSize: 10, color: 'var(--qqq-fg-3)', textAlign: 'center',
            textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
            padding: '4px 0',
          }}>
            {d}
          </div>
        ))}
        {monthDays.map((day, i) => (
          <CalendarDayCell
            key={i}
            day={day}
            session={day.iso ? byDate.get(day.iso) : null}
            isToday={day.iso === todayDateStringETLocal()}
            isSelected={day.iso === selectedDate}
            onClick={() => day.iso && setSelectedDate(day.iso === selectedDate ? null : day.iso)}
          />
        ))}
      </div>

      {/* Expanded day panel */}
      {selected && (
        <CalendarDayExpanded
          session={selected}
          onClose={() => setSelectedDate(null)}
        />
      )}
      {!selected && (
        <div style={{
          padding: '14px 16px',
          background: 'rgba(77,184,184,.04)',
          border: '1px dashed var(--qqq-b2)',
          borderRadius: 6,
          fontSize: 12, color: 'var(--qqq-fg-2)',
          textAlign: 'center', fontStyle: 'italic',
        }}>
          Click any day to see its full analysis · chart + context + journal
        </div>
      )}
    </>
  );
}

const navBtnStyle = {
  padding: '6px 10px',
  background: 'var(--surface-3)',
  color: 'var(--qqq-fg-2)',
  border: '1px solid var(--qqq-b2)',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: 'inherit',
};

function StatTileCal({ label, value, sub, tone }) {
  const toneColor = ({
    sage: 'var(--qqq-sage)', gold: 'var(--qqq-gold)',
    purple: 'var(--qqq-purple)', terra: 'var(--qqq-terra)', teal: 'var(--qqq-teal)',
  })[tone] || 'var(--qqq-fg-1)';
  return (
    <div style={{
      padding: '10px 12px',
      background: 'var(--qqq-s1)',
      border: '1px solid var(--qqq-b1)',
      borderRadius: 6,
    }}>
      <div style={{
        fontSize: 10, color: 'var(--qqq-fg-3)',
        textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 22, fontWeight: 700, color: toneColor,
        fontFamily: 'Literata, Georgia, serif', marginTop: 2, lineHeight: 1,
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)', marginTop: 4, fontFamily: 'JetBrains Mono, monospace' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function CalendarDayCell({ day, session, isToday, isSelected, onClick }) {
  if (!day.iso) {
    // Padding cell for days not in the month
    return <div style={{ minHeight: 90 }} />;
  }

  const hit200 = session?.hit_2_00;
  const hit150 = session?.hit_1_50;
  const hasSession = !!session;

  // Cell tone based on outcome
  let bg = 'var(--qqq-s1)';
  let border = 'var(--qqq-b1)';
  let outcomeLabel = null;
  let outcomeTone = 'muted';
  if (hit200 === true)       { bg = 'rgba(122,158,130,.10)'; border = 'var(--qqq-sage)'; outcomeLabel = '✓ $2'; outcomeTone = 'sage'; }
  else if (hit150 === true)  { bg = 'rgba(200,169,110,.08)'; border = 'var(--qqq-gold)'; outcomeLabel = '$1.50'; outcomeTone = 'gold'; }
  else if (hit200 === false || hit150 === false)
                             { bg = 'rgba(196,96,80,.08)';  border = 'var(--qqq-terra)'; outcomeLabel = '✗ miss'; outcomeTone = 'terra'; }
  else if (hasSession)       { bg = 'rgba(155,143,196,.05)'; border = 'var(--qqq-purple)'; outcomeLabel = 'logged'; outcomeTone = 'purple'; }

  if (isSelected) {
    border = 'var(--qqq-gold)';
  }
  const toneColor = (t) => ({
    sage: 'var(--qqq-sage)', gold: 'var(--qqq-gold)',
    purple: 'var(--qqq-purple)', terra: 'var(--qqq-terra)', muted: 'var(--qqq-fg-3)',
  })[t];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!hasSession}
      style={{
        minHeight: 90,
        padding: '6px 8px',
        background: bg,
        border: `2px solid ${border}`,
        borderRadius: 6,
        cursor: hasSession ? 'pointer' : 'default',
        textAlign: 'left',
        position: 'relative',
        fontFamily: 'inherit',
        color: 'inherit',
        display: 'flex', flexDirection: 'column', gap: 4,
        opacity: hasSession ? 1 : 0.55,
      }}
    >
      {/* Date number */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{
          fontSize: 13, fontWeight: isToday ? 700 : 500,
          color: isToday ? 'var(--qqq-gold)' : 'var(--qqq-fg-1)',
          fontFamily: 'JetBrains Mono, monospace',
        }}>
          {day.d}
        </span>
        {isToday && (
          <span style={{
            fontSize: 8, color: 'var(--qqq-gold)',
            textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
          }}>
            today
          </span>
        )}
      </div>

      {/* Session content */}
      {hasSession && (
        <>
          {typeof session.score === 'number' && (
            <div style={{
              fontSize: 11, color: 'var(--qqq-fg-2)',
              fontFamily: 'JetBrains Mono, monospace',
            }}>
              Score <strong style={{ color: 'var(--qqq-fg-1)' }}>{session.score}/10</strong>
            </div>
          )}
          {outcomeLabel && (
            <div style={{
              fontSize: 10, fontWeight: 700, color: toneColor(outcomeTone),
              padding: '2px 6px', borderRadius: 3,
              background: 'var(--surface-3)',
              alignSelf: 'flex-start',
              fontFamily: 'JetBrains Mono, monospace',
              letterSpacing: '0.03em',
            }}>
              {outcomeLabel}
            </div>
          )}
          {session.bias_correct != null && (
            <div style={{
              fontSize: 9, color: session.bias_correct ? 'var(--qqq-sage)' : 'var(--qqq-terra)',
              marginTop: 'auto',
            }}>
              {session.bias_correct ? '✓ bias right' : '✗ bias wrong'}
            </div>
          )}
        </>
      )}
    </button>
  );
}

function CalendarDayExpanded({ session, onClose }) {
  const ca = session.chart_analysis;
  return (
    <div style={{
      background: 'var(--qqq-s1)',
      border: '1px solid var(--qqq-b1)',
      borderLeft: '3px solid var(--qqq-teal)',
      borderRadius: 8,
      padding: 0,
      marginBottom: 20,
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 18px',
        borderBottom: '1px solid var(--qqq-b1)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      }}>
        <div>
          <div style={{
            fontSize: 11, color: 'var(--qqq-teal)', textTransform: 'uppercase',
            letterSpacing: '0.05em', fontWeight: 700,
          }}>
            Session Detail
          </div>
          <div style={{
            fontSize: 16, fontWeight: 600, color: 'var(--qqq-fg-1)',
            fontFamily: 'Literata, Georgia, serif', marginTop: 4,
          }}>
            {fmtBriefDate(session.brief_date)}
          </div>
        </div>
        <button type="button" onClick={onClose} style={{
          ...navBtnStyle, padding: '4px 10px',
        }}>
          ✕ close
        </button>
      </div>

      {/* Top-line stats */}
      <div style={{
        padding: '12px 18px',
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10,
        borderBottom: '1px solid var(--qqq-b1)',
      }}>
        <StatTileCal label="Shift Score" value={typeof session.score === 'number' ? `${session.score}/10` : '—'} tone="gold" />
        <StatTileCal label="Direction"   value={session.direction?.toUpperCase() || '—'} />
        <StatTileCal label="Outcome"
          value={session.hit_2_00 === true ? '✓ $2.00'
               : session.hit_1_50 === true ? '$1.50 only'
               : (session.hit_2_00 === false || session.hit_1_50 === false) ? '✗ miss'
               : '—'}
          tone={session.hit_2_00 === true ? 'sage' : (session.hit_2_00 === false ? 'terra' : 'muted')}
        />
        <StatTileCal label="Bias" value={session.bias_correct === true ? '✓ right' : session.bias_correct === false ? '✗ wrong' : '—'}
          tone={session.bias_correct === true ? 'sage' : session.bias_correct === false ? 'terra' : 'muted'}
        />
      </div>

      {/* Full chart analysis if present */}
      {ca && (
        <div style={{ padding: '14px 18px' }}>
          <ChartAnalysis analysis={ca} />
        </div>
      )}

      {/* Regime tags if present */}
      {session.regime_tags && (
        <div style={{
          padding: '10px 18px', borderTop: '1px solid var(--qqq-b1)',
          fontSize: 11, color: 'var(--qqq-fg-2)',
          fontFamily: 'JetBrains Mono, monospace',
        }}>
          <strong style={{ color: 'var(--qqq-teal)' }}>regime:</strong>{' '}
          vix {session.regime_tags.vix_bucket || '—'} · gamma {session.regime_tags.gamma_sign || '—'} ·
          {' '}{session.regime_tags.sector_leader || '—'} leading · {session.regime_tags.event_proximity || 'clean'}
        </div>
      )}

      {/* Footer */}
      <div style={{
        padding: '10px 18px', background: 'rgba(77,184,184,.03)',
        borderTop: '1px solid var(--qqq-b1)',
        fontSize: 11, color: 'var(--qqq-fg-3)', fontStyle: 'italic',
      }}>
        Real $ P&L appears here once the Trade Log (Commit 59) is wired.
        Currently showing underlying move + Shift Score + bias calibration.
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────
function buildMonthGrid(year, monthIndex) {
  const firstOfMonth = new Date(year, monthIndex, 1);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const startWeekday = firstOfMonth.getDay(); // 0 = Sunday
  const cells = [];
  // Leading padding
  for (let i = 0; i < startWeekday; i += 1) {
    cells.push({ d: null, iso: null });
  }
  // Days of month
  for (let d = 1; d <= daysInMonth; d += 1) {
    const iso = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ d, iso });
  }
  // Trailing padding to complete the last row
  while (cells.length % 7 !== 0) {
    cells.push({ d: null, iso: null });
  }
  return cells;
}

function todayDateStringETLocal() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === 'year').value;
  const m = parts.find((p) => p.type === 'month').value;
  const d = parts.find((p) => p.type === 'day').value;
  return `${y}-${m}-${d}`;
}

// ═══════════════════════════════════════════════════════════
// LIBRARY TAB — Commit 56 (repository for uploads/articles/analysis)
// ═══════════════════════════════════════════════════════════
// The filing surface. Three sub-views toggleable via pill filter:
//   UPLOADS   — every chart parsed, filterable by date/pattern/outcome
//   ARTICLES  — external articles you've clipped (V2 in Commit 57)
//   ANALYSIS  — chronological feed of ALL Shift-generated outputs
//                (chart analyses + method sheets + context briefs +
//                 your journal entries)
//
// All data already persisted in qqq_session_briefs or
// qqq_method_sheets — this view composes them into a browseable
// repository without new tables.
// ═══════════════════════════════════════════════════════════
// LIBRARY — Commit 87 (2 pills + Timeline 60/40 + Curated merge)
// ═══════════════════════════════════════════════════════════
// Timeline pill: 4 read tabs (Daily / Files / Finalized / 7d)
//                on the LEFT 60%, EndOfDayReviewCard (drops +
//                converge + canonical) on the RIGHT 40%.
// Curated pill:  Articles + Collections merged. Clip form at top
//                (via LibraryArticlesView) + collections list
//                below (via LibraryFoldersView).
// Killed: the old Calendar pill (Journal has its own calendar).
// ═══════════════════════════════════════════════════════════

const LIBRARY_PILLS = [
  { key: 'timeline',  label: 'Timeline',  sub: 'daily · files · finalized · 7d' },
  { key: 'curated',   label: 'Curated',   sub: 'articles + collections · ask your AI' },
  { key: 'patterns',  label: 'Patterns',  sub: 'QQQ 0DTE TOS pattern guide' },
  { key: 'setup',     label: 'Setup',     sub: 'A+/B/C tier reference for the 5 patterns' },
];

const TIMELINE_TABS = [
  { key: 'daily',     label: 'Daily',      sub: 'day by day' },
  { key: 'files',     label: 'Files',      sub: 'every image uploaded' },
  { key: 'finalized', label: 'Finalized',  sub: 'canonical locked only' },
  { key: 'seven_day', label: '7-Day',      sub: 'weekly synthesis' },
];

function LibraryTab() {
  // Top-level pill (timeline | curated)
  const [pill, setPill] = useState(() => {
    if (typeof window === 'undefined') return 'timeline';
    const raw = window.localStorage.getItem('qqq_library_view');
    const migrated = migrateLegacyLibraryView(raw);
    if (raw !== migrated) {
      try { window.localStorage.setItem('qqq_library_view', migrated); } catch { /* ignore */ }
    }
    return migrated;
  });
  const switchPill = (v) => {
    setPill(v);
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem('qqq_library_view', v); } catch { /* ignore */ }
    }
  };

  // Timeline inner tab (daily | files | finalized | seven_day)
  const [timelineTab, setTimelineTab] = useState(() => {
    if (typeof window === 'undefined') return 'daily';
    const raw = window.localStorage.getItem('qqq_timeline_tab');
    const valid = new Set(['daily', 'files', 'finalized', 'seven_day']);
    return valid.has(raw) ? raw : 'daily';
  });
  const switchTimelineTab = (v) => {
    setTimelineTab(v);
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem('qqq_timeline_tab', v); } catch { /* ignore */ }
    }
  };

  return (
    <>
      <PhaseHeader
        num="📚"
        title="Library"
        subtitle="Timeline · Curated · Patterns · Setup"
        phaseKey="library"
      />

      {/* Pill nav — 2 pills */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {LIBRARY_PILLS.map((p) => {
          const active = pill === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => switchPill(p.key)}
              style={{
                padding: '10px 18px',
                background: active ? 'rgba(77,184,184,.12)' : 'var(--surface-2)',
                color: active ? 'var(--qqq-teal)' : 'var(--qqq-fg-2)',
                border: `1px solid ${active ? 'var(--qqq-teal)' : 'var(--qqq-b2)'}`,
                borderRadius: 8,
                cursor: 'pointer', textAlign: 'left', minWidth: 220,
                fontFamily: 'inherit',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.02em' }}>
                {p.label}
              </div>
              <div style={{
                fontSize: 10, color: 'var(--qqq-fg-3)',
                marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                {p.sub}
              </div>
            </button>
          );
        })}
      </div>

      {pill === 'timeline' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '60fr 40fr',
          gap: 24,
          alignItems: 'start',
        }}>
          {/* LEFT 60% — 4 read tabs */}
          <div>
            <TimelineTabNav tabs={TIMELINE_TABS} active={timelineTab} onChange={switchTimelineTab} />
            <div style={{ marginTop: 14 }}>
              {timelineTab === 'daily'     && <LibraryTimelineView />}
              {timelineTab === 'files'     && <LibraryFilesView />}
              {timelineTab === 'finalized' && <LibraryFinalizedView />}
              {timelineTab === 'seven_day' && <LibrarySevenDayView />}
            </div>
          </div>
          {/* RIGHT 40% — work column */}
          <div>
            <EndOfDayReviewCard />
          </div>
        </div>
      )}

      {pill === 'curated'   && <LibraryCuratedView />}
      {pill === 'patterns'  && <LibraryPatternsView />}
      {pill === 'setup'     && <LibrarySetupView />}
    </>
  );
}

// ─── TimelineTabNav — underline-style inner tabs ─────────
function TimelineTabNav({ tabs, active, onChange }) {
  return (
    <div style={{
      display: 'flex', gap: 2,
      borderBottom: '1px solid var(--qqq-b2)',
    }}>
      {tabs.map((t) => {
        const isActive = active === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            style={{
              padding: '10px 16px 11px',
              background: 'transparent',
              color: isActive ? 'var(--qqq-teal)' : 'var(--qqq-fg-2)',
              border: 0,
              borderBottom: isActive ? '2px solid var(--qqq-teal)' : '2px solid transparent',
              marginBottom: -1,
              fontSize: 13,
              fontWeight: isActive ? 700 : 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
            }}
          >
            <span>{t.label}</span>
            <span style={{ fontSize: 9, color: 'var(--qqq-fg-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>
              {t.sub}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Files tab (new) ─────────────────────────────────────
// Image gallery pulled from chart_analysis._meta.image_url
// and charts.*._meta.image_url (Commit 67 multi-chart shape).
function LibraryFilesView() {
  const data = useQqqData();
  const archive = Array.isArray(data._chartArchive) ? data._chartArchive : [];

  const items = useMemo(() => {
    const out = [];
    for (const r of archive) {
      const legacyUrl = r.chart_analysis?._meta?.image_url;
      if (legacyUrl) {
        out.push({
          key: `${r.brief_date}-legacy`,
          url: legacyUrl,
          date: r.brief_date,
          kind: 'price',
          pattern: r.chart_analysis?.pattern || null,
        });
      }
      if (r.charts) {
        for (const [ck, cv] of Object.entries(r.charts)) {
          const u = cv?._meta?.image_url;
          if (u && u !== legacyUrl) {
            out.push({
              key:  `${r.brief_date}-${ck}`,
              url:  u,
              date: r.brief_date,
              kind: ck.includes('option') ? 'option' : 'price',
            });
          }
        }
      }
    }
    return out.sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [archive]);

  const [lightbox, setLightbox] = useState(null);

  if (items.length === 0) {
    return (
      <LibraryEmptyState
        label="Files"
        body="Nothing here yet. Drop a chart in the End-of-Day Review on the right and it shows up as a thumbnail on next load."
      />
    );
  }

  return (
    <>
      <div style={{
        fontSize: 10, color: 'var(--qqq-fg-3)',
        textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
        marginBottom: 10,
      }}>
        {items.length} image{items.length === 1 ? '' : 's'} · newest first
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: 10,
      }}>
        {items.map((it) => (
          <button
            key={it.key}
            type="button"
            onClick={() => setLightbox(it)}
            style={{
              padding: 0, background: 'transparent', border: 0, cursor: 'pointer',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}
          >
            <div style={{
              width: '100%', aspectRatio: '16 / 10',
              background: 'var(--surface-3)',
              border: `1px solid ${it.kind === 'option' ? 'var(--qqq-amber, #d4956a)' : 'var(--qqq-teal)'}`,
              borderRadius: 4, overflow: 'hidden',
            }}>
              <img src={it.url} alt="" loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </div>
            <div style={{
              fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
              color: 'var(--qqq-fg-3)',
              display: 'flex', justifyContent: 'space-between',
            }}>
              <span>{it.date}</span>
              <span style={{ color: it.kind === 'option' ? 'var(--qqq-amber, #d4956a)' : 'var(--qqq-teal)' }}>
                {it.kind}
              </span>
            </div>
            {it.pattern && (
              <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {it.pattern}
              </div>
            )}
          </button>
        ))}
      </div>
      {lightbox && (
        <LibraryLightbox item={lightbox} onClose={() => setLightbox(null)} />
      )}
    </>
  );
}

function LibraryLightbox({ item, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(7,7,10,0.88)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 40, cursor: 'pointer',
      }}
    >
      <div onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '90vw', maxHeight: '90vh', cursor: 'default' }}
      >
        <div style={{
          marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          color: 'var(--qqq-fg-1)', fontSize: 13,
        }}>
          <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {item.date} · <span style={{ color: item.kind === 'option' ? 'var(--qqq-amber, #d4956a)' : 'var(--qqq-teal)' }}>{item.kind}</span>
          </span>
          <button type="button" onClick={onClose}
            style={{
              background: 'transparent', border: 0, color: 'var(--qqq-fg-2)',
              fontSize: 20, cursor: 'pointer', fontFamily: 'inherit',
            }}
            title="Close (Esc)"
          >
            ✕
          </button>
        </div>
        <img src={item.url} alt=""
          style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 4 }}
        />
      </div>
    </div>
  );
}

// ─── Finalized tab (Commit 88) ───────────────────────────
// Lists only canonical-locked day records. Server filter via
// /api/workstations/qqq-canonical-list. Highest-trust records.
function LibraryFinalizedView() {
  const data = useQqqData();
  const workstationId = data._workstationId;
  const userId = data._userId;

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!workstationId || !userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ workstation_id: workstationId, user_id: userId, limit: '100' });
        const res = await fetch(`/api/workstations/qqq-canonical-list?${qs}`);
        const payload = await res.json();
        if (cancelled) return;
        if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
        else { setRecords(payload.records || []); setError(null); }
      } catch (e) { if (!cancelled) setError(e?.message || 'load failed'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [workstationId, userId]);

  if (loading) return <div style={{ padding: 12, color: 'var(--qqq-fg-3)', fontSize: 12 }}>Loading…</div>;
  if (error)   return <LibraryEmptyState label="Finalized — error" body={error} />;
  if (records.length === 0) {
    return (
      <LibraryEmptyState
        label="Finalized"
        body={
          <>
            No canonical day records yet. Lock a day via the End-of-Day Review → Section 6 (<span style={{ fontFamily: 'JetBrains Mono, monospace' }}>★ Lock as canonical</span>) and it&apos;ll land here as the highest-trust record for that day.
          </>
        }
      />
    );
  }

  return (
    <>
      <div style={{
        fontSize: 10, color: 'var(--qqq-fg-3)',
        textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
        marginBottom: 10,
      }}>
        {records.length} canonical record{records.length === 1 ? '' : 's'} · newest first
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {records.map((r) => {
          const isOpen = expandedId === r.id;
          const chartPattern = r.chart_analysis?.pattern
            || r.charts?.price_pair?.method_lens?.setup_name
            || r.charts?.price_15m?.pattern
            || null;
          return (
            <div key={r.id} style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--qqq-b2)',
              borderLeft: '3px solid var(--qqq-sage)',
              borderRadius: 6,
              overflow: 'hidden',
            }}>
              <button
                type="button"
                onClick={() => setExpandedId(isOpen ? null : r.id)}
                style={{
                  width: '100%', background: 'transparent', border: 0,
                  padding: '12px 14px',
                  display: 'grid',
                  gridTemplateColumns: '80px 1fr auto 20px',
                  gap: 12, alignItems: 'center',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: 'var(--qqq-fg-1)', fontWeight: 600 }}>
                  {r.brief_date}
                </span>
                <span style={{
                  fontSize: 13, color: 'var(--qqq-fg-1)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {chartPattern || '(canonical)'}
                </span>
                <span style={{
                  fontSize: 10, color: 'var(--qqq-sage)', fontWeight: 700,
                  fontFamily: 'JetBrains Mono, monospace',
                  padding: '2px 8px', borderRadius: 3,
                  background: 'rgba(122,158,130,.12)',
                  border: '1px solid var(--qqq-sage)',
                }}>
                  ★ v{r.canonical_version} · {fmtRelative(r.canonical_locked_at)}
                </span>
                <span style={{
                  color: 'var(--qqq-fg-3)', fontSize: 14,
                  transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s',
                }}>›</span>
              </button>
              {isOpen && (
                <div style={{
                  padding: '10px 14px 14px',
                  borderTop: '1px solid var(--qqq-b1)',
                  background: 'rgba(122,158,130,.04)',
                  fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.65,
                  whiteSpace: 'pre-wrap',
                }}>
                  {r.canonical_draft || '(locked but empty)'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ─── 7-Day Shift Read (Commit 88) ────────────────────────
// Lists past weekly reads + on-demand generate button.
// The cron at /api/cron/qqq-weekly-read generates one per
// workstation every Sunday automatically.
function LibrarySevenDayView() {
  const data = useQqqData();
  const aiName = useAiName();
  const workstationId = data._workstationId;
  const userId = data._userId;

  const [reads, setReads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    if (!workstationId || !userId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ workstation_id: workstationId, user_id: userId, limit: '20' });
      const res = await fetch(`/api/workstations/qqq-weekly-read?${qs}`);
      const payload = await res.json();
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      else { setReads(payload.reads || []); setError(null); }
    } catch (e) { setError(e?.message || 'load failed'); }
    finally { setLoading(false); }
  }, [workstationId, userId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (generating) return;
    setGenerating(true);
    setGenError(null);
    try {
      const res = await fetch('/api/workstations/qqq-weekly-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workstation_id: workstationId, user_id: userId, trigger: 'on_demand' }),
      });
      const payload = await res.json();
      if (!res.ok) setGenError(payload?.error || 'generate failed');
      else await load();
    } catch (e) { setGenError(e?.message || 'generate failed'); }
    finally { setGenerating(false); }
  };

  return (
    <>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 12, gap: 12,
      }}>
        <div style={{
          fontSize: 10, color: 'var(--qqq-fg-3)',
          textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
        }}>
          {loading ? 'loading…' : `${reads.length} week${reads.length === 1 ? '' : 's'} · newest first`}
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={generating}
          style={{
            padding: '8px 14px',
            background: generating ? 'var(--surface-3)' : 'var(--qqq-purple)',
            color: generating ? 'var(--qqq-fg-3)' : 'var(--surface-0)',
            border: 0, borderRadius: 4,
            fontSize: 12, fontWeight: 700,
            cursor: generating ? 'default' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {generating ? `${aiName} is thinking…` : `↻ Generate this week`}
        </button>
      </div>
      {genError && (
        <div style={{ marginBottom: 10, fontSize: 11, color: 'var(--qqq-terra)' }}>
          {genError}
        </div>
      )}
      {error && !loading && (
        <LibraryEmptyState label="7-Day Shift Read — error" body={error} />
      )}
      {!loading && !error && reads.length === 0 && (
        <LibraryEmptyState
          label="7-Day Shift Read"
          body={
            <>
              {aiName}&apos;s rolling weekly synthesis — reads the last 7 days of canonical records + open observations + hypothesis deltas + trades, and surfaces what&apos;s compounding, what&apos;s breaking, what to watch.
              <br /><br />
              Click <span style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--qqq-purple)' }}>↻ Generate this week</span> above. The Sunday 6pm ET cron also auto-generates one per week.
            </>
          }
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {reads.map((r) => (
          <WeeklyReadCard
            key={r.id}
            read={r}
            isOpen={expandedId === r.id}
            onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)}
          />
        ))}
      </div>
    </>
  );
}

function WeeklyReadCard({ read, isOpen, onToggle }) {
  const r = read.report || {};
  const theme = r.theme || '(no theme)';
  const sections = [
    { key: 'patterns_emerged', label: 'Patterns emerged',  tone: 'teal'   },
    { key: 'hypotheses_moved', label: 'Hypotheses moved',  tone: 'gold'   },
    { key: 'novel_recurring',  label: 'Novel recurring',   tone: 'purple' },
    { key: 'costly_mistakes',  label: 'Costly mistakes',   tone: 'terra'  },
    { key: 'next_week',        label: 'Next week',         tone: 'sage'   },
  ];
  const toneColor = (t) => ({ teal: 'var(--qqq-teal)', gold: 'var(--qqq-gold)', purple: 'var(--qqq-purple)', terra: 'var(--qqq-terra)', sage: 'var(--qqq-sage)' })[t];

  return (
    <div style={{
      background: 'var(--surface-2)',
      border: '1px solid var(--qqq-b2)',
      borderLeft: '3px solid var(--qqq-purple)',
      borderRadius: 6,
      overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%', background: 'transparent', border: 0,
          padding: '12px 16px', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          marginBottom: 4, gap: 10,
        }}>
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--qqq-fg-3)' }}>
            {read.week_start} → {read.week_end}
          </span>
          <span style={{
            fontSize: 9, color: 'var(--qqq-fg-3)', fontFamily: 'JetBrains Mono, monospace',
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            {read.trigger} · {fmtRelative(read.generated_at)} · {read.source_count} canonical
          </span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.55 }}>
          {theme}
        </div>
      </button>
      {isOpen && (
        <div style={{
          padding: '4px 16px 16px',
          borderTop: '1px solid var(--qqq-b1)',
          background: 'rgba(155,143,196,.03)',
        }}>
          {sections.map((s) => {
            const val = r[s.key];
            if (!Array.isArray(val) || val.length === 0) return null;
            return (
              <div key={s.key} style={{ marginTop: 12 }}>
                <div style={{
                  fontSize: 10, color: toneColor(s.tone),
                  textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
                  marginBottom: 4,
                }}>
                  {s.label}
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: 'var(--qqq-fg-1)', lineHeight: 1.55 }}>
                  {val.map((item, i) => <li key={i} style={{ marginBottom: 3 }}>{item}</li>)}
                </ul>
              </div>
            );
          })}
          {r.parse_error && (
            <div style={{ marginTop: 10, fontSize: 11, color: 'var(--qqq-terra)' }}>
              Model returned unparseable output.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LibraryEmptyState({ label, body }) {
  return (
    <div style={{
      padding: '24px 20px',
      background: 'var(--surface-2)',
      border: '1px dashed var(--qqq-b2)',
      borderRadius: 8,
    }}>
      <div style={{
        fontSize: 10, color: 'var(--qqq-teal)',
        textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
        marginBottom: 8,
      }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.6 }}>
        {body}
      </div>
    </div>
  );
}

// ─── Curated pill (Articles + Collections merged) ───────
// Renders the existing Articles clipper (top) + Collections
// list (below) as one stacked surface. Articles stay their
// own DB table; Collections stay theirs; both work unchanged
// and cross-file via the existing AddToFolderAction on each
// article row.
function LibraryCuratedView() {
  return (
    <div>
      <LibraryArticlesView />
      <div style={{ marginTop: 24 }}>
        <LibraryFoldersView />
      </div>
    </div>
  );
}

// ─── Patterns pill — QQQ 0DTE TOS pattern reference ──────
// Embeds /public/qqq-patterns.html in an iframe so the Tim-
// authored visual reference is preserved 1:1 (custom palette,
// SVG candle/RSI charts, pattern stat ribbons). Re-skinning to
// Shift tokens would lose the carefully-tuned chart legibility.
// To swap in a new pattern doc: replace public/qqq-patterns.html.
function LibraryPatternsView() {
  return (
    <div style={{
      background: 'var(--surface-2)',
      border: '1px solid var(--qqq-b2)',
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      <iframe
        src="/qqq-patterns.html"
        title="QQQ 0DTE Pattern Guide — TOS Visual Reference"
        style={{
          width: '100%',
          height: 'calc(100vh - 240px)',
          minHeight: 600,
          border: 0,
          display: 'block',
          background: '#0a0d18',
        }}
      />
    </div>
  );
}

// ─── Setup pill — A+/B/C tier reference (5×3 cards) ──────
// Static visual reference for the tier grading rubric: 5
// patterns × 3 conviction tiers. Same iframe pattern as
// Patterns — preserves the Tim-authored palette + layout 1:1.
// Live tier-tracking lives on the Today tab (PatternTrackerPanel)
// — this pill is the printable/scannable rules card.
// To swap in a new tier doc: replace public/qqq-setup.html.
function LibrarySetupView() {
  return (
    <div style={{
      background: 'var(--surface-2)',
      border: '1px solid var(--qqq-b2)',
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      <iframe
        src="/qqq-setup.html"
        title="QQQ 0DTE Pattern Grading — A+/B/C Tier Reference"
        style={{
          width: '100%',
          height: 'calc(100vh - 240px)',
          minHeight: 600,
          border: 0,
          display: 'block',
          background: '#0a0d18',
        }}
      />
    </div>
  );
}

// Commit 87 — legacy Library view migration. New valid set:
// 'timeline' | 'curated'. Everything else maps to 'timeline'.
// Articles/Collections users land on 'curated'.
function migrateLegacyLibraryView(raw) {
  const VALID = new Set(['timeline', 'curated', 'patterns', 'setup']);
  if (VALID.has(raw)) return raw;
  if (raw === 'articles' || raw === 'collections' || raw === 'folders') return 'curated';
  return 'timeline';
}

// ═══════════════════════════════════════════════════════════
// LIBRARY FOLDERS VIEW — Commit 63
// ═══════════════════════════════════════════════════════════
// Folder list + create/delete + click-to-expand + Ask Jarvis per
// folder. Items inside use the same ChartAnalysis / journal / etc
// renderers from the Analysis view.
function LibraryFoldersView() {
  const data = useQqqData();
  const workstationId = data._workstationId;
  const userId = data._userId;
  const aiName = useAiName();

  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newColor, setNewColor] = useState('teal');
  const [expandedId, setExpandedId] = useState(null);
  const [error, setError] = useState(null);

  // Load folders on mount
  useEffect(() => {
    if (!workstationId || !userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/workstations/qqq-folders?workstation_id=${workstationId}&user_id=${userId}`);
        const payload = await res.json();
        if (!cancelled) {
          if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
          else setFolders(payload.folders || []);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) { setError(e?.message || 'load failed'); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [workstationId, userId]);

  const createFolder = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const res = await fetch('/api/workstations/qqq-folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstationId, user_id: userId,
          name: newName, description: newDesc || null, color: newColor,
        }),
      });
      const payload = await res.json();
      if (!res.ok) { setError(payload?.error || 'create failed'); return; }
      setFolders((f) => [payload.folder, ...f]);
      setNewName(''); setNewDesc(''); setNewColor('teal');
    } catch (e) { setError(e?.message || 'create failed'); }
    finally { setCreating(false); }
  };

  const deleteFolder = async (id) => {
    if (!confirm('Delete this collection? Items inside are unfiled but not deleted.')) return;
    try {
      const res = await fetch(`/api/workstations/qqq-folders?id=${id}`, { method: 'DELETE' });
      if (res.ok) {
        setFolders((f) => f.filter((x) => x.id !== id));
        if (expandedId === id) setExpandedId(null);
      }
    } catch { /* ignore */ }
  };

  // Commit 80a — set or clear which chart-type this collection
  // auto-receives. Server atomically swaps the role (clears any
  // other folder that had it); we update local state to match.
  const setFolderDefault = async (id, role) => {
    try {
      const res = await fetch('/api/workstations/qqq-folders', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, default_for: role }),
      });
      if (!res.ok) return;
      // Optimistic: the one we just set gets the role; any other
      // folder that had it gets cleared.
      setFolders((arr) => arr.map((f) => {
        if (f.id === id) return { ...f, default_for: role };
        if (role && f.default_for === role) return { ...f, default_for: null };
        return f;
      }));
    } catch { /* ignore */ }
  };

  const colorStyle = (c) => ({
    sage:   'var(--qqq-sage)', gold: 'var(--qqq-gold)',
    purple: 'var(--qqq-purple)', terra: 'var(--qqq-terra)', teal: 'var(--qqq-teal)',
  })[c] || 'var(--qqq-teal)';

  return (
    <DataCard
      label="Your Collections · curated · scoped Q&A"
      source={`${folders.length} collection${folders.length === 1 ? '' : 's'}`}
      tone="teal"
    >
      {/* Create form */}
      <div style={{
        padding: '14px 18px', borderBottom: '1px solid var(--qqq-b1)',
      }}>
        <div style={{
          fontSize: 11, color: 'var(--qqq-teal)',
          textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
          marginBottom: 10,
        }}>
          + New collection
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value.slice(0, 80))}
            placeholder="Folder name (e.g. FOMC research)"
            style={{
              flex: 1, minWidth: 200, padding: '8px 12px',
              background: 'var(--surface-3)', color: 'var(--qqq-fg-1)',
              border: '1px solid var(--qqq-b2)', borderRadius: 4,
              fontFamily: 'inherit', fontSize: 13,
            }}
          />
          <select
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            style={{
              padding: '8px 12px',
              background: 'var(--surface-3)', color: colorStyle(newColor),
              border: `1px solid ${colorStyle(newColor)}`, borderRadius: 4,
              fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
            }}
          >
            <option value="teal">Teal</option>
            <option value="sage">Sage</option>
            <option value="gold">Gold</option>
            <option value="purple">Purple</option>
            <option value="terra">Terra</option>
          </select>
          <button
            type="button"
            onClick={createFolder}
            disabled={!newName.trim() || creating}
            style={{
              padding: '8px 16px',
              background: newName.trim() ? 'var(--qqq-teal)' : 'var(--surface-3)',
              color: newName.trim() ? 'var(--surface-0)' : 'var(--qqq-fg-3)',
              border: 0, borderRadius: 4,
              fontSize: 12, fontWeight: 600,
              cursor: newName.trim() ? 'pointer' : 'not-allowed',
            }}
          >
            {creating ? 'Creating…' : 'Create'}
          </button>
        </div>
        <input
          type="text"
          value={newDesc}
          onChange={(e) => setNewDesc(e.target.value.slice(0, 500))}
          placeholder="Description (optional)"
          style={{
            width: '100%', padding: '8px 12px',
            background: 'var(--surface-3)', color: 'var(--qqq-fg-1)',
            border: '1px solid var(--qqq-b2)', borderRadius: 4,
            fontFamily: 'inherit', fontSize: 12,
          }}
        />
        {error && (
          <div style={{
            marginTop: 8, padding: '6px 10px',
            background: 'rgba(196,96,80,.08)', borderLeft: '2px solid var(--qqq-terra)',
            fontSize: 11, color: 'var(--qqq-terra)',
          }}>
            {error}
          </div>
        )}
      </div>

      {/* Folder list */}
      <div style={{ padding: '8px 0' }}>
        {loading && (
          <div style={{ padding: '16px 18px', color: 'var(--qqq-fg-2)', fontSize: 13 }}>
            Loading…
          </div>
        )}
        {!loading && folders.length === 0 && (
          <div style={{
            padding: '20px 18px', color: 'var(--qqq-fg-2)',
            fontSize: 13, lineHeight: 1.6, fontStyle: 'italic',
          }}>
            No collections yet. Create your first — e.g. &ldquo;FOMC research&rdquo;,
            &ldquo;My best trades&rdquo;, &ldquo;Loss autopsies&rdquo;. Then
            file items from Timeline or Articles into them.
            Each collection gets a scoped &ldquo;Ask {aiName}&rdquo; — {aiName} reads
            the contents and answers your questions using just that set.
          </div>
        )}
        {[...folders].sort((a, b) => {
          // Commit 87 — pin the default "Articles" collection first
          // (auto-created by the articles API on first clip). All
          // other collections keep their server-side order
          // (updated_at desc).
          if (a.name === 'Articles') return -1;
          if (b.name === 'Articles') return 1;
          return 0;
        }).map((f) => (
          <FolderRow
            key={f.id}
            folder={f}
            expanded={expandedId === f.id}
            onExpand={() => setExpandedId(expandedId === f.id ? null : f.id)}
            onDelete={() => deleteFolder(f.id)}
            onSetDefault={(role) => setFolderDefault(f.id, role)}
          />
        ))}
      </div>
    </DataCard>
  );
}

function FolderRow({ folder, expanded, onExpand, onDelete, onSetDefault }) {
  const color = ({
    sage: 'var(--qqq-sage)', gold: 'var(--qqq-gold)',
    purple: 'var(--qqq-purple)', terra: 'var(--qqq-terra)', teal: 'var(--qqq-teal)',
  })[folder.color] || 'var(--qqq-teal)';

  const [askingOpen, setAskingOpen] = useState(false);

  // Commit 80a — badge label for the default-for role (so Tim
  // can see which collection auto-receives which chart type at
  // a glance, even without expanding the row).
  const defaultBadge = folder.default_for === 'price_chart'
    ? '⭐ price charts'
    : folder.default_for === 'option_chart'
      ? '⭐ option charts'
      : null;

  return (
    <div style={{ borderBottom: '1px solid var(--qqq-b1)' }}>
      <button
        type="button"
        onClick={onExpand}
        style={{
          width: '100%', background: 'transparent', border: 0,
          padding: '12px 18px',
          display: 'grid',
          gridTemplateColumns: '32px 1fr auto auto auto 24px',
          gap: 12, alignItems: 'center',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <div style={{
          width: 28, height: 28, borderRadius: 6,
          background: `rgba(${({ sage: '122,158,130', gold: '200,169,110', purple: '155,143,196', terra: '196,96,80', teal: '77,184,184' })[folder.color] || '77,184,184'},.18)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14,
        }}>
          {folder.icon || '📁'}
        </div>
        <div>
          <div style={{ fontSize: 14, color: 'var(--qqq-fg-1)', fontWeight: 600 }}>
            {folder.name}
          </div>
          {folder.description && (
            <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', marginTop: 2 }}>
              {folder.description}
            </div>
          )}
        </div>
        {defaultBadge ? (
          <div style={{
            fontSize: 10, color: 'var(--qqq-gold)',
            fontWeight: 700, letterSpacing: '0.04em',
            padding: '2px 8px', borderRadius: 3,
            background: 'rgba(200,169,110,.1)',
            border: '1px solid rgba(200,169,110,.3)',
          }}>
            {defaultBadge}
          </div>
        ) : <div />}
        <div style={{
          fontSize: 11, color, fontWeight: 700,
          padding: '3px 10px', borderRadius: 10,
          background: `rgba(${({ sage: '122,158,130', gold: '200,169,110', purple: '155,143,196', terra: '196,96,80', teal: '77,184,184' })[folder.color] || '77,184,184'},.1)`,
          border: `1px solid ${color}`,
        }}>
          {folder.item_count || 0} {(folder.item_count === 1) ? 'item' : 'items'}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          style={{
            background: 'transparent', border: 0, cursor: 'pointer',
            color: 'var(--qqq-fg-3)', fontSize: 14, padding: 4,
          }}
          title="Delete collection"
        >
          ✕
        </button>
        <div style={{
          color: 'var(--qqq-fg-3)', fontSize: 14,
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s',
        }}>
          ›
        </div>
      </button>
      {expanded && (
        <FolderExpandedView
          folder={folder}
          askingOpen={askingOpen}
          setAskingOpen={setAskingOpen}
          onSetDefault={onSetDefault}
        />
      )}
    </div>
  );
}

function FolderExpandedView({ folder, askingOpen, setAskingOpen, onSetDefault }) {
  const aiName = useAiName();
  const [items, setItems] = useState(null);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Commit 79c — lifted to a callback so the picker can refresh
  // this folder's items after adding.
  const reloadItems = useCallback(async () => {
    try {
      const res = await fetch(`/api/workstations/qqq-folders?id=${folder.id}`);
      const payload = await res.json();
      if (res.ok) setItems(payload.items || []);
    } catch { /* ignore */ }
  }, [folder.id]);

  useEffect(() => {
    let cancelled = false;
    reloadItems().catch(() => {});
    return () => { cancelled = true; };
  }, [reloadItems]);

  const askFolder = async () => {
    if (!question.trim()) return;
    setAsking(true);
    setAnswer(null);
    setError(null);
    try {
      const res = await fetch('/api/workstations/qqq-folders?action=ask_jarvis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder_id: folder.id, question }),
      });
      const payload = await res.json();
      if (!res.ok) setError(payload?.error || 'ask failed');
      else setAnswer(payload.answer);
    } catch (e) { setError(e?.message || 'ask failed'); }
    finally { setAsking(false); }
  };

  return (
    <div style={{
      padding: '10px 18px 16px',
      background: 'rgba(77,184,184,.03)',
    }}>
      {/* Commit 80a — auto-file settings. Marking this collection as
          the default destination for a chart type means every chart
          upload of that type files into here automatically — zero
          additional clicks after drop. One collection per type per
          workstation; setting here clears the role on whichever
          collection had it before. */}
      {onSetDefault && (
        <div style={{
          marginBottom: 10,
          padding: '8px 10px',
          background: 'rgba(200,169,110,.04)',
          border: '1px solid rgba(200,169,110,.2)',
          borderRadius: 4,
        }}>
          <div style={{
            fontSize: 10, color: 'var(--qqq-gold)',
            textTransform: 'uppercase', letterSpacing: '0.08em',
            fontWeight: 700, marginBottom: 6,
          }}>
            Auto-file uploads here
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { role: 'price_chart',  label: 'Price charts' },
              { role: 'option_chart', label: 'Option charts' },
            ].map(({ role, label }) => {
              const active = folder.default_for === role;
              return (
                <button
                  key={role}
                  type="button"
                  onClick={() => onSetDefault(active ? null : role)}
                  style={{
                    padding: '5px 10px',
                    background: active ? 'var(--qqq-gold)' : 'transparent',
                    color: active ? 'var(--surface-0)' : 'var(--qqq-fg-2)',
                    border: `1px solid ${active ? 'var(--qqq-gold)' : 'var(--qqq-b2)'}`,
                    borderRadius: 4,
                    fontSize: 11, fontWeight: active ? 700 : 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {active ? '✓ ' : '+ '}{label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Commit 79c — primary action: add items straight from inside
          the collection. This is the fix for "how do I add files to
          folders" — the previous UX required navigating away to
          Timeline/Articles, finding a row, expanding it, then using
          + Add to collection. Now it's one click from here. */}
      <div style={{ marginBottom: 10 }}>
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          style={{
            padding: '7px 14px',
            background: pickerOpen ? 'rgba(77,184,184,.12)' : 'var(--qqq-teal)',
            color: pickerOpen ? 'var(--qqq-teal)' : 'var(--surface-0)',
            border: pickerOpen ? '1px solid var(--qqq-teal)' : 0,
            borderRadius: 4,
            fontSize: 12, fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {pickerOpen ? '× Close picker' : '+ Add items to this collection'}
        </button>
      </div>
      {pickerOpen && (
        <FolderItemPicker
          folder={folder}
          existingItems={items || []}
          onAdded={async () => {
            await reloadItems();
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {/* Item list */}
      {items == null ? (
        <div style={{ fontSize: 12, color: 'var(--qqq-fg-3)', padding: 8 }}>Loading items…</div>
      ) : items.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--qqq-fg-2)', padding: 8, fontStyle: 'italic' }}>
          No items yet. Click &ldquo;+ Add items to this collection&rdquo; above
          to pick charts, analyses, journals, or articles to file here.
        </div>
      ) : (
        <div>
          <div style={{
            fontSize: 10, color: 'var(--qqq-fg-3)',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            fontWeight: 600, marginBottom: 6,
          }}>
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </div>
          {items.slice(0, 10).map((i) => (
            <div key={i.id} style={{
              padding: '6px 10px',
              marginBottom: 4,
              background: 'var(--surface-3)',
              borderLeft: '2px solid var(--qqq-teal)', borderRadius: 3,
              fontSize: 12, color: 'var(--qqq-fg-2)',
            }}>
              <span style={{
                fontSize: 9, padding: '1px 6px', borderRadius: 2,
                background: 'var(--qqq-s1)', color: 'var(--qqq-teal)',
                textTransform: 'uppercase', letterSpacing: '0.05em',
                marginRight: 8, fontWeight: 600,
              }}>
                {i.item_type.replace(/_/g, ' ')}
              </span>
              <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {i.item_ref}
              </span>
              {i.note && <span style={{ marginLeft: 8, fontStyle: 'italic' }}>— {i.note}</span>}
            </div>
          ))}
          {items.length > 10 && (
            <div style={{ fontSize: 11, color: 'var(--qqq-fg-3)', marginTop: 6 }}>
              … and {items.length - 10} more
            </div>
          )}
        </div>
      )}

      {/* Scoped Q&A — reads only this folder's items */}
      <div style={{ marginTop: 14 }}>
        {!askingOpen && (
          <button type="button" onClick={() => setAskingOpen(true)} style={{
            padding: '8px 14px',
            background: 'var(--qqq-purple)', color: 'var(--surface-0)',
            border: 0, borderRadius: 4,
            fontSize: 12, fontWeight: 700,
            cursor: 'pointer',
          }}>
            Ask {aiName} about this collection
          </button>
        )}
        {askingOpen && (
          <div style={{
            padding: '10px 12px',
            background: 'rgba(155,143,196,.06)',
            borderLeft: '3px solid var(--qqq-purple)', borderRadius: 4,
          }}>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value.slice(0, 2000))}
              placeholder={`Ask anything about "${folder.name}"…  e.g. "What's the common theme across these items?" or "What patterns do they share?"`}
              rows={3}
              style={{
                width: '100%', padding: '8px 10px',
                background: 'var(--surface-3)', color: 'var(--qqq-fg-1)',
                border: '1px solid var(--qqq-b2)', borderRadius: 4,
                fontFamily: 'inherit', fontSize: 13, resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="button" onClick={askFolder}
                disabled={asking || !question.trim()}
                style={{
                  padding: '6px 14px',
                  background: question.trim() ? 'var(--qqq-purple)' : 'var(--surface-3)',
                  color: question.trim() ? 'var(--surface-0)' : 'var(--qqq-fg-3)',
                  border: 0, borderRadius: 4, fontSize: 12, fontWeight: 700,
                  cursor: question.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {asking ? 'Thinking…' : 'Ask'}
              </button>
              <button type="button" onClick={() => { setAskingOpen(false); setAnswer(null); setQuestion(''); }}
                style={{
                  padding: '6px 14px',
                  background: 'transparent', color: 'var(--qqq-fg-2)',
                  border: '1px solid var(--qqq-b2)', borderRadius: 4,
                  fontSize: 12, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>

            {error && (
              <div style={{
                marginTop: 8, padding: '6px 10px',
                background: 'rgba(196,96,80,.08)',
                color: 'var(--qqq-terra)', fontSize: 12, borderRadius: 3,
              }}>
                {error}
              </div>
            )}
            {answer && (
              <div style={{
                marginTop: 10, padding: '10px 12px',
                background: 'var(--surface-3)',
                borderLeft: '3px solid var(--qqq-purple)', borderRadius: 4,
                fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.65,
                whiteSpace: 'pre-wrap',
              }}>
                {answer}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// FOLDER ITEM PICKER — Commit 79c
// ═══════════════════════════════════════════════════════════
// Inline picker that opens from inside an expanded collection.
// Groups every eligible item by type (charts / analyses /
// journals / articles), shows which ones are already in the
// collection (disabled + "added"), and lets the user check
// multiple + submit in one go. Fixes the "how do I add files
// to folders" discoverability problem from Commit 77.
// ═══════════════════════════════════════════════════════════
function FolderItemPicker({ folder, existingItems, onAdded, onClose }) {
  const data = useQqqData();
  const workstationId = data._workstationId;
  const userId = data._userId;
  const archive = Array.isArray(data._chartArchive) ? data._chartArchive : [];

  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [activeType, setActiveType] = useState('chart_analysis');
  const [selected, setSelected] = useState({}); // key = `${type}|${ref}`
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch articles once. Other types come from useQqqData.
  useEffect(() => {
    if (!workstationId || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/workstations/qqq-articles?workstation_id=${workstationId}&user_id=${userId}`);
        const payload = await res.json();
        if (!cancelled && res.ok) setArticles(payload.articles || []);
      } catch { /* ignore */ }
      finally { if (!cancelled) setArticlesLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [workstationId, userId]);

  // Existing-item index — (type|ref) -> true
  const existingSet = useMemo(() => {
    const s = new Set();
    for (const i of existingItems) s.add(`${i.item_type}|${i.item_ref}`);
    return s;
  }, [existingItems]);

  // Available items per type
  const pools = useMemo(() => {
    const charts    = archive.filter((r) => r.chart_analysis).map((r) => ({
      ref: r.brief_date,
      title: r.chart_analysis?.pattern || '(unnamed pattern)',
      sub:   `CHOP ${r.chart_analysis?.chop_score?.score || '—'} · hit $2.00 ${r.chart_analysis?.path_to_2_00?.hit === true ? '✓' : r.chart_analysis?.path_to_2_00?.hit === false ? '✗' : '—'}`,
      date: r.brief_date,
    }));
    const analyses  = archive.filter((r) => r.context_brief).map((r) => ({
      ref: r.brief_date,
      title: r.context_brief?.insight || 'Context brief',
      sub:   `regime ${r.context_brief?.context?.regime || '—'} · bias ${r.context_brief?.action?.bias || '—'}`,
      date: r.brief_date,
    }));
    const journals  = archive.filter((r) => r.notes).map((r) => ({
      ref: r.brief_date,
      title: r.notes.length > 80 ? r.notes.slice(0, 80) + '…' : r.notes,
      sub:   null,
      date: r.brief_date,
    }));
    const articleItems = articles.map((a) => ({
      ref: a.id,
      title: a.title || a.url,
      sub:   `${a.source_domain || '—'}${a.linked_brief_date ? ' · ' + a.linked_brief_date : ''}${Array.isArray(a.tags) && a.tags.length ? ' · #' + a.tags[0] : ''}`,
      date: a.linked_brief_date || a.created_at?.slice(0, 10) || '',
    }));
    return {
      chart_analysis: charts.sort((a, b) => String(b.date).localeCompare(String(a.date))),
      context_brief:  analyses.sort((a, b) => String(b.date).localeCompare(String(a.date))),
      journal:        journals.sort((a, b) => String(b.date).localeCompare(String(a.date))),
      article:        articleItems.sort((a, b) => String(b.date).localeCompare(String(a.date))),
    };
  }, [archive, articles]);

  const typeTabs = [
    { key: 'chart_analysis', label: 'Charts',   tone: 'teal',   n: pools.chart_analysis.length },
    { key: 'context_brief',  label: 'Analyses', tone: 'sage',   n: pools.context_brief.length },
    { key: 'journal',        label: 'Journals', tone: 'gold',   n: pools.journal.length },
    { key: 'article',        label: 'Articles', tone: 'purple', n: pools.article.length },
  ];
  const tone = typeTabs.find((t) => t.key === activeType)?.tone || 'teal';
  const toneColor = ({ teal: 'var(--qqq-teal)', sage: 'var(--qqq-sage)', gold: 'var(--qqq-gold)', purple: 'var(--qqq-purple)' })[tone];

  const toggle = (type, ref) => {
    const k = `${type}|${ref}`;
    if (existingSet.has(k)) return;
    setSelected((s) => ({ ...s, [k]: !s[k] }));
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const submit = async () => {
    if (selectedCount === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const tasks = Object.entries(selected)
        .filter(([, v]) => v)
        .map(async ([k]) => {
          const [item_type, item_ref] = k.split('|');
          return fetch('/api/workstations/qqq-folders?action=add_item', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ folder_id: folder.id, item_type, item_ref }),
          });
        });
      const results = await Promise.all(tasks);
      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) setError(`${failed.length} failed to add`);
      else {
        setSelected({});
        await onAdded();
        onClose();
      }
    } catch (e) { setError(e?.message || 'add failed'); }
    finally { setSubmitting(false); }
  };

  const pool = pools[activeType] || [];

  return (
    <div style={{
      padding: '10px 12px 12px',
      background: 'var(--surface-2)',
      border: '1px solid var(--qqq-b2)',
      borderRadius: 6,
      marginBottom: 14,
    }}>
      {/* Type tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {typeTabs.map((t) => {
          const active = activeType === t.key;
          const tc = ({ teal: 'var(--qqq-teal)', sage: 'var(--qqq-sage)', gold: 'var(--qqq-gold)', purple: 'var(--qqq-purple)' })[t.tone];
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveType(t.key)}
              style={{
                padding: '5px 10px',
                background: active ? `${tc}22` : 'transparent',
                color: active ? tc : 'var(--qqq-fg-2)',
                border: `1px solid ${active ? tc : 'var(--qqq-b2)'}`,
                borderRadius: 4,
                fontSize: 11, fontWeight: active ? 700 : 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {t.label} <span style={{ opacity: 0.7, marginLeft: 3 }}>({t.n})</span>
            </button>
          );
        })}
      </div>

      {/* Pool list */}
      <div style={{
        maxHeight: 300,
        overflowY: 'auto',
        background: 'var(--surface-0)',
        border: '1px solid var(--qqq-b3)',
        borderRadius: 4,
        scrollbarWidth: 'thin',
      }}>
        {activeType === 'article' && articlesLoading && (
          <div style={{ padding: 10, fontSize: 12, color: 'var(--qqq-fg-3)' }}>Loading articles…</div>
        )}
        {pool.length === 0 && !articlesLoading && (
          <div style={{ padding: 12, fontSize: 12, color: 'var(--qqq-fg-3)', fontStyle: 'italic' }}>
            No {activeType.replace(/_/g, ' ')} yet. {activeType === 'article' && (<span>Save one in the Articles pill.</span>)}
            {activeType !== 'article' && (<span>Come back after you have some logged.</span>)}
          </div>
        )}
        {pool.map((item) => {
          const k = `${activeType}|${item.ref}`;
          const isExisting = existingSet.has(k);
          const isSelected = !!selected[k];
          return (
            <label
              key={k}
              style={{
                display: 'grid',
                gridTemplateColumns: '22px 80px 1fr',
                gap: 10, alignItems: 'center',
                padding: '7px 10px',
                borderBottom: '1px solid var(--qqq-b3)',
                cursor: isExisting ? 'default' : 'pointer',
                background: isSelected ? `${toneColor}15` : 'transparent',
                opacity: isExisting ? 0.5 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={isExisting || isSelected}
                disabled={isExisting}
                onChange={() => toggle(activeType, item.ref)}
                style={{ cursor: isExisting ? 'default' : 'pointer' }}
              />
              <span style={{
                fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
                color: 'var(--qqq-fg-3)',
              }}>
                {item.date ? fmtBriefDate(item.date) : ''}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: 12, color: 'var(--qqq-fg-1)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {item.title}
                  {isExisting && (
                    <span style={{
                      marginLeft: 8, fontSize: 10, color: 'var(--qqq-sage)',
                      fontWeight: 700,
                    }}>
                      ✓ added
                    </span>
                  )}
                </div>
                {item.sub && (
                  <div style={{
                    fontSize: 10, color: 'var(--qqq-fg-3)',
                    marginTop: 1,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {item.sub}
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {/* Actions */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center',
        marginTop: 10,
      }}>
        <button
          type="button"
          onClick={submit}
          disabled={selectedCount === 0 || submitting}
          style={{
            padding: '7px 14px',
            background: selectedCount > 0 ? toneColor : 'var(--surface-3)',
            color: selectedCount > 0 ? 'var(--surface-0)' : 'var(--qqq-fg-3)',
            border: 0, borderRadius: 4,
            fontSize: 12, fontWeight: 700,
            cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
            fontFamily: 'inherit',
          }}
        >
          {submitting ? 'Adding…' : `Add selected (${selectedCount})`}
        </button>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: '7px 14px',
            background: 'transparent',
            color: 'var(--qqq-fg-2)',
            border: '1px solid var(--qqq-b2)',
            borderRadius: 4,
            fontSize: 12, cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Cancel
        </button>
        {error && (
          <span style={{ fontSize: 11, color: 'var(--qqq-terra)' }}>{error}</span>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ADD TO FOLDER ACTION — Commit 77
// ═══════════════════════════════════════════════════════════
// Inline "+ Add to collection" button that lives on any library
// item (chart row, analysis feed entry, article). Complements
// FolderItemPicker (Commit 79c) — same API, opposite direction:
// this one adds ONE item to an arbitrary collection, the picker
// adds MANY items to ONE specific collection.
//
// Props:
//   itemType — 'chart_analysis' | 'journal' | 'context_brief'
//              | 'method_sheet' | 'article'
//   itemRef  — brief_date for session-sourced items, UUID for
//              method_sheet or article
// ═══════════════════════════════════════════════════════════
function AddToFolderAction({ itemType, itemRef }) {
  const data = useQqqData();
  const workstationId = data._workstationId;
  const userId = data._userId;

  const [open, setOpen] = useState(false);
  const [folders, setFolders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [filedIn, setFiledIn] = useState([]);
  const [error, setError] = useState(null);

  const loadFolders = async () => {
    if (!workstationId || !userId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/workstations/qqq-folders?workstation_id=${workstationId}&user_id=${userId}`);
      const payload = await res.json();
      if (res.ok) setFolders(payload.folders || []);
      else setError(payload?.error || 'load failed');
    } catch (e) { setError(e?.message || 'load failed'); }
    finally { setLoading(false); }
  };

  const togglePanel = () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    setError(null);
    loadFolders();
  };

  const addToFolder = async (folder) => {
    setAddingId(folder.id);
    setError(null);
    try {
      const res = await fetch('/api/workstations/qqq-folders?action=add_item', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          folder_id: folder.id,
          item_type: itemType,
          item_ref: itemRef,
        }),
      });
      const payload = await res.json();
      if (res.ok) setFiledIn((prev) => prev.includes(folder.name) ? prev : [...prev, folder.name]);
      else setError(payload?.error || 'add failed');
    } catch (e) { setError(e?.message || 'add failed'); }
    finally { setAddingId(null); }
  };

  const colorVar = (c) => ({
    sage: 'var(--qqq-sage)', gold: 'var(--qqq-gold)',
    purple: 'var(--qqq-purple)', terra: 'var(--qqq-terra)', teal: 'var(--qqq-teal)',
  })[c] || 'var(--qqq-teal)';

  return (
    <div style={{ position: 'relative', marginTop: 10, display: 'inline-block' }}>
      <button
        type="button"
        onClick={togglePanel}
        style={{
          padding: '6px 12px',
          background: open ? 'rgba(77,184,184,.1)' : 'transparent',
          color: filedIn.length > 0 ? 'var(--qqq-sage)' : 'var(--qqq-teal)',
          border: `1px solid ${filedIn.length > 0 ? 'var(--qqq-sage)' : 'var(--qqq-teal)'}`,
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {filedIn.length > 0
          ? `✓ Filed in ${filedIn.length} collection${filedIn.length === 1 ? '' : 's'}`
          : '+ Add to collection'}
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)', left: 0, zIndex: 20,
          minWidth: 260, maxWidth: 340,
          padding: 6,
          background: 'var(--surface-2)',
          border: '1px solid var(--qqq-b2)',
          borderRadius: 6,
          boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
        }}>
          <div style={{
            fontSize: 10, color: 'var(--qqq-fg-3)',
            textTransform: 'uppercase', letterSpacing: '0.08em',
            padding: '6px 10px 4px',
          }}>
            File this {itemType.replace(/_/g, ' ')} in a collection
          </div>
          {loading && (
            <div style={{ padding: '8px 10px', fontSize: 12, color: 'var(--qqq-fg-3)' }}>Loading folders…</div>
          )}
          {!loading && folders && folders.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: 12, color: 'var(--qqq-fg-2)', fontStyle: 'italic' }}>
              No folders yet. Create one in Library → Folders.
            </div>
          )}
          {!loading && folders && folders.map((f) => {
            const isFiled = filedIn.includes(f.name);
            const isAdding = addingId === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => !isFiled && !isAdding && addToFolder(f)}
                disabled={isFiled || isAdding}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', padding: '8px 10px',
                  background: isFiled ? 'rgba(122,158,130,.08)' : 'transparent',
                  color: isFiled ? 'var(--qqq-sage)' : 'var(--qqq-fg-1)',
                  border: 0, borderRadius: 4,
                  fontSize: 12, textAlign: 'left',
                  cursor: (isFiled || isAdding) ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <span style={{ color: colorVar(f.color), fontSize: 14 }}>{f.icon || '📁'}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.name}
                </span>
                <span style={{ fontSize: 10, color: 'var(--qqq-fg-3)', fontFamily: 'JetBrains Mono, monospace' }}>
                  {isAdding ? '…' : isFiled ? '✓ added' : `${f.item_count || 0} items`}
                </span>
              </button>
            );
          })}
          {error && (
            <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--qqq-terra)' }}>
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TIMELINE VIEW — Commit 79b (default Library surface)
// ═══════════════════════════════════════════════════════════
// Replaces the old Uploads + Analysis pills with a single day-keyed
// view. Each day card stacks ALL materials for that trading day in
// one place: charts (teal), analyses (sage), journals (gold),
// articles (purple). No more tab-hopping to see a day's full story.
//
// Filter chips at the top let Tim slice by material type: clicking
// "Charts" flattens the timeline into a single chronological list
// of just chart analyses. "All" is the default day-card view.
//
// Data: joins _chartArchive (session briefs with chart_analysis /
// notes / context_brief) + qqq_articles (fetched fresh on mount,
// filtered by linked_brief_date). No new table needed.
// ═══════════════════════════════════════════════════════════
function LibraryTimelineView() {
  const data = useQqqData();
  const workstationId = data._workstationId;
  const userId = data._userId;
  const archive = Array.isArray(data._chartArchive) ? data._chartArchive : [];

  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);

  // Fetch articles
  useEffect(() => {
    if (!workstationId || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/workstations/qqq-articles?workstation_id=${workstationId}&user_id=${userId}`);
        const payload = await res.json();
        if (cancelled) return;
        if (res.ok) setArticles(payload.articles || []);
      } catch { /* ignore */ }
      finally { if (!cancelled) setArticlesLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [workstationId, userId]);

  // Join day map — each date has four possible material arrays
  const dayMap = useMemo(() => {
    const m = new Map();
    const touch = (d) => {
      if (!m.has(d)) m.set(d, { date: d, charts: [], analyses: [], journals: [], articles: [], regime_tags: null });
      return m.get(d);
    };
    for (const r of archive) {
      if (!r.brief_date) continue;
      const day = touch(r.brief_date);
      if (r.regime_tags) day.regime_tags = r.regime_tags;
      if (r.chart_analysis) day.charts.push({ id: `chart-${r.brief_date}`, analysis: r.chart_analysis, brief_date: r.brief_date });
      if (r.context_brief)  day.analyses.push({ id: `ctx-${r.brief_date}`, brief: r.context_brief, brief_date: r.brief_date });
      if (r.notes)          day.journals.push({ id: `journal-${r.brief_date}`, notes: r.notes, brief_date: r.brief_date });
    }
    for (const a of articles) {
      if (!a.linked_brief_date) continue;
      touch(a.linked_brief_date).articles.push(a);
    }
    return m;
  }, [archive, articles]);

  const days = useMemo(
    () => Array.from(dayMap.values()).sort((a, b) => String(b.date).localeCompare(String(a.date))),
    [dayMap],
  );

  const unlinkedArticles = useMemo(
    () => articles.filter((a) => !a.linked_brief_date),
    [articles],
  );

  const counts = useMemo(() => {
    let charts = 0, analyses = 0, journals = 0;
    for (const d of days) {
      charts += d.charts.length;
      analyses += d.analyses.length;
      journals += d.journals.length;
    }
    return {
      all:       charts + analyses + journals + articles.length,
      charts,
      analyses,
      journals,
      articles:  articles.length,
    };
  }, [days, articles.length]);

  // Empty state
  if (days.length === 0 && articles.length === 0 && !articlesLoading) {
    return (
      <div className="qqq-data-card" style={{
        borderStyle: 'dashed', opacity: 0.85,
        borderLeft: '3px dashed var(--qqq-teal)',
      }}>
        <div className="qqq-dc-hdr">
          <div className="qqq-dc-lbl" style={{ color: 'var(--qqq-teal)' }}>
            Timeline · day by day
          </div>
          <div className="qqq-dc-src" style={{ color: 'var(--qqq-fg-3)' }}>
            unlocks with first session or article
          </div>
        </div>
        <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          Everything you capture stacks here, day by day — parsed charts,
          context briefs, journal notes, linked articles. Upload a chart
          in the Log tab or save an article in the Articles pill to start.
        </div>
      </div>
    );
  }

  const chips = [
    { key: 'all',      label: 'All',       n: counts.all },
    { key: 'charts',   label: 'Charts',    n: counts.charts,    tone: 'teal' },
    { key: 'analyses', label: 'Analyses',  n: counts.analyses,  tone: 'sage' },
    { key: 'journals', label: 'Journals',  n: counts.journals,  tone: 'gold' },
    { key: 'articles', label: 'Articles',  n: counts.articles,  tone: 'purple' },
  ];

  return (
    <>
      {/* Filter chips */}
      <div style={{
        display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap',
      }}>
        {chips.map((c) => {
          const active = filter === c.key;
          const toneColor = c.tone ? ({ teal: 'var(--qqq-teal)', sage: 'var(--qqq-sage)', gold: 'var(--qqq-gold)', purple: 'var(--qqq-purple)' })[c.tone] : 'var(--qqq-teal)';
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => { setFilter(c.key); setExpanded(null); }}
              style={{
                padding: '6px 12px',
                background: active ? `${toneColor}22` : 'var(--surface-2)',
                color: active ? toneColor : 'var(--qqq-fg-2)',
                border: `1px solid ${active ? toneColor : 'var(--qqq-b2)'}`,
                borderRadius: 6,
                fontSize: 12, fontWeight: active ? 700 : 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {c.label} <span style={{ opacity: 0.7, marginLeft: 4 }}>({c.n})</span>
            </button>
          );
        })}
      </div>

      {filter === 'all' ? (
        <TimelineDayCards days={days} expanded={expanded} setExpanded={setExpanded} unlinkedArticles={unlinkedArticles} />
      ) : (
        <TimelineFlatList type={filter} days={days} articles={articles} />
      )}
    </>
  );
}

// ─── Timeline: day cards (default 'all' view) ──────────────
function TimelineDayCards({ days, expanded, setExpanded, unlinkedArticles }) {
  return (
    <>
      {days.map((day) => (
        <TimelineDayCard
          key={day.date}
          day={day}
          isOpen={expanded === day.date}
          onToggle={() => setExpanded(expanded === day.date ? null : day.date)}
        />
      ))}
      {unlinkedArticles.length > 0 && (
        <div style={{
          marginTop: 16, padding: '12px 18px',
          background: 'rgba(155,143,196,.04)',
          border: '1px solid var(--qqq-b2)', borderRadius: 6,
        }}>
          <div style={{
            fontSize: 10, color: 'var(--qqq-purple)',
            textTransform: 'uppercase', letterSpacing: '0.08em',
            fontWeight: 700, marginBottom: 10,
          }}>
            Evergreen · articles not pinned to a day ({unlinkedArticles.length})
          </div>
          {unlinkedArticles.slice(0, 10).map((a) => (
            <TimelineArticleRow key={a.id} article={a} />
          ))}
          {unlinkedArticles.length > 10 && (
            <div style={{ fontSize: 11, color: 'var(--qqq-fg-3)', marginTop: 6 }}>
              … and {unlinkedArticles.length - 10} more (see Articles pill for full list)
            </div>
          )}
        </div>
      )}
    </>
  );
}

function TimelineDayCard({ day, isOpen, onToggle }) {
  const totalCount = day.charts.length + day.analyses.length + day.journals.length + day.articles.length;
  const regime = day.regime_tags;
  return (
    <div style={{
      marginBottom: 10,
      background: 'var(--surface-2)',
      border: '1px solid var(--qqq-b2)', borderRadius: 6,
      overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%', background: 'transparent', border: 0,
          padding: '12px 16px',
          display: 'grid',
          gridTemplateColumns: '100px 1fr auto 20px',
          gap: 12, alignItems: 'center',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <div style={{
          fontFamily: 'JetBrains Mono, monospace', fontSize: 12,
          color: 'var(--qqq-fg-1)', fontWeight: 600,
        }}>
          {fmtBriefDate(day.date)}
        </div>
        <div style={{
          fontSize: 11, color: 'var(--qqq-fg-3)',
          fontFamily: 'JetBrains Mono, monospace',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {regime
            ? `${regime.vix_bucket || '—'} vix · ${regime.gamma_sign || '—'} gamma · ${regime.sector_leader || '—'} lead · ${regime.event_proximity || 'clean'}`
            : '—'}
        </div>
        <div style={{ display: 'flex', gap: 4, fontSize: 10 }}>
          {day.charts.length > 0 && (
            <span style={{ padding: '2px 7px', borderRadius: 3, background: 'rgba(77,184,184,.12)', color: 'var(--qqq-teal)', fontWeight: 700 }}>
              {day.charts.length} chart{day.charts.length === 1 ? '' : 's'}
            </span>
          )}
          {day.analyses.length > 0 && (
            <span style={{ padding: '2px 7px', borderRadius: 3, background: 'rgba(122,158,130,.12)', color: 'var(--qqq-sage)', fontWeight: 700 }}>
              {day.analyses.length} analysis
            </span>
          )}
          {day.journals.length > 0 && (
            <span style={{ padding: '2px 7px', borderRadius: 3, background: 'rgba(200,169,110,.12)', color: 'var(--qqq-gold)', fontWeight: 700 }}>
              journal
            </span>
          )}
          {day.articles.length > 0 && (
            <span style={{ padding: '2px 7px', borderRadius: 3, background: 'rgba(155,143,196,.12)', color: 'var(--qqq-purple)', fontWeight: 700 }}>
              {day.articles.length} article{day.articles.length === 1 ? '' : 's'}
            </span>
          )}
          {totalCount === 0 && (
            <span style={{ padding: '2px 7px', borderRadius: 3, background: 'var(--surface-3)', color: 'var(--qqq-fg-3)' }}>
              empty
            </span>
          )}
        </div>
        <div style={{
          color: 'var(--qqq-fg-3)', fontSize: 14,
          transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s',
        }}>
          ›
        </div>
      </button>

      {isOpen && (
        <div style={{
          padding: '4px 16px 16px',
          borderTop: '1px solid var(--qqq-b1)',
        }}>
          {day.charts.length > 0 && (
            <TimelineSection tone="teal" label={`Charts · ${day.charts.length}`}>
              {day.charts.map((c) => (
                <div key={c.id} style={{ marginBottom: 10 }}>
                  <ChartAnalysis analysis={c.analysis} />
                  <AddToFolderAction itemType="chart_analysis" itemRef={c.brief_date} />
                </div>
              ))}
            </TimelineSection>
          )}
          {day.analyses.length > 0 && (
            <TimelineSection tone="sage" label="Analysis">
              {day.analyses.map((a) => (
                <div key={a.id}>
                  <div style={{
                    padding: '10px 12px', background: 'var(--surface-3)',
                    borderLeft: '3px solid var(--qqq-sage)', borderRadius: 4,
                  }}>
                    <div style={{ fontSize: 11, color: 'var(--qqq-sage)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                      Context brief · {a.brief.context?.regime || '—'}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.6 }}>
                      {a.brief.insight || '—'}
                    </div>
                    {a.brief.action?.bias && (
                      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--qqq-fg-2)' }}>
                        Bias: <strong>{a.brief.action.bias}</strong>
                      </div>
                    )}
                  </div>
                  <AddToFolderAction itemType="context_brief" itemRef={a.brief_date} />
                </div>
              ))}
            </TimelineSection>
          )}
          {day.journals.length > 0 && (
            <TimelineSection tone="gold" label="Journal">
              {day.journals.map((j) => (
                <div key={j.id}>
                  <div style={{
                    padding: '10px 12px', background: 'var(--surface-3)',
                    borderLeft: '3px solid var(--qqq-gold)', borderRadius: 4,
                    fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.65,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {j.notes}
                  </div>
                  <AddToFolderAction itemType="journal" itemRef={j.brief_date} />
                </div>
              ))}
            </TimelineSection>
          )}
          {day.articles.length > 0 && (
            <TimelineSection tone="purple" label={`Articles · ${day.articles.length}`}>
              {day.articles.map((a) => (
                <TimelineArticleRow key={a.id} article={a} />
              ))}
            </TimelineSection>
          )}
        </div>
      )}
    </div>
  );
}

function TimelineSection({ tone, label, children }) {
  const toneColor = ({ teal: 'var(--qqq-teal)', sage: 'var(--qqq-sage)', gold: 'var(--qqq-gold)', purple: 'var(--qqq-purple)' })[tone];
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        fontSize: 10, color: toneColor,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        fontWeight: 700, marginBottom: 8,
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function TimelineArticleRow({ article }) {
  const [open, setOpen] = useState(false);
  const tags = Array.isArray(article.tags) ? article.tags : [];
  return (
    <div style={{
      marginBottom: 6,
      background: 'var(--surface-3)',
      borderLeft: '2px solid var(--qqq-purple)', borderRadius: 4,
      fontSize: 12, color: 'var(--qqq-fg-2)',
    }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', background: 'transparent', border: 0,
          padding: '6px 10px',
          display: 'flex', alignItems: 'center', gap: 10,
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ flex: 1, fontSize: 12, color: 'var(--qqq-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {article.title || article.url}
        </span>
        {article.source_domain && (
          <span style={{ fontSize: 10, color: 'var(--qqq-fg-3)', fontFamily: 'JetBrains Mono, monospace' }}>
            {article.source_domain}
          </span>
        )}
        {tags.length > 0 && (
          <span style={{ fontSize: 10, color: 'var(--qqq-purple)' }}>
            #{tags[0]}{tags.length > 1 ? ` +${tags.length - 1}` : ''}
          </span>
        )}
      </button>
      {open && (
        <div style={{ padding: '0 10px 10px' }}>
          <a href={article.url} target="_blank" rel="noopener noreferrer"
            style={{
              display: 'inline-block', marginBottom: 8,
              padding: '3px 8px', borderRadius: 3,
              background: 'var(--qqq-s1)', color: 'var(--qqq-purple)',
              fontSize: 10, fontFamily: 'JetBrains Mono, monospace',
              textDecoration: 'none',
            }}
          >
            ↗ {article.url}
          </a>
          {article.note && (
            <div style={{
              padding: '8px 10px',
              background: 'var(--qqq-s1)',
              borderRadius: 3,
              fontSize: 12, color: 'var(--qqq-fg-1)', lineHeight: 1.55,
              whiteSpace: 'pre-wrap',
              marginBottom: 8,
            }}>
              {article.note}
            </div>
          )}
          <AddToFolderAction itemType="article" itemRef={article.id} />
        </div>
      )}
    </div>
  );
}

// ─── Timeline: flat filtered list ──────────────────────────
function TimelineFlatList({ type, days, articles }) {
  const rows = useMemo(() => {
    if (type === 'charts') {
      return days.flatMap((d) => d.charts.map((c) => ({ key: c.id, date: d.date, ...c })));
    }
    if (type === 'analyses') {
      return days.flatMap((d) => d.analyses.map((a) => ({ key: a.id, date: d.date, ...a })));
    }
    if (type === 'journals') {
      return days.flatMap((d) => d.journals.map((j) => ({ key: j.id, date: d.date, ...j })));
    }
    if (type === 'articles') {
      return articles.map((a) => ({ key: a.id, date: a.linked_brief_date || '(evergreen)', article: a }));
    }
    return [];
  }, [type, days, articles]);

  if (rows.length === 0) {
    return (
      <div style={{ padding: 20, color: 'var(--qqq-fg-3)', fontStyle: 'italic', fontSize: 13, textAlign: 'center' }}>
        No {type} yet.
      </div>
    );
  }

  return (
    <div>
      {rows.map((r) => (
        <div key={r.key} style={{
          marginBottom: 10,
          background: 'var(--surface-2)',
          border: '1px solid var(--qqq-b2)', borderRadius: 6,
          padding: '10px 14px',
        }}>
          <div style={{
            fontSize: 10, color: 'var(--qqq-fg-3)',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            fontFamily: 'JetBrains Mono, monospace',
            marginBottom: 8,
          }}>
            {r.date}
          </div>
          {type === 'charts'   && <><ChartAnalysis analysis={r.analysis} /><AddToFolderAction itemType="chart_analysis" itemRef={r.brief_date} /></>}
          {type === 'analyses' && (
            <>
              <div style={{ fontSize: 11, color: 'var(--qqq-sage)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Context brief · {r.brief.context?.regime || '—'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.6 }}>
                {r.brief.insight || '—'}
              </div>
              <AddToFolderAction itemType="context_brief" itemRef={r.brief_date} />
            </>
          )}
          {type === 'journals' && (
            <>
              <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                {r.notes}
              </div>
              <AddToFolderAction itemType="journal" itemRef={r.brief_date} />
            </>
          )}
          {type === 'articles' && <TimelineArticleRow article={r.article} />}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ARTICLES VIEW — Commit 79a (replaces V1 stub)
// ═══════════════════════════════════════════════════════════
// Clipper surface for external research reads — macro commentary,
// analyst reports, FinTwit threads, newsletters. Paste URL +
// optional title + tags + note. Tags are comma-separated;
// optional link-to-day ties the article to a specific brief.
//
// Purple tone throughout per the 7-color palette: "depth /
// synthesis / private" — the exact meaning of user-clipped
// research material.
//
// Every article row also gets the reusable AddToFolderAction
// (Commit 77), so articles can be filed into Collections for
// scoped Q&A (e.g. "ask Shift across just my FOMC reads").
// ═══════════════════════════════════════════════════════════
function LibraryArticlesView() {
  const data = useQqqData();
  const workstationId = data._workstationId;
  const userId = data._userId;

  const [articles, setArticles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Save form
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [tagsStr, setTagsStr] = useState('');
  const [note, setNote] = useState('');
  const [linkedDate, setLinkedDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveHint, setSaveHint] = useState(null);

  useEffect(() => {
    if (!workstationId || !userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/workstations/qqq-articles?workstation_id=${workstationId}&user_id=${userId}`);
        const payload = await res.json();
        if (cancelled) return;
        if (!res.ok) setLoadError(payload?.error || `HTTP ${res.status}`);
        else setArticles(payload.articles || []);
      } catch (e) { if (!cancelled) setLoadError(e?.message || 'load failed'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [workstationId, userId]);

  const resetForm = () => {
    setUrl(''); setTitle(''); setTagsStr(''); setNote(''); setLinkedDate('');
  };

  const saveArticle = async () => {
    if (!url.trim() || saving) return;
    setSaving(true);
    setSaveError(null);
    setSaveHint(null);
    try {
      const tagArr = tagsStr
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const res = await fetch('/api/workstations/qqq-articles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstationId,
          user_id: userId,
          url: url.trim(),
          title: title.trim() || null,
          note: note.trim() || null,
          tags: tagArr,
          linked_brief_date: linkedDate.trim() || null,
        }),
      });
      const payload = await res.json();
      if (!res.ok) { setSaveError(payload?.error || 'save failed'); return; }
      setArticles((a) => {
        const existing = (a || []).filter((x) => x.id !== payload.article.id);
        return [payload.article, ...existing];
      });
      setSaveHint(payload.duplicate ? 'Already saved — showing existing row.' : 'Saved.');
      resetForm();
    } catch (e) { setSaveError(e?.message || 'save failed'); }
    finally { setSaving(false); }
  };

  const deleteArticle = async (id) => {
    if (!confirm('Delete this article? (Removed from folders it was filed in.)')) return;
    try {
      const res = await fetch(`/api/workstations/qqq-articles?id=${id}`, { method: 'DELETE' });
      if (res.ok) {
        setArticles((a) => (a || []).filter((x) => x.id !== id));
        if (expandedId === id) setExpandedId(null);
      }
    } catch { /* ignore */ }
  };

  const count = articles ? articles.length : 0;
  const canSave = url.trim().length > 0 && !saving;

  return (
    <DataCard
      label="Articles · external research you've saved"
      source={loading
        ? 'loading…'
        : loadError
          ? `error: ${loadError}`
          : `${count} article${count === 1 ? '' : 's'} · purple = depth / synthesis`}
      tone="purple"
    >
      {/* ── Save form ── */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--qqq-b1)' }}>
        <div style={{
          fontSize: 11, color: 'var(--qqq-purple)',
          textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
          marginBottom: 10,
        }}>
          + Save an article
        </div>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value.slice(0, 2000))}
          placeholder="Paste URL — e.g. https://www.ft.com/content/..."
          style={{
            width: '100%', padding: '9px 12px', fontSize: 13,
            background: 'var(--surface-3)', color: 'var(--qqq-fg-1)',
            border: '1px solid var(--qqq-b2)', borderRadius: 4,
            fontFamily: 'inherit', marginBottom: 8, boxSizing: 'border-box',
          }}
        />
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, 300))}
          placeholder="Title (optional — copy from the page)"
          style={{
            width: '100%', padding: '9px 12px', fontSize: 13,
            background: 'var(--surface-3)', color: 'var(--qqq-fg-1)',
            border: '1px solid var(--qqq-b2)', borderRadius: 4,
            fontFamily: 'inherit', marginBottom: 8, boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <input
            type="text"
            value={tagsStr}
            onChange={(e) => setTagsStr(e.target.value.slice(0, 400))}
            placeholder="Tags (comma-separated): fomc, tech_rotation, vix_regime"
            style={{
              flex: 1, minWidth: 200, padding: '9px 12px', fontSize: 12,
              background: 'var(--surface-3)', color: 'var(--qqq-fg-1)',
              border: '1px solid var(--qqq-b2)', borderRadius: 4,
              fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
          <input
            type="date"
            value={linkedDate}
            onChange={(e) => setLinkedDate(e.target.value)}
            placeholder="Link to trading day (optional)"
            title="Pin this article to a specific trading day"
            style={{
              padding: '9px 12px', fontSize: 12,
              background: 'var(--surface-3)', color: linkedDate ? 'var(--qqq-fg-1)' : 'var(--qqq-fg-3)',
              border: '1px solid var(--qqq-b2)', borderRadius: 4,
              fontFamily: 'inherit', minWidth: 150,
            }}
          />
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 4000))}
          placeholder="Your take on this article — what it claims, what you believe, what changed your read…"
          rows={3}
          style={{
            width: '100%', padding: '9px 12px', fontSize: 13,
            background: 'var(--surface-3)', color: 'var(--qqq-fg-1)',
            border: '1px solid var(--qqq-b2)', borderRadius: 4,
            fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
          <button
            type="button"
            onClick={saveArticle}
            disabled={!canSave}
            style={{
              padding: '8px 18px',
              background: canSave ? 'var(--qqq-purple)' : 'var(--surface-3)',
              color: canSave ? 'var(--surface-0)' : 'var(--qqq-fg-3)',
              border: 0, borderRadius: 4,
              fontSize: 12, fontWeight: 700,
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'Saving…' : 'Save article'}
          </button>
          {saveHint && (
            <span style={{ fontSize: 11, color: 'var(--qqq-sage)' }}>{saveHint}</span>
          )}
          {saveError && (
            <span style={{ fontSize: 11, color: 'var(--qqq-terra)' }}>{saveError}</span>
          )}
        </div>
      </div>

      {/* ── List ── */}
      <div>
        {loading && (
          <div style={{ padding: '20px 18px', color: 'var(--qqq-fg-2)', fontSize: 13 }}>
            Loading…
          </div>
        )}
        {!loading && articles && articles.length === 0 && (
          <div style={{
            padding: '20px 18px', color: 'var(--qqq-fg-2)',
            fontSize: 13, lineHeight: 1.6, fontStyle: 'italic',
          }}>
            No articles yet. Save your first above — anything that shaped your
            read today (analyst notes, FinTwit threads, newsletters, macro
            commentary). Each one becomes part of the research corpus you
            can later file into folders and ask questions against.
          </div>
        )}
        {!loading && articles && articles.map((a) => {
          const isOpen = expandedId === a.id;
          const tags = Array.isArray(a.tags) ? a.tags : [];
          const displayTitle = a.title || a.url;
          return (
            <div key={a.id} style={{ borderBottom: '1px solid var(--qqq-b1)' }}>
              <button
                type="button"
                onClick={() => setExpandedId(isOpen ? null : a.id)}
                style={{
                  width: '100%', background: 'transparent', border: 0,
                  padding: '10px 18px',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 20px',
                  gap: 12, alignItems: 'center',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, color: 'var(--qqq-fg-1)', fontWeight: 600,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {displayTitle}
                  </div>
                  <div style={{
                    fontSize: 11, color: 'var(--qqq-fg-3)', marginTop: 2,
                    display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
                  }}>
                    {a.source_domain && (
                      <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {a.source_domain}
                      </span>
                    )}
                    {a.linked_brief_date && (
                      <span style={{
                        padding: '1px 6px', borderRadius: 3,
                        background: 'rgba(155,143,196,.1)', color: 'var(--qqq-purple)',
                        fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
                      }}>
                        · {a.linked_brief_date}
                      </span>
                    )}
                    {tags.slice(0, 4).map((t) => (
                      <span key={t} style={{
                        padding: '1px 6px', borderRadius: 3,
                        background: 'var(--surface-3)', color: 'var(--qqq-fg-2)',
                        fontSize: 10,
                      }}>
                        #{t}
                      </span>
                    ))}
                    {tags.length > 4 && (
                      <span style={{ fontSize: 10 }}>+{tags.length - 4}</span>
                    )}
                  </div>
                </div>
                <div style={{
                  fontSize: 10, color: 'var(--qqq-fg-3)',
                  fontFamily: 'JetBrains Mono, monospace',
                }}>
                  {fmtRelative(a.created_at)}
                </div>
                <div style={{
                  color: 'var(--qqq-fg-3)', fontSize: 14,
                  transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s',
                }}>
                  ›
                </div>
              </button>

              {isOpen && (
                <div style={{
                  padding: '6px 18px 16px',
                  background: 'rgba(155,143,196,.04)',
                }}>
                  <a href={a.url} target="_blank" rel="noopener noreferrer"
                    style={{
                      display: 'inline-block', marginBottom: 10,
                      padding: '4px 10px', borderRadius: 3,
                      background: 'var(--surface-3)', color: 'var(--qqq-purple)',
                      fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
                      textDecoration: 'none',
                      maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    ↗ {a.url}
                  </a>
                  {a.note && (
                    <div style={{
                      padding: '10px 12px',
                      background: 'var(--surface-3)',
                      borderLeft: '3px solid var(--qqq-purple)',
                      borderRadius: 4,
                      fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.65,
                      whiteSpace: 'pre-wrap',
                      marginBottom: 10,
                    }}>
                      {a.note}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <AddToFolderAction itemType="article" itemRef={a.id} />
                    <button
                      type="button"
                      onClick={() => deleteArticle(a.id)}
                      style={{
                        padding: '6px 12px',
                        background: 'transparent',
                        color: 'var(--qqq-fg-3)',
                        border: '1px solid var(--qqq-b2)',
                        borderRadius: 4,
                        fontSize: 11,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </DataCard>
  );
}


// ═══════════════════════════════════════════════════════════
// CHART ARCHIVE CARD — Commit 50 (browse past parsed charts)
// ═══════════════════════════════════════════════════════════
// Addresses a gap Tim flagged: the data was persisted in session_briefs
// .chart_analysis JSONB, but there was NO UI to browse it. Shift
// couldn't compare past charts, and neither could Tim.
//
// This card renders every past session that has granular chart features
// as an expandable row. Click a row → see the full ~50-feature
// breakdown + outcome + regime context. The NEXT commit (51) will add
// image thumbnails from Supabase Storage for true visual comparison.
//
// Jarvis (Layer 6) also now has access to this same data in its corpus,
// so "Hey Jarvis, compare today to last Tuesday" is answerable.
function ChartArchiveCard() {
  const data = useQqqData();
  const aiName = useAiName();
  const archive = Array.isArray(data._chartArchive) ? data._chartArchive : [];
  const [expandedDate, setExpandedDate] = useState(null);
  const [filter, setFilter] = useState('all'); // all | hit | miss | near_miss

  if (archive.length === 0) {
    return (
      <div className="qqq-data-card" style={{
        borderStyle: 'dashed', opacity: 0.85, marginTop: 16,
        borderLeft: '3px dashed var(--qqq-teal, #4db8b8)',
      }}>
        <div className="qqq-dc-hdr">
          <div className="qqq-dc-lbl" style={{ color: 'var(--qqq-teal)' }}>
            Chart Archive · past parsed charts
          </div>
          <div className="qqq-dc-src" style={{ color: 'var(--qqq-fg-3)' }}>
            unlocks after first chart upload (Log tab)
          </div>
        </div>
        <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          Once you upload a chart in the Log tab, every past chart becomes
          browseable here with full feature breakdowns. Shift + Jarvis can
          compare any past session to today; you can click back to any
          date to see what the extraction said.
        </div>
      </div>
    );
  }

  // Apply filter
  const filtered = archive.filter((r) => {
    const ca = r.chart_analysis;
    if (filter === 'all') return true;
    if (filter === 'hit')       return ca.path_to_2_00?.hit === true;
    if (filter === 'miss')      return ca.path_to_2_00?.hit === false;
    if (filter === 'near_miss') return ca.stop_proximity?.near_miss === true;
    return true;
  });

  return (
    <DataCard
      label="Chart Archive · past parsed charts"
      source={`${archive.length} chart${archive.length === 1 ? '' : 's'} · click a row for full breakdown · feeds the pattern engine + chat`}
      tone="teal"
    >
      {/* Filter bar */}
      <div style={{
        display: 'flex', gap: 6, padding: '10px 18px 0',
        fontSize: 11,
      }}>
        {[
          { key: 'all',       label: 'All',         n: archive.length },
          { key: 'hit',       label: '$2.00 hit',   n: archive.filter((r) => r.chart_analysis.path_to_2_00?.hit === true).length },
          { key: 'miss',      label: '$2.00 miss',  n: archive.filter((r) => r.chart_analysis.path_to_2_00?.hit === false).length },
          { key: 'near_miss', label: 'Near-miss',   n: archive.filter((r) => r.chart_analysis.stop_proximity?.near_miss === true).length },
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => { setFilter(f.key); setExpandedDate(null); }}
            style={{
              padding: '4px 10px',
              background: filter === f.key ? 'rgba(77,184,184,.15)' : 'var(--surface-3, #171723)',
              color: filter === f.key ? 'var(--qqq-teal)' : 'var(--qqq-fg-2)',
              border: `1px solid ${filter === f.key ? 'var(--qqq-teal)' : 'var(--qqq-b2)'}`,
              borderRadius: 4, cursor: 'pointer',
              fontSize: 11, fontWeight: filter === f.key ? 700 : 500,
            }}
          >
            {f.label} ({f.n})
          </button>
        ))}
      </div>

      {/* Archive rows */}
      <div style={{ padding: '10px 0' }}>
        {filtered.length === 0 && (
          <div style={{
            padding: '20px 18px', color: 'var(--qqq-fg-3)',
            fontSize: 13, fontStyle: 'italic', textAlign: 'center',
          }}>
            No charts matching &ldquo;{filter}&rdquo; filter.
          </div>
        )}
        {filtered.map((r) => {
          const isOpen = expandedDate === r.brief_date;
          const ca = r.chart_analysis || {};
          const hit200 = ca.path_to_2_00?.hit;
          const outcomeTone = hit200 === true ? 'sage'
            : hit200 === false ? 'terra'
            : 'muted';
          const outcomeColor = outcomeTone === 'sage' ? 'var(--qqq-sage)'
            : outcomeTone === 'terra' ? 'var(--qqq-terra)'
            : 'var(--qqq-fg-3)';
          return (
            <div key={r.brief_date} style={{
              borderBottom: '1px solid var(--qqq-b1, #1e1e2a)',
            }}>
              {/* Row header — click to expand */}
              <button
                type="button"
                onClick={() => setExpandedDate(isOpen ? null : r.brief_date)}
                style={{
                  width: '100%', background: 'transparent', border: 0,
                  padding: '10px 18px',
                  display: 'grid',
                  gridTemplateColumns: '56px 80px 1fr auto auto 20px',
                  gap: 12, alignItems: 'center',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                {/* Commit 62.1 — thumbnail preview in row header */}
                <div style={{
                  width: 56, height: 36,
                  background: 'var(--surface-3)',
                  borderRadius: 3, overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9, color: 'var(--qqq-fg-3)',
                  border: '1px solid var(--qqq-b2)',
                }}>
                  {ca._meta?.image_url ? (
                    <img src={ca._meta.image_url} alt=""
                      style={{
                        width: '100%', height: '100%',
                        objectFit: 'cover', display: 'block',
                      }}
                    />
                  ) : (
                    <span>no img</span>
                  )}
                </div>
                <div style={{
                  fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
                  color: 'var(--qqq-fg-2)',
                }}>
                  {fmtBriefDate(r.brief_date)}
                </div>
                <div style={{
                  fontSize: 13, color: 'var(--qqq-fg-1)', fontWeight: 500,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {ca.pattern || '(unnamed pattern)'}
                </div>
                <div style={{
                  display: 'flex', gap: 4, fontSize: 10,
                }}>
                  {ca.chop_score?.score && (
                    <span style={{
                      padding: '2px 6px', borderRadius: 3,
                      background: 'var(--surface-3)', color: 'var(--qqq-gold)',
                      fontFamily: 'JetBrains Mono, monospace',
                    }}>
                      CHOP {ca.chop_score.score}
                    </span>
                  )}
                  {ca.break_candle?.index != null && (
                    <span style={{
                      padding: '2px 6px', borderRadius: 3,
                      background: 'var(--surface-3)', color: 'var(--qqq-fg-2)',
                      fontFamily: 'JetBrains Mono, monospace',
                    }}>
                      T+{ca.break_candle.index}
                    </span>
                  )}
                </div>
                <div style={{
                  fontSize: 11, fontWeight: 700,
                  color: outcomeColor,
                  padding: '2px 8px', borderRadius: 3,
                  background: hit200 === true ? 'rgba(122,158,130,.12)'
                    : hit200 === false ? 'rgba(196,96,80,.12)'
                    : 'var(--surface-3)',
                  minWidth: 60, textAlign: 'center',
                }}>
                  {hit200 === true ? '✓ $2.00' : hit200 === false ? '✗ miss' : '—'}
                </div>
                <div style={{
                  color: 'var(--qqq-fg-3)', fontSize: 14,
                  transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s',
                }}>
                  ›
                </div>
              </button>

              {/* Expanded feature breakdown */}
              {isOpen && (
                <div style={{
                  padding: '8px 18px 16px',
                  background: 'rgba(77,184,184,.03)',
                }}>
                  <ChartAnalysis analysis={ca} />
                  {r.regime_tags && (
                    <div style={{
                      marginTop: 10,
                      padding: '8px 10px',
                      background: 'var(--surface-3)',
                      borderRadius: 4,
                      fontSize: 11, color: 'var(--qqq-fg-2)',
                      fontFamily: 'JetBrains Mono, monospace',
                    }}>
                      regime · vix {r.regime_tags.vix_bucket || '—'}
                      {' · '}gamma {r.regime_tags.gamma_sign || '—'}
                      {' · '}{r.regime_tags.sector_leader || '—'} leading
                      {' · '}{r.regime_tags.event_proximity || 'clean'}
                    </div>
                  )}
                  {/* Commit 77 — file this chart into one or more folders.
                      The picker reads live from /api/workstations/qqq-folders
                      so any folder you create in Library → Folders shows
                      up instantly. */}
                  <AddToFolderAction itemType="chart_analysis" itemRef={r.brief_date} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{
        padding: '10px 18px', fontSize: 11,
        color: 'var(--qqq-fg-3)',
        background: 'rgba(77,184,184,.04)',
        borderTop: '1px solid var(--qqq-b1)',
        fontStyle: 'italic', lineHeight: 1.55,
      }}>
        Each chart here also feeds: the Chart-Feature Rules engine (above),
        the Method Sheet (Sunday refinement), and the full-corpus dialogue.
        Ask {aiName} &ldquo;compare today to last Tuesday&rdquo; — it sees every row.
      </div>
    </DataCard>
  );
}

function MethodSheetCard() {
  const data = useQqqData();
  const sheet = data._methodSheet;

  // Empty state — no sheet yet (cron hasn't run, or <10 sessions)
  if (!sheet) {
    return (
      <div className="qqq-data-card" style={{
        borderStyle: 'dashed', opacity: 0.85, marginTop: 16,
        borderLeft: '3px dashed var(--qqq-gold, #c8a96e)',
      }}>
        <div className="qqq-dc-hdr">
          <div className="qqq-dc-lbl" style={{ color: 'var(--qqq-gold, #c8a96e)' }}>
            The Method · Layer 3 (Bruce Lee)
          </div>
          <div className="qqq-dc-src" style={{ color: 'var(--qqq-fg-3)' }}>
            unlocks at 10+ resolved sessions · refreshes Sunday noon ET
          </div>
        </div>
        <div style={{ padding: '16px 18px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.65 }}>
          Every Sunday, Claude analyzes your last 60 days of sessions
          (forces, opening candles, window outcomes, drivers, attribution
          notes, chart uploads) and refines THE method. ONE evolving
          playbook. No triage required. Just read.
          <br /><br />
          <strong>Bruce Lee:</strong> &ldquo;I fear not the man who has
          practiced 10,000 kicks once, but I fear the man who has
          practiced one kick 10,000 times.&rdquo; This is that one kick.
          <br /><br />
          Activates after 10+ resolved sessions exist.
        </div>
      </div>
    );
  }

  return (
    <DataCard
      label={`The Method · v${sheet.version}`}
      source={`built on ${sheet.source_session_count} sessions · ${sheet.source_chart_count} charts · ${fmtRelative(sheet.generated_at)}`}
      tone="gold"
    >
      {/* ① Method Statement */}
      {sheet.method_statement && (
        <div style={{
          padding: '16px 18px',
          background: 'rgba(200,169,110,.04)',
          borderLeft: '3px solid var(--qqq-gold, #c8a96e)',
          borderRadius: 4, margin: '0 0 16px',
        }}>
          <div style={{
            fontSize: 11, color: 'var(--qqq-gold)', textTransform: 'uppercase',
            letterSpacing: '0.05em', fontWeight: 600, marginBottom: 8,
          }}>
            ① Your Method
          </div>
          <div style={{ fontSize: 14, color: 'var(--qqq-fg-1)', lineHeight: 1.65 }}>
            {sheet.method_statement}
          </div>
        </div>
      )}

      {/* ② Stage 1 — Direction Read (Tim's "entry_rules" key in DB) */}
      {Array.isArray(sheet.entry_rules) && sheet.entry_rules.length > 0 && (
        <MethodSection
          number="②"
          title="Stage 1 — Direction Read (PUT vs CALL)"
          tone="bull"
          subtitle="What's correlated with directional outcome from your data"
          items={sheet.entry_rules}
          renderItem={(r) => (
            <>
              <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.55, fontWeight: 500 }}>
                {r.rule}
              </div>
              {r.evidence && (
                <div style={{ fontSize: 11, color: 'var(--qqq-fg-3)', marginTop: 4 }}>
                  evidence: {r.evidence}
                </div>
              )}
              {r.why && (
                <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', marginTop: 4, fontStyle: 'italic' }}>
                  why: {r.why}
                </div>
              )}
            </>
          )}
        />
      )}

      {/* ③ Stage 2 — Timing Observations */}
      {Array.isArray(sheet.exit_rules) && sheet.exit_rules.length > 0 && (
        <MethodSection
          number="③"
          title="Stage 2 — Timing Observations (entry/exit)"
          tone="teal"
          subtitle="What we've seen AFTER candle confirmation"
          items={sheet.exit_rules}
          renderItem={(r) => (
            <>
              <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', lineHeight: 1.55, fontWeight: 500 }}>
                {r.rule}
              </div>
              {r.evidence && (
                <div style={{ fontSize: 11, color: 'var(--qqq-fg-3)', marginTop: 4 }}>
                  evidence: {r.evidence}
                </div>
              )}
              {r.why && (
                <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', marginTop: 4, fontStyle: 'italic' }}>
                  why: {r.why}
                </div>
              )}
            </>
          )}
        />
      )}

      {/* ④ Signal Hierarchy */}
      {Array.isArray(sheet.signal_hierarchy) && sheet.signal_hierarchy.length > 0 && (
        <MethodSection
          number="④"
          title="Signal Hierarchy"
          tone="purple"
          subtitle="Predictive power ranked by your data"
          items={sheet.signal_hierarchy}
          renderItem={(s) => {
            const wColor = s.weight === 'high' ? 'var(--qqq-sage)'
              : s.weight === 'medium' ? 'var(--qqq-gold)'
              : 'var(--qqq-fg-3)';
            const tArrow = s.trend === 'improving' ? '↑'
              : s.trend === 'degrading' ? '↓'
              : '→';
            const tColor = s.trend === 'improving' ? 'qqq-up'
              : s.trend === 'degrading' ? 'qqq-dn'
              : '';
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', fontWeight: 600 }}>
                    {s.signal}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--qqq-fg-3)', marginTop: 2 }}>
                    {s.hit_rate_pct}% hit · {s.sample} samples
                  </div>
                </div>
                <div style={{
                  fontSize: 10, padding: '3px 8px', borderRadius: 3,
                  background: 'transparent', color: wColor,
                  border: `1px solid ${wColor}`, textTransform: 'uppercase', fontWeight: 700,
                }}>
                  {s.weight}
                </div>
                <div className={tColor} style={{ fontSize: 14, fontWeight: 700, minWidth: 16, textAlign: 'center' }}>
                  {tArrow}
                </div>
              </div>
            );
          }}
        />
      )}

      {/* ⑤ Regime Modifiers */}
      {sheet.regime_modifiers && Object.keys(sheet.regime_modifiers).length > 0 && (
        <MethodSection
          number="⑤"
          title="Regime Modifiers"
          tone="steel"
          subtitle="What changes per market regime"
          items={Object.entries(sheet.regime_modifiers)}
          renderItem={([regime, modifiers]) => (
            <>
              <div style={{ fontSize: 12, color: 'var(--qqq-fg-1)', fontWeight: 600, marginBottom: 4 }}>
                {regime.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </div>
              {Array.isArray(modifiers) && modifiers.map((m, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--qqq-fg-2)', lineHeight: 1.5, marginLeft: 8 }}>
                  • {m}
                </div>
              ))}
            </>
          )}
        />
      )}

      {/* ⑥ Recent Refinements */}
      {Array.isArray(sheet.recent_refinements) && sheet.recent_refinements.length > 0 && (
        <MethodSection
          number="⑥"
          title="Recent Refinements"
          tone="sage"
          subtitle={`What changed since v${(sheet.prior_version || sheet.version - 1) || '0'}`}
          items={sheet.recent_refinements}
          renderItem={(r) => (
            <>
              <div style={{ fontSize: 11, color: 'var(--qqq-sage)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                {r.section?.replace(/_/g, ' ')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', fontWeight: 500 }}>
                {r.change}
              </div>
              {r.why && (
                <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', marginTop: 4, fontStyle: 'italic' }}>
                  {r.why}
                </div>
              )}
            </>
          )}
        />
      )}

      {/* ⑦ Decay Watch */}
      {Array.isArray(sheet.decay_watch) && sheet.decay_watch.length > 0 && (
        <MethodSection
          number="⑦"
          title="Decay Watch"
          tone="terra"
          subtitle="Signals losing edge — tighten or stop trading"
          items={sheet.decay_watch}
          renderItem={(d) => (
            <>
              <div style={{ fontSize: 13, color: 'var(--qqq-fg-1)', fontWeight: 600 }}>
                {d.signal}
              </div>
              <div style={{ fontSize: 12, color: 'var(--qqq-fg-2)', marginTop: 4 }}>
                <span className="qqq-dn">{d.was_pct}% → {d.now_pct}%</span>
                <span style={{ color: 'var(--qqq-fg-3)', marginLeft: 8 }}>over {d.samples} samples</span>
              </div>
              {d.note && (
                <div style={{ fontSize: 11, color: 'var(--qqq-fg-2)', marginTop: 4, fontStyle: 'italic' }}>
                  {d.note}
                </div>
              )}
            </>
          )}
        />
      )}

      {/* ⑧ Sharpening Focus — ONE thing this week (Bruce Lee) */}
      {sheet.sharpening_focus && (
        <div style={{
          margin: '16px 0 0',
          padding: '16px 18px',
          background: 'linear-gradient(135deg, rgba(200,169,110,.10), rgba(122,158,130,.06))',
          borderLeft: '4px solid var(--qqq-gold)',
          borderRadius: 4,
        }}>
          <div style={{
            fontSize: 11, color: 'var(--qqq-gold)', textTransform: 'uppercase',
            letterSpacing: '0.05em', fontWeight: 700, marginBottom: 10,
          }}>
            ⚡ ⑧ This Week's Sharpening Focus · Bruce Lee
          </div>
          <div style={{ fontSize: 15, color: 'var(--qqq-fg-1)', lineHeight: 1.65, fontWeight: 500 }}>
            {sheet.sharpening_focus}
          </div>
        </div>
      )}

      {/* Reasoning footer */}
      {sheet.reasoning && (
        <div style={{
          padding: '12px 18px', marginTop: 12,
          fontSize: 12, color: 'var(--qqq-fg-2)',
          background: 'rgba(155,143,196,.04)',
          borderTop: '1px solid var(--qqq-b1)',
          lineHeight: 1.6, fontStyle: 'italic',
        }}>
          <strong style={{ color: 'var(--qqq-purple)' }}>Synthesis:</strong> {sheet.reasoning}
          <div style={{ marginTop: 8, fontSize: 10, color: 'var(--qqq-fg-3)', fontStyle: 'normal' }}>
            {sheet.generation_model} · v{sheet.version} · refreshes Sunday noon ET
          </div>
        </div>
      )}
    </DataCard>
  );
}

// ─── Tactical Refinement Card (Commit 34 — Layer 4) ─────────
// Reads tactical_refinements from API. Renders concrete stop/
// exit/sizing suggestions derived from MFE/MAE distributions.
// Empty state until 5+ resolved sessions exist.
function TacticalRefinementCard() {
  const data = useQqqData();
  const tr = data._tacticalRefinements;

  if (!tr) {
    return (
      <div className="qqq-data-card" style={{
        borderStyle: 'dashed', opacity: 0.85, marginTop: 16,
        borderLeft: '3px dashed var(--qqq-sage, #7a9e82)',
      }}>
        <div className="qqq-dc-hdr">
          <div className="qqq-dc-lbl" style={{ color: 'var(--qqq-sage, #7a9e82)' }}>
            Tactical Refinement · Layer 4
          </div>
          <div className="qqq-dc-src" style={{ color: 'var(--qqq-fg-3)' }}>
            unlocks at 5+ resolved sessions
          </div>
        </div>
        <div style={{ padding: '14px 18px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          Once you have 5+ resolved sessions, Shift analyzes your
          MFE/MAE distributions and surfaces concrete refinements:
          where to set stops, when trailing stops would have captured
          more, whether your asymmetric R:R math (-50% / +100-200%
          option premium) is supported by what the underlying does,
          and how to match exit timing to the dominant window shape.
        </div>
      </div>
    );
  }

  return (
    <DataCard
      label="Tactical Refinement · Layer 4"
      source={`${tr.sample_size} sessions analyzed · ${tr.winners} winners · ${tr.losers} losers`}
      tone="sage"
    >
      {/* Top stats summary */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10,
        padding: '14px 18px', borderBottom: '1px solid var(--qqq-b1)',
      }}>
        <StatTile label="Winners avg MFE" value={tr.stats.winner_avg_mfe} suffix="%" tone="up" />
        <StatTile label="Winners avg MAE" value={tr.stats.winner_avg_mae} suffix="%" tone="dn" sign="neg" />
        <StatTile label="Losers avg MAE"  value={tr.stats.loser_avg_mae}  suffix="%" tone="dn" sign="neg" />
        <StatTile label="Worst loser MAE" value={tr.stats.loser_max_mae}  suffix="%" tone="dn" sign="neg" />
      </div>

      {/* Refinement suggestions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 18px' }}>
        {tr.refinements.map((r, i) => {
          const tone = r.kind === 'stop_placement' || r.kind === 'tail_risk' ? 'terra'
            : r.kind === 'exit_timing' ? 'sage'
            : r.kind === 'risk_reward' ? 'gold'
            : 'teal';
          const railColor = tone === 'terra' ? 'var(--qqq-terra)'
            : tone === 'sage' ? 'var(--qqq-sage)'
            : tone === 'gold' ? 'var(--qqq-gold)'
            : 'var(--qqq-teal)';
          return (
            <div key={i} style={{
              background: 'var(--surface-3, #171723)',
              borderRadius: 8,
              padding: '12px 14px',
              borderLeft: `3px solid ${railColor}`,
            }}>
              <div style={{
                fontSize: 10, color: railColor, textTransform: 'uppercase',
                letterSpacing: '0.05em', fontWeight: 700, marginBottom: 4,
              }}>
                {r.kind.replace(/_/g, ' ')}
              </div>
              <div style={{ fontSize: 14, color: 'var(--qqq-fg-1)', fontWeight: 600, marginBottom: 6, lineHeight: 1.4 }}>
                {r.title}
              </div>
              <div style={{ fontSize: 12, color: 'var(--qqq-fg-2)', lineHeight: 1.55 }}>
                {r.why}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{
        padding: '12px 18px', fontSize: 11, color: 'var(--qqq-fg-3)',
        background: 'rgba(155,143,196,.04)', borderTop: '1px solid var(--qqq-b1)',
        fontStyle: 'italic', lineHeight: 1.5,
      }}>
        These come from your actual MFE/MAE on the underlying. Translate
        to option premium % using your -50%/+100-200% framework. The
        more sessions you log, the sharper these get.
      </div>
    </DataCard>
  );
}

function StatTile({ label, value, suffix, tone, sign }) {
  const v = Number(value);
  if (!Number.isFinite(v)) {
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: 18, color: 'var(--qqq-fg-3)', marginTop: 4 }}>—</div>
      </div>
    );
  }
  const toneColor = tone === 'up' ? 'var(--qqq-sage)' : tone === 'dn' ? 'var(--qqq-terra)' : 'var(--qqq-fg-1)';
  const display = sign === 'neg' ? `-${Math.abs(v).toFixed(2)}` : `${v >= 0 ? '+' : ''}${v.toFixed(2)}`;
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 18, color: toneColor, fontWeight: 700, marginTop: 4 }}>
        {display}{suffix}
      </div>
    </div>
  );
}

// ─── Jarvis Dialogue Card (Commit 36 — Layer 6) ─────────────
// "Hey Jarvis, why did I lose Tuesday?"
// Conversational interface with full corpus context. The capstone.
//
// On mount: fetches conversation history (GET).
// User types question, hits Send → POST → Claude reasons over
// session corpus + method sheet → returns assistant turn.
// History persists per workstation forever.
function JarvisDialogueCard() {
  const data = useQqqData();
  const aiName = useAiName();
  const workstationId = data._workstationId;
  const userId = data._userId;

  const [turns, setTurns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Load conversation history once on mount
  useEffect(() => {
    if (!workstationId || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ workstation_id: workstationId, user_id: userId });
        const res = await fetch(`/api/workstations/qqq-jarvis-chat?${qs}`, { cache: 'no-store' });
        const payload = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setError(payload?.error || `HTTP ${res.status}`);
          setLoading(false);
          return;
        }
        setTurns(Array.isArray(payload.turns) ? payload.turns : []);
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'load failed');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [workstationId, userId]);

  async function send() {
    const text = draft.trim();
    if (!text || sending || !workstationId || !userId) return;
    setSending(true);
    setError(null);
    // Optimistic: append user turn locally
    const tempUserTurn = {
      id:         `temp-${Date.now()}`,
      role:       'user',
      content:    text,
      created_at: new Date().toISOString(),
    };
    setTurns((arr) => [...arr, tempUserTurn]);
    setDraft('');
    try {
      const res = await fetch('/api/workstations/qqq-jarvis-chat', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          workstation_id: workstationId,
          user_id:        userId,
          message:        text,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(payload?.error || `HTTP ${res.status}`);
        return;
      }
      // Replace optimistic with persisted (if server returns the user
      // turn) and append the assistant response
      setTurns((arr) => [
        ...arr.filter((t) => t.id !== tempUserTurn.id),
        tempUserTurn,
        payload.turn,
      ]);
    } catch (e) {
      setError(e?.message || 'send failed');
    } finally {
      setSending(false);
    }
  }

  return (
    <DataCard
      label={`Hey ${aiName} · ask your corpus`}
      source={
        loading ? 'loading conversation…'
        : error ? `error: ${error}`
        : `${turns.length} turn${turns.length === 1 ? '' : 's'} · per-workstation history`
      }
      tone="purple"
    >
      {/* Conversation transcript */}
      <div style={{
        padding: '14px 18px',
        maxHeight: 480,
        overflowY: 'auto',
      }}>
        {turns.length === 0 && !loading && (
          <div style={{
            padding: '24px 0', textAlign: 'center',
            color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.65,
          }}>
            Ask anything about your trading data.
            <div style={{ fontSize: 12, color: 'var(--qqq-fg-3)', marginTop: 12, lineHeight: 1.7, textAlign: 'left' }}>
              Examples:
              <ul style={{ marginTop: 6, paddingLeft: 18 }}>
                <li>&ldquo;Why did I lose last Tuesday?&rdquo;</li>
                <li>&ldquo;What does my data say about FOMC days?&rdquo;</li>
                <li>&ldquo;Show me my best winning setups in the last month.&rdquo;</li>
                <li>&ldquo;When sector rotation tagged + gamma+ — what was my hit rate?&rdquo;</li>
                <li>&ldquo;Has my exit timing improved?&rdquo;</li>
                <li>&ldquo;What pattern keeps tripping me up?&rdquo;</li>
              </ul>
            </div>
          </div>
        )}
        {turns.map((t) => (
          <div key={t.id} style={{
            marginBottom: 14,
            padding: '10px 14px',
            background: t.role === 'user' ? 'transparent' : 'var(--surface-3, #171723)',
            borderRadius: 8,
            borderLeft: t.role === 'user' ? '0' : '3px solid var(--qqq-purple, #9b8fc4)',
          }}>
            <div style={{
              fontSize: 10, color: t.role === 'user' ? 'var(--qqq-fg-3)' : 'var(--qqq-purple)',
              textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
              marginBottom: 6,
            }}>
              {t.role === 'user' ? 'You' : aiName}
              <span style={{ color: 'var(--qqq-fg-3)', fontWeight: 400, marginLeft: 8 }}>
                · {fmtRelative(t.created_at)}
              </span>
            </div>
            <div style={{
              fontSize: 13.5, color: 'var(--qqq-fg-1)', lineHeight: 1.65,
              whiteSpace: 'pre-wrap', wordWrap: 'break-word',
            }}>
              {t.content}
            </div>
          </div>
        ))}
        {sending && (
          <div style={{
            padding: '10px 14px', fontSize: 11, color: 'var(--qqq-fg-3)',
            fontStyle: 'italic',
          }}>
            {aiName} is thinking…
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{
        padding: '12px 18px', borderTop: '1px solid var(--qqq-b1)',
        display: 'flex', gap: 8,
      }}>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 4000))}
          placeholder={`Ask ${aiName} about your trading data…`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          disabled={sending}
          style={{
            flex: 1, padding: '10px 12px', fontSize: 13,
            background: 'var(--surface-3, #171723)',
            color: 'var(--qqq-fg-1)',
            border: '1px solid var(--qqq-b1)',
            borderRadius: 6, fontFamily: 'inherit',
          }}
        />
        <button
          type="button"
          onClick={send}
          disabled={sending || !draft.trim()}
          style={{
            padding: '10px 16px', fontSize: 13, fontWeight: 600,
            background: !draft.trim() ? 'var(--surface-3)' : 'var(--qqq-gold)',
            color: !draft.trim() ? 'var(--qqq-fg-3)' : 'var(--surface-0)',
            border: 0, borderRadius: 6,
            cursor: !draft.trim() || sending ? 'not-allowed' : 'pointer',
          }}
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </DataCard>
  );
}

// ─── Reusable section block for Method Sheet ────────────────
function MethodSection({ number, title, tone, subtitle, items, renderItem }) {
  const railColor = tone === 'bull' ? 'var(--qqq-sage)'
    : tone === 'teal' ? 'var(--qqq-teal)'
    : tone === 'purple' ? 'var(--qqq-purple)'
    : tone === 'steel' ? 'var(--qqq-steel)'
    : tone === 'sage' ? 'var(--qqq-sage)'
    : tone === 'terra' ? 'var(--qqq-terra)'
    : 'var(--qqq-fg-3)';
  return (
    <div style={{ margin: '0 0 16px' }}>
      <div style={{
        fontSize: 11, color: railColor, textTransform: 'uppercase',
        letterSpacing: '0.05em', fontWeight: 600,
        padding: '0 18px', marginBottom: 4,
      }}>
        {number} {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: 11, color: 'var(--qqq-fg-3)', padding: '0 18px', marginBottom: 8, fontStyle: 'italic' }}>
          {subtitle}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 18px' }}>
        {items.map((item, i) => (
          <div key={i} style={{
            background: 'var(--surface-3, #171723)',
            borderRadius: 8,
            padding: '12px 14px',
            borderLeft: `3px solid ${railColor}`,
          }}>
            {renderItem(item, i)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Derived Patterns Card (Commit 29 — Layer 3: Jarvis) ───
// THE BREAKTHROUGH. Every Sunday at noon ET, the
// qqq-pattern-discovery cron sends Tim's last 30 sessions to
// Claude with a "find patterns he hasn't named" prompt. Claude
// returns 5-10 hypotheses. Tim triages each one here:
//   monitoring → "watching for more samples"
//   validated  → "yes — this is real, lock it in"
//   noise      → "not useful, dismiss"
//
// Triage state persists. Validated patterns become Layer 5
// alerts (Commit 32) when today matches one of them.
function DerivedPatternsCard() {
  const data = useQqqData();
  const patterns = Array.isArray(data._derivedPatterns) ? data._derivedPatterns : [];
  const workstationId = data._workstationId;
  const userId = data._userId;
  const [busy, setBusy] = useState({});  // { [patternId]: true } during triage call

  // Group by status for visual separation
  const validated  = patterns.filter((p) => p.status === 'validated');
  const monitoring = patterns.filter((p) => p.status === 'monitoring');

  async function triage(patternId, status) {
    if (!workstationId || !userId) return;
    setBusy((b) => ({ ...b, [patternId]: true }));
    try {
      await fetch('/api/workstations/qqq-session-brief?action=triage_pattern', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pattern_id: patternId,
          user_id:    userId,
          status,
        }),
      });
      // Optimistic UI: just reload data on next render. For now, the
      // user can refresh to see the moved pattern. A future commit
      // can wire optimistic state in-place.
    } catch {
      // Error handling intentionally silent — surface in dev tools
    } finally {
      setBusy((b) => ({ ...b, [patternId]: false }));
    }
  }

  // ─── Empty state — no patterns yet (cron hasn't run, or <10 sessions) ──
  if (patterns.length === 0) {
    return (
      <div className="qqq-data-card" style={{
        borderStyle: 'dashed', opacity: 0.85, marginTop: 16,
        borderLeft: '3px dashed var(--qqq-purple, #9b8fc4)',
      }}>
        <div className="qqq-dc-hdr">
          <div className="qqq-dc-lbl" style={{ color: 'var(--qqq-purple, #9b8fc4)' }}>
            This Week&apos;s Discoveries · Layer 3
          </div>
          <div className="qqq-dc-src" style={{ color: 'var(--qqq-fg-3)' }}>
            unlocks at 10+ resolved sessions · refreshes Sunday noon ET
          </div>
        </div>
        <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          Every Sunday, Claude analyzes your last 30 days of sessions
          (forces, opening candles, window outcomes, drivers, your
          notes) and surfaces patterns YOU haven&apos;t named yet — both
          wins and losses. Triage each as monitoring / validated /
          noise. Validated patterns become alerts when today matches
          (coming in Layer 5).
          <br /><br />
          <strong>Activates after 10+ resolved sessions exist.</strong>
        </div>
      </div>
    );
  }

  return (
    <DataCard
      label="This Week's Discoveries · pattern synthesis"
      source={`${patterns.length} pattern${patterns.length === 1 ? '' : 's'} surfaced · refreshes Sunday noon ET`}
      tone="purple"
    >
      {/* Validated patterns first */}
      {validated.length > 0 && (
        <>
          <div style={{
            fontSize: 11, color: 'var(--qqq-sage, #7a9e82)', textTransform: 'uppercase',
            letterSpacing: '0.05em', padding: '12px 16px 6px', fontWeight: 600,
          }}>
            ✓ Validated ({validated.length})
          </div>
          {validated.map((p) => (
            <DerivedPatternRow
              key={p.id}
              pattern={p}
              onTriage={triage}
              busy={!!busy[p.id]}
            />
          ))}
        </>
      )}

      {/* Monitoring patterns */}
      {monitoring.length > 0 && (
        <>
          <div style={{
            fontSize: 11, color: 'var(--qqq-gold, #c8a96e)', textTransform: 'uppercase',
            letterSpacing: '0.05em', padding: '12px 16px 6px', fontWeight: 600,
          }}>
            ◌ Monitoring ({monitoring.length})
          </div>
          {monitoring.map((p) => (
            <DerivedPatternRow
              key={p.id}
              pattern={p}
              onTriage={triage}
              busy={!!busy[p.id]}
            />
          ))}
        </>
      )}

      <div style={{
        padding: '12px 16px', fontSize: 12,
        background: 'rgba(155,143,196,.04)',
        borderLeft: '2px solid var(--qqq-purple, #9b8fc4)',
        margin: '6px 0 0',
        color: 'var(--qqq-fg-2, #aaa)', lineHeight: 1.55,
      }}>
        Patterns are <strong>hypotheses</strong>, not proven truths. Validate by
        watching them fire across more sessions before sizing trades on them.
        Validated patterns will trigger proactive alerts in Phase 1 when
        today&apos;s setup matches (Layer 5 — coming next).
      </div>
    </DataCard>
  );
}

function DerivedPatternRow({ pattern, onTriage, busy }) {
  const [expanded, setExpanded] = useState(false);
  const winLoseColor = pattern.win_or_lose === 'win' ? 'var(--qqq-sage, #7a9e82)'
    : pattern.win_or_lose === 'lose' ? 'var(--qqq-terra, #c46050)'
    : 'var(--qqq-fg-2)';
  const confColor = pattern.confidence === 'high' ? 'var(--qqq-sage)'
    : pattern.confidence === 'moderate' ? 'var(--qqq-gold)'
    : 'var(--qqq-fg-3)';
  return (
    <div style={{
      padding: '12px 16px',
      borderBottom: '1px solid var(--qqq-b1, #2a2a2a)',
      background: pattern.status === 'validated' ? 'rgba(122,158,130,.03)' : 'transparent',
    }}>
      {/* Headline */}
      <div style={{
        display: 'flex', gap: 10, alignItems: 'flex-start',
        marginBottom: 4,
      }}>
        <span style={{
          width: 4, alignSelf: 'stretch', minHeight: 36,
          background: winLoseColor, borderRadius: 2, flexShrink: 0,
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 600,
            color: 'var(--qqq-fg-1, #eee)',
            lineHeight: 1.45, marginBottom: 4,
          }}>
            {pattern.hypothesis}
          </div>
          <div style={{
            display: 'flex', gap: 12, fontSize: 11, flexWrap: 'wrap',
            color: 'var(--qqq-fg-3, #888)', textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>
            <span style={{ color: winLoseColor, fontWeight: 700 }}>{pattern.win_or_lose}</span>
            <span>{pattern.sample_size} samples</span>
            <span style={{ color: 'var(--qqq-fg-2)' }}>{pattern.hit_rate_pct}% hit rate</span>
            {pattern.lift && <span>{pattern.lift}× lift</span>}
            <span style={{ color: confColor }}>{pattern.confidence} conf</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          style={{
            background: 'transparent', border: 0,
            color: 'var(--qqq-fg-3)', fontSize: 11,
            cursor: 'pointer', padding: 0,
          }}
        >
          {expanded ? '▼' : '▶'}
        </button>
      </div>

      {/* Expanded: reasoning + triage */}
      {expanded && (
        <div style={{ marginLeft: 14, marginTop: 10 }}>
          {pattern.reasoning && (
            <div style={{
              fontSize: 12, color: 'var(--qqq-fg-2)',
              lineHeight: 1.55, marginBottom: 10, fontStyle: 'italic',
            }}>
              {pattern.reasoning}
            </div>
          )}
          {Array.isArray(pattern.evidence_dates) && pattern.evidence_dates.length > 0 && (
            <div style={{
              fontSize: 11, color: 'var(--qqq-fg-3)', marginBottom: 12,
            }}>
              Evidence dates: {pattern.evidence_dates.join(' · ')}
            </div>
          )}
          {/* Triage buttons */}
          <div style={{ display: 'flex', gap: 6 }}>
            {pattern.status !== 'validated' && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onTriage(pattern.id, 'validated')}
                style={{
                  padding: '6px 12px', fontSize: 11, fontWeight: 600,
                  background: 'rgba(122,158,130,.10)',
                  color: 'var(--qqq-sage)',
                  border: '1px solid var(--qqq-sage)',
                  borderRadius: 4, cursor: busy ? 'wait' : 'pointer',
                }}
              >
                ✓ Validate
              </button>
            )}
            {pattern.status !== 'monitoring' && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onTriage(pattern.id, 'monitoring')}
                style={{
                  padding: '6px 12px', fontSize: 11, fontWeight: 600,
                  background: 'rgba(200,169,110,.06)',
                  color: 'var(--qqq-gold)',
                  border: '1px solid var(--qqq-gold)',
                  borderRadius: 4, cursor: busy ? 'wait' : 'pointer',
                }}
              >
                ◌ Keep Monitoring
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => onTriage(pattern.id, 'noise')}
              style={{
                padding: '6px 12px', fontSize: 11, fontWeight: 600,
                background: 'transparent',
                color: 'var(--qqq-fg-2)',
                border: '1px solid var(--qqq-b1)',
                borderRadius: 4, cursor: busy ? 'wait' : 'pointer',
              }}
            >
              ✗ Noise
            </button>
          </div>
          <div style={{ fontSize: 10, color: 'var(--qqq-fg-3)', marginTop: 6 }}>
            {pattern.generation_model} · {fmtRelative(pattern.generated_at)}
          </div>
        </div>
      )}
    </div>
  );
}

function PatternEdgesCard() {
  const data = useQqqData();
  const edges = data._patternEdges;
  const labeled = edges?.sample_size ?? 0;

  // ─── State 1: Pending (can't even try yet) ────────────────
  if (!edges) {
    return (
      <div className="qqq-data-card" style={{
        borderStyle: 'dashed', opacity: 0.85, marginTop: 16,
        borderLeft: '3px dashed var(--qqq-purple, #9b8fc4)',
      }}>
        <div className="qqq-dc-hdr">
          <div className="qqq-dc-lbl" style={{ color: 'var(--qqq-purple, #9b8fc4)' }}>
            Pattern Engine · Cross-correlation
          </div>
          <div className="qqq-dc-src" style={{ color: 'var(--qqq-fg-3)' }}>
            requires ≥5 fully-labeled sessions
          </div>
        </div>
        <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          The cross-correlation engine mines combinations of morning
          forces × opening candle × gamma regime that predict the
          window outcome shape (trend / chop / fade / reversal /
          breakout). Activates once 5+ sessions have BOTH the
          opening candle AND the window outcome captured. Until then,
          single-feature patterns surface in the cards above.
        </div>
      </div>
    );
  }

  // ─── State 2: Empty (have data but no pattern crosses lift) ──
  if (!edges.patterns || edges.patterns.length === 0) {
    return (
      <div className="qqq-data-card" style={{
        marginTop: 16,
        borderLeft: '3px solid var(--qqq-purple, #9b8fc4)',
      }}>
        <div className="qqq-dc-hdr">
          <div className="qqq-dc-lbl" style={{ color: 'var(--qqq-purple, #9b8fc4)' }}>
            Pattern Engine · Cross-correlation
          </div>
          <div className="qqq-dc-src">
            {labeled} fully-labeled session{labeled === 1 ? '' : 's'} · no pattern with ≥1.3× lift yet
          </div>
        </div>
        <div style={{ padding: '14px 16px', color: 'var(--qqq-fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          {renderMarkers(edges.insight)}
        </div>
      </div>
    );
  }

  // ─── State 3: Live patterns ───────────────────────────────
  return (
    <DataCard
      label="Pattern Engine · Cross-correlation"
      source={`${labeled} fully-labeled session${labeled === 1 ? '' : 's'} · ${edges.patterns.length} edge${edges.patterns.length === 1 ? '' : 's'} surfaced`}
      tone="purple"
    >
      {edges.patterns.map((p, i) => {
        const dirTone = p.dominant_shape.includes('up') || p.dominant_shape === 'late_breakout_up' ? 'bull'
          : p.dominant_shape.includes('down') || p.dominant_shape === 'late_breakdown' ? 'bear'
          : p.dominant_shape === 'fade' || p.dominant_shape === 'inverted_v' ? 'bear'
          : p.dominant_shape === 'v_reversal_up' ? 'bull'
          : 'neutral';
        const confTone = p.confidence === 'high' ? 'hi'
          : p.confidence === 'moderate' ? 'mid'
          : 'lo';
        return (
          <div key={i} className="qqq-pattern-row">
            <div className="qqq-pr-name">
              <div className="qqq-pr-n">{p.name}</div>
              <div className="qqq-pr-m">
                → {prettifyShapeForUi(p.dominant_shape)} {p.dominant_pct}%
                <span style={{ color: 'var(--qqq-fg-3)', marginLeft: 8 }}>
                  · {p.lift}× over base rate
                </span>
              </div>
            </div>
            <div className="qqq-pr-sess">
              {p.n} sample{p.n === 1 ? '' : 's'}
            </div>
            <div className={`qqq-pr-rate ${confTone}`}>
              {p.confidence}
            </div>
          </div>
        );
      })}
      <div style={{
        fontSize: 13,
        color: 'var(--qqq-fg-1)',
        padding: '10px 16px',
        background: 'rgba(155,143,196,.04)',
        borderLeft: '2px solid var(--qqq-purple, #9b8fc4)',
        margin: '6px 0 0',
        lineHeight: 1.55,
      }}>
        {renderMarkers(edges.insight)}
      </div>
    </DataCard>
  );
}

function prettifyShapeForUi(s) {
  return ({ trend_up: 'Trend Up', trend_down: 'Trend Down',
            v_reversal_up: 'V-Reversal Up', inverted_v: 'Inverted V',
            late_breakout_up: 'Late Breakout', late_breakdown: 'Late Breakdown',
            fade: 'Fade', chop: 'Chop' }[s]) || s;
}
